<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, shallowRef, watch, watchEffect } from 'vue'
import { defaultSizeOption, findMatchingSizeOption } from '../../utils/sizeMatch'
import { v4 as uuidv4 } from 'uuid'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import {
  ArrowLeft,
  CheckSquare,
  XSquare,
  Copy,
  Plus,
  Monitor,
  Smartphone,
  QrCode,
  RefreshCcw,
  Trash2,
  SlidersHorizontal,
  ShoppingCart,
  ShoppingBag,
  Maximize2,
  RotateCw,
  Pencil,
  Upload,
  ChevronDown,
  AlertCircle,
  Scissors,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from 'lucide-vue-next'
import { offersDesigner, resolveEditorConfig } from '../../utils/editorConfigResolver'

import { readImageDimensions, createEditorPreview, createThumb, normalizeImageForUpload, isHeicBlob, isTiffBlob } from '../../utils/imageOps'
import { useImageDB, type ImageRecord } from '../../utils/imageDb'
import { deleteImageRecordsRemoteBulk, duplicateEditorUpload, isUploadInUseError, markRemoteDeleted, uploadImageRecordsBatch, wasRemoteDeleted, type BlockingDesign, type UploadInUseError } from '../../services/uploadService'
import UploadInUseDialog from './UploadInUseDialog.vue'
import { useProjectStore } from '../../store/useProjectStore'
import { useTransientUploadStore } from '../../store/useTransientUploadStore'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../services/editorCatalogApi'
import CanvasCropperLite from './CanvasCropperLite.vue'
import TileCropperCanvas from './TileCropperCanvas.vue'
import EditorImageTile from './EditorImageTile.vue'
import { invalidateProjectUploadsCache, mergeServerAndLocalImageRecord, OPEN_REMOTE_UPLOAD_STATUSES } from '../../services/projectUploadBridge'

import { useCartStore } from '../../store/cart'
import { useToast } from '../../composables/useToast'
import UploadBox from './utils/upload-box.vue'
import PostUploadOverlay from './PostUploadOverlay.vue'
import CartDrawer from '../CartDrawer.vue'
import ConfiguratorPreview from './configurator/preview.vue'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { BUILTIN_LIBRARY, loadPreview3dLibrary, mergePreview3dConfigs, resolvePreview3dState, type Preview3dLibrary } from '../../preview3d'
import { frame2dFromState, type Frame2d } from '../../preview3d/flat2d'
import FlatSelect, { type FlatSelectOption } from '../ui/FlatSelect.vue'
import QRCode from 'qrcode'
import { buildMobileUploadUrl, MOBILE_UPLOAD_QR_OPTIONS } from '../../utils/mobileUploadLink'
import { fetchProjectUploads, findServerDeletedRecords, mapRemoteUploadToRecordMeta, upsertProjectUploadsCacheItem } from '../../services/projectUploadBridge'
import { syncUploadMetaBatch } from '../../services/uploadMetaApi'
import { collectTags, hasTag, normalizeTags } from '../../utils/tags'
import { subscribeToUploadEvents } from '../../services/uploadEvents'
import { emitUploadEvent, onUploadEvent } from '../../utils/uploadBus'
import { importImageToDb } from '../../services/imageImportService'
import { fetchAdobeStockPreviewFile } from '../../services/stockImportService'
import { buildMaxCropForRecord, sizeAspectWithBleed } from '../../utils/cropPreset'
import { geometryHasGuides, normalizeGeometry } from '@pfe/design-renderer'
import { getUploadsRunner, scheduleUploadsRunnerTick } from '../../runtime/uploads-runner.client'

/* ------------------------------------------------
 * Responsive / Menus
 * ------------------------------------------------ */
const isMobile = ref(false)
const viewportW = ref(1280)
const isCompactHeader = computed(() => viewportW.value <= 1180)
const route = useRoute()
const router = useRouter()
const handledStockImportKeys = new Set<string>()
const stockImportBusy = ref(false)
const qualityTooltip = ref<{ text: string; x: number; y: number } | null>(null)
const catalogBootstrap = ref<EditorCatalogBootstrapResponse | null>(null)

async function loadCatalogBootstrap() {
  try {
    catalogBootstrap.value = await fetchEditorCatalogBootstrap({
      editorBaseUrl: getWidgetConfig().editorBaseUrl,
    })
  } catch {}
}

function updateIsMobile() {
  const w = window.innerWidth || 1024
  viewportW.value = w
  isMobile.value = w <= 820
}

async function backToProductSelection() {
  await router.push({
    path: '/',
    query: {
      category: String(props.categoryId),
      categoryId: String(props.categoryId),
    },
  })
}

/* ---------- Wechsel in den Designer (editorConfig.designAvailable) ---------- */
// Ziel: Produkt der Auswahl, sonst das aktuell geöffnete Produkt.
const designerTarget = computed<{ productId: number; categoryId: number } | null>(() => {
  const firstSelected = selectedIds.value.length
    ? images.value.find((img) => img.id === selectedIds.value[0]) || null
    : null
  const rec = (firstSelected?.record || null) as any
  const productId = Number(rec?.productId || rec?.edits?.preset?.productId || props.productId || 0)
  const categoryId = Number(rec?.categoryId || rec?.edits?.preset?.categoryId || props.categoryId || 0)
  if (!(productId > 0)) return null
  const product = catalogBootstrap.value?.products?.[String(productId)] ?? null
  if (!product) return null
  const config = resolveEditorConfig({ product: product as any, categoryId })
  return offersDesigner(config) ? { productId, categoryId } : null
})
const designerButtonTitle = computed(() => hasSelection.value
  ? 'Ausgewählte Bilder im Designer mit Vorlagen, Text und Collage gestalten'
  : 'Im Designer mit Vorlagen, Text und Collage gestalten')

async function openDesigner() {
  const target = designerTarget.value
  if (!target) return
  const projectCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()
  const imageIds = selectedIds.value.slice(0, 20)
  await router.push({
    path: '/design',
    query: {
      productId: String(target.productId),
      ...(target.categoryId > 0 ? { categoryId: String(target.categoryId) } : {}),
      ...(projectCode ? { projectCode } : {}),
      ...(imageIds.length ? { imageIds: imageIds.join(',') } : {}),
    },
  })
}

const tbMenuOpen = ref(false)
const mobileFiltersOpen = ref(false)
const selectionAnchorIndex = ref<number | null>(null)
function closeAllTopMenus() {
  tbMenuOpen.value = false
  mobileFiltersOpen.value = false
}
function toggleTbMenu() {
  const next = !tbMenuOpen.value
  closeAllTopMenus()
  tbMenuOpen.value = next
}
function toggleMobileFilters() {
  const next = !mobileFiltersOpen.value
  closeAllTopMenus()
  mobileFiltersOpen.value = next
}
function closeItemMenu() {
  return
}

function onDocPointerDown(e: PointerEvent) {
  const t = e.target as HTMLElement | null
  if (!t) return
  if (!t.closest('.dd')) closeAllTopMenus()
  if (!t.closest('.mfilters')) mobileFiltersOpen.value = false
}
function onKeydown(e: KeyboardEvent) {
  const lowerKey = String(e.key || '').toLowerCase()
  const wantsReload = e.key === 'F5' || ((e.metaKey || e.ctrlKey) && lowerKey === 'r')
  if (wantsReload && hasBlockingUploads.value) {
    e.preventDefault()
    openUploadLeaveGuard('reload')
    return
  }
  if (e.key === 'Escape') {
    closeAllTopMenus()
    closeItemMenu()
    qualityTooltip.value = null
  }
}

function openQualityTooltip(e: MouseEvent | FocusEvent, text: string) {
  const target = e.currentTarget as HTMLElement | null
  if (!target || !text) return
  const rect = target.getBoundingClientRect()
  qualityTooltip.value = {
    text,
    x: Math.max(12, rect.right - 180),
    y: Math.max(12, rect.top - 10),
  }
}

function moveQualityTooltip(e: MouseEvent) {
  if (!qualityTooltip.value) return
  const target = e.currentTarget as HTMLElement | null
  if (!target) return
  const rect = target.getBoundingClientRect()
  qualityTooltip.value = {
    ...qualityTooltip.value,
    x: Math.max(12, rect.right - 180),
    y: Math.max(12, rect.top - 10),
  }
}

function closeQualityTooltip() {
  qualityTooltip.value = null
}

function onViewportChanged() {
  qualityTooltip.value = null
}

let lastEmittedSelectionKey = ''
const pendingVmInsertIds = new Set<string>()
const pendingUploadPatchedEvents = new Map<string, any>()
let uploadPatchedFlushTimer: ReturnType<typeof setTimeout> | null = null
const UPLOAD_PATCH_FLUSH_MS = 48

function hasStableStoredCrop(crop: any): boolean {
  if (!crop || typeof crop !== 'object') return false
  const width = Math.max(0, Number((crop as any)?.w ?? (crop as any)?.width ?? 0) || 0)
  const height = Math.max(0, Number((crop as any)?.h ?? (crop as any)?.height ?? 0) || 0)
  if (width <= 1 || height <= 1) return false
  const sourceWidth = Math.max(0, Number((crop as any)?.sourceWidth ?? 0) || 0)
  const sourceHeight = Math.max(0, Number((crop as any)?.sourceHeight ?? 0) || 0)
  const normalizedWidth = Math.max(0, Number((crop as any)?.nw ?? 0) || 0)
  const normalizedHeight = Math.max(0, Number((crop as any)?.nh ?? 0) || 0)
  return (sourceWidth > 1 && sourceHeight > 1) || (normalizedWidth > 0 && normalizedHeight > 0)
}

function hasRemoteUploadAsset(rec: ImageRecord | any): boolean {
  const remoteUrl = String(rec?.upload?.remoteUrl || '').trim()
  const previewUrl = String(rec?.upload?.previewUrl || '').trim()
  const thumbUrl = String(rec?.upload?.thumbUrl || '').trim()
  const fullscreenPreviewUrl = String(rec?.upload?.fullscreenPreviewUrl || '').trim()
  return !!(remoteUrl || previewUrl || thumbUrl || fullscreenPreviewUrl)
}

function hasStoredImageDimensions(rec: ImageRecord | any): boolean {
  const editorWidth = Math.max(0, Number(rec?.editorWidth || 0) || 0)
  const editorHeight = Math.max(0, Number(rec?.editorHeight || 0) || 0)
  const width = Math.max(0, Number(rec?.width || 0) || 0)
  const height = Math.max(0, Number(rec?.height || 0) || 0)
  return (editorWidth > 1 && editorHeight > 1) || (width > 1 && height > 1)
}

function needsStableCropMeta(rec: ImageRecord | any): boolean {
  return !!((rec as any)?.edits?.preset?.size || null)
}

function hasStableUploadMeta(rec: ImageRecord | any): boolean {
  if (!hasStoredImageDimensions(rec)) return false
  if (!needsStableCropMeta(rec)) return true
  return hasStableStoredCrop((rec as any)?.edits?.crop)
}

function isRecordUploadEffectivelyDone(
  rec: ImageRecord | any,
  live?: { state?: string; phase?: string; progressPct?: number } | null | undefined,
): boolean {
  if (!rec) return false
  if (!hasRemoteUploadAsset(rec)) return false

  const state = String(live?.state || rec?.upload?.state || '').trim()
  const phase = String(live?.phase || rec?.upload?.phase || '').trim()
  const editorReadyAt = Number(rec?.upload?.editorReadyAt || 0)

  if (state === 'failed' || phase === 'failed') return false
  // Ein als fertig gespeicherter Datensatz schlägt einen liegengebliebenen
  // Live-Eintrag ('queued'/'uploading'), der erst beim Seiten-Reload verschwindet.
  if (String(rec?.upload?.state || '').trim() === 'done' || String(rec?.upload?.phase || '').trim() === 'done') return true
  if (state === 'done' || phase === 'done') return true
  if (Number.isFinite(editorReadyAt) && editorReadyAt > 0) return true

  if ((state === 'processing' || state === 'completing' || phase === 'processing' || phase === 'completing') && hasStableUploadMeta(rec)) {
    return true
  }

  return false
}

function applyUploadPatchedEvent(evt: any) {
  const id = String(evt?.id || '').trim()
  if (!id) return
  const uploadPatch = (((evt?.patch || {}) as any)?.upload || null) as any
  if (uploadPatch && typeof uploadPatch === 'object') {
    const prevLive = liveUploadState.value[id]
    const baseRecord = transientUploads.get(id) || sessionRecordsState.value[id] || images.value.find((x) => x.id === id)?.record || null
    const patchedRecord = mergeRecordPatch((baseRecord as any) || {}, (evt?.patch || {}) as any) as any
    let nextState = String(uploadPatch.state || prevLive?.state || 'queued')
    let nextPhase = String(uploadPatch.phase || prevLive?.phase || '')
    const nextProgressPct = Math.max(0, Math.min(100, Number(uploadPatch.progressPct ?? prevLive?.progressPct ?? 0) || 0))
    if (nextState !== 'failed' && nextPhase !== 'failed' && isRecordUploadEffectivelyDone(patchedRecord, { state: nextState, phase: nextPhase, progressPct: nextProgressPct })) {
      nextState = 'done'
      nextPhase = 'done'
    }
    trackedUploadIds.value = {
      ...trackedUploadIds.value,
      [id]: true,
    }
    liveUploadState.value = {
      ...liveUploadState.value,
      [id]: {
        state: nextState,
        phase: nextPhase,
        progressPct: nextState === 'done' ? 100 : nextProgressPct,
        uploadedBytes: Math.max(0, Number(uploadPatch.uploadedBytes ?? prevLive?.uploadedBytes ?? 0) || 0),
        totalBytes: Math.max(0, Number(uploadPatch.totalBytes ?? prevLive?.totalBytes ?? 0) || 0),
      },
    }
  }
  const transientCurrent = transientUploads.get(id)
  if (transientCurrent) {
    const transientMerged = mergeRecordPatch(transientCurrent as any, (evt?.patch || {}) as any) as any
    transientUploads.patch(id, transientMerged)
    rescheduleDeferredMetaSync(id, transientMerged as any)
  }
  const visualPatch = patchTouchesTileVisuals(evt?.patch || {})
  const img = images.value.find((x) => x.id === id)
  if (!img) {
    if (!visualPatch) return
    const rec = transientUploads.get(id) || sessionRecordsState.value[id] || null
    if (rec) {
      void (async () => {
        const vm = await hydrateVmFromRecord(rec)
        if (!vm) return
        if (pendingVmInsertIds.has(vm.id)) return
        pendingVmInsertIds.add(vm.id)
        if (images.value.some((x) => x.id === vm.id)) {
          pendingVmInsertIds.delete(vm.id)
          return
        }
        markRecentInsert(vm.id)
        images.value = [...images.value, vm]
        if (!canvasPrototypeId.value) canvasPrototypeId.value = vm.id
        vm.ui.appliedOnce = false
        pendingApply.add(vm.id)
        pendingVmInsertIds.delete(vm.id)
      })()
    }
    return
  }
  img.record = mergeRecordPatch((img.record as any) || {}, (evt?.patch || {}) as any) as any
  rescheduleDeferredMetaSync(id, img.record as any)
  if (!visualPatch) return
  const nextEditorW = Number((img.record as any)?.editorWidth || (img.record as any)?.width || 0) || 0
  const nextEditorH = Number((img.record as any)?.editorHeight || (img.record as any)?.height || 0) || 0
  if (nextEditorW > 0 && nextEditorH > 0) {
    img.ui.imgW = nextEditorW
    img.ui.imgH = nextEditorH
    img.ui.baseW = nextEditorW
    img.ui.baseH = nextEditorH
    const currentCrop = img.crop
    const storedCrop = (img.record as any)?.edits?.crop || null
    const needsInitialCrop = !hasStableStoredCrop(currentCrop) && !hasStableStoredCrop(storedCrop)
    if (needsInitialCrop) {
      const hadStoredCrop = hasStableStoredCrop(storedCrop)
      const presetSize = (img.record as any)?.edits?.preset?.size || null
      if (presetSize) {
        img.crop = buildMaxCropForRecord({
          ...(img.record as any),
          width: nextEditorW,
          height: nextEditorH,
          editorWidth: nextEditorW,
          editorHeight: nextEditorH,
        } as any, presetSize)
      } else {
        setMaxCrop(img, Number((currentCrop as any)?.rot || 0) || 0)
      }
      if (!hadStoredCrop && img.crop) {
        void persistCropNow(img.id, img.crop, img.crop.rot ?? 0)
      }
    }
  }
  syncCropMode(img, { persist: false, apply: false })
  applyVisualPatchToVm(img, evt?.patch || {})
  if (visualPatch && img.crop) {
    queueMicrotask(async () => {
      await applyCropToCropper(img, 'upload-patch-grid', cropperRefs.value.get(img.id), false)
      if (fs.open && fs.id === img.id) {
        await applyCropToCropper(img, 'upload-patch-fs', fsCropperRef.value, true)
      }
    })
  }
}

function flushPendingUploadPatchedEvents() {
  if (uploadPatchedFlushTimer) {
    clearTimeout(uploadPatchedFlushTimer)
    uploadPatchedFlushTimer = null
  }
  if (!pendingUploadPatchedEvents.size) return
  const entries = Array.from(pendingUploadPatchedEvents.values())
  pendingUploadPatchedEvents.clear()
  for (const evt of entries) applyUploadPatchedEvent(evt)
}

function scheduleUploadPatchedFlush() {
  if (uploadPatchedFlushTimer) return
  uploadPatchedFlushTimer = setTimeout(() => {
    flushPendingUploadPatchedEvents()
  }, UPLOAD_PATCH_FLUSH_MS)
}

function onExternalEditorImagesChanged(e: Event) {
  const detail = (e as CustomEvent<any>)?.detail || {}
  const ids = Array.isArray(detail?.ids) ? detail.ids.filter(Boolean) : []
  const action = String(detail?.action || 'updated')
  const patch = detail?.patch && typeof detail.patch === 'object' ? detail.patch : null
  if (action === 'inserted') {
    if (!ids.length) return
    void (async () => {
      for (const rawId of ids) {
        const id = String(rawId || '').trim()
        if (!id) continue
        if (images.value.some((img) => img.id === id)) continue
        if (pendingVmInsertIds.has(id)) continue
        pendingVmInsertIds.add(id)
        const rec = transientUploads.get(id) || sessionRecordsState.value[id] || await db.get(id)
        if (!rec) {
          pendingVmInsertIds.delete(id)
          continue
        }
        const vm = await hydrateVmFromRecord(rec)
        if (!vm) {
          pendingVmInsertIds.delete(id)
          continue
        }
        if (images.value.some((img) => img.id === vm.id)) {
          pendingVmInsertIds.delete(id)
          continue
        }
        markRecentInsert(vm.id)
        images.value = [...images.value, vm]
        if (!canvasPrototypeId.value) canvasPrototypeId.value = vm.id
        vm.ui.appliedOnce = false
        pendingApply.add(vm.id)
        pendingVmInsertIds.delete(id)
      }
    })()
    return
  }
  if (action === 'removed') {
    reload().then(() => {
      const alive = new Set(images.value.map((img) => img.id))
      const nextSelected = selectedIds.value.filter((id) => alive.has(id))
      if (nextSelected.length !== selectedIds.value.length) {
        selectedIds.value = nextSelected
        emitSelection()
      }
    }).catch(() => {})
    return
  }
  // Optionen aus der Sidebar sofort in die Kacheln übernehmen. Der Warenkorb
  // liest die Optionen aus vm.ui.options; ein nachgelagerter (oder wegen einer
  // Größen-Übernahme übersprungener) Reload käme zu spät und Set 01 statt
  // Set 02 o. ä. landete im Warenkorb.
  const optionsById = patch?.optionsById && typeof patch.optionsById === 'object' ? patch.optionsById : null
  if (optionsById) {
    for (const id of ids) {
      const vm = images.value.find((img) => img.id === id)
      const list = optionsById[id]
      if (!vm || !Array.isArray(list)) continue
      const prevFitInMode = isFitInMode(vm)
      const options: UIOption[] = list
        .map((o: any) => ({
          groupId: normId(o?.groupId ?? o?.id ?? ''),
          variantId: normId(o?.variantId ?? ''),
          value: String(o?.value ?? ''),
        }))
        .filter((o: UIOption) => !!o.groupId && !!o.value)
      vm.ui.options = options
      vm.record = {
        ...(vm.record as any),
        edits: {
          ...((vm.record as any)?.edits || {}),
          preset: {
            ...((((vm.record as any)?.edits || {}) as any)?.preset || {}),
            options,
          },
        },
      } as any
      // Wechsel Beschnitt <-> „Weißer Rand“ (isCrop) sofort zeichnen. Der folgende
      // Reload sieht keinen Moduswechsel mehr, weil die Optionen schon hier stehen.
      if (prevFitInMode !== isFitInMode(vm)) {
        if (prevFitInMode) setMaxCrop(vm, normRot(vm.crop?.rot ?? 0))
        syncCropMode(vm, { persist: false, apply: false })
        vm.ui.rev += 1
        vm.ui.appliedOnce = false
        pendingApply.add(vm.id)
        queueMicrotask(async () => {
          await applyCropToCropper(vm, 'options-fit-grid', cropperRefs.value.get(vm.id), false)
          if (fs.open && fs.id === vm.id) {
            await applyCropToCropper(vm, 'options-fit-fs', fsCropperRef.value, true)
          }
        })
      }
    }
  }
  // Lightweight, presentational patches (quantity + grayscale/S-W) applied
  // in-memory without a reload. Handle them together: onApplyConfig always sends
  // `quantity`, so a separate early-returning quantity branch would swallow a
  // grayscale change in the same apply and the tile would only turn S/W after a
  // full reload.
  if (patch && (patch.quantity != null || patch.grayscale != null)) {
    const qty = patch.quantity != null ? Math.max(1, Number(patch.quantity || 1)) : null
    const grayscale = patch.grayscale != null ? !!patch.grayscale : null
    for (const id of ids) {
      const vm = images.value.find((img) => img.id === id)
      if (!vm) continue
      if (qty != null) vm.ui.quantity = qty
      vm.record = {
        ...(vm.record as any),
        edits: {
          ...((vm.record as any)?.edits || {}),
          preset: {
            ...((((vm.record as any)?.edits || {}) as any)?.preset || {}),
            ...(qty != null ? { quantity: qty } : {}),
            ...(grayscale != null ? { grayscale } : {}),
          },
        },
      } as any
    }
    return
  }
  reload(ids).catch(() => {})
}

function emitEditorImagesChanged(detail?: Record<string, any>) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('pfe:editor-images-changed', { detail: detail || {} }))
}

onMounted(() => {
  project.loadFromStorage()
  loadCatalogBootstrap().catch(() => {})
  queueMicrotask(() => {
    loadImages().catch(() => {})
  })
  if (!project.projectCode.value) {
    project.ensureProject().then(() => {
      loadImages().catch(() => {})
      scheduleRefreshUploadStats(0)
    }).catch(() => {})
  }
  updateIsMobile()
  loadUploadQueuePrefs()
  scheduleRefreshUploadStats(0)
  uploadStatsPollTimer = setInterval(() => { refreshUploadStatsFromDb().catch(() => {}) }, 5000)
  window.addEventListener('resize', updateIsMobile, { passive: true })
  window.addEventListener('resize', onViewportChanged, { passive: true })
  window.addEventListener('scroll', onViewportChanged, { passive: true, capture: true })
  document.addEventListener('touchmove', onViewportChanged, { passive: true, capture: true })
  document.addEventListener('pointerdown', onDocPointerDown, { capture: true })
  document.addEventListener('keydown', onKeydown, { capture: true })
  window.addEventListener('beforeunload', onWindowBeforeUnload)
  window.addEventListener('pfe:editor-images-changed', onExternalEditorImagesChanged as EventListener)
  window.addEventListener('pfe:show-upload-queue', openUploadQueueFromDialog as EventListener)
  offUploadBus?.()
  offUploadBus = onUploadEvent((evt: any) => {
    if (evt?.type === 'upload:planned') {
      const incoming = Math.max(0, Number(evt?.total || 0))
      if (!incoming) return
      if (uploadStats.value.active <= 0 && uploadStats.value.failed <= 0) {
        plannedUploadTotal.value = 0
        liveUploadState.value = {}
        trackedUploadIds.value = {}
      }
      plannedUploadTotal.value = Math.max(uploadStats.value.total, plannedUploadTotal.value + incoming)
      return
    }
    if (evt?.type === 'upload:planned-adjust') {
      const delta = Number(evt?.delta || 0)
      if (!Number.isFinite(delta) || delta === 0) return
      plannedUploadTotal.value = Math.max(uploadStats.value.total, plannedUploadTotal.value + delta)
      return
    }
    if (evt?.type === 'upload:preparing') {
      const delta = Number(evt?.delta || 0)
      if (!Number.isFinite(delta) || delta === 0) return
      preparingUploadCount.value = Math.max(0, preparingUploadCount.value + delta)
      return
    }
    if (evt?.type !== 'upload:patched') return
    const id = String(evt?.id || '').trim()
    if (!id) return
    const prev = pendingUploadPatchedEvents.get(id)
    pendingUploadPatchedEvents.set(
      id,
      prev
        ? { ...prev, patch: mergeRecordPatch(prev.patch || {}, evt?.patch || {}) }
        : evt,
    )
    scheduleUploadPatchedFlush()
  })
  tilePreviewObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement
      const id = String(el.dataset.imageId || '').trim()
      if (!id) continue
      const img = images.value.find((x) => x.id === id)
      if (!img) continue

      if (entry.isIntersecting) {
        visibleTileIds.add(id)
        retainTileVisibility(id)
        if (!(fs.open && fs.id === img.id)) {
          ensureObjectUrlsForImage(img)
        }
        if (!isFitInMode(img) && fs.open && fs.id === img.id) {
          ensureObjectUrlsForImage(img, { full: true })
        }
        scheduleHeavyTileSync()
        continue
      }

      visibleTileIds.delete(id)
      retainTileVisibility(id)
      scheduleHeavyTileSync()
    }
  }, {
    root: null,
    rootMargin: '120px 0px',
    threshold: 0.01,
  })
})

onBeforeUnmount(() => {
  if (uploadStatsPollTimer) clearInterval(uploadStatsPollTimer)
  if (uploadStatsRefreshTimer) clearTimeout(uploadStatsRefreshTimer)
  for (const timer of duplicateRemotePollTimers.values()) clearInterval(timer)
  duplicateRemotePollTimers.clear()
  if (processingUploadsPollTimer) clearTimeout(processingUploadsPollTimer)
  if (uploadPatchedFlushTimer) clearTimeout(uploadPatchedFlushTimer)
  uploadPatchedFlushTimer = null
  pendingUploadPatchedEvents.clear()
  stopUploadQueueDrag()
  window.removeEventListener('resize', updateIsMobile as any)
  window.removeEventListener('resize', onViewportChanged as any)
  window.removeEventListener('scroll', onViewportChanged as any, { capture: true } as any)
  document.removeEventListener('touchmove', onViewportChanged as any, { capture: true } as any)
  document.removeEventListener('pointerdown', onDocPointerDown as any, { capture: true } as any)
  document.removeEventListener('keydown', onKeydown as any, { capture: true } as any)
  window.removeEventListener('beforeunload', onWindowBeforeUnload)
  window.removeEventListener('pfe:editor-images-changed', onExternalEditorImagesChanged as EventListener)
  window.removeEventListener('pfe:show-upload-queue', openUploadQueueFromDialog as EventListener)
  offUploadBus?.()
  offUploadBus = null
  stopRealtimeUploadSubscription()
  tilePreviewObserver?.disconnect()
  tilePreviewObserver = null
  tileElMap.clear()
  visibleTileIds.clear()
  liveTileCropperIds.value = {}
  passiveTileCropperIds.value = {}
  primaryLiveTileId.value = ''
  for (const frame of scheduledApplyCropFrames.values()) cancelAnimationFrame(frame)
  scheduledApplyCropFrames.clear()
  for (const frame of scheduledCropChangeFrames.values()) cancelAnimationFrame(frame)
  scheduledCropChangeFrames.clear()
  scheduledCropChangePayloads.clear()
  for (const timer of idleReleaseTimers.values()) clearTimeout(timer)
  idleReleaseTimers.clear()
  recentlyActiveIds.clear()
  if (emitSelectionTimer) clearTimeout(emitSelectionTimer)
  if (uploadMetaSyncTimer) clearTimeout(uploadMetaSyncTimer)
  if (pendingCartReconcileTimer) clearTimeout(pendingCartReconcileTimer)
})

/* ------------------------------------------------
 * Cart
 * ------------------------------------------------ */
const cart = useCartStore()
const toast = useToast()
const cartOpen = ref(false)
const deleteBusy = ref(false)
const deleteBusyCount = ref(0)
const cartUrl = computed(() => String((getWidgetConfig() as any)?.cartUrl || '').trim())
let offUploadBus: null | (() => void) = null
const cartInfoModal = ref<{ title: string; body: string; progress: number; done: number; total: number } | null>(null)
const uploadPreparingModal = ref<{ title: string; body: string } | null>(null)
const cartSyncModal = ref<{ title: string; body: string } | null>(null)
const uploadLeaveGuardModal = ref<null | { kind: 'reload' | 'navigate'; target?: string }>(null)
let allowUploadLeaveOnce = false
const cartSyncBusy = computed(() => !!cartSyncModal.value)

function goToCart() {
  if (!cartUrl.value) return
  let navigated = false
  const commit = () => {
    if (navigated) return
    navigated = true
    window.location.href = cartUrl.value
  }
  window.setTimeout(commit, 180)
  void cart.primeCheckoutSession().catch(() => {}).finally(commit)
}

const hasBlockingUploads = computed(() => {
  const stats = uploadStats.value
  return stats.preparing > 0 || stats.active > 0
})

function onWindowBeforeUnload(e: BeforeUnloadEvent) {
  if (allowUploadLeaveOnce || !hasBlockingUploads.value) return
  e.preventDefault()
  e.returnValue = ''
}

function openUploadLeaveGuard(kind: 'reload' | 'navigate', target?: string) {
  uploadLeaveGuardModal.value = { kind, ...(target ? { target } : {}) }
}

function cancelUploadLeaveGuard() {
  uploadLeaveGuardModal.value = null
}

async function confirmUploadLeaveGuard() {
  const modal = uploadLeaveGuardModal.value
  uploadLeaveGuardModal.value = null
  if (!modal) return
  allowUploadLeaveOnce = true
  try {
    if (modal.kind === 'reload') {
      window.location.reload()
      return
    }
    if (modal.target) await router.push(modal.target)
  } finally {
    if (modal.kind !== 'reload') {
      setTimeout(() => {
        allowUploadLeaveOnce = false
      }, 0)
    }
  }
}

onBeforeRouteLeave((to) => {
  if (allowUploadLeaveOnce) {
    allowUploadLeaveOnce = false
    return true
  }
  if (!hasBlockingUploads.value) return true
  openUploadLeaveGuard('navigate', to.fullPath)
  return false
})

function isRecordUploadDone(rec: ImageRecord | any): boolean {
  return isRecordUploadEffectivelyDone(rec)
}

function isRecordCartBlocked(rec: ImageRecord | any): boolean {
  if (isRecordUploadDone(rec)) return false
  const state = String(rec?.upload?.state || '').trim()
  const phase = String(rec?.upload?.phase || '').trim()
  return state === 'queued' || state === 'uploading' || phase === 'queued' || phase === 'presigning' || phase === 'uploading'
}

function isImageCartReady(img: ImageVM | null | undefined): boolean {
  if (!img) return false
  if (isRecordUploadDone(img.record)) return true
  const id = String(img.id || '').trim()
  const live = id ? liveUploadState.value[id] : null
  const liveState = String(live?.state || '').trim()
  const livePhase = String(live?.phase || '').trim()
  if (isUploadVisualState(liveState, livePhase)) return false
  if (isRecordCartBlocked(img.record)) return false
  return false
}

function showCartUploadInfo(scope: 'selection' | 'all' | 'single' = 'all') {
  const stats = uploadStats?.value ?? { queued: 0, uploading: 0, done: 0, total: 0, progress: 0 }
  const active = Number((stats as any).uploading ?? 0)
  const queued = Number((stats as any).queued ?? 0)
  const done = Number((stats as any).done ?? 0)
  const total = Number((stats as any).total ?? 0)
  const intro =
    scope === 'selection'
      ? 'Die ausgewählten Bilder sind noch nicht vollständig hochgeladen.'
      : scope === 'single'
        ? 'Dieses Bild ist noch nicht vollständig hochgeladen.'
        : 'Noch nicht alle Bilder sind vollständig hochgeladen.'
  cartInfoModal.value = {
    title: 'Warenkorb noch nicht verfügbar',
    body: total > 0
      ? `${intro} Sie können lokal weiter bearbeiten und alle Änderungen bleiben erhalten. Der Warenkorb wird freigegeben, sobald der Upload abgeschlossen ist.\n\nAktueller Stand: ${done} von ${total} Bildern abgeschlossen, ${active} aktiv, ${queued} in Queue.`
      : `${intro} Sie können lokal weiter bearbeiten und alle Änderungen bleiben erhalten. Der Warenkorb wird freigegeben, sobald der Upload abgeschlossen ist.\n\nDer Uploadstatus wird gerade vorbereitet.`,
    progress: Number((stats as any).progress ?? 0),
    done,
    total,
  }
}

function openUploadQueueFromDialog() {
  uploadQueueMinimized.value = false
  persistUploadQueuePrefs()
  cartInfoModal.value = null
}

function cartInfoStatusLabel(modal: { done?: number; total?: number } | null | undefined): string {
  const total = Number(modal?.total ?? 0)
  const done = Number(modal?.done ?? 0)
  if (total <= 0) return 'Uploadstatus wird vorbereitet'
  return `${done} / ${total}`
}

function cartInfoProgressWidth(modal: { progress?: number } | null | undefined): string {
  const progress = Math.max(0, Math.min(100, Number(modal?.progress ?? 0)))
  return `${progress}%`
}

function cartInfoBodyLines(modal: { body?: string } | null | undefined): string[] {
  const body = String(modal?.body ?? '').trim()
  return body ? body.split('\n\n') : []
}

function openCartSyncModal(scope: 'selection' | 'all' | 'single', count: number) {
  const safeCount = Math.max(1, Number(count || 0))
  const noun = safeCount === 1 ? 'Produkt' : 'Produkte'
  const body =
    scope === 'single'
      ? 'Die Konfiguration wird vorbereitet und in den Warenkorb übertragen.'
      : `Die Konfiguration von ${safeCount} Bildern wird vorbereitet und in den Warenkorb übertragen.`
  cartSyncModal.value = {
    title: `${noun} werden in den Warenkorb gelegt`,
    body,
  }
}

function closeCartSyncModal() {
  cartSyncModal.value = null
}

/* ------------------------------------------------
 * Types
 * ------------------------------------------------ */
type Money = number | { amount: number; currency?: string }
type SizeOpt = {
  id?: string
  title: string
  widthMm?: number
  heightMm?: number
  price?: Money
  priceCents?: number
  variantId?: string
  featured?: boolean
  /** Produktgeometrie aus Bootstrap/GraphQL (Form, Beschnitt, Sicherheitsbereich) */
  geometry?: any
}

type PxCrop = { x: number; y: number; w: number; h: number; rot?: number; nx?: number; ny?: number; nw?: number; nh?: number; sourceWidth?: number; sourceHeight?: number }
type UIOption = { groupId: string; variantId: string; value: string }
type UIState = {
  previewUrl: string
  fullPreviewUrl: string
  previewBlobRef?: Blob | null
  fullBlobRef?: Blob | null
  remotePreviewKey?: string | null
  previewLoadKey?: string | null
  previewLoading: boolean
  imgW: number
  imgH: number
  baseW: number
  baseH: number
  ready: boolean
  applying: boolean
  armedGrid: boolean
  armedFs: boolean
  lockAR: boolean
  swapAR: boolean
  orientMode: 'auto' | 'manual'
  appliedOnce: boolean
  quantity: number
  sizeKey: string
  sizeTitleHint: string
  options: UIOption[]
  /** true, wenn dieses Bild NICHT zum aktuell gewählten Produkt gehört */
  locked: boolean
  lockedProductId: number | null
  lockedProductTitle: string
  /** bump, um selektiv Tile/Cropper zu remounten (ohne komplette Liste zu resetten) */
  rev: number
}
type ImageVM = {
  id: string
  name: string
  createdAt: number
  record: ImageRecord
  crop: PxCrop | null
  ui: UIState
}

function editorDimsForRecord(rec: ImageRecord | any) {
  const crop = ((rec as any)?.edits?.crop || {}) as any
  const w = Number(crop?.sourceWidth || (rec as any)?.editorWidth || (rec as any)?.width || (rec as any)?.thumbWidth || 1)
  const h = Number(crop?.sourceHeight || (rec as any)?.editorHeight || (rec as any)?.height || (rec as any)?.thumbHeight || 1)
  return { width: Math.max(1, w), height: Math.max(1, h) }
}

function resolvedEditorDimsForRecord(
  rec: ImageRecord | any,
  fallback?: { width?: number; height?: number } | null,
) {
  const editorDims = editorDimsForRecord(rec)
  if (editorDims.width > 1 && editorDims.height > 1) return editorDims
  const width = Math.max(1, Number(fallback?.width || (rec as any)?.width || (rec as any)?.thumbWidth || editorDims.width || 1))
  const height = Math.max(1, Number(fallback?.height || (rec as any)?.height || (rec as any)?.thumbHeight || editorDims.height || 1))
  return { width, height }
}

function mainDimsForRecord(rec: ImageRecord | any) {
  const crop = ((rec as any)?.edits?.crop || {}) as any
  const w = Number((rec as any)?.width || (rec as any)?.editorWidth || crop?.sourceWidth || (rec as any)?.thumbWidth || 1)
  const h = Number((rec as any)?.height || (rec as any)?.editorHeight || crop?.sourceHeight || (rec as any)?.thumbHeight || 1)
  return { width: Math.max(1, w), height: Math.max(1, h) }
}

function cropToNormalized(crop: PxCrop | null | undefined, sourceWidth: number, sourceHeight: number) {
  if (!crop) return null
  const W = Math.max(1, Number(sourceWidth || 1))
  const H = Math.max(1, Number(sourceHeight || 1))
  const hasPixelCrop =
    [crop.x, crop.y, crop.w, crop.h].every((value) => Number.isFinite(Number(value)))
  const nx = Math.max(0, Math.min(1, Number(hasPixelCrop ? (crop.x / W) : crop.nx) || 0))
  const ny = Math.max(0, Math.min(1, Number(hasPixelCrop ? (crop.y / H) : crop.ny) || 0))
  const nw = Math.max(0, Math.min(1, Number(hasPixelCrop ? (crop.w / W) : crop.nw) || 0))
  const nh = Math.max(0, Math.min(1, Number(hasPixelCrop ? (crop.h / H) : crop.nh) || 0))
  return { nx, ny, nw, nh, sourceWidth: W, sourceHeight: H }
}

function cropFromStored(rec: ImageRecord | any, dims: { width: number; height: number }): PxCrop | null {
  const stored: any = rec?.edits?.crop
  if (!stored) return null
  const width = Math.max(1, Number(dims.width || 1))
  const height = Math.max(1, Number(dims.height || 1))
  const absX = Number(stored?.x)
  const absY = Number(stored?.y)
  const absW = Number(stored?.w)
  const absH = Number(stored?.h)
  const storedSourceWidth = Math.max(1, Number(stored?.sourceWidth || width))
  const storedSourceHeight = Math.max(1, Number(stored?.sourceHeight || height))

  // Prefer exact absolute crop data when it already matches the current editor image size.
  // This keeps existing records stable and avoids rehydrating from stale normalized values.
  if (
    [absX, absY, absW, absH].every((v) => Number.isFinite(v)) &&
    Math.abs(storedSourceWidth - width) <= 1 &&
    Math.abs(storedSourceHeight - height) <= 1
  ) {
    return {
      x: Math.round(absX),
      y: Math.round(absY),
      w: Math.max(2, Math.round(absW)),
      h: Math.max(2, Math.round(absH)),
      rot: normRot((rec?.edits as any)?.rotate ?? 0),
      ...((cropToNormalized({
        x: absX,
        y: absY,
        w: absW,
        h: absH,
      }, width, height)) || {}),
      sourceWidth: width,
      sourceHeight: height,
    }
  }

  const nx = Number(stored?.nx)
  const ny = Number(stored?.ny)
  const nw = Number(stored?.nw)
  const nh = Number(stored?.nh)
  if ([nx, ny, nw, nh].every((v) => Number.isFinite(v) && v >= 0)) {
    const clampedNx = Math.max(0, Math.min(1, nx))
    const clampedNy = Math.max(0, Math.min(1, ny))
    const clampedNw = Math.max(0, Math.min(1, nw))
    const clampedNh = Math.max(0, Math.min(1, nh))
    const clamped = clampCoordsToBox(
      {
        left: Math.round(clampedNx * width),
        top: Math.round(clampedNy * height),
        width: Math.max(2, Math.round(clampedNw * width)),
        height: Math.max(2, Math.round(clampedNh * height)),
      },
      width,
      height,
    )
    return {
      x: clamped.left,
      y: clamped.top,
      w: clamped.width,
      h: clamped.height,
      rot: normRot((rec?.edits as any)?.rotate ?? 0),
      ...(cropToNormalized({
        x: clamped.left,
        y: clamped.top,
        w: clamped.width,
        h: clamped.height,
      }, width, height) || {}),
      sourceWidth: width,
      sourceHeight: height,
    }
  }

  const sourceWidth = Math.max(1, Number(stored?.sourceWidth || rec?.width || width))
  const sourceHeight = Math.max(1, Number(stored?.sourceHeight || rec?.height || height))
  const scaleX = width / sourceWidth
  const scaleY = height / sourceHeight
  const clamped = clampCoordsToBox(
    {
      left: Math.round((Number(stored?.x || 0) || 0) * scaleX),
      top: Math.round((Number(stored?.y || 0) || 0) * scaleY),
      width: Math.max(2, Math.round((Number(stored?.w || 2) || 2) * scaleX)),
      height: Math.max(2, Math.round((Number(stored?.h || 2) || 2) * scaleY)),
    },
    width,
    height,
  )
  return {
    x: clamped.left,
    y: clamped.top,
    w: clamped.width,
    h: clamped.height,
    rot: normRot((rec?.edits as any)?.rotate ?? 0),
    ...((cropToNormalized({
      x: clamped.left,
      y: clamped.top,
      w: clamped.width,
      h: clamped.height,
    }, width, height)) || {}),
    sourceWidth: width,
    sourceHeight: height,
  }
}

function isClearlyInvalidTinyCrop(crop: PxCrop | null | undefined): boolean {
  if (!crop) return false
  const sourceWidth = Math.max(1, Number(crop.sourceWidth || 1))
  const sourceHeight = Math.max(1, Number(crop.sourceHeight || 1))
  const w = Math.max(0, Number(crop.w || 0))
  const h = Math.max(0, Number(crop.h || 0))
  const x = Math.max(0, Number(crop.x || 0))
  const y = Math.max(0, Number(crop.y || 0))
  if (sourceWidth <= 1 || sourceHeight <= 1) return true
  if (w <= 4 || h <= 4) return true
  const areaRatio = (w * h) / (sourceWidth * sourceHeight)
  const widthRatio = w / sourceWidth
  const heightRatio = h / sourceHeight
  const nearTopLeft = x <= Math.max(8, sourceWidth * 0.03) && y <= Math.max(8, sourceHeight * 0.03)
  if (nearTopLeft && areaRatio > 0 && areaRatio <= 0.005 && widthRatio <= 0.08 && heightRatio <= 0.08) {
    return true
  }
  return false
}

function objectUrlForBlob(blob?: Blob | null) {
  return blob ? URL.createObjectURL(blob) : ''
}

function remotePreviewUrl(rec: ImageRecord | any): string {
  return String((rec as any)?.upload?.previewUrl || (rec as any)?.upload?.thumbUrl || '').trim()
}

function remoteFullUrl(rec: ImageRecord | any): string {
  return String((rec as any)?.upload?.fullscreenPreviewUrl || (rec as any)?.upload?.remoteUrl || remotePreviewUrl(rec) || '').trim()
}

function remoteTileUrl(rec: ImageRecord | any): string {
  return String(remotePreviewUrl(rec) || remoteFullUrl(rec) || '').trim()
}

function shouldHoldUploadSkeleton(rec: ImageRecord | any): boolean {
  const state = String((rec as any)?.upload?.state || '').trim()
  const phase = String((rec as any)?.upload?.phase || '').trim()
  if (remoteTileUrl(rec)) return false
  return state === 'queued' || state === 'uploading' || state === 'processing' || phase === 'queued' || phase === 'presigning' || phase === 'uploading' || phase === 'completing' || phase === 'processing'
}

function isUploadVisualState(state: string, phase: string): boolean {
  return state === 'queued' || state === 'uploading' || phase === 'queued' || phase === 'presigning' || phase === 'uploading'
}

function shouldShowUploadingOverlay(img: ImageVM | null | undefined): boolean {
  const id = String(img?.id || '').trim()
  const live = id ? liveUploadState.value[id] : null
  const rec = (img?.record || {}) as any
  // A record that already has a finished remote asset (editorReadyAt / remoteUrl)
  // is done, even if a stale live upload entry still lingers in liveUploadState.
  // Without this guard a leftover 'queued'/'uploading' live state kept the
  // "Wird hochgeladen" overlay stuck after e.g. a multi-select S/W apply until a
  // full page reload wiped liveUploadState. Mirrors the guard in uploadIssueKind.
  if (isRecordUploadEffectivelyDone(rec, live)) return false
  const state = String(live?.state || rec?.upload?.state || '').trim()
  const phase = String(live?.phase || rec?.upload?.phase || '').trim()
  if (state === 'done' || state === 'failed') return false
  if (phase === 'done' || phase === 'failed') return false
  return isUploadVisualState(state, phase)
}

function uploadIssueKind(img: ImageVM | null | undefined): 'processing' | 'failed' | null {
  if (!img) return null
  const id = String(img?.id || '').trim()
  const live = id ? liveUploadState.value[id] : null
  const rec = (img?.record || {}) as any
  const state = String(live?.state || rec?.upload?.state || '').trim()
  const phase = String(live?.phase || rec?.upload?.phase || '').trim()
  if (state === 'failed' || phase === 'failed') return 'failed'
  if (isRecordUploadEffectivelyDone(rec, live)) return null
  if (state === 'processing' || phase === 'processing' || state === 'queued' || state === 'uploading' || phase === 'queued' || phase === 'presigning' || phase === 'uploading' || phase === 'completing') {
    return 'processing'
  }
  return null
}

function uploadIssueLabel(img: ImageVM | null | undefined): string {
  const kind = uploadIssueKind(img)
  if (kind === 'failed') return 'Upload fehlgeschlagen'
  if (kind === 'processing') return 'In Verarbeitung'
  return ''
}

function hasImmediateTilePreview(rec: ImageRecord | any): boolean {
  return !!(tilePreviewBlob(rec) || heavyPreviewBlob(rec) || remoteTileUrl(rec))
}

function shouldShowUploadSkeleton(rec: ImageRecord | any): boolean {
  void rec
  return false
}

function shouldDeferMetaSync(rec: ImageRecord | null | undefined): boolean {
  if (!rec) return true
  if (isRecordUploadEffectivelyDone(rec)) return false
  const state = String((rec as any)?.upload?.state || '').trim()
  const phase = String((rec as any)?.upload?.phase || '').trim()
  return isUploadVisualState(state, phase)
}

function isUploadSettled(rec: ImageRecord | any): boolean {
  if (isRecordUploadEffectivelyDone(rec)) return true
  const state = String((rec as any)?.upload?.state || '').trim()
  return state === 'failed'
}

function canRunMetaSyncNow(rec: ImageRecord | null | undefined): boolean {
  return !!rec && !shouldDeferMetaSync(rec)
}

function patchTouchesTileVisuals(patch: any): boolean {
  if (!patch || typeof patch !== 'object') return false
  if (
    patch.width !== undefined ||
    patch.height !== undefined ||
    patch.editorWidth !== undefined ||
    patch.editorHeight !== undefined ||
    patch.originalBlob !== undefined ||
    patch.workingBlob !== undefined ||
    patch.editorBlob !== undefined ||
    patch.thumbBlob !== undefined ||
    patch.edits !== undefined
  ) return true

  const upload = patch.upload
  if (!upload || typeof upload !== 'object') return false
  if (upload.remoteUrl || upload.previewUrl || upload.thumbUrl) return true
  const state = String(upload.state || '').trim()
  const phase = String(upload.phase || '').trim()
  return state === 'done' || state === 'failed' || phase === 'done' || phase === 'failed'
}

function applyVisualPatchToVm(img: ImageVM | undefined, patch: any) {
  if (!img || !patch || typeof patch !== 'object') return
  const currentRecord: any = (img.record && typeof img.record === 'object') ? img.record : {}
  const nextRecord: any = {
    ...currentRecord,
    ...patch,
    ...(patch.upload !== undefined ? { upload: { ...(currentRecord.upload || {}), ...(patch.upload || {}) } } : {}),
    ...(patch.edits !== undefined ? { edits: { ...(currentRecord.edits || {}), ...(patch.edits || {}) } } : {}),
  }

  img.record = nextRecord

  const dims = editorDimsForRecord(nextRecord)
  img.ui.imgW = dims.width
  img.ui.imgH = dims.height
  img.ui.baseW = dims.width
  img.ui.baseH = dims.height

  const preset = ((nextRecord as any)?.edits?.preset || {}) as any
  const options: UIOption[] = Array.isArray(preset?.options)
    ? preset.options
        .map((o: any) => ({
          groupId: normId(o?.groupId ?? o?.id ?? ''),
          variantId: normId(o?.variantId ?? ''),
          value: String(o?.value ?? ''),
        }))
        .filter((o: UIOption) => !!o.groupId && !!o.value)
    : []
  img.ui.options = options
  img.ui.quantity = Math.max(1, Number(preset?.quantity ?? img.ui.quantity ?? 1))

  const storedCrop = cropFromStored(nextRecord, dims)
  if (storedCrop) img.crop = storedCrop
  else if (!img.crop) setMaxCrop(img, 0)

  const inferredPresetSize = preset?.size || resolveSizeFromVariantOptions(options) || null
  const resolvedSize = resolveSizeUiState(
    normalizedSizesForImg(img),
    inferredPresetSize,
    String(inferredPresetSize?.variantId || inferredPresetSize?.id || inferredPresetSize?.title || img.ui.sizeKey || ''),
    String(inferredPresetSize?.title || img.ui.sizeTitleHint || ''),
    !!img.ui.locked,
  )
  img.ui.sizeKey = resolvedSize.sizeKey
  img.ui.sizeTitleHint = resolvedSize.sizeTitleHint

  syncCropMode(img, { persist: false, apply: false })
  if (patchTouchesTileVisuals(patch)) {
    ensureObjectUrlsForImage(img)
    if (!shouldShowUploadSkeleton(nextRecord)) img.ui.previewLoading = false
    img.ui.rev += 1
  }
}

function revokeLocalObjectUrl(url?: string | null) {
  const value = String(url || '').trim()
  if (!value || !value.startsWith('blob:')) return
  try { URL.revokeObjectURL(value) } catch {}
}

function tilePreviewBlob(rec: ImageRecord | any): Blob | null {
  return ((rec as any)?.thumbBlob || (rec as any)?.editorBlob || null) as Blob | null
}

function fullPreviewBlob(rec: ImageRecord | any): Blob | null {
  return ((rec as any)?.editorBlob || (rec as any)?.thumbBlob || null) as Blob | null
}

function heavyPreviewBlob(rec: ImageRecord | any): Blob | null {
  return ((rec as any)?.workingBlob || (rec as any)?.originalBlob || (rec as any)?.editorBlob || (rec as any)?.thumbBlob || null) as Blob | null
}

function isUnsupportedDirectPreviewBlob(blob: Blob | null | undefined) {
  if (!blob) return false
  return isHeicBlob(blob) || isTiffBlob(blob)
}

const workingBlobLoads = new Map<string, Promise<void>>()
const tileElMap = new Map<string, HTMLElement>()
let revealSelectedTimer: ReturnType<typeof setTimeout> | null = null
const visibleTileIds = new Set<string>()
const retainedVisibleTileIds = new Set<string>()
const activatedTileCropperIds = new Set<string>()
const recentlyActiveIds = new Set<string>()
const idleReleaseTimers = new Map<string, ReturnType<typeof setTimeout>>()
const tileVisibilityReleaseTimers = new Map<string, ReturnType<typeof setTimeout>>()
let tilePreviewObserver: IntersectionObserver | null = null
const IDLE_RELEASE_MS = 8000
const TILE_VISIBILITY_GRACE_MS = 1200

function scheduleTilePreviewReady(img: ImageVM | null | undefined, srcRaw?: string | null) {
  if (!img) return
  const src = String(srcRaw || '').trim()
  if (!src) {
    img.ui.previewLoadKey = null
    img.ui.previewLoading = false
    return
  }
  if (img.ui.previewLoadKey === src && !img.ui.previewLoading) return
  img.ui.previewLoadKey = src
  img.ui.previewLoading = true

  const preload = new Image()
  preload.decoding = 'async'
  const done = () => {
    if (img.ui.previewLoadKey !== src) return
    img.ui.previewLoading = false
  }
  preload.onload = done
  preload.onerror = done
  preload.src = src
  if (preload.complete) done()
}

function ensureObjectUrlsForImage(img: ImageVM | null | undefined, opts?: { full?: boolean; preferHeavy?: boolean }) {
  if (!img) return
  const holdUpload = shouldHoldUploadSkeleton(img.record)
  const localTileBlob = tilePreviewBlob(img.record)
  const heavyBlob = holdUpload ? heavyPreviewBlob(img.record) : null
  const previewBlob = localTileBlob || (!isUnsupportedDirectPreviewBlob(heavyBlob) ? heavyBlob : null)
  const remoteUrl = remoteTileUrl(img.record)

  if (previewBlob && (!img.ui.previewUrl || img.ui.previewBlobRef !== previewBlob)) {
    revokeLocalObjectUrl(img.ui.previewUrl)
    img.ui.previewUrl = objectUrlForBlob(previewBlob)
    img.ui.previewBlobRef = previewBlob
    scheduleTilePreviewReady(img, img.ui.previewUrl)
  } else if (!previewBlob) {
    if (remoteUrl && (img.ui.previewBlobRef || !img.ui.previewUrl || img.ui.previewUrl.startsWith('blob:'))) {
      revokeLocalObjectUrl(img.ui.previewUrl)
      img.ui.previewUrl = remoteUrl
      img.ui.previewBlobRef = null
      scheduleTilePreviewReady(img, img.ui.previewUrl)
    }
  } else if (img.ui.previewUrl) {
    scheduleTilePreviewReady(img, img.ui.previewUrl)
  }

  if (!previewBlob && !remoteUrl) {
    img.ui.previewLoadKey = null
    img.ui.previewLoading = holdUpload
  }

  if (opts?.full) {
    const fullBlob = opts?.preferHeavy ? heavyPreviewBlob(img.record) : fullPreviewBlob(img.record)
    if (fullBlob && (!img.ui.fullPreviewUrl || img.ui.fullBlobRef !== fullBlob)) {
      revokeLocalObjectUrl(img.ui.fullPreviewUrl)
      img.ui.fullPreviewUrl = objectUrlForBlob(fullBlob)
      img.ui.fullBlobRef = fullBlob
    } else if (!fullBlob) {
      const remoteUrl = remoteFullUrl(img.record)
      if (remoteUrl && !img.ui.fullPreviewUrl) {
        revokeLocalObjectUrl(img.ui.fullPreviewUrl)
        img.ui.fullPreviewUrl = remoteUrl
        img.ui.fullBlobRef = null
      }
    }
  }
}

function applyRemotePreviewToVm(img: ImageVM | null | undefined) {
  if (!img) return
  const previewUrl = remotePreviewUrl(img.record)
  const remoteUrl = remoteFullUrl(img.record)
  if (!previewUrl && !remoteUrl) return
  const tileBlob = tilePreviewBlob(img.record)
  const heavyBlob = heavyPreviewBlob(img.record)
  const hasLocalTilePreview = !!(tileBlob || (heavyBlob && !isUnsupportedDirectPreviewBlob(heavyBlob)))
  if (hasLocalTilePreview && !fs.open) {
    img.ui.previewLoading = false
    if (!img.ui.fullPreviewUrl && remoteUrl) {
      img.ui.fullPreviewUrl = remoteUrl
      img.ui.fullBlobRef = null
    }
    return
  }
  const nextPreviewUrl = previewUrl || remoteUrl
  const nextFullUrl = remoteUrl || previewUrl
  if (!nextPreviewUrl) return
  const nextRemoteKey = `${nextPreviewUrl}::${nextFullUrl || ''}`
  const canSwapPreviewInPlace =
    !!img.ui.ready &&
    !!img.ui.appliedOnce &&
    !!String(img.ui.previewUrl || '').trim()
  if (img.ui.remotePreviewKey === nextRemoteKey) {
    img.ui.previewLoading = false
    return
  }
  const prevPreviewUrl = String(img.ui.previewUrl || '')
  const prevFullPreviewUrl = String(img.ui.fullPreviewUrl || '')

  const commit = () => {
    const previewChanged = !!nextPreviewUrl && img.ui.previewUrl !== nextPreviewUrl
    const fullChanged = !!nextFullUrl && img.ui.fullPreviewUrl !== nextFullUrl
    if (nextPreviewUrl && img.ui.previewUrl !== nextPreviewUrl) {
      revokeLocalObjectUrl(img.ui.previewUrl)
      img.ui.previewUrl = nextPreviewUrl
      img.ui.previewBlobRef = null
    }
    if (nextFullUrl && img.ui.fullPreviewUrl !== nextFullUrl) {
      revokeLocalObjectUrl(img.ui.fullPreviewUrl)
      img.ui.fullPreviewUrl = nextFullUrl
      img.ui.fullBlobRef = null
    }
    img.ui.remotePreviewKey = nextRemoteKey
    if (previewChanged || fullChanged) {
      if (!canSwapPreviewInPlace) {
        img.ui.ready = false
        img.ui.armedGrid = false
        img.ui.armedFs = false
        img.ui.appliedOnce = false
        img.ui.rev += 1
        pendingApply.add(img.id)
      }
    }
    img.ui.previewLoading = false
  }

  if (!nextPreviewUrl || nextPreviewUrl.startsWith('blob:')) {
    commit()
    return
  }

  if (!canSwapPreviewInPlace) img.ui.previewLoading = true
  const preload = new Image()
  preload.decoding = 'async'
  preload.onload = () => {
    const naturalW = Math.max(1, Number(preload.naturalWidth || 0) || 1)
    const naturalH = Math.max(1, Number(preload.naturalHeight || 0) || 1)
    if (naturalW > 1 && naturalH > 1) {
      if (!(Number((img.record as any)?.editorWidth || 0) > 1) || !(Number((img.record as any)?.editorHeight || 0) > 1)) {
        img.ui.imgW = naturalW
        img.ui.imgH = naturalH
        img.ui.baseW = naturalW
        img.ui.baseH = naturalH
        ;(img.record as any).editorWidth = naturalW
        ;(img.record as any).editorHeight = naturalH
      }
    }
    commit()
  }
  preload.onerror = () => {
    // Keep the existing local preview if the remote asset is not ready yet.
    img.ui.previewLoading = false
    if (!prevPreviewUrl && nextPreviewUrl) {
      img.ui.previewUrl = nextPreviewUrl
      img.ui.previewBlobRef = null
    }
    if (!prevFullPreviewUrl && nextFullUrl) {
      img.ui.fullPreviewUrl = nextFullUrl
      img.ui.fullBlobRef = null
    }
  }
  preload.src = nextPreviewUrl
}

function shouldRetainFullPreview(img: ImageVM | null | undefined) {
  if (!img) return false
  if (fs.open && fs.id === img.id) return true
  if (useCanvasPrototypeFor(img.id)) return true
  return false
}

async function releaseHeavyResourcesForImage(img: ImageVM | null | undefined) {
  if (!img || shouldRetainFullPreview(img)) return

  const fullBlob = fullPreviewBlob(img.record)
  const canKeepLocalEditorPreview =
    !!(img.record as any)?.editorBlob &&
    !!fullBlob &&
    fullBlob === (img.record as any).editorBlob

  if (img.ui.fullPreviewUrl && !canKeepLocalEditorPreview) {
    URL.revokeObjectURL(img.ui.fullPreviewUrl)
    img.ui.fullPreviewUrl = ''
    img.ui.fullBlobRef = null
  }

  const remoteUrl = String((img.record as any)?.upload?.remoteUrl || '').trim()
  if (!remoteUrl) return
  if (!(img.record as any)?.workingBlob && !(img.record as any)?.originalBlob) return
  if (!(img.record as any)?.editorBlob && !(img.record as any)?.thumbBlob) return

  try {
    await persistRecordUpdate(img.id, { workingBlob: undefined, originalBlob: undefined } as any)
  } catch {}

  img.record = {
    ...(img.record as any),
    workingBlob: undefined,
    originalBlob: undefined,
  } as any
}

async function releaseSettledUploadSourceBlobs(img: ImageVM | null | undefined) {
  if (!img) return
  const rec: any = img.record ?? {}
  const state = String(rec?.upload?.state || '').trim()
  if (state !== 'done') return
  if (!(rec?.workingBlob || rec?.originalBlob)) return
  if (!(rec?.thumbBlob || rec?.editorBlob)) return

  try {
    await persistRecordUpdate(img.id, { workingBlob: undefined, originalBlob: undefined } as any)
  } catch {}

  img.record = {
    ...rec,
    workingBlob: undefined,
    originalBlob: undefined,
  } as any
}

function cancelIdleRelease(id: string) {
  const timer = idleReleaseTimers.get(id)
  if (timer) clearTimeout(timer)
  idleReleaseTimers.delete(id)
}

function retainTileVisibility(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  retainedVisibleTileIds.add(id)
  const timer = tileVisibilityReleaseTimers.get(id)
  if (timer) clearTimeout(timer)
  tileVisibilityReleaseTimers.set(id, setTimeout(() => {
    retainedVisibleTileIds.delete(id)
    tileVisibilityReleaseTimers.delete(id)
    scheduleHeavyTileSync()
  }, TILE_VISIBILITY_GRACE_MS))
}

function activateTileCropper(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  activatedTileCropperIds.add(id)
  if (primaryLiveTileId.value !== id) {
    primaryLiveTileId.value = id
    scheduleHeavyTileSync()
  }
}

function markImageActive(img: ImageVM | null | undefined) {
  if (!img) return
  const id = String(img.id || '').trim()
  if (!id) return
  activateTileCropper(id)
  recentlyActiveIds.add(id)
  cancelIdleRelease(id)

  idleReleaseTimers.set(id, setTimeout(() => {
    recentlyActiveIds.delete(id)
    idleReleaseTimers.delete(id)
    void releaseHeavyResourcesForImage(images.value.find((x) => x.id === id))
  }, IDLE_RELEASE_MS))
}

function setTileObserverRef(el: Element | null, id: string) {
  const prev = tileElMap.get(id)
  if (prev && prev !== el && tilePreviewObserver) tilePreviewObserver.unobserve(prev)

  if (el instanceof HTMLElement) {
    tileElMap.set(id, el)
    const rect = el.getBoundingClientRect()
    const vh = typeof window !== 'undefined' ? window.innerHeight || document.documentElement.clientHeight || 0 : 0
    const vw = typeof window !== 'undefined' ? window.innerWidth || document.documentElement.clientWidth || 0 : 0
    const nearViewport = vh > 0 && vw > 0
      && rect.bottom >= -140
      && rect.right >= 0
      && rect.top <= vh + 140
      && rect.left <= vw
    if (nearViewport) {
      const nextId = String(id || '').trim()
      visibleTileIds.add(nextId)
      retainTileVisibility(nextId)
    }
    if (tilePreviewObserver) tilePreviewObserver.observe(el)
    return
  }

  if (prev && tilePreviewObserver) tilePreviewObserver.unobserve(prev)
  tileElMap.delete(id)
}

function revealImageInGrid(idRaw: string, behavior: ScrollBehavior = 'smooth') {
  const id = String(idRaw || '').trim()
  if (!id) return
  const el = tileElMap.get(id)
  if (!el) return
  el.scrollIntoView({ behavior, block: 'center', inline: 'nearest' })
  markImageActive(images.value.find((img) => img.id === id))
}

function scheduleRevealPreselectedImage(behavior: ScrollBehavior = 'smooth') {
  const targetId = String((props.preselectIds ?? []).filter(Boolean)[0] || '').trim()
  if (!targetId) return
  if (revealSelectedTimer) clearTimeout(revealSelectedTimer)
  revealSelectedTimer = setTimeout(() => {
    revealSelectedTimer = null
    revealImageInGrid(targetId, behavior)
  }, 60)
}

async function ensureWorkingBlobForImage(img: ImageVM | null | undefined): Promise<void> {
  if (!img) return
  if ((img.record as any)?.workingBlob) {
    ensureObjectUrlsForImage(img, { full: true, preferHeavy: true })
    return
  }

  const existingJob = workingBlobLoads.get(img.id)
  if (existingJob) return existingJob

  const remoteUrl = String((img.record as any)?.upload?.remoteUrl || '').trim()
  if (!remoteUrl) return

  const job = (async () => {
    img.ui.previewLoading = true
    try {
      const resp = await fetch(remoteUrl)
      if (!resp.ok) throw new Error(`Image fetch failed (${resp.status})`)
      const blob = await resp.blob()
      const editorPreview = await createEditorPreview(blob, 1600, 'image/webp', 0.82).catch(() => null)
      const dims = (img.record as any)?.width && (img.record as any)?.height
        ? { width: Number((img.record as any).width || 0), height: Number((img.record as any).height || 0) }
        : await readImageDimensions(blob)

      await persistRecordUpdate(img.id, {
        workingBlob: blob,
        editorBlob: editorPreview?.blob,
        editorWidth: editorPreview?.width,
        editorHeight: editorPreview?.height,
        bytes: blob.size,
        width: dims.width || (img.record as any)?.width,
        height: dims.height || (img.record as any)?.height,
      } as any)
      const prevPreviewUrl = img.ui.previewUrl
      const prevFullPreviewUrl = img.ui.fullPreviewUrl
      img.record = {
        ...(img.record as any),
        workingBlob: blob,
        editorBlob: editorPreview?.blob,
        editorWidth: editorPreview?.width,
        editorHeight: editorPreview?.height,
        bytes: blob.size,
        width: dims.width || (img.record as any)?.width,
        height: dims.height || (img.record as any)?.height,
      } as any
      img.ui.previewUrl = ''
      img.ui.fullPreviewUrl = ''
      ensureObjectUrlsForImage(img)
      ensureObjectUrlsForImage(img, { full: true, preferHeavy: true })
      if (prevPreviewUrl && prevPreviewUrl !== img.ui.previewUrl) URL.revokeObjectURL(prevPreviewUrl)
      if (prevFullPreviewUrl && prevFullPreviewUrl !== img.ui.fullPreviewUrl) URL.revokeObjectURL(prevFullPreviewUrl)
    } catch (err) {
      console.warn('[editor] could not restore working blob', img.id, err)
    } finally {
      img.ui.previewLoading = false
      workingBlobLoads.delete(img.id)
    }
  })()

  workingBlobLoads.set(img.id, job)
  return job
}

/* ------------------------------------------------
 * Props / Emits
 * ------------------------------------------------ */
const props = defineProps<{
  categoryId: number
  productId: number
  sizeOptions?: SizeOpt[]
  tileVariantGroups?: Array<{
    id: string
    title: string
    isSize?: boolean
    showInTile?: boolean
    options?: Array<{ id: string; title?: string; priceLabel?: string; price?: number; isCrop?: boolean }>
  }>
  cropEngine?: 'cropper' | 'canvas'
  sidebarMode?: 'auto' | 'manual'
  preselectIds?: string[]
  categoryLabel?: string
  /** Map von categoryId → Kategoriename für alle bekannten Kategorien (Root + Sub) */
  categoryTitlesMap?: Record<number, string>
  /** Map von categoryId → Kategorie-Thumbnail */
  categoryThumbnailsMap?: Record<number, string>
  /** Map von categoryId → Root-/Hauptkategorie-ID */
  categoryRootIdsMap?: Record<number, number>
  /** Map von Root-/Hauptkategorie-ID → Root-/Hauptkategorie-Titel */
  rootCategoryTitlesMap?: Record<number, string>
  /** Stabile Reihenfolge wie im linken Kategorienpanel */
  categoryOrderMap?: Record<number, number>
  /** Stabile Root-Reihenfolge wie im linken Kategorienpanel */
  rootCategoryOrderMap?: Record<number, number>
  /** Optionaler Klartext-Name des Produkts (aus übergeordneter Seite) */
  productName?: string
  currentEditorType?: string | null
  isMultiMode?: boolean
  /** Wenn gesetzt, nur Bilder dieser Kategorien anzeigen (für Root-Kategorie-Ansicht) */
  filterCategoryIds?: number[]
  /** Kategorien, die im Grid priorisiert oben stehen sollen */
  priorityCategoryIds?: number[]
  /** Shop-Logo-URL – wird zentriert über der Toolbar angezeigt, scrollt weg */
  logoUrl?: string
  /** Gesamtpreis-Label aus der Sidebar (für Topbar-Anzeige) */
  totalPriceLabel?: string
  /** editorType-Map: categoryId → 'single' | 'multi' — für 3D-Preview-Button in Tiles */
  categoryEditorTypes?: Record<number, 'single' | 'multi'>
  /** editorConfig-Map: categoryId → editorConfig — z. B. Tile-Preview steuerbar */
  categoryEditorConfigs?: Record<number, any>
  /** 3D-Vorschau-Modell-Config aus der Kategorie/Produkt */
  previewModelConfig?: any
}>()

const cropEngine = computed<'cropper' | 'canvas'>(() => (props.cropEngine === 'canvas' ? 'canvas' : 'cropper'))
const canvasPrototypeId = ref<string | null>(null)
const initialLoadingGroups = computed(() => {
  return [0, 1].map((index) => ({
    key: `initial-${index}`,
    count: index === 0 ? 6 : 4,
  }))
})

function useCanvasPrototypeFor(imageId: string) {
  return cropEngine.value === 'canvas' && canvasPrototypeId.value === imageId
}

watch(() => canvasPrototypeId.value, () => {
  scheduleHeavyTileSync()
})

// Titel fuer einen Produktwechsel: nie den Titel des vorherigen Produkts stehen lassen
// (sonst landet z. B. "Fotoleinwand Economy" an einer Fotopapier-Position im Warenkorb/XML).
// Leerer String => die API setzt den Titel des Produkts aus der Datenbank.
function productTitleForSwitch(prevProductId: unknown, nextProductId: unknown, givenTitle: unknown, prevTitle: unknown): string {
  const given = String(givenTitle ?? '').trim()
  if (given) return given
  const nextId = Number(nextProductId || 0)
  if (nextId > 0 && Number(prevProductId || 0) === nextId) {
    const prev = String(prevTitle ?? '').trim()
    if (prev) return prev
  }
  return String(catalogBootstrap.value?.products?.[String(nextId)]?.title ?? '').trim()
}

const bootstrapCurrentProduct = computed<any | null>(() => {
  const id = Number(props.productId || 0)
  if (!(id > 0)) return null
  return catalogBootstrap.value?.products?.[String(id)] ?? null
})

const bootstrapCurrentProductSizeOptions = computed<any[]>(() => {
  const groups = bootstrapCurrentProduct.value?.configurator?.groups ?? []
  const sizeGroup = groups.find((g: any) => g?.isSize)
  return Array.isArray(sizeGroup?.sizeOptions) ? sizeGroup.sizeOptions : []
})

const productsInCategory = computed(() => {
  const categoryId = Number(props.categoryId || 0)
  if (!(categoryId > 0)) return []
  const ids = catalogBootstrap.value?.productsByCategory?.[String(categoryId)] ?? []
  return ids
    .map((id: any) => catalogBootstrap.value?.products?.[String(Number(id || 0))])
    .filter(Boolean)
    .sort((a: any, b: any) => {
      const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
      if (pos !== 0) return pos
      return String(a?.titleShort || a?.title || '').localeCompare(String(b?.titleShort || b?.title || ''), 'de')
    })
})

const productListsByCategory = ref<Map<number, any[]>>(new Map())
watch(
  [() => Number(props.categoryId || 0), () => productsInCategory.value],
  ([cid, products]) => {
    if (!cid || !products.length) return
    const prev = productListsByCategory.value.get(cid) || null
    if (prev === products) return
    const next = new Map(productListsByCategory.value)
    next.set(cid, products)
    productListsByCategory.value = next
  },
  { immediate: true, flush: 'post' },
)

async function loadProductsForCategory(categoryId: number) {
  if (!categoryId || productListsByCategory.value.has(categoryId)) return
  const ids = catalogBootstrap.value?.productsByCategory?.[String(categoryId)] ?? []
  const rows = ids
    .map((id: any) => catalogBootstrap.value?.products?.[String(Number(id || 0))])
    .filter(Boolean)
    .sort((a: any, b: any) => {
      const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
      if (pos !== 0) return pos
      return String(a?.titleShort || a?.title || '').localeCompare(String(b?.titleShort || b?.title || ''), 'de')
    })
  const next = new Map(productListsByCategory.value)
  next.set(categoryId, rows)
  productListsByCategory.value = next
}

function productsForImg(img: ImageVM): any[] {
  const recordCategoryId = Number((img.record as any)?.categoryId ?? 0)
  const parentCategoryId = Number((img.record as any)?.parentCategoryId ?? 0)
  const propCategoryId = Number(props.categoryId || 0)
  const candidates = [recordCategoryId, parentCategoryId, propCategoryId].filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)

  for (const cid of candidates) {
    if (productListsByCategory.value.has(cid)) {
      const rows = productListsByCategory.value.get(cid) || []
      if (rows.length) return rows
    }
    const ids = catalogBootstrap.value?.productsByCategory?.[String(cid)] ?? []
    const rows = ids
      .map((id: any) => catalogBootstrap.value?.products?.[String(Number(id || 0))])
      .filter(Boolean)
      .sort((a: any, b: any) => {
        const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
        if (pos !== 0) return pos
        return String(a?.titleShort || a?.title || '').localeCompare(String(b?.titleShort || b?.title || ''), 'de')
      })
    if (rows.length) return rows
  }
  return productsInCategory.value
}

function productSelectOptionsForImg(img: ImageVM): FlatSelectOption[] {
  const options = productsForImg(img).map((p: any) => ({
    value: String(p?._id ?? ''),
    label: String((p?.titleShort || p?.title || `Produkt #${p?._id}`).trim()),
  })).filter((x: any) => !!x.value && !!x.label)

  const currentProductId = Number((img.record as any)?.productId ?? 0)
  if (currentProductId > 0 && !options.some((opt) => Number(opt?.value || 0) === currentProductId)) {
    const bootstrapProduct = catalogBootstrap.value?.products?.[String(currentProductId)] ?? null
    const fallbackLabel = String(
      (img.record as any)?.productTitle
      || (img.record as any)?.edits?.preset?.productTitle
      || bootstrapProduct?.titleShort
      || bootstrapProduct?.title
      || `Produkt #${currentProductId}`,
    ).trim()
    options.unshift({
      value: String(currentProductId),
      label: fallbackLabel,
    })
  }

  return options
}

function tileProductValue(img: ImageVM): string | null {
  const pid = Number((img.record as any)?.productId ?? 0)
  if (pid > 0) return String(pid)
  return String(props.productId || '') || null
}

function mapSizeToSizesByTitle(sizes: Array<{ title: string; widthMm: number; heightMm: number; variantId?: string; key: string; isDefault?: boolean }>, oldSize: any) {
  if (!sizes.length) return null
  // Titel exakt → Titel normalisiert → gleiche Maße (auch gedreht) → Default/erste.
  return findMatchingSizeOption(sizes, oldSize) || defaultSizeOption(sizes)
}

function normalizeMmValue(v: any): number {
  const n = Math.round(Number(v || 0))
  return Number.isFinite(n) && n > 0 ? n : 0
}

function formatCmValue(mm: number): string {
  const cm = mm / 10
  if (!Number.isFinite(cm) || cm <= 0) return ''
  if (Math.abs(cm - Math.round(cm)) < 0.0001) return String(Math.round(cm))
  return String(cm).replace(/\.0$/, '').replace('.', ',')
}

function customSizeTitle(size: any): string {
  const title = String(size?.title || '').trim()
  if (title) return title
  const w = normalizeMmValue(size?.widthMm)
  const h = normalizeMmValue(size?.heightMm)
  if (w > 0 && h > 0) return `${formatCmValue(w)} x ${formatCmValue(h)} cm`
  return ''
}

function customSizeKey(size: any): string {
  const w = normalizeMmValue(size?.widthMm)
  const h = normalizeMmValue(size?.heightMm)
  const title = customSizeTitle(size)
  if (w > 0 && h > 0) return `__custom__:${w}x${h}`
  if (title) return `__custom__:${title}`
  return ''
}

function sizeMatchesPreset(entry: any, presetSize: any): boolean {
  if (!entry || !presetSize) return false

  const variantId = String(presetSize?.variantId || '').trim()
  if (variantId && String(entry?.key || '') === variantId) return true
  if (variantId && String(entry?.variantId || '') === variantId) return true

  const title = customSizeTitle(presetSize)
  if (title && String(entry?.title || '').trim() === title) return true

  const w = normalizeMmValue(presetSize?.widthMm)
  const h = normalizeMmValue(presetSize?.heightMm)
  const ew = normalizeMmValue(entry?.widthMm)
  const eh = normalizeMmValue(entry?.heightMm)
  if (w > 0 && h > 0 && ew > 0 && eh > 0) {
    if ((ew === w && eh === h) || (ew === h && eh === w)) return true
  }

  return false
}

function makeCustomSizeEntry(size: any, priceSource?: any) {
  const w = normalizeMmValue(size?.widthMm)
  const h = normalizeMmValue(size?.heightMm)
  if (!(w > 0 && h > 0)) return null

  const key = customSizeKey(size)
  const title = customSizeTitle(size)
  const priceRaw = extractPriceLike(priceSource)

  return {
    key,
    id: '',
    variantId: '',
    title: title || key,
    widthMm: w,
    heightMm: h,
    ar: w / h,
    price: typeof priceRaw === 'number' ? priceRaw : Number(priceRaw) || 0,
    priceLabel: formatMoneyLike(priceRaw, (priceSource as any)?.currency),
  }
}

function sizesWithPresetSupport<T extends { key: string; variantId?: string; title: string; widthMm: number; heightMm: number }>(
  sizes: T[],
  presetSize: any,
): T[] {
  if (!presetSize || !sizes.length) return sizes
  if (sizes.some((entry) => sizeMatchesPreset(entry, presetSize))) return sizes

  const synthetic = makeCustomSizeEntry(presetSize)
  if (!synthetic) return sizes
  return [synthetic as T, ...sizes]
}

function resolveSizeUiState(
  sizes: Array<{ key: string; variantId?: string; title: string; widthMm: number; heightMm: number }>,
  presetSize: any,
  currentKey = '',
  currentTitleHint = '',
  locked = false,
) {
  const list = sizesWithPresetSupport(sizes, presetSize)
  const matched = presetSize ? list.find((entry) => sizeMatchesPreset(entry, presetSize)) || null : null
  const fallback = list.find((entry) => String(entry.key) === String(currentKey || '').trim()) || list[0] || null

  if (locked) {
    return {
      sizeKey: matched?.key || String(presetSize?.variantId || '').trim() || customSizeKey(presetSize) || currentKey || '',
      sizeTitleHint: matched?.title || customSizeTitle(presetSize) || currentTitleHint || '',
    }
  }

  return {
    sizeKey: matched?.key || fallback?.key || '',
    sizeTitleHint: matched?.title || fallback?.title || customSizeTitle(presetSize) || currentTitleHint || '',
  }
}

function enrichCategoryMeta<T extends Record<string, any>>(rec: T): T {
  const next: any = { ...rec }
  const preset: any = { ...((next.edits?.preset || {}) as any) }

  const categoryId = Number(next.categoryId ?? preset.categoryId ?? 0) || null
  const parentCategoryId = Number(next.parentCategoryId ?? preset.parentCategoryId ?? 0) || null

  const mappedCategoryTitle = categoryId ? String(props.categoryTitlesMap?.[categoryId] || '').trim() : ''
  const mappedParentTitle = parentCategoryId ? String(props.categoryTitlesMap?.[parentCategoryId] || '').trim() : ''

  const categoryTitle = String((next.categoryTitle ?? preset.categoryTitle ?? mappedCategoryTitle) || '').trim()
  const parentCategoryTitle = String(
    next.parentCategoryTitle
    ?? preset.parentCategoryTitle
    ?? mappedParentTitle
    ?? (parentCategoryId ? '' : categoryTitle)
    ?? '',
  ).trim()

  if (categoryId) {
    next.categoryId = categoryId
    preset.categoryId = categoryId
  }
  if (parentCategoryId) {
    next.parentCategoryId = parentCategoryId
    preset.parentCategoryId = parentCategoryId
  }
  if (categoryTitle) {
    next.categoryTitle = categoryTitle
    preset.categoryTitle = categoryTitle
  }
  if (parentCategoryTitle) {
    next.parentCategoryTitle = parentCategoryTitle
    preset.parentCategoryTitle = parentCategoryTitle
  }

  next.edits = { ...(next.edits || {}), preset }
  return next
}

async function onTileProductChange(img: ImageVM, next: string) {
  markImageActive(img)
  const pid = Number(next || 0)
  if (!pid) return

  const cur = Number((img.record as any)?.productId ?? 0)
  if (cur === pid) return

  const p = productsForImg(img).find((x: any) => Number(x?._id) === pid)
  const productTitle = String(p?.title || p?.titleShort || `Produkt #${pid}`)

  const rec = (await getStoredRecord(img.id)) || ((img.record as any) as ImageRecord | null)
  if (!rec) return

  await loadVariantGroupsForProduct(pid)
  const nextGroups = productVariantGroupsMap.value.get(pid) || []

  const prevEdits: any = rec.edits ?? {}
  const prevPreset: any = prevEdits.preset ?? {}
  const prevSize = prevPreset?.size ?? null
  const nextOptions = defaultTileOptionsForProduct(nextGroups)

  ;(img.record as any).productId = pid
  ;(img.record as any).productTitle = productTitle
  ;(img.record as any).edits = { ...prevEdits, preset: { ...prevPreset, productId: pid, productTitle } }

  const nextSizeMatch = mapSizeToSizesByTitle(normalizedSizesForImg(img), prevSize)
  const nextSize = nextSizeMatch
    ? persistedSizeValue({
        id: String(nextSizeMatch.id || '').trim() || undefined,
        title: nextSizeMatch.title,
        widthMm: nextSizeMatch.widthMm,
        heightMm: nextSizeMatch.heightMm,
        variantId: nextSizeMatch.variantId || undefined,
      })
    : null
  const prevSizeId = normId(prevSize?.id ?? '')
  const nextSizeId = normId(nextSize?.id ?? '')
  const prevSizeTitle = String(prevSize?.title ?? '').trim()
  const nextSizeTitle = String(nextSize?.title ?? '').trim()
  const prevWidthMm = Math.round(Number(prevSize?.widthMm ?? 0) || 0)
  const prevHeightMm = Math.round(Number(prevSize?.heightMm ?? 0) || 0)
  const nextWidthMm = Math.round(Number(nextSize?.widthMm ?? 0) || 0)
  const nextHeightMm = Math.round(Number(nextSize?.heightMm ?? 0) || 0)
  const sizeChanged =
    (prevSizeId && nextSizeId ? prevSizeId !== nextSizeId : false) ||
    prevSizeTitle !== nextSizeTitle ||
    prevWidthMm !== nextWidthMm ||
    prevHeightMm !== nextHeightMm
  const nextPreset = {
    ...prevPreset,
    productId: pid,
    productTitle,
    ...(nextSize ? { sizeId: String(nextSize.id || '').trim() || undefined } : {}),
    size: nextSize,
    options: nextOptions,
  }

  img.ui.options = nextOptions
  img.ui.sizeKey = nextSizeMatch?.key || ''
  img.ui.sizeTitleHint = nextSizeMatch?.title || ''
  img.ui.lockAR = true
  img.ui.orientMode = 'auto'
  img.ui.swapAR = false
  img.ui.ready = false
  img.ui.armedGrid = false
  img.ui.armedFs = false
  img.ui.rev += 1
  ;(img.record as any).edits = { ...prevEdits, preset: nextPreset }

  const rot = normRot((img.crop?.rot ?? (rec.edits as any)?.rotate ?? 0))
  if (sizeChanged) {
    setMaxCrop(img, rot)
  } else {
    syncCropMode(img, { persist: false, apply: false })
  }

  const cropSource = img.crop
    ? {
        width: Math.max(1, Number(img.crop.sourceWidth || img.ui.baseW || img.ui.imgW || editorDimsForRecord(img.record).width)),
        height: Math.max(1, Number(img.crop.sourceHeight || img.ui.baseH || img.ui.imgH || editorDimsForRecord(img.record).height)),
      }
    : null
  const storedCrop = img.crop && cropSource
    ? {
        x: img.crop.x,
        y: img.crop.y,
        w: img.crop.w,
        h: img.crop.h,
        sourceWidth: cropSource.width,
        sourceHeight: cropSource.height,
        ...(cropToNormalized(img.crop, cropSource.width, cropSource.height) || {}),
      }
    : undefined

  await persistRecordUpdate(img.id, {
    productId: pid,
    productTitle,
    edits: {
      ...prevEdits,
      crop: storedCrop,
      rotate: rot,
      lockAR: true,
      orientMode: 'auto',
      swapAR: false,
      preset: nextPreset,
    },
    updatedAt: Date.now(),
  } as any)
  mergeSessionRecordPatch(img.id, {
    productId: pid,
    productTitle,
    edits: {
      ...prevEdits,
      crop: storedCrop,
      rotate: rot,
      lockAR: true,
      orientMode: 'auto',
      swapAR: false,
      preset: nextPreset,
    },
    updatedAt: Date.now(),
  } as any)
  ;(img.record as any).updatedAt = Date.now()
  scheduleUploadMetaSync(img.id, true)

  ensureObjectUrlsForImage(img)

  img.ui.appliedOnce = false
  pendingApply.add(img.id)
  queueMicrotask(async () => {
    await applyCropToCropper(img, 'product-change-grid', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) await applyCropToCropper(img, 'product-change-fs', fsCropperRef.value, true)
  })
}

const emit = defineEmits<{
  (e: 'selection-change', ids: string[]): void
  (e: 'open-details'): void
  (e: 'meta-changed', ids: string[]): void
  (e: 'navigate', payload: { categoryId: number; productId: number }): void
  (e: 'initial-loading', loading: boolean): void
}>()

/* ------------------------------------------------
 * External
 * ------------------------------------------------ */
const db = useImageDB('imageDB')
const project = useProjectStore()
const transientUploads = useTransientUploadStore()
const runner = getUploadsRunner()
if (!runner) throw new Error('uploads runner not initialized')

watch(() => project.projectCode.value, (code, prev) => {
  if (!code || code === prev || typeof window === 'undefined') return
  loadImages().catch(() => {})
  scheduleRefreshUploadStats(0)
}, { flush: 'post' })

/* ------------------------------------------------
 * Helpers
 * ------------------------------------------------ */
function debounce<T extends (...args: any[]) => void>(fn: T, wait = 250) {
  let t: any
  return (...args: Parameters<T>) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}
async function getStoredRecord(idRaw: string): Promise<ImageRecord | null> {
  const id = String(idRaw || '').trim()
  if (!id) return null
  return transientUploads.get(id) || sessionRecordsState.value[id] || await db.get(id)
}
function hasTransientRecord(idRaw: string): boolean {
  const id = String(idRaw || '').trim()
  return !!(id && transientUploads.get(id))
}
function mergeSessionRecordPatch(idRaw: string, patch: Partial<ImageRecord>) {
  const id = String(idRaw || '').trim()
  if (!id) return
  const prev = sessionRecordsState.value[id]
  if (!prev) return
  sessionRecordsState.value[id] = {
    ...prev,
    ...patch,
    edits: (patch as any).edits !== undefined
      ? { ...((prev as any)?.edits || {}), ...(((patch as any)?.edits || {}) as any) }
      : prev.edits,
    upload: (patch as any).upload !== undefined
      ? { ...(((prev as any)?.upload || {}) as any), ...((((patch as any)?.upload || {}) as any)) }
      : (prev as any).upload,
  } as ImageRecord
}

function mergeRecordPatch<T extends Record<string, any>>(prev: T | null | undefined, patch: Partial<T> | null | undefined): T {
  const base = (prev || {}) as any
  const nextPatch = (patch || {}) as any
  const nextUpload = (nextPatch.upload || {}) as any
  const prefersServerThumbs =
    !!String(nextUpload.previewUrl || '').trim() ||
    !!String(nextUpload.thumbUrl || '').trim() ||
    !!String(nextUpload.fullscreenPreviewUrl || '').trim()
  return {
    ...base,
    ...nextPatch,
    ...(prefersServerThumbs
      ? {
          thumbBlob: undefined,
          thumbWidth: undefined,
          thumbHeight: undefined,
          editorBlob: undefined,
          editorWidth: undefined,
          editorHeight: undefined,
        }
      : {}),
    edits: nextPatch.edits !== undefined
      ? {
          ...((base?.edits || {}) as any),
          ...((nextPatch.edits || {}) as any),
          preset: ((nextPatch.edits || {}) as any).preset !== undefined
            ? {
                ...((((base?.edits || {}) as any)?.preset || {}) as any),
                ...((((nextPatch.edits || {}) as any)?.preset || {}) as any),
              }
            : (((base?.edits || {}) as any)?.preset || undefined),
        }
      : base?.edits,
    upload: nextPatch.upload !== undefined
      ? {
          ...((base?.upload || {}) as any),
          ...((nextPatch.upload || {}) as any),
        }
      : base?.upload,
  } as T
}

function mergePersistedRecordWithTransientPreview(
  persisted: ImageRecord | null | undefined,
  transient: ImageRecord | null | undefined,
): ImageRecord | null {
  if (!persisted && !transient) return null
  if (!persisted) return (transient || null) as ImageRecord | null
  if (!transient) return persisted

  const transientUpload: any = (transient as any)?.upload || {}
  const mergedPreset = {
    ...((((persisted as any)?.edits?.preset || {}) as any)),
    ...((((transient as any)?.edits?.preset || {}) as any)),
  }
  const mergedEdits = {
    ...(((persisted as any)?.edits || {}) as any),
    ...(((transient as any)?.edits || {}) as any),
    preset: Object.keys(mergedPreset).length ? mergedPreset : undefined,
  }
  return {
    ...(persisted as any),
    ...(Object.keys(mergedEdits).length ? { edits: mergedEdits } : {}),
    ...((transient as any)?.productId != null ? { productId: Number((transient as any).productId || 0) || null } : {}),
    ...((transient as any)?.categoryId != null ? { categoryId: Number((transient as any).categoryId || 0) || null } : {}),
    ...(((transient as any)?.parentCategoryId != null) ? { parentCategoryId: Number((transient as any).parentCategoryId || 0) || null } : {}),
    ...(((transient as any)?.productTitle) ? { productTitle: String((transient as any).productTitle) } : {}),
    ...(((transient as any)?.categoryTitle) ? { categoryTitle: String((transient as any).categoryTitle) } : {}),
    ...(((transient as any)?.parentCategoryTitle) ? { parentCategoryTitle: String((transient as any).parentCategoryTitle) } : {}),
    ...((persisted as any)?.width == null && (transient as any)?.width != null ? { width: Number((transient as any).width) } : {}),
    ...((persisted as any)?.height == null && (transient as any)?.height != null ? { height: Number((transient as any).height) } : {}),
    ...((persisted as any)?.editorWidth == null && (transient as any)?.editorWidth != null ? { editorWidth: Number((transient as any).editorWidth) } : {}),
    ...((persisted as any)?.editorHeight == null && (transient as any)?.editorHeight != null ? { editorHeight: Number((transient as any).editorHeight) } : {}),
    ...((transient as any)?.thumbBlob ? { thumbBlob: (transient as any).thumbBlob } : {}),
    ...((transient as any)?.editorBlob ? { editorBlob: (transient as any).editorBlob } : {}),
    ...((transient as any)?.thumbWidth != null ? { thumbWidth: Number((transient as any).thumbWidth) } : {}),
    ...((transient as any)?.thumbHeight != null ? { thumbHeight: Number((transient as any).thumbHeight) } : {}),
    upload: {
      ...(((persisted as any)?.upload || {}) as any),
      ...((transientUpload.previewUrl || transientUpload.thumbUrl)
        ? {
            previewUrl: transientUpload.previewUrl ?? (persisted as any)?.upload?.previewUrl,
            thumbUrl: transientUpload.thumbUrl ?? (persisted as any)?.upload?.thumbUrl,
          }
        : {}),
      ...((transientUpload.fullscreenPreviewUrl || (persisted as any)?.upload?.fullscreenPreviewUrl)
        ? {
            fullscreenPreviewUrl: transientUpload.fullscreenPreviewUrl ?? (persisted as any)?.upload?.fullscreenPreviewUrl,
          }
        : {}),
    },
  } as ImageRecord
}

async function persistRecordUpdate(idRaw: string, patch: Partial<ImageRecord>) {
  const id = String(idRaw || '').trim()
  if (!id) return
  mergeSessionRecordPatch(id, patch)
  const projectCode = String(project.projectCode.value || '').trim()
  invalidateSessionRecordFetchCache(projectCode)
  const transientCurrent = transientUploads.get(id)
  if (transientCurrent) {
    const mergedTransient = mergeRecordPatch(transientCurrent as any, patch as any) as ImageRecord
    transientUploads.patch(id, patch as any)
    await db.put({
      ...(mergedTransient as any),
      ...(projectCode && !(mergedTransient as any)?.projectCode ? { projectCode } : {}),
    } as any).catch(() => null)
    return
  }
  const localCurrent = await db.get(id).catch(() => null)
  if (localCurrent) {
    await db.update(id, patch as any).catch(() => null)
    return
  }
  const base = (sessionRecordsState.value[id] || null) as ImageRecord | null
  if (!base) return
  const merged = mergeRecordPatch(base as any, patch as any) as ImageRecord
  await db.put({
    ...(merged as any),
    ...(projectCode && !(merged as any)?.projectCode ? { projectCode } : {}),
  } as any).catch(() => null)
}
async function persistRecordPut(record: ImageRecord) {
  const id = String((record as any)?.id || '').trim()
  if (!id) return
  sessionRecordsState.value[id] = record
  invalidateSessionRecordFetchCache(String((record as any)?.projectCode || project.projectCode.value || '').trim())
  if (hasTransientRecord(id)) {
    transientUploads.upsert(record as any)
    await db.put(record).catch(() => null)
    return
  }
  await db.put(record).catch(() => null)
}
async function fetchSessionRecordsServerFirst(sessionCodeRaw: string): Promise<ImageRecord[]> {
  const sessionCode = String(sessionCodeRaw || '').trim()

  if (!sessionCode) {
    const byId = new Map<string, ImageRecord>()
    for (const rec of transientUploads.all) byId.set(String(rec.id), rec)
    const result = Array.from(byId.values())
    sessionRecordsState.value = {}
    return result
  }

  const cached = sessionRecordFetchCache.get(sessionCode)
  const now = Date.now()
  if (cached && (now - cached.ts) < SESSION_RECORD_FETCH_CACHE_MS) {
    return cached.promise
  }

  const request = (async () => {
    const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
    const readLocalRecordsWithFallback = async () => {
      const fallbackRecords = [
        ...Object.values(sessionRecordsState.value || {}),
        ...transientUploads.all.filter((rec: any) => {
          const recProjectCode = String(rec?.projectCode || '').trim()
          return recProjectCode === '' || recProjectCode === sessionCode
        }),
      ] as ImageRecord[]
      const timeoutFallback = new Promise<ImageRecord[]>((resolve) => {
        setTimeout(() => resolve(fallbackRecords), 3000)
      })
      return Promise.race<ImageRecord[]>([
        db.getByProjectCode(sessionCode).catch(() => fallbackRecords),
        timeoutFallback,
      ]).catch(() => fallbackRecords)
    }

    const serverListLimit = 500
    let serverRecords: ImageRecord[] = []
    let localRecords: ImageRecord[] = []
    let serverListComplete = false
    if (editorBaseUrl) {
      const items = await fetchProjectUploads({
        editorBaseUrl,
        projectCode: sessionCode,
        limit: serverListLimit,
        offset: 0,
      }).catch(() => null)
      serverListComplete = Array.isArray(items) && items.length < serverListLimit
      serverRecords = (items || []).map((item) => mapRemoteUploadToRecordMeta(item, { projectCode: sessionCode }))
      localRecords = await readLocalRecordsWithFallback()
    } else {
      localRecords = await readLocalRecordsWithFallback()
    }

    let transientRecords = transientUploads.all.filter((rec: any) => {
      const recProjectCode = String(rec?.projectCode || '').trim()
      return recProjectCode === '' || recProjectCode === sessionCode
    })

    // Auf dem Server gelöschte Bilder (z. B. Position im Warenkorb der Website
    // entfernt, während der Editor zu war) auch lokal entfernen, sonst tauchen
    // sie aus IndexedDB wieder auf.
    if (serverListComplete) {
      const serverIds = new Set(serverRecords.map((rec) => String(rec.id)))
      const goneIds = [...new Set(
        findServerDeletedRecords([...localRecords, ...transientRecords], serverIds, sessionCode).map((rec) => String(rec.id)),
      )]
      if (goneIds.length) {
        const gone = new Set(goneIds)
        markRemoteDeleted(goneIds)
        localRecords = localRecords.filter((rec) => !gone.has(String(rec.id)))
        transientRecords = transientRecords.filter((rec) => !gone.has(String(rec.id)))
        transientUploads.removeMany(goneIds)
        void Promise.allSettled(goneIds.map((id) => db.delete(id))).then(() => {
          emitEditorImagesChanged({ ids: goneIds, action: 'deleted' })
        })
      }
    }
    const localIds = new Set(localRecords.map((rec) => String(rec?.id || '').trim()).filter(Boolean))
    const byId = new Map<string, ImageRecord>()
    for (const rec of serverRecords) byId.set(String(rec.id), rec)
    for (const rec of localRecords) {
      const id = String(rec.id)
      const current = byId.get(id)
      byId.set(id, current ? mergeServerAndLocalImageRecord(current, rec) : rec)
    }
    for (const rec of transientRecords) {
      const id = String(rec.id)
      const current = byId.get(id)
      byId.set(id, mergePersistedRecordWithTransientPreview(current || null, rec) || rec)
    }
    const result = Array.from(byId.values())
    const missingLocalServerRecords = serverRecords.filter((rec) => {
      const id = String((rec as any)?.id || '').trim()
      return !!id && !localIds.has(id)
    })
    if (missingLocalServerRecords.length) {
      void Promise.allSettled(
        missingLocalServerRecords.map((rec) => db.put(rec).catch(() => null)),
      )
    }
    sessionRecordsState.value = Object.fromEntries(result.map((rec) => [String(rec.id), rec]))
    return result
  })()

  sessionRecordFetchCache.set(sessionCode, { ts: now, promise: request })
  request.finally(() => {
    const latest = sessionRecordFetchCache.get(sessionCode)
    if (latest?.promise === request && (Date.now() - latest.ts) >= SESSION_RECORD_FETCH_CACHE_MS) {
      sessionRecordFetchCache.delete(sessionCode)
    }
  })
  return request
}
async function mapWithConcurrencyLocal<T>(items: T[], concurrency: number, work: (item: T, index: number) => Promise<void>) {
  const wanted = Math.max(1, Math.min(items.length || 1, concurrency))
  let cursor = 0
  await Promise.all(
    Array.from({ length: wanted }, async () => {
      while (cursor < items.length) {
        const index = cursor++
        await work(items[index]!, index)
      }
    }),
  )
}
function sleepRaf(times = 1) {
  return new Promise<void>((resolve) => {
    const step = () => (times-- <= 1 ? resolve() : requestAnimationFrame(step))
    requestAnimationFrame(step)
  })
}
function nextMacrotaskLocal() {
  return new Promise<void>((resolve) => setTimeout(resolve, 0))
}
function resolveLocalImageProcessingConcurrency() {
  const nav = typeof navigator !== 'undefined' ? navigator : null
  const ua = String(nav?.userAgent || '').toLowerCase()
  const hardware = Number(nav?.hardwareConcurrency || 0) || 0
  const isWindows = ua.includes('windows')
  const isFirefox = ua.includes('firefox')
  const isEdge = ua.includes('edg/')

  if ((isWindows && isFirefox) || (isWindows && isEdge)) return 1
  if (isFirefox) return 1
  if (isWindows) return hardware >= 8 ? 2 : 1
  return hardware >= 8 ? 3 : 2
}
async function yieldHeavyImageProcessing() {
  await nextMacrotaskLocal()
  await sleepRaf(1)
}
const localImageProcessingQueue: Array<() => Promise<void>> = []
const localImageProcessingQueuedIds = new Set<string>()
let localImageProcessingActive = 0
function pumpLocalImageProcessingQueue() {
  const concurrency = resolveLocalImageProcessingConcurrency()
  while (localImageProcessingActive < concurrency && localImageProcessingQueue.length) {
    const job = localImageProcessingQueue.shift()
    if (!job) break
    localImageProcessingActive += 1
    void job().finally(() => {
      localImageProcessingActive = Math.max(0, localImageProcessingActive - 1)
      if (localImageProcessingQueue.length) pumpLocalImageProcessingQueue()
    })
  }
}
function enqueueLocalImageProcessing(idRaw: string, job: () => Promise<void>) {
  const id = String(idRaw || '').trim()
  if (id && localImageProcessingQueuedIds.has(id)) return
  if (id) localImageProcessingQueuedIds.add(id)
  localImageProcessingQueue.push(async () => {
    try {
      await yieldHeavyImageProcessing()
      await job()
      await yieldHeavyImageProcessing()
    } catch {
      // Heavy local image preparation is best-effort; upload can continue with the raw record.
    } finally {
      if (id) localImageProcessingQueuedIds.delete(id)
    }
  })
  pumpLocalImageProcessingQueue()
}
function n(v: any, fallback = 0) {
  const x = Number(v)
  return Number.isFinite(x) ? x : fallback
}
let lastLocalCreatedAt = 0
function nextLocalCreatedAt() {
  const now = Date.now()
  lastLocalCreatedAt = Math.max(now, lastLocalCreatedAt + 1)
  return lastLocalCreatedAt
}
function normId(v: any): string {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (!s.includes('/')) return s
  const parts = s.split('/').filter(Boolean)
  return parts[parts.length - 1] || s
}
function normRot(deg: number) {
  const r = Number(deg) || 0
  return ((r % 360) + 360) % 360
}
function snapRightAngles(deg: number) {
  const r = normRot(deg)
  if (r < 45 || r >= 315) return 0
  if (r >= 45 && r < 135) return 90
  if (r >= 135 && r < 225) return 180
  return 270
}
function compareImageOrder(a: { id?: string; name?: string; createdAt?: number }, b: { id?: string; name?: string; createdAt?: number }, dir: 'asc' | 'desc') {
  const aCreated = Number(a?.createdAt || 0)
  const bCreated = Number(b?.createdAt || 0)
  if (aCreated !== bCreated) return dir === 'asc' ? aCreated - bCreated : bCreated - aCreated
  const aId = String(a?.id || '')
  const bId = String(b?.id || '')
  if (aId !== bId) return aId.localeCompare(bId, 'de')
  return String(a?.name || '').localeCompare(String(b?.name || ''), 'de')
}
function clampCoordsToBox(
    coords: { left: number; top: number; width: number; height: number },
    boxW: number,
    boxH: number,
) {
  const BW = Math.max(1, Math.round(n(boxW, 1)))
  const BH = Math.max(1, Math.round(n(boxH, 1)))

  let left = Math.max(0, Math.round(n(coords.left, 0)))
  let top = Math.max(0, Math.round(n(coords.top, 0)))
  let width = Math.max(2, Math.round(n(coords.width, 2)))
  let height = Math.max(2, Math.round(n(coords.height, 2)))

  if (left > BW - 2) left = Math.max(0, BW - 2)
  if (top > BH - 2) top = Math.max(0, BH - 2)
  if (left + width > BW) width = Math.max(2, BW - left)
  if (top + height > BH) height = Math.max(2, BH - top)

  return { left, top, width, height }
}
function applyRotationIfPossible(cropper: any, rotate: number) {
  try {
    if (typeof cropper?.setImageTransforms === 'function') return cropper.setImageTransforms({ rotate })
    if (typeof cropper?.setTransforms === 'function') return cropper.setTransforms({ rotate })
    if (typeof cropper?.setRotation === 'function') return cropper.setRotation(rotate)
  } catch {}
}
function activeBox(img: ImageVM, rotDeg: number) {
  const bw = n(img.ui.baseW || img.ui.imgW, 0)
  const bh = n(img.ui.baseH || img.ui.imgH, 0)
  const rot = snapRightAngles(rotDeg)
  if (rot === 90 || rot === 270) return { W: Math.max(1, bh), H: Math.max(1, bw) }
  return { W: Math.max(1, bw), H: Math.max(1, bh) }
}

/* ------------------------------------------------
 * Toolbar prefs
 * ------------------------------------------------ */
const search = ref('')
const sortBy = ref<'newest' | 'oldest' | 'name'>('oldest')

const uploadQueueMinimized = ref(false)
const uploadQueueAutoOpenedForErrorCount = ref(0)
const uploadQueuePos = ref<{ right: number; bottom: number }>({ right: 18, bottom: 18 })
const uploadQueueDragging = ref(false)
const uploadQueueDragOrigin = ref<{ x: number; y: number; right: number; bottom: number } | null>(null)
const uploadQueueStorageKey = 'pfe-upload-queue-v1'
const uploadQueueWidgetStyle = computed(() => ({
  right: `${Math.max(8, Math.round(uploadQueuePos.value.right))}px`,
  bottom: `${Math.max(8, Math.round(uploadQueuePos.value.bottom))}px`,
}))
function loadUploadQueuePrefs() {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(uploadQueueStorageKey)
    if (!raw) return
    const parsed = JSON.parse(raw)
    uploadQueueMinimized.value = !!parsed?.minimized
    const right = Number(parsed?.right)
    const bottom = Number(parsed?.bottom)
    if (Number.isFinite(right) && Number.isFinite(bottom)) {
      uploadQueuePos.value = { right, bottom }
    }
  } catch {}
}
function persistUploadQueuePrefs() {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(uploadQueueStorageKey, JSON.stringify({
      minimized: uploadQueueMinimized.value,
      right: uploadQueuePos.value.right,
      bottom: uploadQueuePos.value.bottom,
    }))
  } catch {}
}
function toggleUploadQueueMinimized() {
  uploadQueueMinimized.value = !uploadQueueMinimized.value
  persistUploadQueuePrefs()
}
async function retryFailedUpload(id: string) {
  const img = images.value.find((x) => x.id === id)
  const lastError = (img?.record as any)?.upload?.lastError
  if (lastError) {
    console.error('[upload] retry failed item', {
      id,
      name: img?.name,
      error: lastError,
    })
  }
  await runner.retry(id).catch(() => {})
  if (!runner.running) {
    try {
      runner.startAuto?.(900)
    } catch {}
  }
}
async function removeFailedUpload(id: string) {
  deleteBusyCount.value = 1
  deleteBusy.value = true
  try {
    await removeOneCore(id)
  } finally {
    deleteBusy.value = false
    deleteBusyCount.value = 0
  }
}
async function retryAllFailedUploads() {
  await runner.retryAllFailed().catch(() => {})
  if (!runner.running) {
    try {
      runner.startAuto?.(900)
    } catch {}
  }
}
function onUploadQueueDragMove(e: PointerEvent) {
  if (!uploadQueueDragging.value || !uploadQueueDragOrigin.value) return
  const dx = e.clientX - uploadQueueDragOrigin.value.x
  const dy = e.clientY - uploadQueueDragOrigin.value.y
  uploadQueuePos.value = {
    right: Math.max(8, uploadQueueDragOrigin.value.right - dx),
    bottom: Math.max(8, uploadQueueDragOrigin.value.bottom - dy),
  }
}
function stopUploadQueueDrag() {
  if (!uploadQueueDragging.value) return
  uploadQueueDragging.value = false
  uploadQueueDragOrigin.value = null
  persistUploadQueuePrefs()
  window.removeEventListener('pointermove', onUploadQueueDragMove)
  window.removeEventListener('pointerup', stopUploadQueueDrag)
}
function startUploadQueueDrag(e: PointerEvent) {
  if (e.button !== 0) return
  uploadQueueDragging.value = true
  uploadQueueDragOrigin.value = {
    x: e.clientX,
    y: e.clientY,
    right: uploadQueuePos.value.right,
    bottom: uploadQueuePos.value.bottom,
  }
  window.addEventListener('pointermove', onUploadQueueDragMove)
  window.addEventListener('pointerup', stopUploadQueueDrag)
}
const canAddSelectionToCart = computed(() => {
  if (!selectedIds.value.length) return false
  return selectedIds.value.every((id) => {
    const img = images.value.find((x) => x.id === id)
    return isImageCartReady(img)
  })
})
const canAddAllImagesToCart = computed(() => {
  if (!images.value.length) return false
  return images.value.every((img) => isImageCartReady(img))
})

function resolveMoneyAmount(m: Money | undefined | null): number {
  if (m == null) return 0
  if (typeof m === 'number') return m
  return m.amount ?? 0
}
function imagePriceLabel(img: ImageVM): string | null {
  const preset = (img.record as any)?.edits?.preset
  const sizeTitle = preset?.size?.title
  const qty = Math.max(1, Number(preset?.quantity ?? 1))
  const opt = props.sizeOptions?.find((o) => o.title === sizeTitle)
  if (!opt) return null
  const unit = resolveMoneyAmount((opt as any).priceSpecial ?? opt.price)
  if (!unit) return null
  const total = unit * qty
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(total)
}
function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((Number(cents || 0) || 0) / 100)
}
const lastKnownTotalPriceLabel = ref('')
watch(
  () => String(props.totalPriceLabel || '').trim(),
  (label) => {
    if (label) lastKnownTotalPriceLabel.value = label
  },
  { immediate: true },
)
const effectiveCartButtonPriceLabel = computed(() => {
  const sessionCode = String(project.projectCode.value || '').trim()
  if (sessionCode && Array.isArray(cart.items)) {
    const projectImageTotalCents = cart.items.reduce((sum, item: any) => {
      if (String(item?.kind || item?.type || '') !== 'image') return sum
      if (String(item?.payload?.editorProjectId || '') !== sessionCode) return sum
      const line = Number(item?.lineTotalCents ?? item?.lineGrossTotalCents ?? 0)
      const fallback = Number(item?.unitPriceCents ?? item?.unitGrossCents ?? 0) * Math.max(1, Number(item?.quantity ?? 1))
      return sum + Math.max(0, line || fallback || 0)
    }, 0)
    if (projectImageTotalCents > 0) return formatCents(projectImageTotalCents)
  }
  return String(props.totalPriceLabel || '').trim() || lastKnownTotalPriceLabel.value
})
const filterProductId = ref<string>('all')
const filterSizeKey = ref<string>('all')
/** Tag-Filter: 'all' | 'untagged' | <tag> */
const filterTag = ref<string>('all')
/** Zuletzt beim Upload vergebene Tags – Vorbelegung für den nächsten Upload-Dialog */
const lastUploadTags = ref<string[]>([])
const LAST_UPLOAD_TAGS_KEY = 'pfe.editor.lastUploadTags'
try { lastUploadTags.value = normalizeTags(JSON.parse(localStorage.getItem(LAST_UPLOAD_TAGS_KEY) || '[]')) } catch {}
const LS_KEY = computed(() => `mi-toolbar:${props.categoryId}:${props.productId}`)
const currentUploadTargetCategoryId = computed(() => Number(pendingTargetCategoryId.value ?? props.categoryId ?? 0) || 0)
const currentUploadTargetProductId = computed(() => Number(pendingTargetProductId.value ?? props.productId ?? 0) || 0)

function loadToolbarPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY.value)
    if (!raw) return
    const p = JSON.parse(raw)
    if (typeof p.search === 'string') search.value = p.search
    if (p.sortBy === 'newest' || p.sortBy === 'oldest' || p.sortBy === 'name') sortBy.value = p.sortBy
    if (typeof p.filterProductId === 'string') filterProductId.value = p.filterProductId
    if (typeof p.filterSizeKey === 'string') filterSizeKey.value = p.filterSizeKey
    if (typeof p.filterTag === 'string') filterTag.value = p.filterTag
  } catch {}
}
const saveToolbarPrefs = debounce(() => {
  try {
    localStorage.setItem(LS_KEY.value, JSON.stringify({
      search: search.value,
      sortBy: sortBy.value,
      filterProductId: filterProductId.value,
      filterSizeKey: filterSizeKey.value,
      filterTag: filterTag.value,
    }))
  } catch {}
}, 200)
watch([search, sortBy, filterProductId, filterSizeKey, filterTag], saveToolbarPrefs)
watch([() => props.categoryId, () => props.productId], loadToolbarPrefs, { immediate: true })

/* ------------------------------------------------
 * State
 * ------------------------------------------------ */
const layoutVersion = ref(0)
const images = ref<ImageVM[]>([])
const plannedUploadTotal = ref(0)
const preparingUploadCount = ref(0)
const sessionRecordsState = ref<Record<string, ImageRecord>>({})
const liveUploadState = ref<Record<string, { state: string; phase: string; progressPct: number; uploadedBytes: number; totalBytes: number }>>({})
const trackedUploadIds = ref<Record<string, true>>({})
const recentInsertOrder = ref<Record<string, number>>({})
let recentInsertSeq = 0

function markRecentInsert(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  recentInsertSeq += 1
  recentInsertOrder.value = {
    ...recentInsertOrder.value,
    [id]: recentInsertSeq,
  }
}
let uploadStatsPollTimer: ReturnType<typeof setInterval> | null = null
let uploadStatsRefreshTimer: ReturnType<typeof setTimeout> | null = null
let processingUploadsPollTimer: ReturnType<typeof setTimeout> | null = null
const duplicateRemotePollTimers = new Map<string, ReturnType<typeof setInterval>>()
const sessionRecordFetchCache = new Map<string, { ts: number; promise: Promise<ImageRecord[]> }>()
const SESSION_RECORD_FETCH_CACHE_MS = 700

function invalidateSessionRecordFetchCache(sessionCodeRaw?: string) {
  const sessionCode = String(sessionCodeRaw || '').trim()
  if (!sessionCode) {
    sessionRecordFetchCache.clear()
    return
  }
  sessionRecordFetchCache.delete(sessionCode)
}

async function refreshUploadStatsFromDb() {
  project.loadFromStorage()
  const sessionCode = project.projectCode.value
  const uploadBusy = preparingUploadCount.value > 0 || plannedUploadTotal.value > 0 || Object.keys(liveUploadState.value).length > 0
  if (uploadBusy) {
    sessionRecordsState.value = Object.fromEntries(transientUploads.all.map((rec) => [String(rec.id), rec]))
  } else if (sessionCode) {
    await fetchSessionRecordsServerFirst(sessionCode)
  } else {
    sessionRecordsState.value = Object.fromEntries(transientUploads.all.map((rec) => [String(rec.id), rec]))
  }
}
function scheduleRefreshUploadStats(delay = 80) {
  if (uploadStatsRefreshTimer) clearTimeout(uploadStatsRefreshTimer)
  uploadStatsRefreshTimer = setTimeout(() => {
    uploadStatsRefreshTimer = null
    refreshUploadStatsFromDb().catch(() => {})
  }, delay)
}
async function pollProcessingUploadsOnce() {
  const processingIds = Object.entries(liveUploadState.value)
    .filter(([, live]) => String(live?.state || '').trim() === 'processing')
    .map(([id]) => String(id))
  if (!processingIds.length) return

  const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
  const projectCode = await project.ensureProject().catch(() => '')
  if (!editorBaseUrl || !projectCode) return

  const items = await fetchProjectUploads({
    editorBaseUrl,
    projectCode,
    limit: Math.max(100, processingIds.length * 2),
    offset: 0,
  }).catch(() => [])
  const byId = new Map(items.map((item) => [String(item?.id || ''), item]))

  for (const id of processingIds) {
    const item = byId.get(id)
    if (!item) continue
    upsertProjectUploadsCacheItem(item, { editorBaseUrl, projectCode })
    let nextState = String((item as any)?.status || ((item as any)?.processing ? 'processing' : 'done') || 'done').trim() || 'done'
    const previewUrl = String((item as any)?.thumbUrl || (item as any)?.previewUrl || '').trim()
    const fullscreenPreviewUrl = String((item as any)?.fullscreenPreviewUrl || '').trim()
    const patch = {
      width: item.width ?? undefined,
      height: item.height ?? undefined,
      upload: {
        state: nextState,
        phase: nextState,
        progressPct: nextState === 'done' ? 100 : 0,
        uploadedBytes: Number(item.bytes || 0),
        totalBytes: Number(item.bytes || 0),
        ...(previewUrl ? { previewUrl, thumbUrl: previewUrl } : {}),
        ...(fullscreenPreviewUrl ? { fullscreenPreviewUrl } : {}),
        remoteUrl: String(item.remoteUrl || ''),
        updatedAt: Date.now(),
      },
    } as any
    const currentRecord = transientUploads.get(id) || sessionRecordsState.value[id] || null
    const patchedRecord = mergeRecordPatch((currentRecord as any) || {}, patch as any) as any
    if (nextState !== 'failed' && isRecordUploadEffectivelyDone(patchedRecord, patch.upload)) {
      nextState = 'done'
      patch.upload.state = 'done'
      patch.upload.phase = 'done'
      patch.upload.progressPct = 100
    }
    emitUploadEvent({
      type: 'upload:patched',
      id,
      patch,
    })
  }
}
function scheduleProcessingUploadsPoll(delay = 1500) {
  if (processingUploadsPollTimer) clearTimeout(processingUploadsPollTimer)
  processingUploadsPollTimer = setTimeout(() => {
    processingUploadsPollTimer = null
    void pollProcessingUploadsOnce().finally(() => {
      const hasProcessing = Object.values(liveUploadState.value).some((live) => String(live?.state || '').trim() === 'processing')
      if (hasProcessing) scheduleProcessingUploadsPoll(1500)
    })
  }, delay)
}
const rawUploadStats = computed(() => {
  const merged = new Map<string, { rec: ImageRecord | any; state: string; phase: string; progressPct: number }>()
  const relevantIds = new Set<string>([
    ...Object.keys(trackedUploadIds.value),
    ...Object.keys(liveUploadState.value),
  ])
  for (const id of relevantIds) {
    const rec = sessionRecordsState.value[id]
    if (!rec) continue
    merged.set(id, {
      rec,
      state: String((rec as any)?.upload?.state || 'idle'),
      phase: String((rec as any)?.upload?.phase || ''),
      progressPct: Math.max(0, Math.min(100, Number((rec as any)?.upload?.progressPct || 0) || 0)),
    })
  }
  for (const rec of Object.values(sessionRecordsState.value)) {
    const id = String((rec as any)?.id || '').trim()
    if (!id || relevantIds.has(id)) continue
    if (!shouldHoldUploadSkeleton(rec)) continue
    merged.set(id, {
      rec,
      state: String((rec as any)?.upload?.state || 'idle'),
      phase: String((rec as any)?.upload?.phase || ''),
      progressPct: Math.max(0, Math.min(100, Number((rec as any)?.upload?.progressPct || 0) || 0)),
    })
  }
  for (const [id, live] of Object.entries(liveUploadState.value)) {
    merged.set(String(id), {
      rec: merged.get(String(id))?.rec || sessionRecordsState.value[String(id)] || transientUploads.get(String(id)) || null,
      state: String(live?.state || merged.get(String(id))?.state || 'queued'),
      phase: String(live?.phase || merged.get(String(id))?.phase || ''),
      progressPct: Math.max(0, Math.min(100, Number(live?.progressPct || 0) || 0)),
    })
  }
  const total = Math.max(merged.size, plannedUploadTotal.value)
  let done = 0
  let uploading = 0
  let queued = 0
  let failed = 0
  let progressUnits = 0
  const resolveVisualUnit = (stateRaw: string, phaseRaw: string, pctRaw: number) => {
    const state = String(stateRaw || '').trim()
    const phase = String(phaseRaw || '').trim()
    const pct = Math.max(0, Math.min(100, Number(pctRaw || 0) || 0))
    if (state === 'done' || phase === 'done') return 1
    if (state === 'failed' || phase === 'failed') return 1
    if (state === 'processing' || phase === 'processing') return 0.97
    if (state === 'completing' || phase === 'completing') return 0.9
    if (state === 'uploading' || phase === 'uploading') return Math.max(0.08, Math.min(0.82, 0.08 + (pct / 100) * 0.74))
    if (state === 'presigning' || phase === 'presigning') return 0.04
    if (state === 'queued' || phase === 'queued') return 0.02
    return 0
  }
  for (const entry of merged.values()) {
    const state = String(entry.state || '').trim()
    const phase = String(entry.phase || '').trim()
    if (isRecordUploadEffectivelyDone(entry.rec, entry)) {
      done++
      progressUnits += 1
      continue
    }
    const unit = resolveVisualUnit(state, phase, entry.progressPct)
    if (state === 'failed' || phase === 'failed') {
      failed++
      progressUnits += unit
      continue
    }
    if (state === 'uploading' || phase === 'uploading') {
      uploading++
      progressUnits += unit
      continue
    }
    if (state === 'queued' || state === 'presigning' || state === 'completing' || state === 'processing' || phase === 'queued' || phase === 'presigning' || phase === 'completing' || phase === 'processing') {
      queued++
      progressUnits += unit
      continue
    }
  }
  const active = queued + uploading
  progressUnits = Math.min(total, progressUnits)
  const progress = total > 0 ? Math.max(0, Math.min(100, Math.round((progressUnits / total) * 100))) : 0
  const visualDone = total > 0 ? Math.max(done, Math.min(total, Math.floor(progressUnits))) : done
  return { queued, uploading, done, visualDone, failed, active, total, progress, preparing: preparingUploadCount.value }
})
const uploadStatsFloor = ref<{ total: number; progress: number; visualDone: number }>({ total: 0, progress: 0, visualDone: 0 })
watch(rawUploadStats, (raw) => {
  const batchActive = raw.total > 0 || raw.active > 0 || raw.preparing > 0
  if (!batchActive) {
    if (uploadStatsFloor.value.total !== 0 || uploadStatsFloor.value.progress !== 0 || uploadStatsFloor.value.visualDone !== 0) {
      uploadStatsFloor.value = { total: 0, progress: 0, visualDone: 0 }
    }
    return
  }

  uploadStatsFloor.value = {
    total: Math.max(uploadStatsFloor.value.total, raw.total),
    progress: Math.max(uploadStatsFloor.value.progress, raw.progress),
    visualDone: Math.max(uploadStatsFloor.value.visualDone, raw.visualDone),
  }
}, { immediate: true })
const uploadStats = computed(() => {
  const raw = rawUploadStats.value
  const batchActive = raw.total > 0 || raw.active > 0 || raw.preparing > 0
  if (!batchActive) return raw

  return {
    ...raw,
    total: uploadStatsFloor.value.total,
    progress: uploadStatsFloor.value.progress,
    visualDone: Math.min(uploadStatsFloor.value.total, uploadStatsFloor.value.visualDone),
  }
})
const failedUploadItems = computed(() =>
  images.value
    .filter((img) => String((img.record as any)?.upload?.state || '') === 'failed')
    .map((img) => ({
      id: img.id,
      name: String(img.name || 'Bild'),
    }))
    .slice(0, 4),
)
const showUploadQueue = computed(() => {
  const stats = uploadStats.value
  if (stats.total <= 0) return false
  if (stats.preparing > 0 || stats.active > 0 || stats.failed > 0) return true
  return (stats.done + stats.failed) < stats.total
})
const uploadQueueSummaryItems = computed(() => ([
  {
    key: 'preparing',
    label: `${uploadStats.value.preparing} wird vorbereitet`,
    visible: uploadStats.value.preparing > 0,
  },
  {
    key: 'queued',
    label: `${uploadStats.value.queued} in Verarbeitung`,
    visible: uploadStats.value.queued > 0,
  },
  {
    key: 'failed',
    label: `${uploadStats.value.failed} Fehler`,
    visible: uploadStats.value.failed > 0,
  },
]))
const uploadQueueMiniLabel = computed(() => {
  const firstVisible = uploadQueueSummaryItems.value.find((item) => item.visible)
  return firstVisible?.label || 'In Verarbeitung'
})
watch(
  uploadStats,
  (raw) => {
    if (uploadPreparingModal.value && (raw.active > 0 || raw.done > 0 || raw.failed > 0)) {
      uploadPreparingModal.value = null
    }
    if (plannedUploadTotal.value > 0 && raw.active <= 0 && (raw.done + raw.failed) >= plannedUploadTotal.value) {
      plannedUploadTotal.value = 0
      if (Object.keys(liveUploadState.value).length > 0) liveUploadState.value = {}
      if (Object.keys(trackedUploadIds.value).length > 0) trackedUploadIds.value = {}
      scheduleRefreshUploadStats(0)
    }
    if (raw.active <= 0 && raw.total <= 0 && plannedUploadTotal.value <= 0) {
      if (preparingUploadCount.value !== 0) preparingUploadCount.value = 0
      if (Object.keys(liveUploadState.value).length > 0) liveUploadState.value = {}
      if (Object.keys(trackedUploadIds.value).length > 0) trackedUploadIds.value = {}
      scheduleRefreshUploadStats(0)
    }
    const hasProcessing = Object.values(liveUploadState.value).some((live) => String(live?.state || '').trim() === 'processing')
    if (hasProcessing) scheduleProcessingUploadsPoll(1200)
  },
  { immediate: true, flush: 'post' },
)
watch(
  () => uploadStats.value.failed,
  (failed) => {
    if (failed > uploadQueueAutoOpenedForErrorCount.value) {
      uploadQueueMinimized.value = false
      uploadQueueAutoOpenedForErrorCount.value = failed
      persistUploadQueuePrefs()
    }
    if (failed === 0) {
      uploadQueueAutoOpenedForErrorCount.value = 0
    }
  },
  { immediate: true },
)
const totalQuantity = computed(() => images.value.reduce((sum, img) => sum + Math.max(1, Number(img.ui?.quantity || 1)), 0))
const uploadIssueFilter = ref<'all' | 'issues'>('all')
const isDragging = ref(false)
const selectedIds = ref<string[]>([])
let heavyTileSyncQueued = false
const liveTileCropperIds = ref<Record<string, true>>({})
const passiveTileCropperIds = ref<Record<string, true>>({})
const interactingCropperIds = new Set<string>()
const primaryLiveTileId = ref('')
const cropPerf = reactive({
  canvasTotal: 0,
  canvasN: 0,
  cropperTotal: 0,
  cropperN: 0,
})
const canvasAvgMs = computed(() => (cropPerf.canvasN ? cropPerf.canvasTotal / cropPerf.canvasN : 0))
const cropperAvgMs = computed(() => (cropPerf.cropperN ? cropPerf.cropperTotal / cropPerf.cropperN : 0))
const showPerfBadge = computed(() => cropEngine.value === 'canvas')
const hasSelection = computed(() => selectedIds.value.length > 0)
const selectedIdSet = computed(() => new Set(selectedIds.value))
const canSelectAll = computed(() => images.value.length > 0)
const allVisibleSelected = computed(() => {
  const total = viewImages.value.length
  if (!total) return false
  if (selectedIds.value.length < total) return false
  const selected = selectedIdSet.value
  return viewImages.value.every((img) => selected.has(img.id))
})
watch(
  () => [selectedIds.value.join(','), images.value.length],
  () => {
    scheduleHeavyTileSync()
  },
  { flush: 'post' },
)

function scheduleHeavyTileSync() {
  if (heavyTileSyncQueued) return
  heavyTileSyncQueued = true
  queueMicrotask(() => {
    heavyTileSyncQueued = false
    syncTileCropperMountBudget()
    void syncHeavyTileBudget()
  })
}

function tileCropperBudget() {
  if (isMobile.value) return 4
  const total = images.value.length
  if (total >= 60) return 4
  if (total >= 40) return 6
  if (total >= 24) return 8
  if (total >= 12) return 12
  return 16
}

function sortedVisibleTileIds() {
  return [...visibleTileIds].sort((a, b) => {
    const aEl = tileElMap.get(a)
    const bEl = tileElMap.get(b)
    if (!aEl && !bEl) return 0
    if (!aEl) return 1
    if (!bEl) return -1
    const aRect = aEl.getBoundingClientRect()
    const bRect = bEl.getBoundingClientRect()
    const topDelta = Math.abs(aRect.top - bRect.top)
    if (topDelta > 8) return aRect.top - bRect.top
    return aRect.left - bRect.left
  })
}

function syncTileCropperMountBudget() {
  const live: Record<string, true> = {}
  const passive: Record<string, true> = {}
  const budget = tileCropperBudget()
  const total = images.value.length
  const allowPassiveVisible = total < 12

  const push = (idRaw: string, target: Record<string, true>, limit = Number.POSITIVE_INFINITY) => {
    const id = String(idRaw || '').trim()
    if (!id) return
    if (target[id]) return
    if (Object.keys(target).length >= limit) return
    target[id] = true
  }

  if (primaryLiveTileId.value) push(primaryLiveTileId.value, live, budget)
  if (selectedIds.value.length === 1) push(selectedIds.value[0], live, budget)
  for (const id of interactingCropperIds) push(id, live, budget)
  for (const id of recentlyActiveIds) push(id, passive)
  for (const id of retainedVisibleTileIds) push(id, passive)

  if (allowPassiveVisible) {
    for (const id of sortedVisibleTileIds()) {
      if (!live[id]) passive[id] = true
    }
  }
  for (const id of recentlyActiveIds) {
    if (!live[id]) passive[id] = true
  }

  liveTileCropperIds.value = live
  passiveTileCropperIds.value = passive
}

function shouldRenderLiveTileCanvas(img: ImageVM) {
  if (isFitInMode(img)) return false
  if (fs.open && fs.id === img.id) return false
  if (img.ui.previewLoading) return false
  if (!String(img.ui.previewUrl || img.ui.fullPreviewUrl || '').trim()) return false
  const id = String(img.id || '').trim()
  if (!id) return false
  return !!liveTileCropperIds.value[id]
}

// Große Fit-In-Vorschau: gespeicherte Vollbild-Links können abgelaufen sein
// (presigned, 3 Tage). Dann der Reihe nach auf frischere Quellen ausweichen.
const fsFitPreviewFailed = ref<Record<string, string[]>>({})
function fsFitPreviewCandidates(img: ImageVM): string[] {
  return [...new Set([
    String(img.ui.fullPreviewUrl || '').trim(),
    remoteFullUrl(img.record),
    String(img.ui.previewUrl || '').trim(),
  ].filter(Boolean))]
}
function fsFitPreviewSrc(img: ImageVM): string {
  const failed = fsFitPreviewFailed.value[img.id] || []
  const list = fsFitPreviewCandidates(img)
  return list.find((url) => !failed.includes(url)) || list[list.length - 1] || ''
}
function onFsFitPreviewError(img: ImageVM) {
  const current = fsFitPreviewSrc(img)
  const failed = fsFitPreviewFailed.value[img.id] || []
  if (!current || failed.includes(current)) return
  fsFitPreviewFailed.value = { ...fsFitPreviewFailed.value, [img.id]: [...failed, current] }
}

// Multi-Sidebar: geänderte, noch nicht übernommene Optionen nur ANZEIGEN.
// Bewusst getrennt von img.ui.options, die gespeichert und in den Warenkorb
// übernommen werden.
const optionPreviewById = ref<Record<string, UIOption[]>>({})
function setOptionPreview(ids: string[], options: any[] | null) {
  if (!options || !options.length) {
    if (Object.keys(optionPreviewById.value).length) optionPreviewById.value = {}
    return
  }
  const patch: UIOption[] = options
    .map((o: any) => ({ groupId: normId(o?.groupId ?? ''), variantId: normId(o?.variantId ?? ''), value: String(o?.value ?? '') }))
    .filter((o) => !!o.groupId)
  const next: Record<string, UIOption[]> = {}
  for (const id of ids || []) {
    const img = images.value.find((x) => x.id === id)
    if (!img) continue
    const map = new Map<string, UIOption>()
    for (const o of img.ui.options || []) map.set(normId(o.groupId), o)
    for (const o of patch) map.set(o.groupId, o)
    next[img.id] = [...map.values()]
  }
  optionPreviewById.value = next
}
function previewVm(img: ImageVM): ImageVM {
  const preview = optionPreviewById.value[img.id]
  return preview ? ({ ...img, ui: { ...img.ui, options: preview } } as ImageVM) : img
}
function displayFitInMode(img: ImageVM) {
  return isFitInMode(previewVm(img))
}
function displayWhiteBorderLabel(img: ImageVM) {
  return whiteBorderLabel(previewVm(img))
}

function tileDisplayUrl(img: ImageVM): string {
  return String(img.ui.previewUrl || img.ui.fullPreviewUrl || '').trim()
}

function shouldMountTileCropper(img: ImageVM) {
  if (isFitInMode(img)) return false
  if (fs.open && fs.id === img.id) return false
  return !!String(img.ui.previewUrl || img.ui.fullPreviewUrl || '').trim()
}

function shouldMountPassiveTileCropper(img: ImageVM) {
  return false
}

async function syncHeavyTileBudget() {
  for (const img of images.value) {
    const keepHeavy = (fs.open && fs.id === img.id)
      || useCanvasPrototypeFor(img.id)

    if (keepHeavy && !isFitInMode(img)) {
      if (!(fs.open && fs.id === img.id)) {
        ensureObjectUrlsForImage(img)
      }
      if (fs.open && fs.id === img.id) {
        ensureObjectUrlsForImage(img, { full: true })
      }
    } else {
      await releaseHeavyResourcesForImage(img)
    }
  }
}

const lastClickedIndex = ref<number | null>(null)

/* ── Global upload zone ref ── */
const globalUploadRef = ref<{ openPicker: () => void; handleFiles: (files: FileList | File[]) => void; dragOver: any } | null>(null)

/* ── Post-Upload Overlay ── */
const pendingFiles = ref<any[]>([])
const pendingImportedRecordIds = ref<string[]>([])
const pendingTargetProductId = ref<number | null>(null)
const pendingTargetCategoryId = ref<number | null>(null)
const showPostUploadOverlay = ref(false)
const uploadPinnedGroupKey = ref<string>('')
const uploadPinnedRootKey = ref<string>('')
const uploadPinnedCategoryId = ref<number | null>(null)
const pendingRevealCategoryId = ref<number | null>(null)
const showEmptyCategoryUploadPrompt = ref(false)
const uploadPromptSource = ref<'desktop' | 'phone'>('desktop')
const inlineUploadSource = ref<'desktop' | 'phone'>('desktop')
const phoneBridgeActive = ref(false)
const phoneUploadUrl = ref('')
const phoneQrDataUrl = ref('')
const phoneQrBusy = ref(false)
const remoteSyncBusy = ref(false)
const lastRemoteSyncAt = ref('')
let remotePollTimer: ReturnType<typeof setInterval> | null = null
let stopMercure: null | (() => void) = null
const mercureSubscribedCategoriesKey = ref('')
const deletedRemoteIds = new Set<string>()
const categoryGroupElMap = new Map<string, HTMLElement>()

// 3D-Vorschau-Overlay
const preview3dImageId = ref<string | null>(null)
const preview3dAspectRatio = ref<number>(75 / 30)
const preview3dModelConfig = ref<any>(null)
const preview3dRecord = ref<any | null>(null)
const preview3dGeometry = ref<any | null>(null)
/** preview3d v2: Ebenen [Elternkategorie, Kategorie, Produkt] + Optionsgruppen für die Auswahl in 3D */
const preview3dV2Layers = ref<any[] | null>(null)
const preview3dOptionGroups = ref<any[]>([])
const preview3dSizeMm = ref<{ w: number; h: number } | null>(null)
const preview3dSelectedIds = computed(() => {
  const img = preview3dImageId.value ? images.value.find((x) => x.id === preview3dImageId.value) : null
  return (img?.ui.options || []).map((o) => String(o.variantId))
})

function openPreview3d(img: ImageVM) {
  const preset = img.record.edits?.preset as any
  const w = Number(preset?.size?.widthMm || 0)
  const h = Number(preset?.size?.heightMm || 0)
  // Orientierungsbewusstes Seitenverhältnis (orientMode/swapAR), damit die
  // Vorschau-Plane bei automatisch gedrehten Formaten nicht verzerrt.
  const effAr = effectiveAR(img)
  preview3dAspectRatio.value =
    Number.isFinite(effAr as number) && (effAr as number) > 0
      ? (effAr as number)
      : (w && h) ? w / h : 75 / 30
  preview3dModelConfig.value = preview3dConfigForImage(img) || props.previewModelConfig || null
  preview3dRecord.value = img.record
  preview3dGeometry.value = (preset?.size as any)?.geometry || null
  preview3dV2Layers.value = preview3dV2LayersForImage(img)
  preview3dOptionGroups.value = preview3dV2Layers.value ? tileVariantGroupsFor(img) : []
  preview3dSizeMm.value = w && h ? { w, h } : null
  ensureWorkingBlobForImage(img).catch(() => {})
  preview3dImageId.value = img.id
}

function categoryConfigFor(id?: number | null): any {
  const cid = Number(id || 0)
  return cid > 0 ? (props.categoryEditorConfigs?.[cid] ?? null) : null
}

function categoryPreviewAllowed(id?: number | null): boolean {
  const cfg = categoryConfigFor(id)
  const tilePreview = cfg?.tilePreview ?? cfg?.previewTile ?? cfg?.singleTilePreview ?? null
  if (tilePreview === false || tilePreview === 'none' || tilePreview?.enabled === false) return false
  if (cfg?.previewInTile === false) return false
  return true
}

function preview3dConfigForImage(img: ImageVM): any | null {
  const parentId = Number((img.record as any).parentCategoryId || 0)
  const catId = Number(img.record.categoryId || 0)
  const currentCategoryId = Number(props.categoryId || 0)
  const candidates = [catId, parentId, currentCategoryId].filter((id, index, arr) => id > 0 && arr.indexOf(id) === index)
  for (const id of candidates) {
    const cfg = categoryConfigFor(id)
    const preview3d = cfg?.preview3d
    if (preview3d && typeof preview3d === 'object') return preview3d
  }
  return null
}

/** preview3d v2 (Wandbilder): Kategorie- und Produkt-Ebenen, spätere gewinnen */
function preview3dV2LayersForImage(img: ImageVM): any[] | null {
  const isV2 = (c: any) => !!c && typeof c === 'object' && Number(c.v) === 2
  const layers: any[] = []
  const push = (c: any) => { if (isV2(c) && !layers.includes(c)) layers.push(c) }
  const parentId = Number((img.record as any).parentCategoryId || 0)
  const catId = Number(img.record.categoryId || props.categoryId || 0)
  const product: any = (catalogBootstrap.value as any)?.products?.[String(recordProductId((img.record as any) || {}) || '')] || null
  for (const id of [parentId, catId]) {
    if (id <= 0) continue
    push(categoryConfigFor(id)?.preview3d ?? product?.editorConfigByCategory?.[id]?.preview3d ?? product?.editorConfigByCategory?.[String(id)]?.preview3d)
  }
  push(product?.editorOverrideConfig?.preview3d)
  return layers.length ? layers : null
}

/** 2D-Rahmen in der Kachel (Schattenfuge, Bilderrahmen, Posterleiste) – gleiche Regeln wie die 3D-Vorschau */
const p3dLibrary = shallowRef<Preview3dLibrary>(BUILTIN_LIBRARY)
const p3dLibraryRev = ref(0)
let p3dLibraryRequested = false
function frame2dFor(img: ImageVM): Frame2d | null {
  const vm = previewVm(img)
  const layers = preview3dV2LayersForImage(vm)
  if (!layers) return null
  if (!p3dLibraryRequested) {
    p3dLibraryRequested = true
    void loadPreview3dLibrary(String((getWidgetConfig() as any)?.editorBaseUrl || '')).then((lib) => {
      p3dLibrary.value = lib
      p3dLibraryRev.value += 1
    })
  }
  const preset = vm.record?.edits?.preset as any
  const w = Number(preset?.size?.widthMm || 0)
  const h = Number(preset?.size?.heightMm || 0)
  if (!(w > 0) || !(h > 0)) return null
  // Druckmaß wie der Ausschnitt ausgerichtet (hoch/quer)
  const ar = Number(effectiveAR(vm) || (vm.crop && vm.crop.h ? vm.crop.w / vm.crop.h : w / h))
  const long = Math.max(w, h)
  const short = Math.min(w, h)
  const printMm = ar >= 1 ? { w: long, h: short } : { w: short, h: long }
  const state = resolvePreview3dState(mergePreview3dConfigs(...layers), (vm.ui.options || []).map((o) => String(o.variantId || '')).filter(Boolean), p3dLibrary.value)
  return frame2dFromState(state, p3dLibrary.value, printMm)
}

/** Auswahl in der 3D-Ansicht → echte Produktoption (wie das Kachel-Dropdown: speichern, Preis, Warenkorb) */
async function onPreview3dSelect(p: { groupId: string; variantId: string }) {
  const img = preview3dImageId.value ? images.value.find((x) => x.id === preview3dImageId.value) : null
  if (!img) return
  const group = tileVariantGroupsFor(img).find((g: any) => normId(g?.id) === normId(p.groupId))
  if (!group) return
  await onTileGroupChange(img, group, p.variantId)
}

function hasPreview3dTile(img: ImageVM): boolean {
  return !!preview3dConfigForImage(img) || !!preview3dV2LayersForImage(img)
}

function resolveCategoryTitleForImage(img: ImageVM, fallbackCategoryId?: number | null): string {
  const rec: any = img.record ?? {}
  const preset: any = rec.edits?.preset ?? {}
  const cid = Number(rec.categoryId ?? preset.categoryId ?? fallbackCategoryId ?? 0)
  const parentId = Number(rec.parentCategoryId ?? preset.parentCategoryId ?? 0)
  const storedTitle = String(rec.categoryTitle ?? preset.categoryTitle ?? '').trim()
  const storedParentTitle = String(rec.parentCategoryTitle ?? preset.parentCategoryTitle ?? '').trim()
  const mappedTitle = String(props.categoryTitlesMap?.[cid] || '').trim()
  const mappedParentTitle = String(props.categoryTitlesMap?.[parentId] || '').trim()

  if (storedTitle) return storedTitle
  if (mappedTitle) return mappedTitle
  if (cid === Number(props.categoryId) && props.categoryLabel) return String(props.categoryLabel)
  if (storedParentTitle && parentId === cid) return storedParentTitle
  if (mappedParentTitle && parentId === cid) return mappedParentTitle
  return cid ? `Kategorie ${cid}` : 'Nicht zugeordnet'
}

/* ── Accordion (collapsible product groups) ── */
const collapsedGroups = ref<Set<string>>(new Set())
function openOnlyCategoryGroup(categoryIdRaw: string | number | null | undefined) {
  const wantedKey = String(Number(categoryIdRaw || 0) || '').trim()
  if (!wantedKey) return
  const wantedGroup = viewImagesByCategory.value.find((group) => String(group?.key || '').trim() === wantedKey)
  const wantedRootKey = String(wantedGroup?.rootCategoryKey || '').trim()
  const rootKeysToKeepOpen = new Set<string>(
    wantedRootKey ? groupKeysForRoot(wantedRootKey) : [wantedKey],
  )
  const next = new Set<string>()
  for (const group of viewImagesByCategory.value) {
    const key = String(group?.key || '').trim()
    if (!key || rootKeysToKeepOpen.has(key)) continue
    next.add(key)
  }
  collapsedGroups.value = next
}
function setCategoryGroupRef(el: Element | null, keyRaw: string) {
  const key = String(keyRaw || '').trim()
  if (!key) return
  if (el instanceof HTMLElement) {
    categoryGroupElMap.set(key, el)
    return
  }
  categoryGroupElMap.delete(key)
}
async function revealCategoryGroup(categoryIdRaw: string | number | null | undefined, behavior: ScrollBehavior = 'smooth') {
  const wantedKey = String(Number(categoryIdRaw || 0) || '').trim()
  if (!wantedKey) return
  openOnlyCategoryGroup(wantedKey)
  await nextTick()
  const wantedGroup = viewImagesByCategory.value.find((group) => String(group?.key || '').trim() === wantedKey)
  const wantedRootKey = String(
    wantedGroup?.rootCategoryKey
    || props.categoryRootIdsMap?.[wantedKey as any]
    || wantedKey,
  ).trim()
  const targetKey = String(
    wantedGroup?.key
    || viewImagesByCategory.value.find((group) => String(group?.rootCategoryKey || '').trim() === wantedRootKey)?.key
    || wantedKey,
  ).trim()
  const el = categoryGroupElMap.get(targetKey)
  if (!el) {
    pendingRevealCategoryId.value = Number(wantedKey || 0) || null
    return
  }
  pendingRevealCategoryId.value = null
  el.scrollIntoView({ behavior, block: 'start', inline: 'nearest' })
}
function toggleGroupCollapse(key: string) {
  const s = new Set(collapsedGroups.value)
  if (s.has(key)) s.delete(key)
  else s.add(key)
  collapsedGroups.value = s
}

const priorityCategoryKeys = computed(() => {
  const ids = Array.isArray(props.priorityCategoryIds) ? props.priorityCategoryIds : []
  return ids
    .map((id) => Number(id || 0))
    .filter((id, idx, arr) => id > 0 && arr.indexOf(id) === idx)
    .map((id) => String(id))
})

function isPriorityGroup(group: { key: string }) {
  return priorityCategoryKeys.value.includes(String(group?.key || ''))
}

function resolveRootCategoryIdForImageRecord(rec: any): number {
  const categoryId = Number(rec?.categoryId ?? 0)
  const parentCategoryId = Number(rec?.parentCategoryId ?? 0)
  const mappedRootId = categoryId > 0 ? Number(props.categoryRootIdsMap?.[categoryId] ?? 0) : 0
  return mappedRootId || parentCategoryId || categoryId || 0
}

function resolveRootCategoryTitle(rootId: number, fallbackTitle: string): string {
  if (!(rootId > 0)) return fallbackTitle || 'Nicht zugeordnet'
  const direct = String(props.rootCategoryTitlesMap?.[rootId] || '').trim()
  const mapped = String(props.categoryTitlesMap?.[rootId] || '').trim()
  return direct || mapped || fallbackTitle || `Kategorie ${rootId}`
}

function resolveCategoryThumbnailForRecord(rec: any): string | null {
  const categoryId = Number(rec?.categoryId ?? rec?.edits?.preset?.categoryId ?? 0) || 0
  const directThumb = String(rec?.categoryThumbnail || '').trim()
  const mappedThumb = categoryId ? String(props.categoryThumbnailsMap?.[categoryId] || '').trim() : ''
  const src = directThumb || mappedThumb
  if (!src) return null
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

function shouldShowRootLabel(group: any, index: number) {
  if (index <= 0) return true
  const prev = viewImagesByCategory.value[index - 1]
  return String(prev?.rootCategoryKey || '') !== String(group?.rootCategoryKey || '')
}

function groupKeysForRoot(rootCategoryKey: string): string[] {
  const rootKey = String(rootCategoryKey || '')
  return viewImagesByCategory.value
    .filter((group) => String(group?.rootCategoryKey || '') === rootKey)
    .map((group) => String(group?.key || ''))
    .filter(Boolean)
}

function areAllRootGroupsCollapsed(rootCategoryKey: string): boolean {
  const keys = groupKeysForRoot(rootCategoryKey)
  return keys.length > 0 && keys.every((key) => collapsedGroups.value.has(key))
}

function toggleRootGroups(rootCategoryKey: string) {
  const keys = groupKeysForRoot(rootCategoryKey)
  if (!keys.length) return
  const next = new Set(collapsedGroups.value)
  const collapseAll = !keys.every((key) => next.has(key))
  for (const key of keys) {
    if (collapseAll) next.add(key)
    else next.delete(key)
  }
  collapsedGroups.value = next
}

/** "Produkt #0" / "Produkt #" sind Platzhalter aus der Zeit vor der Produktwahl - nie als Titel weitergeben. */
const isPlaceholderProductTitle = (value: unknown): boolean => {
  const t = String(value ?? '').trim()
  return t === '' || /^(produkt|product|artikel)\s*#?\s*\d*$/i.test(t)
}

const productLabel = computed(() => {
  if (props.productName && !isPlaceholderProductTitle(props.productName)) return String(props.productName)
  const r: any = images.value?.[0]?.record
  const t = r?.productTitle || r?.edits?.preset?.productTitle
  if (t && !isPlaceholderProductTitle(t)) return String(t)
  const pid = Number(props.productId || 0)
  const bootstrapProduct = pid > 0 ? (catalogBootstrap.value?.products?.[String(pid)] ?? null) : null
  const fromCatalog = String(bootstrapProduct?.title || bootstrapProduct?.titleShort || '').trim()
  return fromCatalog
})

/* Grid: Desktop fix 4/6, Mobile 1/2 */
const gridCols = computed(() => {
  const w = viewportW.value || 1280
  if (isMobile.value) return 2
  return w >= 1700 ? 7 : 5
})

/* ------------------------------------------------
 * Cropper refs
 * ------------------------------------------------ */
const cropperRefs = shallowRef(new Map<string, any>())
function setCropperRef(id: string, el: any) {
  const map = cropperRefs.value
  if (el) map.set(id, el)
  else map.delete(id)
}
const fsCropperRef = shallowRef<any>(null)
function setFsCropperRef(el: any) {
  fsCropperRef.value = el || null
}

/* ------------------------------------------------
 * Sizes
 * ------------------------------------------------ */
function normalizeSizeFromOption(opt: SizeOpt) {
  if ((opt as any)?.widthMm && (opt as any)?.heightMm) {
    return { widthMm: Math.round(Number((opt as any).widthMm)), heightMm: Math.round(Number((opt as any).heightMm)) }
  }
  const m = String(opt?.title || '').replace(',', '.').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i)
  if (m) return { widthMm: Math.round(parseFloat(m[1]) * 10), heightMm: Math.round(parseFloat(m[2]) * 10) }
  return { widthMm: 300, heightMm: 200 }
}
function extractPriceLike(o: any): any {
  if (!o) return null

  const direct = [
    o.price,
    o.priceGross, o.grossPrice, o.gross, o.brutto,
    o.priceNet, o.netPrice, o.net, o.netto,
    o.amount, o.total, o.totalGross, o.totalNet,
    o.unitPrice, o.value,
    o.priceEuro, o.priceEUR,
  ]
  for (const v of direct) {
    if (v === null || v === undefined) continue
    if (typeof v === 'string' && !v.trim()) continue
    return v
  }

  if (o.priceRange && (o.priceRange.min != null || o.priceRange.max != null)) return o.priceRange.min ?? o.priceRange.max

  const prices = Array.isArray(o.prices) ? o.prices : null
  if (prices && prices.length) {
    const first = prices[0]
    return first?.gross ?? first?.priceGross ?? first?.price ?? first?.amount ?? null
  }

  return null
}

function formatMoneyLike(price: any, currency?: string) {
  const cur = String(currency || price?.currency || 'EUR')

  if (typeof price === 'string') {
    const s = price.trim()
    const moneyMatch = s.match(/^(-?\d+(?:[.,]\d+)?)\s*(€|EUR|USD|CHF|\$|£)$/i)
    if (moneyMatch) {
      const rawValue = Number(String(moneyMatch[1] || '').replace(',', '.'))
      const rawCurrency = String(moneyMatch[2] || '').toUpperCase()
      const resolvedCurrency =
        rawCurrency === '€' ? 'EUR'
          : rawCurrency === '$' ? 'USD'
          : rawCurrency === '£' ? 'GBP'
          : rawCurrency
      if (Number.isFinite(rawValue)) {
        try {
          return new Intl.NumberFormat('de-DE', { style: 'currency', currency: resolvedCurrency }).format(rawValue)
        } catch {
          return `${rawValue.toFixed(2).replace('.', ',')} ${resolvedCurrency}`
        }
      }
    }
    if (/[€$£]|EUR|USD|CHF/i.test(s)) return s.replace(/(\d)\.(\d{2})(?=\s*(€|EUR|USD|CHF|\$|£)\b)/gi, '$1,$2')
    const n = Number(s.replace(',', '.'))
    if (Number.isFinite(n)) {
      try { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format(n) } catch { return `${n} ${cur}` }
    }
    return s || '—'
  }

  const v = typeof price === 'number' ? price : Number(price?.amount ?? price)
  if (!Number.isFinite(v)) return '—'
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format(v)
  } catch {
    return `${v} ${cur}`
  }
}
const variantSizeIdMap = computed(() => {
  const map = new Map<string, string>()
  const nodes = bootstrapCurrentProductSizeOptions.value || []
  for (const node of nodes) {
    const variantId = String(node?.variantId || '').trim()
    const sizeId = String(node?.id || '').trim()
    if (variantId && sizeId) map.set(variantId, sizeId)
  }
  return map
})

function resolveSizeEntityId(rawId: any, variantId: any, title: any, widthMm: any, heightMm: any) {
  const directId = String(rawId || '').trim()
  if (directId) return directId

  const mappedVariantSizeId = variantSizeIdMap.value.get(String(variantId || '').trim())
  if (mappedVariantSizeId) return mappedVariantSizeId

  const nodes = bootstrapCurrentProductSizeOptions.value || []
  const wantedTitle = String(title || '').trim()
  if (wantedTitle) {
    const byTitle = nodes.find((node: any) => String(node?.title || '').trim() === wantedTitle)
    const titleId = String(byTitle?.id || '').trim()
    if (titleId) return titleId
  }

  const wantedW = Number(widthMm || 0)
  const wantedH = Number(heightMm || 0)
  if (wantedW > 0 && wantedH > 0) {
    const byDims = nodes.find((node: any) => {
      const mmW = Number(node?.widthMm || 0) || (Number(node?.widthCm || 0) > 0 ? Math.round(Number(node.widthCm) * 10) : 0)
      const mmH = Number(node?.heightMm || 0) || (Number(node?.heightCm || 0) > 0 ? Math.round(Number(node.heightCm) * 10) : 0)
      return (mmW === wantedW && mmH === wantedH) || (mmW === wantedH && mmH === wantedW)
    })
    const dimsId = String(byDims?.id || '').trim()
    if (dimsId) return dimsId
  }

  return ''
}

function resolveSizeFromVariantOptions(options: any[] | null | undefined) {
  const variantIds = (Array.isArray(options) ? options : [])
    .map((o: any) => String(o?.variantId || '').trim())
    .filter(Boolean)

  if (!variantIds.length) return null

  const nodes = bootstrapCurrentProductSizeOptions.value || []
  for (const wantedVariantId of variantIds) {
    const node = nodes.find((entry: any) => String(entry?.variantId || '').trim() === wantedVariantId)
    if (!node) continue

    const sizeId = String(node.id || '').trim() || ''
    const cmWidth = Number(node.widthCm || 0)
    const cmHeight = Number(node.heightCm || 0)

    const geometry = node.geometry ? normalizeGeometry(node.geometry, { widthMm: cmWidth * 10, heightMm: cmHeight * 10 }) : null

    return {
      id: sizeId || undefined,
      title: String(node.title || '').trim() || undefined,
      widthMm: cmWidth > 0 ? Math.round(cmWidth * 10) : undefined,
      heightMm: cmHeight > 0 ? Math.round(cmHeight * 10) : undefined,
      variantId: wantedVariantId || undefined,
      ...(geometry ? { geometry, bleedMm: geometry.bleedMm, safeMarginMm: geometry.safeMarginMm } : {}),
    }
  }

  return null
}
const normalizedSizes = computed(() => {
  const list = props.sizeOptions ?? []
  return list.map((o, idx) => {
    const mm = normalizeSizeFromOption(o)
    const variantId = (o as any)?.variantId ? String((o as any).variantId) : ''
    const id = resolveSizeEntityId((o as any)?.id, variantId, (o as any)?.title, mm.widthMm, mm.heightMm)
    const key = variantId || id || `${idx}-${o.title}`
    const priceRaw = extractPriceLike(o as any)
    const price = priceRaw
    const geometry = normalizeGeometry((o as any)?.geometry, mm)
    return {
      key,
      id,
      variantId,
      title: o.title,
      widthMm: mm.widthMm,
      heightMm: mm.heightMm,
      // Produktgeometrie (Form, Beschnitt, Sicherheitsbereich). Das Seitenverhältnis des
      // Crops enthält den Beschnitt, damit der Ausschnitt der Druckdatei entspricht.
      geometry,
      bleedMm: geometry.bleedMm,
      safeMarginMm: geometry.safeMarginMm,
      ar: sizeAspectWithBleed(mm.widthMm, mm.heightMm, geometry.bleedMm),
      price: typeof price === 'number' ? price : Number(price) || 0,
      priceLabel: formatMoneyLike(priceRaw, (o as any)?.currency),
    }
  })
})
const sizeDropdownOptions = computed(() =>
    normalizedSizes.value.map((s) => ({
      key: s.key,
      title: s.title,
      price: s.price,
      priceLabel: s.priceLabel,
    })),
)

const sizeSelectOptionsAll = computed<FlatSelectOption[]>(() => {
  const byKey = new Map<string, FlatSelectOption>()
  const base = sizeDropdownOptions.value || []

  for (const o of base) {
    byKey.set(String(o.key), {
      value: String(o.key),
      label: String(o.title),
      right: String(o.priceLabel || ''),
    })
  }

  const nodes = bootstrapCurrentProductSizeOptions.value || []
  for (const v of nodes) {
    const sizeTitle = String(v?.title || '').trim()
    if (!sizeTitle) continue
    const variantKey = String(v?.variantId || '').trim()
    const hitByKey = base.find((x: any) => String(x.key) === variantKey)
    const hitByTitle = base.find((x: any) => String(x.title) === sizeTitle)
    const hit = hitByKey || hitByTitle
    if (!hit) continue
    const key = String(hit.key)
    if (byKey.has(key)) continue
    byKey.set(key, {
      value: key,
      label: String(hit.title),
      right: String(hit.priceLabel || ''),
    })
  }

  return [...byKey.values()]
})
function sizeFor(img: ImageVM) {
  const sizes = normalizedSizesForImg(img)
  if (!sizes.length) return null
  const wantedKey = String(img.ui.sizeKey || '').trim()
  if (!wantedKey) return null
  return sizes.find((s) => s.key === wantedKey) ?? null
}

function resolveSizeForPricing(img: ImageVM, fallback: any = null) {
  const live = sizeFor(img)
  if (live && (String(live.id || '').trim() || Number(live.widthMm || 0) > 0 || Number(live.heightMm || 0) > 0)) {
    return live
  }

  const presetSize = fallback && typeof fallback === 'object' ? fallback : ((img.record as any)?.edits?.preset?.size ?? null)
  const sizes = normalizedSizesForImg(img)

  const presetId = String(presetSize?.id || '').trim()
  if (presetId) {
    const byId = sizes.find((s: any) => String(s?.id || '').trim() === presetId)
    if (byId) return byId
  }

  const presetVariantId = String(presetSize?.variantId || '').trim()
  if (presetVariantId) {
    const byVariantId = sizes.find((s: any) => String(s?.variantId || '').trim() === presetVariantId)
    if (byVariantId) return byVariantId
  }

  const presetTitle = String(presetSize?.title || '').trim()
  if (presetTitle) {
    const byTitle = sizes.find((s: any) => String(s?.title || '').trim() === presetTitle)
    if (byTitle) return byTitle
  }

  const presetW = Number(presetSize?.widthMm || 0)
  const presetH = Number(presetSize?.heightMm || 0)
  if (presetW > 0 && presetH > 0) {
    const byDims = sizes.find((s: any) => Number(s?.widthMm || 0) === presetW && Number(s?.heightMm || 0) === presetH)
      || sizes.find((s: any) => Number(s?.widthMm || 0) === presetH && Number(s?.heightMm || 0) === presetW)
    if (byDims) return byDims
  }

  if (presetSize && (presetId || presetVariantId || presetTitle || presetW > 0 || presetH > 0)) {
    return presetSize
  }

  return null
}

function persistedSizeValue(input: any) {
  if (!input || typeof input !== 'object') return null
  const id = String(input?.id || input?.sizeId || '').trim() || undefined
  const variantId = String(input?.variantId || '').trim() || undefined
  const title = String(input?.title || '').trim() || undefined
  const widthMm = Number(input?.widthMm || 0) || undefined
  const heightMm = Number(input?.heightMm || 0) || undefined
  if (!id && !variantId && !title && !widthMm && !heightMm) return null
  // Geometrie (Form/Beschnitt) mitschreiben: Warenkorb und Fulfillment lesen daraus,
  // ob der Crop den Beschnitt enthält und wie die Druckdatei geschnitten wird.
  const geometryRaw = input?.geometry ?? null
  const geometry = geometryRaw ? normalizeGeometry(geometryRaw, { widthMm, heightMm }) : null
  const bleedMm = Number(input?.bleedMm ?? geometry?.bleedMm ?? 0) || 0
  const safeMarginMm = Number(input?.safeMarginMm ?? geometry?.safeMarginMm ?? 0) || 0
  return {
    ...(id ? { id } : {}),
    ...(variantId ? { variantId } : {}),
    ...(title ? { title } : {}),
    ...(widthMm ? { widthMm } : {}),
    ...(heightMm ? { heightMm } : {}),
    ...(bleedMm > 0 ? { bleedMm } : {}),
    ...(safeMarginMm > 0 ? { safeMarginMm } : {}),
    ...(geometry && geometryHasGuides(geometry) ? { geometry } : {}),
  }
}

function buildPresetForPersistence(img: ImageVM, patchPreset: any = {}) {
  const recPreset: any = (img.record as any)?.edits?.preset ?? {}
  const merged: any = { ...recPreset, ...(patchPreset || {}) }
  const options = Array.isArray((patchPreset || {}).options)
    ? (patchPreset || {}).options
    : (Array.isArray(img.ui.options) ? img.ui.options : (recPreset?.options ?? []))
  const quantity = Math.max(1, Number((patchPreset || {}).quantity ?? merged?.quantity ?? img.ui.quantity ?? 1))
  const fallbackSize = (patchPreset && Object.prototype.hasOwnProperty.call(patchPreset, 'size'))
    ? patchPreset.size
    : (merged?.size ?? null)
  const resolvedSize = resolveSizeForPricing(img, fallbackSize || resolveSizeFromVariantOptions(options) || null)
  const size = persistedSizeValue(resolvedSize || fallbackSize)
  const sizeId = String(size?.id || '').trim() || undefined

  return {
    ...merged,
    quantity,
    options,
    lockAR: !!img.ui.lockAR,
    swapAR: !!img.ui.swapAR,
    orientMode: img.ui.orientMode === 'manual' ? 'manual' : 'auto',
    ...(size ? { size, ...(sizeId ? { sizeId } : {}) } : { size: undefined, sizeId: undefined }),
    // Der Crop deckt bei Beschnitt das volle Format ab (siehe sizeAspectWithBleed).
    bleedIncluded: Number((size as any)?.bleedMm || 0) > 0,
  }
}


/* ------------------------------------------------
 * Auto: wenn Sizes später geladen werden, Max-Crop erneut setzen
 * - nur wenn noch KEIN gespeicherter Crop existiert
 * - und aktueller Crop "full" ist (typisch wenn AR noch unbekannt war)
 * ------------------------------------------------ */
const autoCropped = new Set<string>()
watch(
    () => normalizedSizes.value.map((s) => s.key).join('|'),
    async () => {
      if (!normalizedSizes.value.length) return
      for (const img of images.value) {
        if (!img) continue
        const hasSaved = !!(img.record as any)?.edits?.crop
        if (hasSaved) continue
        if (img.ui.locked) continue
        const preset: any = (img.record as any)?.edits?.preset || {}
        const hasSize = !!(preset?.size?.title || preset?.sizeVariantId || preset?.sizeKey || img.ui.sizeKey)
        if (!hasSize) continue
        if (autoCropped.has(img.id)) continue
        if (!isFullCrop(img)) continue

        // jetzt kennen wir die AR -> MaxCrop setzen + in Cropper anwenden
        setMaxCrop(img, img.crop?.rot ?? 0)
        autoCropped.add(img.id)
        // apply to whichever cropper is currently active for this image
        try {
          if (activeImageId.value === img.id) {
            if (isFullscreen.value && fsCropperRef.value) await applyCropToCropper(img, 'auto-maxcrop', fsCropperRef.value, true)
            if (!isFullscreen.value && gridCropperRef.value) await applyCropToCropper(img, 'auto-maxcrop', gridCropperRef.value, true)
          }
        } catch {}
      }
    },
    { immediate: true },
)

watch(
  [
    showEmptyCategoryUploadPrompt,
    uploadPromptSource,
    inlineUploadSource,
    pendingTargetCategoryId,
    pendingTargetProductId,
    () => props.categoryId,
    () => props.productId,
    () => uploadStats.value.active,
    () => Object.values(liveUploadState.value).map((live) => String(live?.state || '')).join('|'),
  ],
  () => {
    if ((showEmptyCategoryUploadPrompt.value && uploadPromptSource.value === 'phone') || inlineUploadSource.value === 'phone') {
      void buildPhoneBridge()
    }
    const shouldRun =
      (showEmptyCategoryUploadPrompt.value && uploadPromptSource.value === 'phone') ||
      inlineUploadSource.value === 'phone' ||
      uploadStats.value.active > 0 ||
      Object.values(liveUploadState.value).some((live) => {
        const state = String(live?.state || '').trim()
        return state === 'processing' || state === 'uploading' || state === 'queued'
      })
    if (shouldRun) {
      void startRealtimeUploadSubscription()
      return
    }
    stopRealtimeUploadSubscription()
  },
  { flush: 'post' },
)

/* ------------------------------------------------
 * Product ownership / locking
 * ------------------------------------------------ */
function normProductId(v: any): number | null {
  if (v == null) return null
  if (typeof v === 'number' && Number.isFinite(v)) return v
  const s = String(v)
  // supports plain numbers and IRIs like "/api/products/123"
  const m = s.match(/(\d+)(?!.*\d)/)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) ? n : null
}

function recordProductId(rec: any): number | null {
  return normProductId(rec?.productId ?? rec?.edits?.preset?.productId ?? null)
}

function recordProductTitle(rec: any): string {
  const t = rec?.productTitle ?? rec?.edits?.preset?.productTitle ?? ''
  return String(t || '')
}

function computeLock(rec: any) {
  const pid = recordProductId(rec)
  const title = recordProductTitle(rec)
  // Kategorie-basierter Editor: Bilder sind nicht mehr produkt-gesperrt.
  return { locked: false, pid: pid ?? null, title }
}

function isLocked(img: ImageVM) {
  return !!img?.ui?.locked
}

function normalizedSizesForImg(img: ImageVM) {
  const groups = tileVariantGroupsFor(img)
  const sizeGroup = groups.find((g: any) => g.isSize)
  const rawSizes: SizeOpt[] = sizeGroup?.sizeOptions ?? []
  const base = (!rawSizes.length ? normalizedSizes.value : rawSizes.map((o: any, idx: number) => {
    const mm = normalizeSizeFromOption(o as SizeOpt)
    const variantId = o.variantId ? String(o.variantId) : ''
    const id = resolveSizeEntityId(o.id, variantId, o.title, mm.widthMm, mm.heightMm)
    const key = variantId || id || `${idx}-${o.title}`
    const priceRaw = extractPriceLike(o)
    const geometry = normalizeGeometry((o as any)?.geometry, mm)
    return {
      key,
      id,
      variantId,
      title: String(o.title || ''),
      widthMm: mm.widthMm,
      heightMm: mm.heightMm,
      geometry,
      bleedMm: geometry.bleedMm,
      safeMarginMm: geometry.safeMarginMm,
      ar: sizeAspectWithBleed(mm.widthMm, mm.heightMm, geometry.bleedMm),
      price: typeof priceRaw === 'number' ? priceRaw : Number(priceRaw) || 0,
      priceLabel: formatMoneyLike(priceRaw, o.currency),
    }
  })) as any[]

  return sizesWithPresetSupport(base, (img.record as any)?.edits?.preset?.size ?? null).sort((a: any, b: any) =>
    (Number(a?.widthMm || 0) - Number(b?.widthMm || 0)) ||
    (Number(a?.heightMm || 0) - Number(b?.heightMm || 0)) ||
    (Number(a?.price || 0) - Number(b?.price || 0)) ||
    String(a?.title || '').localeCompare(String(b?.title || ''), 'de')
  )
}

function sizeDropdownOptionsForImg(img: ImageVM) {
  if (!isLocked(img)) {
    return normalizedSizesForImg(img).map((s) => ({
      key: s.key,
      title: s.title,
      price: s.price,
      priceLabel: s.priceLabel,
    }))
  }
  // Für gesperrte Bilder nur die aktuell gespeicherte Auswahl anzeigen (readonly)
  const title = img.ui.sizeTitleHint || '—'
  const key = img.ui.sizeKey || 'locked'
  return [{ key, title, price: 0, priceLabel: '' }]
}

function sizeSelectOptionsForImg(img: ImageVM): FlatSelectOption[] {
  if (isLocked(img)) {
    return sizeDropdownOptionsForImg(img).map((o: any) => ({
      value: String(o.key),
      label: String(o.title),
    }))
  }
  return normalizedSizesForImg(img).map((s) => ({
    value: String(s.key),
    label: String(s.title),
  }))
}


/* ------------------------------------------------
 * View list
 * ------------------------------------------------ */
const viewImages = computed(() => {
  let list = [...images.value]

  // Auf erlaubte Kategorien einschränken (Root-Kategorie-Ansicht)
  if (props.filterCategoryIds?.length && !props.isMultiMode) {
    const allowed = new Set(props.filterCategoryIds.map((x) => Number(x)))
    list = list.filter((i) => {
      const cid = Number((i.record as any)?.categoryId ?? 0)
      const rootId = Number((i.record as any)?.parentCategoryId ?? cid ?? 0)
      return allowed.has(cid) || allowed.has(rootId)
    })
  }

  if (filterProductId.value !== 'all') {
    if (filterProductId.value === 'unassigned') {
      list = list.filter((i) => {
        const pid = Number((i.record as any)?.productId ?? 0)
        return !pid
      })
    } else {
      const wanted = Number(filterProductId.value || 0)
      list = list.filter((i) => {
        const pid = Number((i.record as any)?.productId ?? 0)
        return wanted > 0 && pid === wanted
      })
    }
  }

  if (filterSizeKey.value !== 'all') {
    if (filterSizeKey.value === 'unassigned') {
      list = list.filter((i) => !String(i.ui.sizeKey || '').trim())
    } else {
      const wanted = String(filterSizeKey.value || '')
      list = list.filter((i) => String(i.ui.sizeKey || '') === wanted)
    }
  }

  if (filterTag.value !== 'all') {
    if (filterTag.value === 'untagged') {
      list = list.filter((i) => normalizeTags((i.record as any)?.tags).length === 0)
    } else {
      const wanted = filterTag.value
      list = list.filter((i) => hasTag((i.record as any)?.tags, wanted))
    }
  }

  const q = search.value.trim().toLowerCase()
  if (q) list = list.filter((i) => (i.name || '').toLowerCase().includes(q) || (i.id || '').toLowerCase().includes(q) || normalizeTags((i.record as any)?.tags).some((t) => t.toLowerCase().includes(q)))
  if (uploadIssueFilter.value === 'issues') list = list.filter((i) => uploadIssueKind(i) !== null)

    if (sortBy.value === 'name') list.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  else if (sortBy.value === 'oldest') list.sort((a, b) => compareImageOrder(a, b, 'asc'))
  else list.sort((a, b) => compareImageOrder(a, b, 'desc'))
  return list
})

const uploadIssueCount = computed(() => images.value.filter((img) => uploadIssueKind(img) !== null).length)

/** Alle Bilder nach Kategorie gruppiert – ungefiltert, für Bestellübersicht */
const allImagesByCategory = computed(() => {
  const groupMap = new Map<string, {
    key: string
    categoryId: number | null
    categoryTitle: string
    thumbnail: string | null
    items: Array<{ img: ImageVM }>
  }>()
  images.value.forEach((img) => {
    const cid = Number((img.record as any)?.categoryId ?? 0)
    const key = cid ? String(cid) : '__unassigned__'
    if (!groupMap.has(key)) {
      const title = cid ? resolveCategoryTitleForImage(img, cid) : 'Nicht zugeordnet'
      const thumbnail = resolveCategoryThumbnailForRecord(img.record as any)
      groupMap.set(key, { key, categoryId: cid || null, categoryTitle: title, thumbnail, items: [] })
    }
    groupMap.get(key)!.items.push({ img })
  })
  return [...groupMap.values()]
})

/** Bilder nach Kategorie gruppiert – für die gruppierten Grid-Sections */
const viewImagesByCategory = computed(() => {
  const groupMap = new Map<string, {
    key: string
    categoryId: number | null
    categoryTitle: string
    rootCategoryKey: string
    rootCategoryTitle: string
    thumbnail: string | null
    items: Array<{ img: ImageVM; vIdx: number }>
  }>()

  viewImages.value.forEach((img, vIdx) => {
    const rec: any = img.record ?? {}
    const cid = Number(rec?.categoryId ?? 0)
    const rootId = resolveRootCategoryIdForImageRecord(rec)
    const key = cid ? String(cid) : '__unassigned__'
    const rootKey = rootId ? String(rootId) : '__unassigned__'

    if (!groupMap.has(key)) {
      const title = cid ? resolveCategoryTitleForImage(img, cid) : 'Nicht zugeordnet'
      const rootTitle = resolveRootCategoryTitle(rootId, rootId === cid ? title : '')
      const thumbnail = resolveCategoryThumbnailForRecord(img.record as any)
      groupMap.set(key, {
        key,
        categoryId: cid || null,
        categoryTitle: title,
        rootCategoryKey: rootKey,
        rootCategoryTitle: rootTitle || title,
        thumbnail,
        items: [],
      })
    }
    groupMap.get(key)!.items.push({ img, vIdx })
  })

  return [...groupMap.values()].sort((a, b) => {
    if (a.key === '__unassigned__') return 1
    if (b.key === '__unassigned__') return -1
    if (a.rootCategoryKey !== b.rootCategoryKey) {
      const aRootOrder = Number(props.rootCategoryOrderMap?.[Number(a.rootCategoryKey || 0)] ?? Number.MAX_SAFE_INTEGER)
      const bRootOrder = Number(props.rootCategoryOrderMap?.[Number(b.rootCategoryKey || 0)] ?? Number.MAX_SAFE_INTEGER)
      if (aRootOrder !== bRootOrder) return aRootOrder - bRootOrder
      return String(a.rootCategoryTitle || '').localeCompare(String(b.rootCategoryTitle || ''), 'de')
    }
    const aCategoryOrder = Number(props.categoryOrderMap?.[Number(a.key || 0)] ?? Number.MAX_SAFE_INTEGER)
    const bCategoryOrder = Number(props.categoryOrderMap?.[Number(b.key || 0)] ?? Number.MAX_SAFE_INTEGER)
    if (aCategoryOrder !== bCategoryOrder) return aCategoryOrder - bCategoryOrder
    return String(a.categoryTitle || '').localeCompare(String(b.categoryTitle || ''), 'de')
  })
})

watch(
  () => viewImagesByCategory.value.map((group) => `${group.rootCategoryKey}:${group.key}`).join('|'),
  () => {
    if (!props.isMultiMode) return
    const preferred = new Set(priorityCategoryKeys.value)
    const current = new Set(collapsedGroups.value)
    const visibleKeys = new Set<string>()
    const next = new Set<string>()

    for (const group of viewImagesByCategory.value) {
      const key = String(group?.key || '')
      if (!key) continue
      visibleKeys.add(key)

      const existsAlready = current.has(key)
      const isPreferred = preferred.has(key) || preferred.has(String(group?.rootCategoryKey || ''))
      const rootKey = String(group?.rootCategoryKey || '')
      const isPinnedUploadGroup = key === String(uploadPinnedGroupKey.value || '')
      const isPinnedUploadRoot = !!uploadPinnedRootKey.value && rootKey === String(uploadPinnedRootKey.value || '')

      if (existsAlready) {
        if (!isPinnedUploadGroup && !isPinnedUploadRoot) next.add(key)
        continue
      }

      if (isPinnedUploadGroup || isPinnedUploadRoot) {
        continue
      }

      if (!isPreferred) next.add(key)
    }

    collapsedGroups.value = new Set([...next].filter((key) => visibleKeys.has(key)))
  },
  { immediate: true },
)

watch(
  [() => pendingRevealCategoryId.value, () => viewImagesByCategory.value.map((group) => group.key).join('|')],
  async ([categoryId]) => {
    const wanted = Number(categoryId || 0) || null
    if (!wanted) return
    const exists = viewImagesByCategory.value.some((group) => String(group?.key || '') === String(wanted))
    if (!exists) return
    await revealCategoryGroup(wanted, 'smooth')
  },
  { immediate: true },
)

watch(
  [() => uploadPinnedCategoryId.value, images],
  ([categoryId]) => {
    const key = String(Number(categoryId || 0) || '').trim()
    if (!key) {
      uploadPinnedGroupKey.value = ''
      uploadPinnedRootKey.value = ''
      return
    }
    const pinnedGroup = viewImagesByCategory.value.find((group) => String(group?.key || '') === key)
    uploadPinnedRootKey.value = String(pinnedGroup?.rootCategoryKey || '').trim()
    const hasActiveUploadsInCategory = images.value.some((img) => {
      const rec: any = img.record ?? {}
      if (String(Number(rec?.categoryId || 0) || '') !== key) return false
      const state = String(rec?.upload?.state || '').trim()
      const phase = String(rec?.upload?.phase || '').trim()
      return ['queued', 'uploading', 'processing'].includes(state) || ['queued', 'presigning', 'uploading', 'completing', 'processing'].includes(phase)
    })
    uploadPinnedGroupKey.value = hasActiveUploadsInCategory ? key : ''
    if (!hasActiveUploadsInCategory) uploadPinnedRootKey.value = ''
  },
  { immediate: true, deep: false },
)

const productFilterOptions = computed(() => {
  const shortById = new Map<number, string>()
  for (const p of productsInCategory.value) {
    const pid = Number((p as any)?._id || 0)
    if (!pid) continue
    const short = String((p as any)?.titleShort || '').trim()
    if (short) shortById.set(pid, short)
  }

  const map = new Map<number, { title: string; count: number }>()
  let unassigned = 0
  for (const i of images.value) {
    const pid = Number((i.record as any)?.productId ?? 0)
    if (!pid) { unassigned++; continue }
    const title = String(
      shortById.get(pid)
      || (i.record as any)?.productTitleShort
      || (i.record as any)?.edits?.preset?.productTitleShort
      || (i.record as any)?.productTitle
      || (i.record as any)?.edits?.preset?.productTitle
      || `Produkt #${pid}`,
    )
    if (!map.has(pid)) map.set(pid, { title, count: 0 })
    map.get(pid)!.count += 1
  }
  const allCount = images.value.length
  const out = [{ value: 'all', label: `Alle Produkte (${allCount})` }]
  if (unassigned > 0) out.push({ value: 'unassigned', label: `Nicht zugeordnet (${unassigned})` })
  out.push(...[...map.entries()].map(([pid, data]) => ({ value: String(pid), label: `${data.title} (${data.count})` })))
  return out
})

const sizeFilterOptions = computed(() => {
  const map = new Map<string, { title: string; count: number }>()
  let unassigned = 0
  for (const i of images.value) {
    const k = String(i.ui.sizeKey || '')
    if (!k) { unassigned++; continue }
    const t = String(i.ui.sizeTitleHint || k)
    if (!map.has(k)) map.set(k, { title: t, count: 0 })
    map.get(k)!.count += 1
  }
  const allCount = images.value.length
  const out = [{ value: 'all', label: `Alle Größen (${allCount})` }]
  if (unassigned > 0) out.push({ value: 'unassigned', label: `Keine Größe (${unassigned})` })
  out.push(...[...map.entries()].map(([value, data]) => ({ value, label: `${data.title} (${data.count})` })))
  return out
})

const sortFilterOptions = computed<FlatSelectOption[]>(() => ([
  { value: 'newest', label: 'Neueste zuerst' },
  { value: 'oldest', label: 'Älteste zuerst' },
  { value: 'name', label: 'Name A–Z' },
]))

/* ------------------------------------------------
 * Selection
 * ------------------------------------------------ */
function isSelected(id: string) {
  return selectedIdSet.value.has(id)
}
let emitSelectionTimer: ReturnType<typeof setTimeout> | null = null
function emitSelection() {
  if (emitSelectionTimer) return
  emitSelectionTimer = setTimeout(() => {
    emitSelectionTimer = null
    const key = selectedIds.value.join('|')
    if (key === lastEmittedSelectionKey) return
    lastEmittedSelectionKey = key
    emit('selection-change', [...selectedIds.value])
  }, 80)
}
function toggleSelect(id: string) {
  const idx = selectedIds.value.indexOf(id)
  if (idx >= 0) selectedIds.value.splice(idx, 1)
  else selectedIds.value.push(id)
  emitSelection()
}

/** Multi-Select über Checkbox:
 * - Shift: Range (ohne Cmd/Ctrl ersetzt Range, mit Cmd/Ctrl addiert Range)
 * - Cmd/Ctrl: toggle
 * - normal: toggle
 */
function onSelectClick(ev: MouseEvent, id: string, vIdx: number) {
  const isShift = ev.shiftKey
  const isCtrl = ev.metaKey || ev.ctrlKey
  const list = viewImages.value

  // Set anchor on "real" clicks (not shift)
  const setAnchor = () => {
    selectionAnchorIndex.value = vIdx
    lastClickedIndex.value = vIdx
  }

  if (isShift && selectionAnchorIndex.value != null) {
    const [a, b] = [selectionAnchorIndex.value, vIdx].sort((x, y) => x - y)
    const range = list.slice(a, b + 1).map(i => i.id)

    // WICHTIG: range wird zur bestehenden Auswahl dazugefügt (nicht ersetzt)
    selectedIds.value = unionIds(selectedIds.value, range)

    // Anchor NICHT ändern (damit Shift wieder weiter "verlängert")
    emitSelection()
    return
  }

  if (isCtrl) {
    toggleSelect(id)
    setAnchor()
    return
  }

  // normal: toggle (kein clearing)
  toggleSelect(id)
  setAnchor()
}

function onTileClick(ev: MouseEvent, id: string, vIdx: number) {
  // optional: tile click "single-select" Verhalten (wenn du es brauchst)
  const isShift = ev.shiftKey
  const isCtrl = ev.metaKey || ev.ctrlKey
  const list = viewImages.value

  if (isShift && lastClickedIndex.value != null) {
    const [a, b] = [lastClickedIndex.value, vIdx].sort((x, y) => x - y)
    const range = list.slice(a, b + 1).map((i) => i.id)
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...range]))
  } else if (isCtrl) {
    toggleSelect(id)
    lastClickedIndex.value = vIdx
    return
  } else {
    if (selectedIds.value.length === 1 && selectedIds.value[0] === id) {
      selectedIds.value = []
      lastClickedIndex.value = null
    } else {
      selectedIds.value = [id]
      lastClickedIndex.value = vIdx
      const img = images.value.find((x) => x.id === id)
      setTimeout(() => {
        const fresh = images.value.find((x) => x.id === id)
        markImageActive(fresh)
        ensureObjectUrlsForImage(fresh)
      }, 0)
    }
  }
  emitSelection()
}

function isInteractiveTileTarget(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null
  if (!el) return false
  return !!el.closest(
    'button, input, select, textarea, a, label, .meta, .actions, .tile-topbar, .control-row, .control-cell, .tile-action, .qty-step, .qty-input, .fs, .fs-trigger'
  )
}

function onTileDblClick(ev: MouseEvent, id: string) {
  if (isInteractiveTileTarget(ev.target)) return
  const img = images.value.find((x) => x.id === id)
  if (!img) return
  openFullscreen(img)
}
function unionIds(a: string[], b: string[]) {
  return Array.from(new Set([...a, ...b]))
}

function onTileDragStart(e: DragEvent, img: ImageVM) {
  // Gesperrte Bilder (anderes Produkt) dürfen nicht „gezogen“/verschoben werden
  if (img?.ui?.locked) {
    e.preventDefault()
    e.stopPropagation()
    return
  }
  try {
    e.dataTransfer?.setData('text/plain', img.id)
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
  } catch {}
}

function selectAll() {
  selectedIds.value = viewImages.value.map((i) => i.id)
  emitSelection()
}

function clearSelection() {
  selectedIds.value = []
  lastClickedIndex.value = null
  selectionAnchorIndex.value = null
  emitSelection()
}
function groupItemIds(group: { items?: Array<{ img?: ImageVM }> }) {
  return (group.items ?? []).map((entry) => String(entry?.img?.id || '')).filter(Boolean)
}
function isGroupFullySelected(group: { items?: Array<{ img?: ImageVM }> }) {
  const ids = groupItemIds(group)
  const selected = selectedIdSet.value
  return ids.length > 0 && ids.every((id) => selected.has(id))
}
function toggleGroupSelection(group: { items?: Array<{ img?: ImageVM }> }) {
  const ids = groupItemIds(group)
  if (!ids.length) return

  const selected = selectedIdSet.value
  if (ids.every((id) => selected.has(id))) {
    const remove = new Set(ids)
    selectedIds.value = selectedIds.value.filter((id) => !remove.has(id))
  } else {
    selectedIds.value = unionIds(selectedIds.value, ids)
  }

  emitSelection()
}
function openDetailsForSingle(id: string) {
  selectedIds.value = [id]
  lastClickedIndex.value = null
  emitSelection()
  emit('open-details')
}
function openDetailsForSelection() {
  if (!selectedIds.value.length) return
  emitSelection()
  emit('open-details')
}

/* ------------------------------------------------
 * Tags / Alben (z. B. "Kindergarten", "Hochzeit")
 * ------------------------------------------------ */
/** Alle Tags im Projekt mit Häufigkeit (ungefiltert) */
const allTags = computed(() => collectTags(images.value.map((i) => i.record as any)))
const tagFilterOptions = computed(() => {
  const untagged = images.value.filter((i) => normalizeTags((i.record as any)?.tags).length === 0).length
  return [
    { value: 'all', label: `Alle Tags (${images.value.length})` },
    ...(untagged ? [{ value: 'untagged', label: `Ohne Tag (${untagged})` }] : []),
    ...allTags.value.map((t) => ({ value: t.tag, label: `${t.tag} (${t.count})` })),
  ]
})
watch(allTags, (list) => {
  // Filter auf einen inzwischen gelöschten Tag zurücksetzen
  if (filterTag.value !== 'all' && filterTag.value !== 'untagged' && !list.some((t) => t.tag.toLowerCase() === filterTag.value.toLowerCase())) filterTag.value = 'all'
})

const tagDialog = ref<{ open: boolean; mode: 'add' | 'remove'; value: string }>({ open: false, mode: 'add', value: '' })
function openTagDialog(mode: 'add' | 'remove') {
  if (!selectedIds.value.length) return
  tagDialog.value = { open: true, mode, value: '' }
}
/** Tags eines Bildes setzen: IndexedDB + Session + Server (meta.tags) */
async function setRecordTags(id: string, tags: string[]) {
  const next = normalizeTags(tags)
  const vm = images.value.find((x) => x.id === id)
  if (vm) (vm.record as any).tags = next
  await persistRecordUpdate(id, { tags: next } as any)
  scheduleUploadMetaSync(id)
}
async function applyTagsToSelection(raw: string) {
  const add = normalizeTags(raw)
  if (!add.length) return
  for (const id of [...selectedIds.value]) {
    const vm = images.value.find((x) => x.id === id)
    if (!vm) continue
    await setRecordTags(id, [...normalizeTags((vm.record as any)?.tags), ...add])
  }
  tagDialog.value.open = false
  layoutVersion.value++
}
async function removeTagsFromSelection(raw: string) {
  const remove = normalizeTags(raw).map((t) => t.toLowerCase())
  for (const id of [...selectedIds.value]) {
    const vm = images.value.find((x) => x.id === id)
    if (!vm) continue
    const current = normalizeTags((vm.record as any)?.tags)
    const next = remove.length ? current.filter((t) => !remove.includes(t.toLowerCase())) : []
    if (next.length !== current.length) await setRecordTags(id, next)
  }
  tagDialog.value.open = false
  layoutVersion.value++
}
/** Tags, die in der aktuellen Auswahl vorkommen (für "Tag entfernen") */
const selectionTags = computed(() => collectTags(selectedIds.value.map((id) => images.value.find((x) => x.id === id)?.record as any).filter(Boolean)))

/* ------------------------------------------------
 * Pills / price
 * ------------------------------------------------ */
function optionPills(img: ImageVM) {
  const opts = img.ui.options ?? []
  return opts.map((o) => String(o.value || '').trim()).filter(Boolean)
}
function priceText(img: ImageVM) {
  if (img.ui.locked) return '—'
  return sizeFor(img)?.priceLabel ?? '—'
}
function priceTitle(_img: ImageVM) {
  return 'Preis'
}
function inchesFromMm(mm: number) {
  return mm / 25.4
}
function tileQualityDpi(img: ImageVM): number | null {
  const size = sizeFor(img)
  const widthMm = Number(size?.widthMm || 0)
  const heightMm = Number(size?.heightMm || 0)
  if (!(widthMm > 0) || !(heightMm > 0)) return null

  const mainDims = mainDimsForRecord(img.record)
  const normalized =
    (img.crop && cropToNormalized(
      img.crop,
      Math.max(1, Number(img.crop.sourceWidth || img.ui.baseW || img.ui.imgW || editorDimsForRecord(img.record).width)),
      Math.max(1, Number(img.crop.sourceHeight || img.ui.baseH || img.ui.imgH || editorDimsForRecord(img.record).height)),
    ))
    || null

  const sourceW = Math.max(
    1,
    normalized?.nw != null
      ? Math.round(Number(normalized.nw || 0) * mainDims.width)
      : Math.round(Number(mainDims.width || 0)),
  )
  const sourceH = Math.max(
    1,
    normalized?.nh != null
      ? Math.round(Number(normalized.nh || 0) * mainDims.height)
      : Math.round(Number(mainDims.height || 0)),
  )
  if (!(sourceW > 0) || !(sourceH > 0)) return null

  // Bei Beschnitt deckt der Ausschnitt das volle Format ab (Endformat + 2 x Beschnitt).
  const bleedMm = Math.max(0, Number((size as any)?.bleedMm || 0))
  const dpiW = sourceW / inchesFromMm(widthMm + 2 * bleedMm)
  const dpiH = sourceH / inchesFromMm(heightMm + 2 * bleedMm)
  const dpi = Math.round(Math.min(dpiW, dpiH))
  return Number.isFinite(dpi) && dpi > 0 ? dpi : null
}
function tileQualityTone(img: ImageVM): 'great' | 'good' | 'warn' | 'bad' | 'unknown' {
  const dpi = tileQualityDpi(img)
  if (!dpi) return 'unknown'
  if (dpi >= 220) return 'great'
  if (dpi >= 180) return 'good'
  if (dpi >= 150) return 'warn'
  return 'bad'
}
function tileQualityShortLabel(img: ImageVM): string {
  const tone = tileQualityTone(img)
  if (tone === 'great') return 'Sehr gut'
  if (tone === 'good') return 'Gut'
  if (tone === 'warn') return 'Okay'
  if (tone === 'bad') return 'Etwas unscharf'
  return 'Prüfung'
}
function tileQualityLabel(img: ImageVM): string {
  const dpi = tileQualityDpi(img)
  if (!dpi) return 'Bildqualität wird geprüft'
  if (dpi >= 220) return `Sehr gut · ca. ${dpi} dpi`
  if (dpi >= 180) return `Gut · ca. ${dpi} dpi`
  if (dpi >= 150) return `Okay · ca. ${dpi} dpi`
  return `Etwas unscharf · ca. ${dpi} dpi`
}
function tileQualityTooltip(img: ImageVM): string {
  const size = sizeFor(img)
  const sizeText = size ? `${size.title}` : 'ohne Größe'
  const base = tileQualityLabel(img)
  return `${base}\nBerechnet aus Bildauflösung, aktuellem Ausschnitt und Format ${sizeText}.`
}

/* ------------------------------------------------
 * Aspect ratio / stencil
 * ------------------------------------------------ */
function effectiveAR(img: ImageVM) {
  const s = sizeFor(img)
  if (!s) return undefined
  let ar = s.ar
  const rot = normRot(img.crop?.rot ?? 0)
  if (img.ui.orientMode === 'auto') {
    if (bestSwapForSize(img, ar, rot)) ar = 1 / ar
  }
  if (img.ui.swapAR) ar = 1 / ar
  return ar
}
function effectiveSwapState(img: ImageVM, rotDeg?: number) {
  const s = sizeFor(img)
  if (!s) return !!img.ui.swapAR
  const rot = normRot(rotDeg ?? img.crop?.rot ?? 0)
  let swapped = false
  if (img.ui.orientMode === 'auto') {
    swapped = bestSwapForSize(img, s.ar, rot)
  }
  if (img.ui.swapAR) swapped = !swapped
  return swapped
}
function bestSwapForSize(img: ImageVM, sizeAr: number, rotDeg: number) {
  const rot = normRot(rotDeg)
  const { W: bw, H: bh } = activeBox(img, rot)
  if (!bw || !bh || !sizeAr || !Number.isFinite(sizeAr)) return false

  const maxArea = (ar: number) => {
    const imgAr = bw / bh
    let w = bw
    let h = bh
    if (imgAr >= ar) {
      h = bh
      w = h * ar
    } else {
      w = bw
      h = w / ar
    }
    return w * h
  }
  return maxArea(1 / sizeAr) > maxArea(sizeAr)
}
/* ------------------------------------------------
 * "So wird produziert": Beschnitt / Schnittkante / Sicherheitsbereich einblenden
 * ------------------------------------------------ */
type GuidesMode = 'full' | 'cut-only' | 'off'
const GUIDES_STORAGE_KEY = 'pfe.editor.guidesMode'
function loadGuidesMode(): GuidesMode {
  try {
    const v = localStorage.getItem(GUIDES_STORAGE_KEY)
    if (v === 'off' || v === 'cut-only' || v === 'full') return v
  } catch {}
  return 'full'
}
const guidesMode = ref<GuidesMode>(loadGuidesMode())
function cycleGuidesMode() {
  guidesMode.value = guidesMode.value === 'full' ? 'cut-only' : guidesMode.value === 'cut-only' ? 'off' : 'full'
  try { localStorage.setItem(GUIDES_STORAGE_KEY, guidesMode.value) } catch {}
}
const guidesButtonTitle = computed(() => {
  if (guidesMode.value === 'full') return 'Produktionsansicht: Beschnitt, Schnittkante und Sicherheitsbereich (Klick: nur Schnittkante)'
  if (guidesMode.value === 'cut-only') return 'Produktionsansicht: nur Schnittkante (Klick: ausblenden)'
  return 'Produktionsansicht ausgeblendet (Klick: einblenden)'
})
/** Mindestens ein Bild hat eine Größe mit Form oder Beschnitt -> Umschalter zeigen. */
const anyGeometryGuides = computed(() => images.value.some((img) => geometryHasGuides((sizeFor(img) as any)?.geometry ?? null)))

function stencilProps(img: ImageVM) {
  const cropAllowed = cropAllowedFor(img)
  const size = sizeFor(img) as any
  return {
    aspectRatio: img.ui.lockAR ? effectiveAR(img) : undefined,
    // Produktgeometrie fuer das Overlay im Cropper / in der Kachel
    geometry: size?.geometry && geometryHasGuides(size.geometry) ? size.geometry : null,
    guides: guidesMode.value,
    whiteBorder: !!displayWhiteBorderLabel(img),
    frame2d: frame2dFor(img),
    movable: cropAllowed,
    resizable: cropAllowed,
    scalable: false,
  }
}

function selectedVariantForGroup(img: ImageVM, group: any) {
  const gid = normId(group?.id || '')
  if (!gid) return null

  const options = Array.isArray(group?.options) ? group.options : []
  const own = (img.ui.options || []).find((o) => normId(o.groupId) === gid)
  if (own?.variantId) {
    const hit = options.find((o: any) => normId(o?.id) === normId(own.variantId))
    if (hit) return hit
  }
  if (own?.value) {
    const hit = options.find((o: any) => String(o?.title || '') === String(own.value || ''))
    if (hit) return hit
  }
  return null
}

function cropAllowedFor(img: ImageVM) {
  const groups = tileVariantGroupsFor(img)
  for (const group of groups) {
    if (!group || group.isSize) continue
    const selected = selectedVariantForGroup(img, group)
    if (selected?.isCrop === false) return false
  }
  return true
}

function isFitInMode(img: ImageVM) {
  return !cropAllowedFor(img)
}

const WHITE_BORDER_RE = /wei(ß|ss)e[rs]?\s+rand/i

/** Titel der gewählten Option „weißer Rand“ (Gruppe Motivrand/Bildausschnitt) oder null. */
function whiteBorderLabel(img: ImageVM): string | null {
  for (const group of tileVariantGroupsFor(img)) {
    if (!group || group.isSize) continue
    const options = Array.isArray(group.options) ? group.options : []
    const selected = selectedVariantForGroup(img, group) || options.find((o: any) => !!o?.isDefault) || null
    const title = String(selected?.title || '')
    if (WHITE_BORDER_RE.test(title)) return title
  }
  return null
}

function fitFrameStyle(img: ImageVM) {
  const ar = effectiveAR(img)
  if (!ar || !Number.isFinite(ar) || ar <= 0) return {}
  if (Math.abs(ar - 1) < 0.001) return { aspectRatio: String(ar), width: '100%', height: '100%' }
  if (ar > 1) return { aspectRatio: String(ar), width: '100%', height: 'auto' }
  return { aspectRatio: String(ar), width: 'auto', height: '100%' }
}

function inactiveCropFrameStyle(img: ImageVM) {
  return { width: '100%', height: '100%' }
}

function inactiveCropImageStyle(img: ImageVM) {
  const crop = img.crop
  const sourceW = Math.max(1, Number(crop?.sourceWidth || img.ui.baseW || img.ui.imgW || 1))
  const sourceH = Math.max(1, Number(crop?.sourceHeight || img.ui.baseH || img.ui.imgH || 1))
  if (!crop || !sourceW || !sourceH) {
    return { width: '100%', height: '100%', transform: 'translate3d(0, 0, 0)' }
  }

  const cropW = Math.max(2, Number(crop.w || sourceW))
  const cropH = Math.max(2, Number(crop.h || sourceH))
  const widthPct = (sourceW / cropW) * 100
  const heightPct = (sourceH / cropH) * 100
  // translate-% bezieht sich auf die eigene (bereits vergrößerte) Bildbreite → nur x/sourceW
  const txPct = -(Number(crop.x || 0) / sourceW) * 100
  const tyPct = -(Number(crop.y || 0) / sourceH) * 100

  return {
    width: `${widthPct}%`,
    height: `${heightPct}%`,
    maxWidth: 'none',
    maxHeight: 'none',
    transform: `translate3d(${txPct}%, ${tyPct}%, 0)`,
    transformOrigin: 'top left',
  }
}

function inactiveCropMetrics(img: ImageVM) {
  const crop = img.crop
  const sourceW = Math.max(1, Number(crop?.sourceWidth || img.ui.baseW || img.ui.imgW || 1))
  const sourceH = Math.max(1, Number(crop?.sourceHeight || img.ui.baseH || img.ui.imgH || 1))
  const imageAr = sourceW / Math.max(1, sourceH)

  let boxW = 100
  let boxH = 100
  let boxX = 0
  let boxY = 0

  if (imageAr >= 1) {
    boxH = 100 / imageAr
    boxY = (100 - boxH) / 2
  } else {
    boxW = 100 * imageAr
    boxX = (100 - boxW) / 2
  }

  const cropX = crop ? boxX + (Number(crop.x || 0) / sourceW) * boxW : boxX
  const cropY = crop ? boxY + (Number(crop.y || 0) / sourceH) * boxH : boxY
  const cropW = crop ? (Math.max(2, Number(crop.w || sourceW)) / sourceW) * boxW : boxW
  const cropH = crop ? (Math.max(2, Number(crop.h || sourceH)) / sourceH) * boxH : boxH

  return { boxX, boxY, boxW, boxH, cropX, cropY, cropW, cropH }
}

function inactiveCropWindowStyle(img: ImageVM) {
  const m = inactiveCropMetrics(img)
  return {
    left: `${m.cropX}%`,
    top: `${m.cropY}%`,
    width: `${m.cropW}%`,
    height: `${m.cropH}%`,
  }
}

function inactiveCropBoxStyle(img: ImageVM) {
  const m = inactiveCropMetrics(img)
  return {
    left: `${m.cropX}%`,
    top: `${m.cropY}%`,
    width: `${m.cropW}%`,
    height: `${m.cropH}%`,
  }
}

function cropSnapshot(img: ImageVM | null) {
  if (!img?.crop) return ''
  return [img.crop.x, img.crop.y, img.crop.w, img.crop.h, normRot(img.crop.rot ?? 0)].join(':')
}

function syncCropMode(img: ImageVM, opts?: { persist?: boolean; apply?: boolean }) {
  const persist = opts?.persist ?? true
  const apply = opts?.apply ?? true
  const before = cropSnapshot(img)
  const rot = normRot(img.crop?.rot ?? 0)

  if (isFitInMode(img)) {
    const { W: bw, H: bh } = activeBox(img, rot)
    img.crop = {
      x: 0,
      y: 0,
      w: bw,
      h: bh,
      rot,
      sourceWidth: bw,
      sourceHeight: bh,
      ...((cropToNormalized({
        x: 0,
        y: 0,
        w: bw,
        h: bh,
      }, bw, bh)) || {}),
    }
  } else if (!img.crop) {
    setMaxCrop(img, rot)
  }

  const changed = before !== cropSnapshot(img)
  if (!changed || !img.crop) return

  if (persist) persistCropNow(img.id, img.crop, img.crop.rot ?? 0).catch(() => {})
  if (!apply) return

  img.ui.rev += 1
  img.ui.appliedOnce = false
  pendingApply.add(img.id)
  queueMicrotask(async () => {
    await applyCropToCropper(img, 'crop-mode-sync-grid', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) {
      await applyCropToCropper(img, 'crop-mode-sync-fs', fsCropperRef.value, true)
    }
  })
}

/* ------------------------------------------------
 * DB patch + persist (serialisiert)
 * ------------------------------------------------ */
const patchQueues = new Map<string, Promise<void>>()
const deferredMetaSyncIds = new Set<string>()
const metaSyncInFlightIds = new Set<string>()
const pendingUploadMetaSyncIds = new Set<string>()
const cartMetaDirtyIds = new Set<string>()
let uploadMetaSyncTimer: ReturnType<typeof setTimeout> | null = null
let uploadMetaSyncFlushRunning = false
const pendingCartReconcileIds = new Set<string>()
let pendingCartReconcileTimer: ReturnType<typeof setTimeout> | null = null

function markCartMetaDirty(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  cartMetaDirtyIds.add(id)
}

function clearCartMetaDirty(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  cartMetaDirtyIds.delete(id)
}

function isImageCurrentlyInCart(imageIdRaw: string, projectCodeRaw: string) {
  const imageId = String(imageIdRaw || '').trim()
  const projectCode = String(projectCodeRaw || '').trim()
  if (!imageId || !projectCode) return false
  const items: any[] = ((cart as any)?.cart?.items || []) as any[]
  return items.some((it: any) => {
    const itemId = String(it?.imageId ?? it?.preset?.imageId ?? it?.meta?.imageId ?? '').trim()
    return itemId === imageId
      && String(it?.payload?.editorProjectId ?? '').trim() === projectCode
      && String(it?.kind || it?.type || '').trim() === 'image'
  })
}

async function flushPendingCartReconcile() {
  if (pendingCartReconcileTimer) {
    clearTimeout(pendingCartReconcileTimer)
    pendingCartReconcileTimer = null
  }
  const ids = [...pendingCartReconcileIds].filter(Boolean)
  pendingCartReconcileIds.clear()
  if (!ids.length) return

  const projectCode = String(project.projectCode.value || '').trim() || await project.ensureProject().catch(() => '')
  if (!projectCode) return

  await cart.ensureCartLoaded().catch(() => null)
  const wantedIds = ids.filter((id) => isImageCurrentlyInCart(id, projectCode))
  if (!wantedIds.length) return

  // IMPORTANT: this is a PARTIAL reconcile of just the edited images. It must use
  // 'upsert' (update only these) — NOT the default 'sync', which makes the backend
  // replace the project's whole cart-image set with this subset and would remove all
  // other already-in-cart images (the "edit one image → cart drops to 2" bug). Only
  // the full-project reconcile (addAllToCart) may use 'sync'.
  await cart.reconcileProjectImages(wantedIds as any[], projectCode, { mode: 'upsert' }).catch(() => {})
}

function schedulePendingCartReconcile(idsRaw: string[], immediate = false) {
  const ids = Array.from(new Set((idsRaw || []).map((id) => String(id || '').trim()).filter(Boolean)))
  if (!ids.length) return
  for (const id of ids) pendingCartReconcileIds.add(id)
  if (pendingCartReconcileTimer) clearTimeout(pendingCartReconcileTimer)
  pendingCartReconcileTimer = setTimeout(() => {
    pendingCartReconcileTimer = null
    flushPendingCartReconcile().catch(() => {})
  }, immediate ? 200 : 450)
}

function buildServerMetaPayload(rec: ImageRecord | null | undefined) {
  if (!rec) return null
  return {
    edits: (rec as any).edits || null,
    tags: normalizeTags((rec as any).tags),
  }
}

function rescheduleDeferredMetaSync(idRaw: string, rec?: ImageRecord | null | undefined) {
  const id = String(idRaw || '').trim()
  if (!id || !deferredMetaSyncIds.has(id)) return
  const nextRec = rec || transientUploads.get(id) || sessionRecordsState.value[id] || null
  if (!canRunMetaSyncNow(nextRec as any)) return
  deferredMetaSyncIds.delete(id)
  pendingUploadMetaSyncIds.add(id)
  if (uploadMetaSyncFlushRunning) return
  if (uploadMetaSyncTimer) clearTimeout(uploadMetaSyncTimer)
  uploadMetaSyncTimer = setTimeout(() => {
    uploadMetaSyncTimer = null
    flushUploadMetaSyncBatch().catch(() => {})
  }, 250)
}

function buildLiveRecordForMetaSync(rec: ImageRecord, vm: ImageVM | null): ImageRecord {
  if (!vm) return enrichCategoryMeta(rec as any) as ImageRecord

  const productId = Number((vm.record as any)?.productId ?? (rec as any)?.productId ?? 0) || null
  const categoryId = Number((vm.record as any)?.categoryId ?? (rec as any)?.categoryId ?? 0) || null
  const parentCategoryId = Number((vm.record as any)?.parentCategoryId ?? (rec as any)?.parentCategoryId ?? 0) || null
  const productTitle = String((vm.record as any)?.productTitle ?? (rec as any)?.productTitle ?? '').trim()
  const categoryTitle = String((vm.record as any)?.categoryTitle ?? (rec as any)?.categoryTitle ?? '').trim()
  const parentCategoryTitle = String((vm.record as any)?.parentCategoryTitle ?? (rec as any)?.parentCategoryTitle ?? '').trim()
  const rot = normRot(vm.crop?.rot ?? (vm.record as any)?.edits?.rotate ?? (rec as any)?.edits?.rotate ?? 0)
  const { W: boxW, H: boxH } = activeBox(vm, rot)
  const crop = vm.crop
    ? (() => {
        const presetSize = (((vm.record as any)?.edits || {}) as any)?.preset?.size || (((rec as any)?.edits || {}) as any)?.preset?.size || null
        if (presetSize && isClearlyInvalidTinyCrop(vm.crop)) {
          return buildMaxCropForRecord({
            ...((rec as any) || {}),
            ...(((vm.record as any) || {}) as any),
            width: Math.max(1, Number(vm.ui.imgW || vm.ui.baseW || editorDimsForRecord(vm.record).width)),
            height: Math.max(1, Number(vm.ui.imgH || vm.ui.baseH || editorDimsForRecord(vm.record).height)),
            editorWidth: Math.max(1, Number(vm.ui.baseW || vm.ui.imgW || editorDimsForRecord(vm.record).width)),
            editorHeight: Math.max(1, Number(vm.ui.baseH || vm.ui.imgH || editorDimsForRecord(vm.record).height)),
          } as any, presetSize) || null
        }
        const clamped = clampCoordsToBox(
          { left: vm.crop.x, top: vm.crop.y, width: vm.crop.w, height: vm.crop.h },
          boxW,
          boxH,
        )
        return {
          x: clamped.left,
          y: clamped.top,
          w: clamped.width,
          h: clamped.height,
          rot,
          sourceWidth: boxW,
          sourceHeight: boxH,
          ...((cropToNormalized({
            x: clamped.left,
            y: clamped.top,
            w: clamped.width,
            h: clamped.height,
          }, boxW, boxH)) || {}),
        }
      })()
    : ((vm.record as any)?.edits?.crop || (rec as any)?.edits?.crop || null)

  const preset = buildPresetForPersistence(vm)
  const edits = {
    ...(((rec as any)?.edits || {}) as any),
    ...((((vm.record as any)?.edits || {}) as any)),
    preset,
    ...(crop ? { crop } : {}),
    rotate: rot,
    lockAR: !!vm.ui.lockAR,
    orientMode: vm.ui.orientMode,
    swapAR: !!vm.ui.swapAR,
  }

  return enrichCategoryMeta({
    ...(rec as any),
    ...(((vm.record || {}) as any)),
    ...(productId ? { productId } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(parentCategoryId ? { parentCategoryId } : {}),
    ...(productTitle ? { productTitle } : {}),
    ...(categoryTitle ? { categoryTitle } : {}),
    ...(parentCategoryTitle ? { parentCategoryTitle } : {}),
    edits,
    upload: {
      ...((((rec as any)?.upload || {}) as any)),
      ...((((vm.record as any)?.upload || {}) as any)),
    },
  } as any) as ImageRecord
}

async function buildUploadMetaBatchItem(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return null
  const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
  const vm = images.value.find((x) => x.id === id) || null
  let rec = vm?.record || transientUploads.get(id) || sessionRecordsState.value[id] || null
  if (!rec) rec = await getStoredRecord(id)
  if (!rec) return null
  const recordProjectCode = String((rec as any)?.projectCode || '').trim()
  const projectCode =
    String(project.projectCode.value || '').trim() ||
    recordProjectCode ||
    await project.ensureProject(recordProjectCode || undefined).catch(() => '')
  if (!editorBaseUrl || !projectCode) return null
  const liveRec = buildLiveRecordForMetaSync(rec, vm)
  if (shouldDeferMetaSync(liveRec)) {
    deferredMetaSyncIds.add(id)
    return null
  }
  deferredMetaSyncIds.delete(id)
  return {
    editorBaseUrl,
    projectCode,
    item: {
      id,
      name: String((liveRec as any)?.name || '').trim() || null,
      productId: Number((liveRec as any)?.productId || 0) || null,
      categoryId: Number((liveRec as any)?.categoryId || 0) || null,
      meta: buildServerMetaPayload(liveRec),
    },
  }
}

async function applyUploadMetaSyncResponses(responseItems: any[], opts: { editorBaseUrl: string; projectCode: string }) {
  const touchedIds: string[] = []
  for (const response of responseItems || []) {
    const id = String((response as any)?.id || '').trim()
    if (!id) continue
    clearCartMetaDirty(id)
    touchedIds.push(id)
    const next = mapRemoteUploadToRecordMeta(response as any, { projectCode: opts.projectCode })
    upsertProjectUploadsCacheItem(response as any, { editorBaseUrl: opts.editorBaseUrl, projectCode: opts.projectCode })
    const merged = mergeServerAndLocalImageRecord(
      next,
      (sessionRecordsState.value[id] || null) as ImageRecord | null,
    ) as any
    sessionRecordsState.value[id] = merged
    await db.put(merged as any).catch(() => null)
    const liveVm = images.value.find((x) => x.id === id) || null
    if (liveVm) {
      liveVm.name = String((merged as any)?.name || liveVm.name || id)
      liveVm.record = merged as any
      continue
    }
    emitUploadEvent({
      type: 'upload:patched',
      id,
      patch: {
        ...(merged?.width != null ? { width: Number(merged.width) } : {}),
        ...(merged?.height != null ? { height: Number(merged.height) } : {}),
        ...(merged?.editorWidth != null ? { editorWidth: Number(merged.editorWidth) } : {}),
        ...(merged?.editorHeight != null ? { editorHeight: Number(merged.editorHeight) } : {}),
        ...(merged?.productId != null ? { productId: Number(merged.productId || 0) || null } : {}),
        ...(merged?.categoryId != null ? { categoryId: Number(merged.categoryId || 0) || null } : {}),
        ...((merged as any)?.parentCategoryId != null ? { parentCategoryId: Number((merged as any).parentCategoryId || 0) || null } : {}),
        ...((merged as any)?.productTitle ? { productTitle: String((merged as any).productTitle) } : {}),
        ...((merged as any)?.categoryTitle ? { categoryTitle: String((merged as any).categoryTitle) } : {}),
        ...((merged as any)?.parentCategoryTitle ? { parentCategoryTitle: String((merged as any).parentCategoryTitle) } : {}),
        ...(!liveVm && merged?.edits ? { edits: merged.edits } : {}),
        ...(merged?.upload ? { upload: merged.upload } : {}),
      },
    })
  }

  if (touchedIds.length) {
    schedulePendingCartReconcile(touchedIds)
  }
}

async function flushUploadMetaSyncImmediate(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id || metaSyncInFlightIds.has(id)) return false
  const entry = await buildUploadMetaBatchItem(id)
  if (!entry?.editorBaseUrl || !entry?.projectCode || !entry?.item) return false
  metaSyncInFlightIds.add(id)
  pendingUploadMetaSyncIds.delete(id)
  try {
    const responseItems = await syncUploadMetaBatch({
      editorBaseUrl: entry.editorBaseUrl,
      projectCode: entry.projectCode,
      items: [entry.item],
    })
    await applyUploadMetaSyncResponses(responseItems, {
      editorBaseUrl: entry.editorBaseUrl,
      projectCode: entry.projectCode,
    })
    return true
  } finally {
    metaSyncInFlightIds.delete(id)
  }
}

async function flushUploadMetaSyncBatch() {
  if (uploadMetaSyncTimer) {
    clearTimeout(uploadMetaSyncTimer)
    uploadMetaSyncTimer = null
  }
  if (uploadMetaSyncFlushRunning) return

  const ids = [...pendingUploadMetaSyncIds].filter(Boolean)
  if (!ids.length) return
  uploadMetaSyncFlushRunning = true
  for (const id of ids) pendingUploadMetaSyncIds.delete(id)

  try {
    const resolved = await Promise.all(ids.map((id) => buildUploadMetaBatchItem(id)))

    const retryIds: string[] = []
    const readyEntries = resolved.map((entry, index) => {
      if (entry) return entry
      const id = String(ids[index] || '').trim()
      if (id) retryIds.push(id)
      return null
    }).filter(Boolean) as any[]

    const first = readyEntries.find(Boolean) as any
    if (!first?.editorBaseUrl || !first?.projectCode) {
      for (const id of retryIds) pendingUploadMetaSyncIds.add(id)
      return
    }

    const items = readyEntries
      .map((entry: any) => entry?.item || null)
      .filter(Boolean)
      .filter((item: any) => {
        const id = String(item?.id || '').trim()
        if (!id || metaSyncInFlightIds.has(id)) return false
        metaSyncInFlightIds.add(id)
        return true
      })

    if (!items.length) {
      for (const id of retryIds) pendingUploadMetaSyncIds.add(id)
      return
    }

    try {
      const responseItems = await syncUploadMetaBatch({
        editorBaseUrl: first.editorBaseUrl,
        projectCode: first.projectCode,
        items,
      })

      await applyUploadMetaSyncResponses(responseItems, {
        editorBaseUrl: first.editorBaseUrl,
        projectCode: first.projectCode,
      })
    } finally {
      for (const item of items) {
        metaSyncInFlightIds.delete(String((item as any)?.id || '').trim())
      }
      for (const id of retryIds) pendingUploadMetaSyncIds.add(id)
    }
  } finally {
    uploadMetaSyncFlushRunning = false
    if (pendingUploadMetaSyncIds.size) {
      uploadMetaSyncTimer = setTimeout(() => {
        uploadMetaSyncTimer = null
        flushUploadMetaSyncBatch().catch(() => {})
      }, 250)
    }
  }
}

async function flushProjectMetaForCart(idsRaw: string[]) {
  const ids = Array.from(new Set((idsRaw || []).map((id) => String(id || '').trim()).filter(Boolean)))
    .filter((id) => cartMetaDirtyIds.has(id))
  if (!ids.length) return

  await Promise.all(ids.map((id) => patchQueues.get(id) ?? Promise.resolve())).catch(() => {})

  const resolved = await Promise.all(ids.map((id) => buildUploadMetaBatchItem(id).catch(() => null)))
  const grouped = new Map<string, { editorBaseUrl: string; projectCode: string; items: any[] }>()
  const retryIds: string[] = []

  resolved.forEach((entry, index) => {
    const id = ids[index] || ''
    if (!entry?.editorBaseUrl || !entry?.projectCode || !entry?.item) {
      if (id) retryIds.push(id)
      return
    }

    const itemId = String(entry.item?.id || '').trim()
    if (!itemId) {
      if (id) retryIds.push(id)
      return
    }

    pendingUploadMetaSyncIds.delete(itemId)
    if (metaSyncInFlightIds.has(itemId)) return
    metaSyncInFlightIds.add(itemId)

    const key = `${entry.editorBaseUrl}::${entry.projectCode}`
    const bucket = grouped.get(key) || {
      editorBaseUrl: entry.editorBaseUrl,
      projectCode: entry.projectCode,
      items: [],
    }
    bucket.items.push(entry.item)
    grouped.set(key, bucket)
  })

  try {
    for (const group of grouped.values()) {
      if (!group.items.length) continue
      const responseItems = await syncUploadMetaBatch({
        editorBaseUrl: group.editorBaseUrl,
        projectCode: group.projectCode,
        items: group.items,
      })

      await applyUploadMetaSyncResponses(responseItems, {
        editorBaseUrl: group.editorBaseUrl,
        projectCode: group.projectCode,
      })
    }
  } finally {
    for (const group of grouped.values()) {
      for (const item of group.items) {
        metaSyncInFlightIds.delete(String((item as any)?.id || '').trim())
      }
    }
    for (const id of retryIds) pendingUploadMetaSyncIds.add(id)
  }

  const deadline = Date.now() + 1800
  while (Date.now() < deadline) {
    const waiting = ids.some((id) => pendingUploadMetaSyncIds.has(id) || metaSyncInFlightIds.has(id))
    if (!waiting && !uploadMetaSyncFlushRunning) break
    await new Promise((resolve) => window.setTimeout(resolve, 60))
  }
}

function scheduleUploadMetaSync(idRaw: string, immediate = false) {
  const id = String(idRaw || '').trim()
  if (!id) return
  markCartMetaDirty(id)
  const vm = images.value.find((x) => x.id === id) || null
  const rec = vm?.record || transientUploads.get(id) || sessionRecordsState.value[id] || null
  if (!canRunMetaSyncNow(rec as any)) deferredMetaSyncIds.add(id)
  pendingUploadMetaSyncIds.add(id)
  if (immediate) {
    void flushUploadMetaSyncImmediate(id).then((sent) => {
      if (sent) return
      if (uploadMetaSyncFlushRunning) return
      if (uploadMetaSyncTimer) clearTimeout(uploadMetaSyncTimer)
      uploadMetaSyncTimer = setTimeout(() => {
        uploadMetaSyncTimer = null
        flushUploadMetaSyncBatch().catch(() => {})
      }, 250)
    })
    return
  }
  if (uploadMetaSyncFlushRunning) return
  if (uploadMetaSyncTimer) clearTimeout(uploadMetaSyncTimer)
  uploadMetaSyncTimer = setTimeout(() => {
    uploadMetaSyncTimer = null
    flushUploadMetaSyncBatch().catch(() => {})
  }, immediate ? 250 : 1200)
}

async function patchEditsNow(id: string, patch: Partial<ImageRecord['edits']>) {
  const rec =
    transientUploads.get(id)
    || sessionRecordsState.value[id]
    || await db.get(id).catch(() => null)
  if (!rec) return

  const prev = rec.edits ?? {}
  const next: any = { ...prev, ...patch }

  if ((patch as any).preset !== undefined) {
    const prevPreset = (prev as any).preset ?? {}
    const patchPreset = (patch as any).preset ?? {}
    next.preset = { ...prevPreset, ...patchPreset }
    if (patchPreset.options === undefined && prevPreset.options !== undefined) {
      next.preset.options = prevPreset.options
    }
  }

  // Nur Edits speichern – productId/categoryId/productTitle NICHT überschreiben.
  // Diese werden ausschließlich beim Upload und bei explizitem Produktwechsel gesetzt.
  const meta: any = { edits: next }

  mergeSessionRecordPatch(id, { edits: next, updatedAt: Date.now() } as any)
  const vm = images.value.find((x) => x.id === id)
  if (vm) {
    vm.record.edits = next
    vm.record.updatedAt = Date.now()
  }
  await persistRecordUpdate(id, meta as any).catch(() => {})
  const touchesServerMeta =
    (patch as any).crop !== undefined ||
    (patch as any).rotate !== undefined ||
    (patch as any).preset !== undefined ||
    (patch as any).lockAR !== undefined ||
    (patch as any).orientMode !== undefined ||
    (patch as any).swapAR !== undefined
  if (touchesServerMeta) scheduleUploadMetaSync(id, true)
}
function patchEdits(id: string, patch: Partial<ImageRecord['edits']>) {
  const prev = patchQueues.get(id) ?? Promise.resolve()
  const job = prev.then(() => patchEditsNow(id, patch)).catch(() => {})
  patchQueues.set(id, job)
  return job
}

const emitMetaChangedDebounced = debounce((ids: string[]) => emit('meta-changed', ids), 350)
const cropDebouncers = new Map<string, ReturnType<typeof debounce>>()
const cropPersistRevisions = new Map<string, number>()
const presetDebouncers = new Map<string, ReturnType<typeof debounce>>()

function cancelQueuedCropPersist(idRaw: string) {
  const id = String(idRaw || '').trim()
  if (!id) return
  const fn = cropDebouncers.get(id) as any
  fn?.cancel?.()
}

async function persistCropNow(id: string, crop: PxCrop | null, rotate: number, revision?: number) {
  if (!crop) return
  markCartMetaDirty(id)
  const wantedRevision = Number(revision || 0)
  const currentRevision = Number(cropPersistRevisions.get(id) || 0)
  if (wantedRevision > 0 && wantedRevision !== currentRevision) return
  const img = images.value.find((x) => x.id === id)
  const sourceDims = {
    width: Math.max(1, Number(crop.sourceWidth || img?.ui.baseW || img?.ui.imgW || 1)),
    height: Math.max(1, Number(crop.sourceHeight || img?.ui.baseH || img?.ui.imgH || 1)),
  }
  const normalized = cropToNormalized(crop, sourceDims.width, sourceDims.height)
  await patchEdits(id, {
    crop: {
      x: crop.x,
      y: crop.y,
      w: crop.w,
      h: crop.h,
      sourceWidth: sourceDims.width,
      sourceHeight: sourceDims.height,
      ...(normalized || {}),
    },
    rotate,
    ...(img ? { preset: buildPresetForPersistence(img) } : {}),
  })
}
function persistCropQueued(id: string, crop: PxCrop | null, rotate: number) {
  if (!crop) return
  const revision = Number(cropPersistRevisions.get(id) || 0) + 1
  cropPersistRevisions.set(id, revision)
  let fn = cropDebouncers.get(id)
  if (!fn) {
    fn = debounce((c: PxCrop | null, r: number, rev: number) => {
      persistCropNow(id, c, r, rev).catch(() => {})
    }, 320)
    cropDebouncers.set(id, fn)
  }
  fn(crop, rotate, revision)
}
async function persistPresetNow(id: string, preset: any) {
  markCartMetaDirty(id)
  const img = images.value.find((x) => x.id === id)
  const nextPreset = img ? buildPresetForPersistence(img, preset) : preset
  await patchEdits(id, { preset: nextPreset })
  mergeSessionRecordPatch(id, {
    edits: {
      ...(((sessionRecordsState.value[id] as any)?.edits || {}) as any),
      preset: {
        ...((((sessionRecordsState.value[id] as any)?.edits?.preset || {}) as any)),
        ...((nextPreset || {}) as any),
      },
    },
    updatedAt: Date.now(),
  } as any)
  if (img) {
    img.record.edits = {
      ...(((img.record as any)?.edits || {}) as any),
      preset: {
        ...((((img.record as any)?.edits?.preset || {}) as any)),
        ...((nextPreset || {}) as any),
      },
    } as any
    img.record.updatedAt = Date.now()
  }
  emitMetaChangedDebounced([id])
}
function persistPresetQueued(id: string, preset: any) {
  let fn = presetDebouncers.get(id)
  if (!fn) {
    fn = debounce((p: any) => {
      persistPresetNow(id, p).catch(() => {})
    }, 250)
    presetDebouncers.set(id, fn)
  }
  fn(preset)
}
async function persistQuantity(img: ImageVM) {
  if (isLocked(img)) return
  markCartMetaDirty(img.id)
  const qty = Math.max(1, Number(img.ui.quantity || 1))
  img.ui.quantity = qty
  await persistPresetNow(img.id, { quantity: qty })
  scheduleUploadMetaSync(img.id, true)
}

function stepTileQty(img: ImageVM, delta: number) {
  if (isLocked(img)) return
  const next = Math.max(1, Number(img.ui.quantity || 1) + Number(delta || 0))
  img.ui.quantity = next
  persistQuantity(img)
}

function setTileQty(img: ImageVM, value: number) {
  if (isLocked(img)) return
  const next = Number(value)
  if (!Number.isFinite(next)) return
  img.ui.quantity = next
}

// Per-product variant groups cache: productId → all groups from that product's config
const productVariantGroupsMap = ref<Map<number, any[]>>(new Map())

// Seed with the globally selected product's groups (from parent)
watch(
  [() => Number(props.productId || 0), () => props.tileVariantGroups ?? []],
  ([pid, groups]) => {
    if (!pid || !groups.length) return
    const prev = productVariantGroupsMap.value.get(pid) || null
    if (prev === groups) return
    const m = new Map(productVariantGroupsMap.value)
    m.set(pid, groups)
    productVariantGroupsMap.value = m
  },
  { immediate: true, flush: 'post' },
)

// Lade Variantengruppen für alle einzigartigen Produkt-IDs aus den Bildern
async function loadVariantGroupsForProduct(productId: number) {
  if (!productId || productVariantGroupsMap.value.has(productId)) return
  const product = catalogBootstrap.value?.products?.[String(productId)]
  const groups = product?.configurator?.groups ?? []
  if (!groups.length) return
  const m = new Map(productVariantGroupsMap.value)
  m.set(productId, groups)
  productVariantGroupsMap.value = m
}

watch(images, (imgs) => {
  const seen = new Set<number>()
  for (const img of imgs) {
    const cid = Number((img.record as any)?.categoryId ?? 0)
    if (cid > 0 && !productListsByCategory.value.has(cid) && !seen.has(cid)) {
      seen.add(cid)
      loadProductsForCategory(cid)
    }
  }
}, { immediate: true })

// Bei Änderung der Bildliste: fehlende Produkt-Configs nachladen
watch(images, (imgs) => {
  const seen = new Set<number>()
  for (const img of imgs) {
    const pid = Number((img.record as any)?.productId ?? 0)
    if (pid > 0 && !productVariantGroupsMap.value.has(pid) && !seen.has(pid)) {
      seen.add(pid)
      loadVariantGroupsForProduct(pid)
    }
  }
}, { immediate: true })

watch(catalogBootstrap, () => {
  const categoryId = Number(props.categoryId || 0)
  if (categoryId > 0) {
    loadProductsForCategory(categoryId).catch(() => {})
  }
  for (const img of images.value) {
    const cid = Number((img.record as any)?.categoryId ?? 0)
    const pid = Number((img.record as any)?.productId ?? 0)
    if (cid > 0) loadProductsForCategory(cid).catch(() => {})
    if (pid > 0) loadVariantGroupsForProduct(pid).catch(() => {})
  }
}, { immediate: true })

// Wenn Produkt-Configs nachgeladen wurden (async): Größenauswahl erneut abgleichen
watch(productVariantGroupsMap, () => {
  if (images.value.length) reconcileUiForNewProduct()
}, { flush: 'post' })

function tileVariantGroupsFor(img: ImageVM): any[] {
  const pid = Number(recordProductId((img.record as any) || {}) || 0)
  if (pid && productVariantGroupsMap.value.has(pid)) {
    return productVariantGroupsMap.value.get(pid)!
  }
  if (pid) {
    const bootstrapGroups = catalogBootstrap.value?.products?.[String(pid)]?.configurator?.groups
    if (Array.isArray(bootstrapGroups) && bootstrapGroups.length) {
      return bootstrapGroups
    }
  }
  return props.tileVariantGroups ?? []
}

function defaultOptionsFromGroups(groups: any[] | null | undefined): Array<{ groupId: string; variantId: string; value: string }> {
  const out: Array<{ groupId: string; variantId: string; value: string }> = []
  for (const group of Array.isArray(groups) ? groups : []) {
    if (!group || group.isSize) continue
    const opts = Array.isArray(group.options) ? group.options : []
    // Ohne Standard die erste Variante – so zeigen es Kachel, Upload-Dialog und Seitenleiste an
    // (sonst blieb z. B. beim Retroprint die Oberfläche leer, S3.26.316421).
    const def = opts.find((o: any) => !!o?.isDefault) || opts[0] || null
    const groupId = String(group.id || '').trim()
    const variantId = String(def?.id || '').trim()
    const value = String(def?.title || '').trim()
    if (!groupId || !variantId || !value) continue
    out.push({ groupId, variantId, value })
  }
  return out
}

function carryTileOptionsToProduct(
  prevOptions: Array<{ groupId: string; variantId: string; value: string }> | null | undefined,
  nextGroups: any[] | null | undefined,
): Array<{ groupId: string; variantId: string; value: string }> {
  const incoming = Array.isArray(prevOptions) ? prevOptions : []
  const groups = Array.isArray(nextGroups) ? nextGroups : []
  if (!incoming.length || !groups.length) return []

  const out: Array<{ groupId: string; variantId: string; value: string }> = []
  for (const own of incoming) {
    const ownValue = String(own?.value || '').trim()
    if (!ownValue) continue

    const matchedGroup = groups.find((group: any) => {
      if (!group || group?.isSize) return false
      const opts = Array.isArray(group?.options) ? group.options : []
      return opts.some((opt: any) => String(opt?.title || '').trim() === ownValue)
    })
    if (!matchedGroup) continue

    const matchedOption = (Array.isArray(matchedGroup?.options) ? matchedGroup.options : []).find((opt: any) =>
      String(opt?.title || '').trim() === ownValue,
    )
    const groupId = String(matchedGroup?.id || '').trim()
    const variantId = String(matchedOption?.id || '').trim()
    if (!groupId || !variantId) continue

    out.push({ groupId, variantId, value: ownValue })
  }

  return out
}

function defaultTileOptionsForProduct(
  groups: any[] | null | undefined,
): Array<{ groupId: string; variantId: string; value: string }> {
  const source = Array.isArray(groups) ? groups : []
  const out: Array<{ groupId: string; variantId: string; value: string }> = []

  for (const group of source) {
    if (!group || group?.isSize) continue
    const fallback = (Array.isArray(group?.options) ? group.options : []).find((opt: any) => opt?.isDefault)
      ?? (Array.isArray(group?.options) ? group.options[0] : null)
      ?? null
    const groupId = String(group?.id || '').trim()
    const variantId = String(fallback?.id || '').trim()
    const value = String(fallback?.title || '').trim()
    if (!groupId || !variantId || !value) continue
    out.push({ groupId, variantId, value })
  }

  return out
}

function tileVisibleGroupsFor(img: ImageVM): any[] {
  const groups = tileVariantGroupsFor(img)
  const isTrue = (v: any) => v === true || v === 1 || String(v).toLowerCase() === 'true' || String(v) === '1'
  return groups.filter((g: any) => {
    const title = String(g?.title || '').trim().toLowerCase()
    const isFill = title.includes('fill in') || title.includes('formatfüllend')
    return !g?.isSize && !isFill && isTrue(g?.showInTile)
  })
}


function tileGroupOptions(group: any) {
  const opts = Array.isArray(group?.options) ? group.options : []
  return opts.map((o: any): FlatSelectOption => ({
    value: normId(o?.id || ''),
    label: String(o?.title || '—'),
    right: typeof o?.price === 'number'
      ? (Number(o.price) === 0 ? '' : formatMoneyLike(o.price, (o as any)?.currency))
      : (Number(o?.priceLabel || 0) === 0 ? '' : formatMoneyLike(o?.priceLabel || '', (o as any)?.currency)),
  })).filter((o: any) => !!o.value)
}

function tileGroupValue(img: ImageVM, group: any): string | null {
  const gid = normId(group?.id || '')
  if (!gid) return null
  const own = (img.ui.options || []).find((o) => normId(o.groupId) === gid)
  if (own?.variantId) {
    const vv = normId(own.variantId)
    const opts = tileGroupOptions(group)
    if (opts.some((o) => normId(o.value) === vv)) return vv
  }

  // fallback: match über gespeicherten Textwert
  if (own?.value) {
    const byLabel = tileGroupOptions(group).find((o) => String(o.label) === String(own.value))
    if (byLabel?.value) return normId(byLabel.value)
  }
  return null
}

async function onTileGroupChange(img: ImageVM, group: any, nextVariantId: string) {
  if (isLocked(img)) return
  const gid = normId(group?.id || '')
  if (!gid || !nextVariantId) return
  const prevFitInMode = isFitInMode(img)

  const opts = Array.isArray(group?.options) ? group.options : []
  const vId = normId(nextVariantId)
  const sel = opts.find((o: any) => normId(o?.id) === vId)
  const next: UIOption = {
    groupId: gid,
    variantId: vId,
    value: String(sel?.title || ''),
  }

  const map = new Map<string, UIOption>()
  for (const o of img.ui.options || []) map.set(normId(o.groupId), { ...o, groupId: normId(o.groupId), variantId: normId(o.variantId) })
  map.set(gid, next)
  img.ui.options = [...map.values()]
  const nextFitInMode = isFitInMode(img)
  if (prevFitInMode && !nextFitInMode) {
    const liveSize = sizeFor(img)
      || normalizedSizesForImg(img).find((x) => x.key === img.ui.sizeKey)
      || null
    const nextStoredCrop = buildMaxCropForRecord(img.record as any, liveSize)
    if (nextStoredCrop) {
      ;(img.record as any).edits = {
        ...((((img.record as any)?.edits || {}) as any)),
        crop: nextStoredCrop,
        lockAR: true,
        orientMode: 'auto',
        swapAR: false,
      }
      const dims = editorDimsForRecord(img.record as any)
      img.crop = cropFromStored(img.record as any, { width: dims.width, height: dims.height })
    } else {
      setMaxCrop(img, normRot(img.crop?.rot ?? 0))
    }
  }
  syncCropMode(img)

  const s = normalizedSizesForImg(img).find((x) => x.key === img.ui.sizeKey)
  const imgPid = Number((img.record as any)?.productId ?? props.productId) || undefined
  const nextPreset = {
    productId: imgPid,
    size: s ? { title: s.title, widthMm: s.widthMm, heightMm: s.heightMm, variantId: s.variantId || undefined } : null,
    options: img.ui.options,
    quantity: Math.max(1, Number(img.ui.quantity || 1)),
  }
  ;(img.record as any).edits = {
    ...(((img.record as any)?.edits || {}) as any),
    preset: buildPresetForPersistence(img, nextPreset),
  }
  await persistPresetNow(img.id, nextPreset)
  scheduleUploadMetaSync(img.id, true)
}

/* ------------------------------------------------
 * Core: setMaxCrop + clamp
 * ------------------------------------------------ */
function setMaxCrop(img: ImageVM, rotDeg?: number) {
  const rot = normRot(rotDeg ?? img.crop?.rot ?? 0)
  const { W: bw, H: bh } = activeBox(img, rot)
  const withMeta = (crop: { x: number; y: number; w: number; h: number; rot: number }) => ({
    ...crop,
    sourceWidth: bw,
    sourceHeight: bh,
    ...((cropToNormalized({
      x: crop.x,
      y: crop.y,
      w: crop.w,
      h: crop.h,
    }, bw, bh)) || {}),
  })

  if (!img.ui.lockAR || isFitInMode(img)) {
    img.crop = withMeta({ x: 0, y: 0, w: bw, h: bh, rot })
    return
  }

  const ar = effectiveAR(img)
  if (!ar || !Number.isFinite(ar) || ar <= 0) {
    img.crop = withMeta({ x: 0, y: 0, w: bw, h: bh, rot })
    return
  }

  const imgAr = bw / bh
  let w = bw
  let h = bh

  if (imgAr >= ar) {
    h = bh
    w = Math.round(h * ar)
  } else {
    w = bw
    h = Math.round(w / ar)
  }

  w = Math.max(2, Math.min(bw, w))
  h = Math.max(2, Math.min(bh, h))

  img.crop = withMeta({
    x: Math.round((bw - w) / 2),
    y: Math.round((bh - h) / 2),
    w,
    h,
    rot,
  })
}
function clampStoredCropToCurrentBox(img: ImageVM) {
  if (!img.crop) return false
  const rot = normRot(img.crop.rot ?? 0)
  const { W: boxW, H: boxH } = activeBox(img, rot)
  const sourceWidth = Math.max(1, Number((img.crop as any).sourceWidth || boxW))
  const sourceHeight = Math.max(1, Number((img.crop as any).sourceHeight || boxH))
  const needsRescale =
    Math.abs(sourceWidth - boxW) > 1 ||
    Math.abs(sourceHeight - boxH) > 1

  if (needsRescale) {
    const scaleX = boxW / sourceWidth
    const scaleY = boxH / sourceHeight
    img.crop = {
      ...img.crop,
      x: Math.round(img.crop.x * scaleX),
      y: Math.round(img.crop.y * scaleY),
      w: Math.max(2, Math.round(img.crop.w * scaleX)),
      h: Math.max(2, Math.round(img.crop.h * scaleY)),
      rot,
      sourceWidth: boxW,
      sourceHeight: boxH,
      ...((cropToNormalized({
        x: Math.round(img.crop.x * scaleX),
        y: Math.round(img.crop.y * scaleY),
        w: Math.max(2, Math.round(img.crop.w * scaleX)),
        h: Math.max(2, Math.round(img.crop.h * scaleY)),
      }, boxW, boxH)) || {}),
    }
  }

  const clamped = clampCoordsToBox(
      { left: img.crop.x, top: img.crop.y, width: img.crop.w, height: img.crop.h },
      boxW,
      boxH,
  )
  const changed =
    img.crop.x !== clamped.left ||
    img.crop.y !== clamped.top ||
    img.crop.w !== clamped.width ||
    img.crop.h !== clamped.height ||
    needsRescale
  img.crop = {
    ...img.crop,
    x: clamped.left,
    y: clamped.top,
    w: clamped.width,
    h: clamped.height,
    rot,
    sourceWidth: boxW,
    sourceHeight: boxH,
    ...((cropToNormalized({
      x: clamped.left,
      y: clamped.top,
      w: clamped.width,
      h: clamped.height,
    }, boxW, boxH)) || {}),
  }
  return changed
}

function clampLiveCropToCurrentBox(img: ImageVM) {
  if (!img.crop) return false
  const rot = normRot(img.crop.rot ?? 0)
  const { W: boxW, H: boxH } = activeBox(img, rot)
  const sourceWidth = Math.max(1, Number((img.crop as any).sourceWidth || 0) || 0)
  const sourceHeight = Math.max(1, Number((img.crop as any).sourceHeight || 0) || 0)
  const fallbackSourceWidth = Math.max(1, Number(img.ui.imgW || img.ui.baseW || boxW))
  const fallbackSourceHeight = Math.max(1, Number(img.ui.imgH || img.ui.baseH || boxH))
  const baseSourceWidth = sourceWidth || fallbackSourceWidth
  const baseSourceHeight = sourceHeight || fallbackSourceHeight
  const needsRescale =
    Math.abs(baseSourceWidth - boxW) > 1 ||
    Math.abs(baseSourceHeight - boxH) > 1

  let nextX = img.crop.x
  let nextY = img.crop.y
  let nextW = img.crop.w
  let nextH = img.crop.h

  if (needsRescale) {
    const scaleX = boxW / Math.max(1, baseSourceWidth)
    const scaleY = boxH / Math.max(1, baseSourceHeight)
    nextX = Math.round(nextX * scaleX)
    nextY = Math.round(nextY * scaleY)
    nextW = Math.max(2, Math.round(nextW * scaleX))
    nextH = Math.max(2, Math.round(nextH * scaleY))
  }

  const clamped = clampCoordsToBox(
    { left: nextX, top: nextY, width: nextW, height: nextH },
    boxW,
    boxH,
  )
  const changed =
    img.crop.x !== clamped.left ||
    img.crop.y !== clamped.top ||
    img.crop.w !== clamped.width ||
    img.crop.h !== clamped.height ||
    normRot(img.crop.rot ?? 0) !== rot ||
    needsRescale ||
    Number((img.crop as any).sourceWidth || 0) !== boxW ||
    Number((img.crop as any).sourceHeight || 0) !== boxH

  img.crop = {
    ...img.crop,
    x: clamped.left,
    y: clamped.top,
    w: clamped.width,
    h: clamped.height,
    rot,
    sourceWidth: boxW,
    sourceHeight: boxH,
    ...((cropToNormalized({
      x: clamped.left,
      y: clamped.top,
      w: clamped.width,
      h: clamped.height,
    }, boxW, boxH)) || {}),
  }

  return changed
}
function rotateCropRect90(
    rect: { x: number; y: number; w: number; h: number },
    box: { W: number; H: number },
    delta: number,
) {
  const step = ((((delta % 360) + 360) % 360) / 90) | 0
  const turns = ((step % 4) + 4) % 4
  let x = Math.round(n(rect.x, 0))
  let y = Math.round(n(rect.y, 0))
  let w = Math.max(2, Math.round(n(rect.w, 2)))
  let h = Math.max(2, Math.round(n(rect.h, 2)))
  let W = Math.max(1, Math.round(n(box.W, 1)))
  let H = Math.max(1, Math.round(n(box.H, 1)))

  for (let i = 0; i < turns; i++) {
    const nx = H - (y + h)
    const ny = x
    const nw = h
    const nh = w
    x = nx
    y = ny
    w = nw
    h = nh
    const nextW = H
    const nextH = W
    W = nextW
    H = nextH
  }

  return { x, y, w, h, W, H }
}
function rotateSelectionRect90(rect: { x: number; y: number; w: number; h: number }) {
  const cx = rect.x + rect.w / 2
  const cy = rect.y + rect.h / 2
  const nw = Math.max(2, Math.round(n(rect.h, 2)))
  const nh = Math.max(2, Math.round(n(rect.w, 2)))
  const nx = Math.round(cx - nw / 2)
  const ny = Math.round(cy - nh / 2)
  return { x: nx, y: ny, w: nw, h: nh }
}

function isFullCrop(img: ImageVM): boolean {
  if (!img.crop) return true
  const rot = normRot(img.crop.rot ?? 0)
  const { W: bw, H: bh } = activeBox(img, rot)
  return Math.abs((img.crop.x ?? 0) - 0) < 0.001 &&
      Math.abs((img.crop.y ?? 0) - 0) < 0.001 &&
      Math.abs((img.crop.w ?? 0) - bw) < 0.001 &&
      Math.abs((img.crop.h ?? 0) - bh) < 0.001
}

/* ------------------------------------------------
 * Apply crop to cropper (never persists)
 * ------------------------------------------------ */
async function applyCropToCropper(img: ImageVM, _reason: string, cropper: any, inFs: boolean) {
  if (!img.ui.ready) return
  if (!cropper || !img.crop) return
  if (typeof cropper.setCoordinates !== 'function') return

  img.ui.applying = true
  try {
    const rot = normRot(img.crop.rot ?? 0)
    applyRotationIfPossible(cropper, rot)

    await nextTick()
    await sleepRaf(2)

    const shouldResetVisibleArea =
      _reason.includes('max-btn') ||
      _reason.includes('size-change') ||
      _reason.includes('auto-maxcrop')

    if (shouldResetVisibleArea && typeof cropper.reset === 'function') {
      cropper.reset()
      await nextTick()
      await sleepRaf(2)
      applyRotationIfPossible(cropper, rot)
      await nextTick()
      await sleepRaf(1)
    }

    const { W: boxW, H: boxH } = activeBox(img, rot)
    const clamped = clampCoordsToBox(
        { left: img.crop.x, top: img.crop.y, width: img.crop.w, height: img.crop.h },
        boxW,
        boxH,
    )

    cropper.setCoordinates(clamped)
    img.ui.appliedOnce = true

    if (inFs) img.ui.armedFs = true
    else img.ui.armedGrid = true
  } finally {
    img.ui.applying = false
  }
}

/* ------------------------------------------------
 * Cropper events
 * ------------------------------------------------ */
function getRotateFromPayload(payload: any) {
  const r = payload?.imageTransforms?.rotate ?? payload?.transforms?.rotate ?? payload?.rotation ?? 0
  return normRot(Number(r) || 0)
}

const pendingApply = new Set<string>()
const scheduledApplyCropFrames = new Map<string, number>()
const scheduledCropChangeFrames = new Map<string, number>()
const scheduledCropChangePayloads = new Map<string, { img: ImageVM; payload: any; inFs: boolean; engine: 'cropper' | 'canvas' }>()

function scheduleApplyCropToCropper(img: ImageVM, reason: string, inFs: boolean) {
  const key = `${inFs ? 'fs' : 'grid'}:${String(img.id || '').trim()}`
  const prev = scheduledApplyCropFrames.get(key)
  if (prev) cancelAnimationFrame(prev)
  const frame = requestAnimationFrame(() => {
    scheduledApplyCropFrames.delete(key)
    void applyCropToCropper(img, reason, inFs ? fsCropperRef.value : cropperRefs.value.get(img.id), inFs)
  })
  scheduledApplyCropFrames.set(key, frame)
}

function flushScheduledCropChange(key: string) {
  const next = scheduledCropChangePayloads.get(key)
  if (!next) return
  scheduledCropChangeFrames.delete(key)
  scheduledCropChangePayloads.delete(key)
  processCropChange(next.img, next.payload, next.inFs, next.engine)
}

function onCropperReady(img: ImageVM, payload: any, inFs: boolean) {
  markImageActive(img)
  const cropper = inFs ? fsCropperRef.value : cropperRefs.value.get(img.id)
  const result = typeof cropper?.getResult === 'function' ? cropper.getResult() : null
  const recordDims = editorDimsForRecord(img.record as any)
  const fallbackBaseW = Math.max(1, Number(img.ui.baseW || img.ui.imgW || recordDims.width || 1))
  const fallbackBaseH = Math.max(1, Number(img.ui.baseH || img.ui.imgH || recordDims.height || 1))
  img.ui.baseW = n(result?.image?.width, fallbackBaseW)
  img.ui.baseH = n(result?.image?.height, fallbackBaseH)
  img.ui.imgW = img.ui.baseW
  img.ui.imgH = img.ui.baseH
  img.ui.ready = true
  if (inFs) img.ui.armedFs = false
  else img.ui.armedGrid = false

  let migratedStoredCrop = false
  if (img.crop) migratedStoredCrop = clampStoredCropToCurrentBox(img)
  else setMaxCrop(img, 0)

  if (img.crop) {
    const migratedLiveCrop = clampLiveCropToCurrentBox(img)
    if (migratedLiveCrop) {
      pendingApply.add(img.id)
      img.ui.appliedOnce = false
    }
  }

  if (pendingApply.has(img.id)) {
    pendingApply.delete(img.id)
    img.ui.appliedOnce = false
  }

  queueMicrotask(() => {
    void applyCropToCropper(img, inFs ? 'cropper-ready-fs' : 'cropper-ready-grid', cropper, inFs)
  })
}

function processCropChange(img: ImageVM, payload: any, inFs: boolean, engine: 'cropper' | 'canvas' = 'cropper') {
  markImageActive(img)
  const t0 = performance.now()
  const isFinal = !!payload?.final
  const interactionId = String(img.id || '').trim()
  if (interactionId) {
    if (isFinal) interactingCropperIds.delete(interactionId)
    else interactingCropperIds.add(interactionId)
  }
  if (!img.ui.ready) return
  if (img.ui.applying) return
  if (isLocked(img)) return
  if (isFitInMode(img)) return
  if (inFs && !img.ui.armedFs && !isFinal) return
  if (!inFs && !img.ui.armedGrid && !isFinal) return

  const c = payload?.coordinates
  if (!c) return

  const payloadImageSize = payload?.imageSize || null
  const cropper = inFs ? fsCropperRef.value : cropperRefs.value.get(img.id)
  const result = typeof cropper?.getResult === 'function' ? cropper.getResult() : null
  const rot = getRotateFromPayload(payload)
  const { W: boxW, H: boxH } = activeBox(img, rot)
  const clamped = clampCoordsToBox(
    {
      left: Math.max(0, Math.round(n(c.left, 0))),
      top: Math.max(0, Math.round(n(c.top, 0))),
      width: Math.max(2, Math.round(n(c.width, 2))),
      height: Math.max(2, Math.round(n(c.height, 2))),
    },
    boxW,
    boxH,
  )
  const next: PxCrop = {
    x: clamped.left,
    y: clamped.top,
    w: clamped.width,
    h: clamped.height,
    rot,
    sourceWidth: boxW,
    sourceHeight: boxH,
  }

  const prev = img.crop
  const forcePersist = isFinal
  const changed =
      !prev ||
      prev.x !== next.x ||
      prev.y !== next.y ||
      prev.w !== next.w ||
      prev.h !== next.h ||
      normRot(prev.rot ?? 0) !== rot

  if (!changed && !forcePersist) return

  const commitLiveCrop = !inFs || !!payload?.final
  if (commitLiveCrop) {
    img.crop = next
  }
  if (payload?.final) {
    cancelQueuedCropPersist(img.id)
    const revision = Number(cropPersistRevisions.get(img.id) || 0) + 1
    cropPersistRevisions.set(img.id, revision)
    if (interactionId) {
      window.setTimeout(() => {
        interactingCropperIds.delete(interactionId)
        scheduleHeavyTileSync()
      }, 180)
    }
    persistCropNow(img.id, next, rot, revision).catch(() => {})
  }
  if (inFs && fs.open && fs.id === img.id && payload?.final) {
    queueMicrotask(() => applyCropToCropper(img, 'fs->grid-sync', cropperRefs.value.get(img.id), false))
  }

  const dt = Number(payload?.perfMs)
  if (engine === 'canvas' && Number.isFinite(dt) && dt >= 0) {
    cropPerf.canvasTotal += dt
    cropPerf.canvasN += 1
  } else {
    cropPerf.cropperTotal += Math.max(0, performance.now() - t0)
    cropPerf.cropperN += 1
  }
}

function onCropChange(img: ImageVM, payload: any, inFs: boolean, engine: 'cropper' | 'canvas' = 'cropper') {
  const key = `${inFs ? 'fs' : 'grid'}:${String(img.id || '').trim()}`
  if (!key.endsWith(':')) {
    if (payload?.final) {
      const frame = scheduledCropChangeFrames.get(key)
      if (frame) cancelAnimationFrame(frame)
      scheduledCropChangeFrames.delete(key)
      scheduledCropChangePayloads.delete(key)
      processCropChange(img, payload, inFs, engine)
      return
    }

    scheduledCropChangePayloads.set(key, { img, payload, inFs, engine })
    if (scheduledCropChangeFrames.has(key)) return
    const frame = requestAnimationFrame(() => flushScheduledCropChange(key))
    scheduledCropChangeFrames.set(key, frame)
    return
  }

  processCropChange(img, payload, inFs, engine)
}

function onCanvasCropComplete(img: ImageVM, payload: any, inFs: boolean) {
  onCropChange(img, { ...(payload || {}), final: true }, inFs, 'canvas')
}


async function applySizeKey(img: ImageVM, nextKey: string, opts?: { persist?: boolean }) {
  if (isLocked(img)) return
  const persist = opts?.persist ?? false
  if (!nextKey || img.ui.sizeKey === nextKey) return
  markImageActive(img)

  img.ui.sizeKey = nextKey
  const s = normalizedSizesForImg(img).find((x) => x.key === nextKey)
  if (s) img.ui.sizeTitleHint = s.title

  img.ui.lockAR = true
  const currentRot = normRot(img.crop?.rot ?? 0)
  if (s?.ar) {
    img.ui.orientMode = 'auto'
    img.ui.swapAR = false
  }

  setMaxCrop(img, currentRot)

  img.ui.appliedOnce = false
  pendingApply.add(img.id)

  if (persist) {
    const imgPid2 = Number((img.record as any)?.productId ?? props.productId) || undefined
    const nextPreset = {
      productId: imgPid2,
      size: s
        ? {
            id: String(s.id || '').trim() || undefined,
            title: s.title,
            widthMm: s.widthMm,
            heightMm: s.heightMm,
            variantId: sizeVariantRef(s) || undefined,
          }
        : null,
      options: img.ui.options ?? [],
      quantity: Math.max(1, Number(img.ui.quantity || 1)),
    }
    ;(img.record as any).edits = {
      ...(((img.record as any)?.edits || {}) as any),
      preset: buildPresetForPersistence(img, nextPreset),
    }
    await persistPresetNow(img.id, nextPreset).catch(() => {})
    await patchEdits(img.id, { swapAR: img.ui.swapAR, orientMode: img.ui.orientMode, lockAR: img.ui.lockAR }).catch(() => {})
    if (img.crop) await persistCropNow(img.id, img.crop, img.crop.rot ?? 0).catch(() => {})
    scheduleUploadMetaSync(img.id, true)
    emitMetaChangedDebounced([img.id])
  }

  queueMicrotask(async () => {
    await applyCropToCropper(img, persist ? 'size-change-grid' : 'size-change-preview', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) {
      await applyCropToCropper(img, persist ? 'size-change-fs' : 'size-change-preview-fs', fsCropperRef.value, true)
    }
  })
}
function onSizeSelectKey(img: ImageVM, nextKey: string) {
  applySizeKey(img, nextKey, { persist: true })
}

/* ------------------------------------------------
 * Ungültige gespeicherte Größe reparieren
 * Steht im gespeicherten Preset eine Größe, die es für das Produkt nicht gibt (z. B. Silk 21x30,
 * Produkt Velvet Deep Matt), zeigt die Kachel zwar eine gültige Größe – der Warenkorb wird aber aus
 * dem gespeicherten Preset gebaut und die Position kostete 0 € (Bestellung S3.26.316177).
 * Darum die angezeigte bzw. nächstliegende echte Größe speichern.
 * ------------------------------------------------ */
const healedSizeSignatures = new Set<string>()

/** Echte Größen des Produkts (mit Varianten-ID) – nur wenn die Gruppen dieses Produkts bekannt sind */
function realSizesForImg(img: ImageVM) {
  const pid = Number(recordProductId((img.record as any) || {}) || 0)
  if (!pid) return []
  const known = productVariantGroupsMap.value.has(pid) || !!catalogBootstrap.value?.products?.[String(pid)]?.configurator?.groups?.length
  if (!known) return []
  const sizeGroup = tileVariantGroupsFor(img).find((g: any) => g?.isSize)
  if (!sizeGroup?.sizeOptions?.length) return []
  return normalizedSizesForImg(img).filter((s: any) => !!String(s.variantId || '').trim())
}

function nearestRealSize(sizes: any[], stored: any) {
  const w = normalizeMmValue(stored?.widthMm)
  const h = normalizeMmValue(stored?.heightMm)
  if (!(w > 0 && h > 0)) return sizes[0] ?? null
  const ar = Math.max(w, h) / Math.min(w, h)
  const area = w * h
  let best: any = null
  let bestScore = Infinity
  for (const s of sizes) {
    const sw = Number(s.widthMm || 0)
    const sh = Number(s.heightMm || 0)
    if (!(sw > 0 && sh > 0)) continue
    const score = 2 * Math.abs(Math.log((Math.max(sw, sh) / Math.min(sw, sh)) / ar)) + Math.abs(Math.log((sw * sh) / area))
    if (score < bestScore) { bestScore = score; best = s }
  }
  return best ?? sizes[0] ?? null
}

async function healInvalidStoredSize(img: ImageVM) {
  if (isLocked(img) || optionPreviewById.value[img.id]) return
  const stored = (img.record as any)?.edits?.preset?.size
  if (!stored || typeof stored !== 'object') return
  const sizes = realSizesForImg(img)
  if (!sizes.length) return
  const storedVariant = String(stored.variantId || '').trim()
  if (storedVariant && sizes.some((s: any) => String(s.variantId) === storedVariant)) return

  const pid = Number(recordProductId((img.record as any) || {}) || 0)
  // Individuelle Maße (Breite/Höhe ohne Varianten-ID) sind bei Produkten mit freien Größen gültig –
  // nicht auf die nächstliegende Listengröße umbiegen (z. B. 30x40 → 30x45 nach dem Upload-Dialog).
  if (!storedVariant && normalizeMmValue(stored.widthMm) > 0 && normalizeMmValue(stored.heightMm) > 0) {
    const bootstrapProduct = catalogBootstrap.value?.products?.[String(pid)] ?? null
    if (!bootstrapProduct || bootstrapProduct?.configurator?.product?.hasIndividualSizes === true) return
  }
  const signature = `${img.id}|${pid}|${storedVariant}|${stored.title || ''}|${stored.widthMm || ''}x${stored.heightMm || ''}`
  if (healedSizeSignatures.has(signature)) return
  healedSizeSignatures.add(signature)

  // gleiches Format (Titel/Maße) ohne Varianten-ID → nur die ID nachtragen, Ausschnitt bleibt
  const same = sizes.find((s: any) => sizeMatchesPreset(s, stored)) || null
  if (same) {
    img.ui.sizeKey = same.key
    img.ui.sizeTitleHint = same.title
    const nextPreset = {
      productId: pid || undefined,
      size: { id: String(same.id || '').trim() || undefined, title: same.title, widthMm: same.widthMm, heightMm: same.heightMm, variantId: sizeVariantRef(same) || undefined },
      options: img.ui.options ?? [],
      quantity: Math.max(1, Number(img.ui.quantity || 1)),
    }
    ;(img.record as any).edits = { ...(((img.record as any)?.edits || {}) as any), preset: buildPresetForPersistence(img, nextPreset) }
    await persistPresetNow(img.id, nextPreset).catch(() => {})
    scheduleUploadMetaSync(img.id, true)
    emitMetaChangedDebounced([img.id])
    return
  }

  // Größe gibt es für dieses Produkt nicht → angezeigte echte Größe, sonst die nächstliegende
  const shown = sizes.find((s: any) => s.key === img.ui.sizeKey) || null
  const target = shown || nearestRealSize(sizes, stored)
  if (!target) return
  img.ui.sizeKey = ''
  await applySizeKey(img, target.key, { persist: true })
}

const invalidSizeScanKey = computed(() => {
  if (!catalogBootstrap.value && !productVariantGroupsMap.value.size) return ''
  return images.value
    .map((img) => {
      const size = (img.record as any)?.edits?.preset?.size
      return `${img.id}:${recordProductId((img.record as any) || {}) || ''}:${size?.variantId || ''}:${size?.title || ''}:${img.ui.locked ? 1 : 0}`
    })
    .join('|') + `#${productVariantGroupsMap.value.size}`
})
let invalidSizeScanTimer: ReturnType<typeof setTimeout> | null = null
watch(invalidSizeScanKey, (key) => {
  if (!key) return
  if (invalidSizeScanTimer) clearTimeout(invalidSizeScanTimer)
  invalidSizeScanTimer = setTimeout(() => {
    invalidSizeScanTimer = null
    for (const img of images.value) void healInvalidStoredSize(img)
  }, 400)
})

async function applySidebarSizeLocally(ids: string[], size: any) {
  const wanted = (ids || []).map((id) => String(id || '').trim()).filter(Boolean)
  if (!wanted.length || !size) return

  for (const id of wanted) {
    const img = images.value.find((x) => x.id === id)
    if (!img || isLocked(img)) continue

    ;(img.record as any).edits = {
      ...(((img.record as any)?.edits || {}) as any),
      preset: {
        ...((((img.record as any)?.edits?.preset || {}) as any)),
        size: { ...(size as any) },
      },
      lockAR: true,
      orientMode: 'auto',
      swapAR: false,
    }

    const resolvedSize = resolveSizeUiState(
      normalizedSizesForImg(img),
      size,
      String((size as any)?.variantId || ''),
      String((size as any)?.title || ''),
      false,
    )
    if (resolvedSize.sizeKey) img.ui.sizeKey = resolvedSize.sizeKey
    if (resolvedSize.sizeTitleHint) img.ui.sizeTitleHint = resolvedSize.sizeTitleHint
    img.ui.lockAR = true
    img.ui.orientMode = 'auto'
    img.ui.swapAR = false

    setMaxCrop(img, normRot((img.record as any)?.edits?.rotate ?? img.crop?.rot ?? 0))
    img.ui.appliedOnce = false
    pendingApply.add(img.id)

    queueMicrotask(async () => {
      await applyCropToCropper(img, 'sidebar-size-grid', cropperRefs.value.get(img.id), false)
      if (fs.open && fs.id === img.id) {
        await applyCropToCropper(img, 'sidebar-size-fs', fsCropperRef.value, true)
      }
    })
  }
}

/* ------------------------------------------------
 * Fullscreen
 * ------------------------------------------------ */
const fs = reactive({ open: false, id: '' })
const fsImg = computed(() => images.value.find((i) => i.id === fs.id) ?? null)

watch([() => images.value.length, () => fs.open, () => fs.id], () => {
  scheduleHeavyTileSync()
})

async function openFullscreen(img: ImageVM) {
  markImageActive(img)
  const hasRemoteFullscreenPreview = !!String((img.record as any)?.upload?.fullscreenPreviewUrl || '').trim()
  const hasLocalFullscreenPreview = !!fullPreviewBlob(img.record)
  ensureObjectUrlsForImage(img, {
    full: true,
    preferHeavy: !hasRemoteFullscreenPreview && !hasLocalFullscreenPreview,
  })
  if (!hasRemoteFullscreenPreview && !hasLocalFullscreenPreview) {
    await ensureWorkingBlobForImage(img)
  }
  fs.id = img.id
  fs.open = true
  closeItemMenu()
  closeAllTopMenus()

  queueMicrotask(async () => {
    await nextTick()
    await sleepRaf(2)
    if (!fsImg.value) return
    pendingApply.add(fsImg.value.id)
    fsImg.value.ui.appliedOnce = false
    await applyCropToCropper(fsImg.value, 'fs-open', fsCropperRef.value, true)
  })
}
function closeFullscreen() {
  const prev = fsImg.value
  fs.open = false
  fs.id = ''
  fsCropperRef.value = null
  void releaseHeavyResourcesForImage(prev)
}

/* Im großen Ausschnitt-Dialog zwischen den Bildern blättern – in der sichtbaren Reihenfolge
   (Gruppen wie im Raster, aktive Filter berücksichtigt). Ausschnitt wird vor dem Wechsel gespeichert. */
const fsOrder = computed<ImageVM[]>(() => viewImagesByCategory.value.flatMap((group: any) => (group.items || []).map((entry: any) => entry.img)).filter(Boolean))
const fsIndex = computed(() => fsOrder.value.findIndex((img) => img.id === fs.id))
const fsCanPrev = computed(() => fsIndex.value > 0)
const fsCanNext = computed(() => fsIndex.value >= 0 && fsIndex.value < fsOrder.value.length - 1)
let fsStepping = false
async function stepFullscreen(dir: 1 | -1) {
  if (fsStepping || !fs.open) return
  const next = fsOrder.value[fsIndex.value + dir]
  if (!next) return
  fsStepping = true
  try {
    const prev = fsImg.value
    if (prev?.crop) await persistCropNow(prev.id, prev.crop, prev.crop.rot ?? 0).catch(() => {})
    fsCropperRef.value = null
    await openFullscreen(next)
    if (prev && prev.id !== next.id) void releaseHeavyResourcesForImage(prev)
  } finally {
    fsStepping = false
  }
}
function onFullscreenKeydown(e: KeyboardEvent) {
  if (!fs.open) return
  const target = e.target as HTMLElement | null
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
  if (e.key === 'ArrowRight') { e.preventDefault(); void stepFullscreen(1) }
  else if (e.key === 'ArrowLeft') { e.preventDefault(); void stepFullscreen(-1) }
  else if (e.key === 'Escape') { e.preventDefault(); closeFullscreen() }
}
watch(() => fs.open, (open) => {
  if (open) window.addEventListener('keydown', onFullscreenKeydown)
  else window.removeEventListener('keydown', onFullscreenKeydown)
})
onBeforeUnmount(() => window.removeEventListener('keydown', onFullscreenKeydown))
function setMaxCropAndApply(img: ImageVM) {
  const rot = normRot(img.crop?.rot ?? 0)
  setMaxCrop(img, rot)
  img.ui.appliedOnce = false
  pendingApply.add(img.id)

  queueMicrotask(async () => {
    await applyCropToCropper(img, 'max-btn-grid', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) await applyCropToCropper(img, 'max-btn-fs', fsCropperRef.value, true)
  })

  if (img.crop) persistCropNow(img.id, img.crop, img.crop.rot ?? 0).catch(() => {})
}

/* ------------------------------------------------
 * Cart: add selection / add one
 * ------------------------------------------------ */
function toVariantNumber(v: any): number {
  if (v == null) return 0
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  const s = String(v).trim()
  if (!s) return 0
  const m = s.match(/(\d+)\s*$/)
  if (m) return Number(m[1]) || 0
  const n2 = Number(s)
  return Number.isFinite(n2) ? n2 : 0
}
function sizeVariantRef(size: any): string {
  return String(size?.variantId || size?.id || '').trim()
}


function cartCropForImage(img: ImageVM | undefined, preset: any) {
  const crop = img?.crop
  if (!crop) return preset?.crop ?? null
  const cropSource = {
    width: Math.max(1, Number(crop.sourceWidth || img?.ui.baseW || img?.ui.imgW || editorDimsForRecord(img?.record).width)),
    height: Math.max(1, Number(crop.sourceHeight || img?.ui.baseH || img?.ui.imgH || editorDimsForRecord(img?.record).height)),
  }
  const mainDims = mainDimsForRecord(img?.record)
  const normalized = cropToNormalized(crop, cropSource.width, cropSource.height)
  if (!normalized) return preset?.crop ?? null
  const width = Math.max(1, Math.round(normalized.nw * mainDims.width))
  const height = Math.max(1, Math.round(normalized.nh * mainDims.height))
  if (width <= 0 || height <= 0) return preset?.crop ?? null
  return {
    x: Math.max(0, Math.round(normalized.nx * mainDims.width)),
    y: Math.max(0, Math.round(normalized.ny * mainDims.height)),
    width,
    height,
    nx: normalized.nx,
    ny: normalized.ny,
    nw: normalized.nw,
    nh: normalized.nh,
    sourceWidth: mainDims.width,
    sourceHeight: mainDims.height,
  }
}

function cartPresetForImage(img: ImageVM | undefined, preset: any, size: any, options: any[], qty: number) {
  const crop = cartCropForImage(img, preset)
  const rotate = normRot(img?.crop?.rot ?? preset?.rotate ?? preset?.crop?.rot ?? 0)
  const persistedSize = persistedSizeValue(size)
  return {
    ...preset,
    ...(persistedSize ? { size: persistedSize, ...(persistedSize.id ? { sizeId: persistedSize.id } : {}) } : { size, sizeId: undefined }),
    options,
    quantity: qty,
    grayscale: !!(preset?.grayscale),
    crop,
    rotate,
  }
}
function cartSyncPayloadForImage(img: ImageVM) {
  const rec: any = img.record ?? {}
  const preset = rec?.edits?.preset ?? {}
  const qty = Math.max(1, Number(preset?.quantity ?? img.ui.quantity ?? 1))
  const options = (Array.isArray(img?.ui.options) ? img.ui.options : (preset?.options ?? [])) as any[]
  const inferredSize = resolveSizeFromVariantOptions(options)
  const s = sizeFor(img) || resolveSizeForPricing(img, inferredSize || preset?.size || null)
  const size = persistedSizeValue((s ? {
    id: String(s.id || '').trim() || undefined,
    title: s.title,
    widthMm: s.widthMm,
    heightMm: s.heightMm,
    variantId: sizeVariantRef(s) || undefined,
  } : null) ?? preset?.size ?? null)
  const productId = Number(rec?.productId ?? preset?.productId ?? props.productId ?? 0)
  const selectedVariantIds = Array.from(new Set([
    toVariantNumber(sizeVariantRef(size) || 0),
    ...options.map((o: any) => toVariantNumber(o?.variantId || 0)),
  ].filter((v) => v > 0)))
  const productTitle = recordProductTitle(rec) || productLabel.value
  const meta = [
    productTitle ? String(productTitle) : null,
    size?.title ? String(size.title) : null,
    ...options.map((o: any) => (o?.value ? String(o.value) : null)),
  ].filter(Boolean) as string[]

  return {
    imageId: img.id,
    assetId: String(rec?.assetId || '').trim() || undefined,
    ...(String(rec?.assetId || '').trim() ? { stockAssetId: String(rec.assetId).trim(), stockId: String(rec.assetId).trim() } : {}),
    productId,
    quantity: qty,
    grayscale: !!(preset?.grayscale),
    preset: cartPresetForImage(img, preset, size, options, qty),
    selectedVariantIds,
    title: img?.name ?? img.id,
    productTitle,
    thumbUrl: cartThumbForImage(img),
    meta,
  }
}
function bulkPriceRequestForImage(img: ImageVM) {
  const payload = cartSyncPayloadForImage(img)
  const preset = (payload as any)?.preset || {}
  const selectedSize = resolveSizeForPricing(img, preset?.size || resolveSizeFromVariantOptions(preset?.options || []) || null)
  const sizeEntityId = String(selectedSize?.id ?? '').trim()
  const widthMm = Number(selectedSize?.widthMm || 0) || undefined
  const heightMm = Number(selectedSize?.heightMm || 0) || undefined
  const size = selectedSize && (sizeEntityId || widthMm || heightMm)
    ? {
        ...(sizeEntityId ? { id: sizeEntityId } : {}),
        widthMm,
        heightMm,
      }
    : undefined
  const variants = (Array.isArray(preset?.options) ? preset.options : [])
    .map((o: any) => ({
      groupId: String(o?.groupId || '').trim(),
      optionId: String(o?.variantId || '').trim(),
    }))
    .filter((o: any) => o.groupId && o.optionId)
  if (!(Number(payload?.productId || 0) > 0)) return null
  if (variants.length && !size) return null
  if (!variants.length && !size) return null
  return {
    key: String(img.id || ''),
    productId: Number(payload.productId),
    categoryId: Number((img.record as any)?.categoryId ?? 0) || undefined,
    quantity: Math.max(1, Number(payload.quantity ?? 1)),
    ...(sizeEntityId ? { sizeId: sizeEntityId } : {}),
    size,
    variants,
  }
}
function bulkPriceRequestsForCurrentImages() {
  return images.value
    .map((img) => bulkPriceRequestForImage(img))
    .filter(Boolean)
}

function cartThumbForImage(img: ImageVM | undefined) {
  const rec: any = img?.record ?? {}
  return String(
    img?.ui?.previewUrl
    || rec?.upload?.thumbUrl
    || rec?.upload?.previewUrl
    || rec?.upload?.remoteUrl
    || ''
  ).trim() || undefined
}

function duplicateImageName(name: string, existingNames: string[]) {
  const base = String(name || '').trim() || 'Bild'
  const lowerExisting = new Set(existingNames.map((entry) => String(entry || '').trim().toLowerCase()).filter(Boolean))
  if (!lowerExisting.has(base.toLowerCase())) return base

  const match = base.match(/^(.*?)(?:\s+Kopie(?:\s+(\d+))?)?$/i)
  const root = String(match?.[1] || base).trim() || base
  let index = 2
  let candidate = `${root} Kopie`
  if (!lowerExisting.has(candidate.toLowerCase())) return candidate
  while (lowerExisting.has(`${root} Kopie ${index}`.toLowerCase())) index += 1
  return `${root} Kopie ${index}`
}

function clonePlainForDuplicate<T>(value: T): T {
  if (value == null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((entry) => clonePlainForDuplicate(entry)) as T
  const out: any = {}
  for (const key of Object.keys(value as any)) out[key] = clonePlainForDuplicate((value as any)[key])
  return out as T
}
function recordSnapshotForCart(img: ImageVM) {
  const rec: any = img.record ?? {}
  const preset = rec?.edits?.preset ?? {}
  const qty = Math.max(1, Number(preset?.quantity ?? img.ui.quantity ?? 1))
  const s = normalizedSizesForImg(img).find((x) => x.key === img.ui.sizeKey)
  const size = persistedSizeValue((s ? {
    id: String(s.id || '').trim() || undefined,
    title: s.title,
    widthMm: s.widthMm,
    heightMm: s.heightMm,
    variantId: sizeVariantRef(s) || undefined,
  } : null) ?? preset?.size ?? null)
  const options = (Array.isArray(img?.ui.options) ? img.ui.options : (preset?.options ?? [])) as any[]
  const productId = Number(rec?.productId ?? preset?.productId ?? props.productId ?? 0)
  const categoryId = Number(rec?.categoryId ?? preset?.categoryId ?? props.categoryId ?? 0)
  const productTitle = recordProductTitle(rec) || productLabel.value
  const categoryTitle = String(rec?.categoryTitle ?? preset?.categoryTitle ?? props.categoryLabel ?? '').trim()
  const rotate = normRot(img?.crop?.rot ?? rec?.edits?.rotate ?? preset?.rotate ?? 0)
  const persistedPreset = buildPresetForPersistence(img, cartPresetForImage(img, preset, size, options, qty))
  const liveUpload = String(img?.id || '').trim() ? (liveUploadState.value[String(img.id).trim()] || null) : null
  const cropSource = img?.crop
    ? {
        width: Math.max(
          1,
          Number(
            img.crop.sourceWidth
            || activeBox(img, rotate).W
            || img.ui.baseW
            || img.ui.imgW
            || editorDimsForRecord(img.record).width,
          ),
        ),
        height: Math.max(
          1,
          Number(
            img.crop.sourceHeight
            || activeBox(img, rotate).H
            || img.ui.baseH
            || img.ui.imgH
            || editorDimsForRecord(img.record).height,
          ),
        ),
      }
    : null

  return enrichCategoryMeta({
    ...rec,
    productId,
    ...(categoryId ? { categoryId } : {}),
    ...(productTitle ? { productTitle } : {}),
    ...(categoryTitle ? { categoryTitle } : {}),
    edits: {
      ...(rec?.edits || {}),
      lockAR: !!img.ui.lockAR,
      swapAR: !!img.ui.swapAR,
      orientMode: img.ui.orientMode === 'manual' ? 'manual' : 'auto',
      ...(img?.crop ? {
        crop: {
          x: img.crop.x,
          y: img.crop.y,
          w: img.crop.w,
          h: img.crop.h,
          ...(cropSource ? { sourceWidth: cropSource.width, sourceHeight: cropSource.height } : {}),
          ...(cropSource ? (cropToNormalized(img.crop, cropSource.width, cropSource.height) || {}) : {}),
        },
      } : {}),
      rotate,
      preset: {
        ...persistedPreset,
        productId,
        ...(productTitle ? { productTitle } : {}),
      },
    },
    upload: {
      ...((rec?.upload || {}) as any),
      ...(liveUpload ? {
        state: String((liveUpload as any)?.state || '').trim() || undefined,
        phase: String((liveUpload as any)?.phase || '').trim() || undefined,
        progressPct: Math.max(0, Math.min(100, Number((liveUpload as any)?.progressPct ?? 0) || 0)),
        uploadedBytes: Math.max(0, Number((liveUpload as any)?.uploadedBytes ?? 0) || 0),
        totalBytes: Math.max(0, Number((liveUpload as any)?.totalBytes ?? 0) || 0),
      } : {}),
    },
  } as any)
}

function mergeCartRecordUploadState(imageIdRaw: string, storedUpload: any, fallbackUpload: any) {
  const imageId = String(imageIdRaw || '').trim()
  const live = imageId ? liveUploadState.value[imageId] : null
  const stored = (storedUpload && typeof storedUpload === 'object') ? storedUpload : {}
  const fallback = (fallbackUpload && typeof fallbackUpload === 'object') ? fallbackUpload : {}
  const merged = {
    ...stored,
    ...fallback,
    ...(live ? {
      state: String((live as any)?.state || '').trim() || fallback?.state || stored?.state,
      phase: String((live as any)?.phase || '').trim() || fallback?.phase || stored?.phase,
      progressPct: Math.max(0, Math.min(100, Number((live as any)?.progressPct ?? fallback?.progressPct ?? stored?.progressPct ?? 0) || 0)),
      uploadedBytes: Math.max(0, Number((live as any)?.uploadedBytes ?? fallback?.uploadedBytes ?? stored?.uploadedBytes ?? 0) || 0),
      totalBytes: Math.max(0, Number((live as any)?.totalBytes ?? fallback?.totalBytes ?? stored?.totalBytes ?? 0) || 0),
    } : {}),
  } as any

  const fallbackReady = isRecordUploadEffectivelyDone({ upload: fallback } as any, live as any)
  const storedReady = isRecordUploadEffectivelyDone({ upload: stored } as any, live as any)
  if (fallbackReady && !storedReady) {
    return {
      ...stored,
      ...fallback,
      ...merged,
    }
  }

  return merged
}

async function normalizeDbRecordForCart(rec: any, sessionCode: string) {
  if (!rec || typeof rec !== 'object') return rec
  const preset: any = rec?.edits?.preset ?? {}
  const options = Array.isArray(preset?.options) ? preset.options : []
  const fallbackSize = preset?.size || resolveSizeFromVariantOptions(options) || null
  const resolvedSize = persistedSizeValue(fallbackSize)
  const upload = (rec?.upload && typeof rec.upload === 'object') ? { ...rec.upload } : {}
  const remoteUrl = String(upload?.remoteUrl || '').trim()
  const state = String(upload?.state || '').trim()
  const phase = String(upload?.phase || '').trim()
  if (remoteUrl && (state === 'done' || phase === 'done' || phase === 'completing')) {
    upload.state = 'done'
    if (!String(upload.phase || '').trim()) upload.phase = 'done'
  }
  const nextPreset = {
    ...preset,
    ...(resolvedSize ? { size: resolvedSize, sizeId: String(resolvedSize.id || '').trim() || undefined } : {}),
  }
  const nextRec = {
    ...rec,
    ...(sessionCode ? { projectCode: rec?.projectCode || sessionCode } : {}),
    upload,
    edits: {
      ...(rec?.edits || {}),
      preset: nextPreset,
    },
  }
  if (
    String(rec?.projectCode || '').trim() !== String(nextRec?.projectCode || '').trim()
    || JSON.stringify((rec?.upload || null)) !== JSON.stringify((nextRec?.upload || null))
    || JSON.stringify(preset || null) !== JSON.stringify(nextPreset || null)
  ) {
    await persistRecordUpdate(String(rec.id || ''), {
      ...(sessionCode ? { projectCode: nextRec.projectCode } : {}),
      upload: nextRec.upload,
      edits: nextRec.edits,
    } as any).catch(() => {})
  }
  return nextRec
}

async function resolveFreshCartRecord(imageIdRaw: string, fallback?: any): Promise<any | null> {
  const imageId = String(imageIdRaw || '').trim()
  if (!imageId) return fallback || null
  const stored = await getStoredRecord(imageId).catch(() => null)
  if (!stored) return fallback || null
  if (!fallback || typeof fallback !== 'object') return stored
  return {
    ...stored,
    ...fallback,
    edits: {
      ...((stored as any)?.edits || {}),
      ...((fallback as any)?.edits || {}),
    },
    upload: mergeCartRecordUploadState(imageId, (stored as any)?.upload || null, (fallback as any)?.upload || null),
  }
}

function cartHasProjectImage(imageIdRaw: string, projectCodeRaw?: string): boolean {
  const imageId = String(imageIdRaw || '').trim()
  if (!imageId) return false
  const projectCode = String(projectCodeRaw || '').trim()
  const items: any[] = ((cart as any)?.items ?? (cart as any)?.cart?.items ?? []) as any[]
  return items.some((it: any) => {
    const itemId = String(it?.imageId ?? it?.preset?.imageId ?? it?.meta?.imageId ?? '').trim()
    if (itemId !== imageId) return false
    if (String(it?.kind || it?.type || '') !== 'image') return false
    if (!projectCode) return true
    return String(it?.payload?.editorProjectId ?? '') === projectCode
  })
}

function countProjectImagesInCart(imageIdsRaw: string[], projectCodeRaw?: string): number {
  const ids = Array.from(new Set((imageIdsRaw || []).map((id) => String(id || '').trim()).filter(Boolean)))
  if (!ids.length) return 0
  const projectCode = String(projectCodeRaw || '').trim()
  return ids.reduce((count, id) => count + (cartHasProjectImage(id, projectCode) ? 1 : 0), 0)
}

async function isFreshImageCartReady(img: ImageVM | null | undefined): Promise<boolean> {
  if (!img) return false
  const freshRecord = await resolveFreshCartRecord(String(img.id || ''), recordSnapshotForCart(img) as any)
  return isRecordUploadDone(freshRecord)
}

async function syncImageInCart(img: ImageVM) {
  const freshRecord = await resolveFreshCartRecord(String(img.id || ''), recordSnapshotForCart(img) as any)
  if (!isRecordUploadDone(freshRecord)) return cart.cart
  await cart.syncImageRecord(freshRecord as any)
}

async function collectFreshCartRecordsForImages(sourceImages: ImageVM[], sessionCode: string) {
  if (!sourceImages.length) return [] as any[]

  const snapshots = new Map<string, any>()
  for (const img of sourceImages) {
    const id = String(img.id || '').trim()
    if (!id) continue
    snapshots.set(id, recordSnapshotForCart(img) as any)
  }

  const freshList = await Promise.all(
    sourceImages.map(async (img) => {
      const id = String(img.id || '').trim()
      if (!id) return null
      const freshRecord = await resolveFreshCartRecord(id, snapshots.get(id) || img.record)
      if (!freshRecord || !isRecordUploadDone(freshRecord)) return null
      return {
        ...freshRecord,
        projectCode: String((freshRecord as any)?.projectCode || sessionCode).trim() || sessionCode,
      }
    }),
  )

  return freshList.filter(Boolean) as any[]
}

async function collectProjectCartRecords(sessionCode: string, targetIds?: string[]) {
  const wantedIds = new Set((targetIds || []).map((id) => String(id || '').trim()).filter(Boolean))
  const storedRecords = await fetchSessionRecordsServerFirst(sessionCode).catch(() => [] as any[])
  const liveImageMap = new Map(
    images.value
      .map((img) => [String(img.id || '').trim(), img] as const)
      .filter(([id]) => !!id),
  )

  const recordMap = new Map<string, any>()
  for (const rec of storedRecords) {
    const id = String((rec as any)?.id || '').trim()
    if (!id) continue
    if (wantedIds.size && !wantedIds.has(id)) continue
    recordMap.set(id, rec)
  }
  for (const [id, img] of liveImageMap) {
    if (wantedIds.size && !wantedIds.has(id)) continue
    if (!recordMap.has(id)) recordMap.set(id, img.record ?? null)
  }

  const ids = [...recordMap.keys()]
  if (!ids.length) return [] as any[]

  await flushProjectMetaForCart(ids)

  const freshList = await Promise.all(
    ids.map(async (id) => {
      const liveImg = liveImageMap.get(id) || null
      const fallback = liveImg ? (recordSnapshotForCart(liveImg) as any) : recordMap.get(id)
      const freshRecord = await resolveFreshCartRecord(id, fallback)
      if (!freshRecord || !isRecordUploadDone(freshRecord)) return null
      return {
        ...freshRecord,
        projectCode: String((freshRecord as any)?.projectCode || sessionCode).trim() || sessionCode,
      }
    }),
  )

  return freshList.filter(Boolean) as any[]
}

async function syncProjectImagesInCart(targetIds?: string[], opts?: { mode?: 'sync' | 'upsert'; fullProject?: boolean }) {
  const sessionCode = project.projectCode.value || await project.ensureProject().catch(() => '')
  if (!sessionCode) return cart.cart
  const wantedIds = new Set((targetIds || []).map((id) => String(id || '').trim()).filter(Boolean))

  if (opts?.fullProject) {
    const fullRecords = await collectProjectCartRecords(sessionCode, wantedIds.size ? [...wantedIds] : undefined)
    if (!fullRecords.length) return cart.cart
    return cart.reconcileProjectImages(fullRecords as any[], sessionCode, { mode: opts?.mode || 'sync' })
  }

  const candidateImages = images.value
    .filter((img) => !wantedIds.size || wantedIds.has(String(img.id || '').trim()))
  if (!candidateImages.length) return cart.cart

  const readyImageIds = await Promise.all(
    candidateImages.map(async (img) => ((await isFreshImageCartReady(img)) ? String(img.id || '').trim() : '')),
  )
  const liveWantedIds = readyImageIds.filter(Boolean)
  const alreadyInCartIds = candidateImages
    .map((img) => String(img.id || '').trim())
    .filter((id) => !!id && cartHasProjectImage(id, sessionCode))
  if (!liveWantedIds.length && !alreadyInCartIds.length) return cart.cart

  await flushProjectMetaForCart(liveWantedIds)
  const readyImages = candidateImages.filter((img) => liveWantedIds.includes(String(img.id || '').trim()))
  const freshRecords = await collectFreshCartRecordsForImages(readyImages, sessionCode)
  const freshLookup = new Map<string, any>(
    freshRecords
      .map((rec) => [String((rec as any)?.id || '').trim(), rec] as const)
      .filter(([id]) => !!id),
  )

  const orderedRecords: any[] = []
  const pushOrdered = (idRaw: string) => {
    const id = String(idRaw || '').trim()
    if (!id) return
    const rec = freshLookup.get(id)
    if (!rec) return
    orderedRecords.push(rec)
    freshLookup.delete(id)
  }

  const sourceOrder = candidateImages.map((img) => String(img.id || '').trim()).filter(Boolean)
  for (const id of sourceOrder) {
    if (wantedIds.size && !wantedIds.has(id)) continue
    pushOrdered(id)
  }

  for (const id of liveWantedIds) {
    if (wantedIds.size && !wantedIds.has(id)) continue
    pushOrdered(id)
  }

  for (const [id] of freshLookup) {
    if (wantedIds.size && !wantedIds.has(id)) continue
    pushOrdered(id)
  }

  if (!orderedRecords.length) return cart.cart

  return cart.reconcileProjectImages(orderedRecords as any[], sessionCode, { mode: opts?.mode || 'sync' })
}
const cartQtyForImage = (imageId: string): number => {
  // ✅ Passe den Pfad an deinen cart-store an:
  const items: any[] = (cart as any)?.items ?? (cart as any)?.cart?.items ?? []

  // Fälle:
  // - items: [{ imageId, quantity }, ...]
  // - oder nested: { preset: { imageId }, quantity }
  let qty = 0
  for (const it of items) {
    const id =
        String(it?.imageId ?? it?.preset?.imageId ?? it?.meta?.imageId ?? '')

    if (id === String(imageId)) {
      qty += Number(it?.quantity ?? 1) || 1
    }
  }
  return qty
}

// Add-to-cart only accepts fully-uploaded images (collectProjectCartRecords drops
// anything that isn't isRecordUploadDone). Freshly imported images upload via a
// throttled background pipeline, so without waiting we'd silently add only the
// already-finished ones. This drives the uploader and waits until the wanted
// images are uploaded (or a deadline / unrecoverable failure is reached), then the
// caller's server-first reconcile picks up the full set.
const UPLOAD_WAIT_MAX_MS = 5 * 60_000
const UPLOAD_WAIT_POLL_MS = 600

function imageUploadReadyState(id: string): 'done' | 'failed' | 'pending' {
  const rec =
    (sessionRecordsState.value[id] as any) ||
    (transientUploads.get(id) as any) ||
    images.value.find((x) => x.id === id)?.record ||
    null
  const live = liveUploadState.value[id] || null
  if (isRecordUploadEffectivelyDone(rec, live)) return 'done'
  const state = String((live as any)?.state || rec?.upload?.state || '').trim()
  const phase = String((live as any)?.phase || rec?.upload?.phase || '').trim()
  const tries = Number(rec?.upload?.tries || 0)
  // Treat as terminal failure only once retries are exhausted, so we keep waiting
  // through transient retryable failures.
  if ((state === 'failed' || phase === 'failed') && tries >= 4) return 'failed'
  return 'pending'
}

async function waitForImageUploadsReady(
  idsRaw: string[],
  scope: 'selection' | 'all' = 'all',
  maxWaitMs = UPLOAD_WAIT_MAX_MS,
): Promise<{ done: number; failed: number; pending: number; total: number }> {
  const ids = idsRaw.map((id) => String(id || '').trim()).filter(Boolean)
  const total = ids.length
  if (!total) return { done: 0, failed: 0, pending: 0, total: 0 }

  // Make sure the uploader is actively draining the queue.
  try { getUploadsRunner()?.resume?.() } catch {}
  scheduleUploadsRunnerTick(0)

  const title = `${total === 1 ? 'Produkt' : 'Produkte'} werden in den Warenkorb gelegt`
  const deadline = Date.now() + Math.max(0, maxWaitMs)
  let snapshot = { done: 0, failed: 0, pending: total, total }

  for (;;) {
    let done = 0
    let failed = 0
    for (const id of ids) {
      const s = imageUploadReadyState(id)
      if (s === 'done') done++
      else if (s === 'failed') failed++
    }
    const pending = total - done - failed
    snapshot = { done, failed, pending, total }

    cartSyncModal.value = {
      title,
      body:
        scope === 'selection'
          ? `Ausgewählte Bilder werden hochgeladen … ${done}/${total} fertig.`
          : `Bilder werden hochgeladen … ${done}/${total} fertig.`,
    }

    if (pending <= 0) break
    if (Date.now() >= deadline) break

    scheduleUploadsRunnerTick(0)
    await new Promise((resolve) => setTimeout(resolve, UPLOAD_WAIT_POLL_MS))
  }

  return snapshot
}

async function addSelectionToCart() {
  const ids = [...selectedIds.value]
  if (!ids.length) return

  try {
    openCartSyncModal('selection', ids.length)
    await waitForImageUploadsReady(ids, 'selection')
    await syncProjectImagesInCart(ids, { mode: 'upsert' })
    const sessionCode = project.projectCode.value || await project.ensureProject().catch(() => '')
    const inCartCount = countProjectImagesInCart(ids, sessionCode)
    if (!inCartCount) {
      showCartUploadInfo('selection')
      return
    }
    const skipped = Math.max(0, ids.length - inCartCount)
    if (skipped > 0) {
      toast.success(`${inCartCount} Bild(er) im Warenkorb, ${skipped} noch nicht fertig`)
    } else {
      toast.success(`${inCartCount} Bild(er) im Warenkorb`)
    }
    cartOpen.value = true
  } catch (e: any) {
    toast.error(e?.message ?? 'Konnte nicht hinzufügen')
  } finally {
    closeCartSyncModal()
  }
}

async function addAllToCart() {
  if (!images.value.length) return

  try {
    const allIds = images.value.map((img) => String(img.id || '').trim()).filter(Boolean)
    openCartSyncModal('all', allIds.length)
    await waitForImageUploadsReady(allIds, 'all')
    // Add-to-cart must be ADDITIVE. Using mode:'sync' here makes the backend set
    // the project's cart to EXACTLY the freshly-collected list, so any already-in-
    // cart image that fails to perfectly re-resolve on this run (e.g. after leaving
    // and re-entering the editor: missing remote-asset/done flag, or a preset/size/
    // variantId not yet rebuilt because the catalog hasn't reloaded) gets silently
    // WIPED from the cart. That is the "re-enter editor, add 1 more → only that one
    // is in the cart" bug. Removal of images is handled by the delete paths
    // (removeOneCore/deleteSelected, which intentionally use sync), so add-all only
    // ever needs to upsert — it must never drop existing cart items.
    await syncProjectImagesInCart(undefined, { mode: 'upsert', fullProject: true })
    const sessionCode = project.projectCode.value || await project.ensureProject().catch(() => '')
    const inCartCount = countProjectImagesInCart(allIds, sessionCode)
    if (!inCartCount) {
      showCartUploadInfo('all')
      return
    }
    const skipped = Math.max(0, allIds.length - inCartCount)
    if (skipped > 0) {
      toast.success(`${inCartCount} Bild(er) im Warenkorb, ${skipped} noch nicht fertig`)
    } else {
      toast.success(`${inCartCount} Bild(er) im Warenkorb aktualisiert`)
    }
    cartOpen.value = true
  } catch (e: any) {
    toast.error(e?.message ?? 'Konnte nicht hinzufügen')
  } finally {
    closeCartSyncModal()
  }
}

async function addOneToCart(imageId: string) {
  try {
    const img = images.value.find((x) => x.id === imageId)
    if (!img) throw new Error('Bild nicht gefunden')
    openCartSyncModal('single', 1)
    await syncImageInCart(img)
    const sessionCode = project.projectCode.value || await project.ensureProject().catch(() => '')
    if (!cartHasProjectImage(imageId, sessionCode)) {
      showCartUploadInfo('single')
      return
    }
    toast.success('In den Warenkorb gelegt')
    cartOpen.value = true
  } catch (e: any) {
    toast.error(e?.message ?? 'Konnte nicht in den Warenkorb legen')
  } finally {
    closeCartSyncModal()
  }
}

/* ------------------------------------------------
 * Sofort-Vorschau aus der Sidebar (preview-change)
 * ------------------------------------------------ */
function previewPreset(ids: string[], preset: any) {
  for (const id of ids) {
    const img = images.value.find((i) => i.id === id)
    if (!img || img.ui.locked) continue

    const prevEdits = (((img.record as any).edits || {}) as any)
    const prevPreset = ((prevEdits.preset || {}) as any)
    const prevFitInMode = isFitInMode(img)
    let nextPreset: any = { ...prevPreset }

    if (preset.categoryId != null) {
      const cid = Number(preset.categoryId || 0) || null
      ;(img.record as any).categoryId = cid
      nextPreset.categoryId = cid
    }
    if (preset.parentCategoryId !== undefined) {
      const parentId = Number(preset.parentCategoryId || 0) || null
      ;(img.record as any).parentCategoryId = parentId
      nextPreset.parentCategoryId = parentId
    }
    if (preset.categoryTitle !== undefined) {
      const title = String(preset.categoryTitle || '').trim()
      ;(img.record as any).categoryTitle = title
      nextPreset.categoryTitle = title
    }
    if (preset.productId) {
      const pid = Number(preset.productId)
      const prevPid = Number((img.record as any).productId || 0)
      ;(img.record as any).productId = pid
      nextPreset.productId = pid
      if (preset.productTitle !== undefined || prevPid !== pid) {
        const productTitle = productTitleForSwitch(prevPid, pid, preset.productTitle, (img.record as any).productTitle)
        ;(img.record as any).productTitle = productTitle
        nextPreset.productTitle = productTitle
      }
      const afterGroups = () => {
        const sizes = normalizedSizesForImg(img)
        if (sizes.length && !sizes.find((s) => s.key === img.ui.sizeKey)) {
          img.ui.sizeKey = sizes[0]?.key || ''
          img.ui.sizeTitleHint = sizes[0]?.title || ''
        }
      }
      if (!productVariantGroupsMap.value.has(pid)) {
        loadVariantGroupsForProduct(pid).then(afterGroups).catch(() => {})
      } else {
        afterGroups()
      }
    } else if (preset.productId === null) {
      ;(img.record as any).productId = null
      ;(img.record as any).productTitle = ''
      nextPreset.productId = null
      nextPreset.productTitle = ''
      img.ui.sizeKey = ''
      img.ui.sizeTitleHint = ''
    }

    const presetSize = preset?.size ?? null
    if (preset.options !== undefined) nextPreset.options = Array.isArray(preset.options) ? preset.options : []
    if (preset.quantity != null) nextPreset.quantity = Math.max(1, Number(preset.quantity))
    if (presetSize !== undefined) {
      nextPreset.size = presetSize
      nextPreset.sizeId = String((presetSize as any)?.id || '').trim() || undefined
    }

    ;(img.record as any).edits = {
      ...prevEdits,
      preset: nextPreset,
    }

    if (preset.options !== undefined) {
      img.ui.options = Array.isArray(nextPreset.options)
        ? (nextPreset.options as any[])
            .map((o: any) => ({
              groupId: normId(o?.groupId ?? o?.id ?? ''),
              variantId: normId(o?.variantId ?? ''),
              value: String(o?.value ?? ''),
            }))
            .filter((o: any) => !!o.groupId && !!o.value)
        : []
    }

    const effectivePresetSize = nextPreset?.size ?? null
    if (effectivePresetSize || !String(img.ui.sizeKey || '').trim()) {
      const resolvedSize = resolveSizeUiState(
        normalizedSizesForImg(img),
        effectivePresetSize ?? (preset.sizeTitle ? { title: preset.sizeTitle } : null),
        img.ui.sizeKey,
        img.ui.sizeTitleHint || String(preset?.sizeTitle || ''),
        false,
      )
      if (resolvedSize.sizeKey) img.ui.sizeKey = resolvedSize.sizeKey
      if (resolvedSize.sizeTitleHint) img.ui.sizeTitleHint = resolvedSize.sizeTitleHint
    }

    const liveResolvedSize =
      persistedSizeValue(
        sizeFor(img)
        || resolveSizeForPricing(img, effectivePresetSize ?? (preset.sizeTitle ? { title: preset.sizeTitle } : null))
        || effectivePresetSize,
      ) ?? effectivePresetSize

    if (preset.sizeTitle || presetSize) {
      img.ui.lockAR = true
      img.ui.orientMode = 'auto'
      img.ui.swapAR = false
      const nextStoredCrop = buildMaxCropForRecord(img.record as any, liveResolvedSize)
      if (nextStoredCrop) {
        ;(img.record as any).edits = {
          ...(((img.record as any).edits || {}) as any),
          crop: nextStoredCrop,
          lockAR: true,
          orientMode: 'auto',
          swapAR: false,
        }
        const dims = editorDimsForRecord(img.record as any)
        img.crop = cropFromStored(img.record as any, { width: dims.width, height: dims.height })
      } else {
        setMaxCrop(img, normRot(img.crop?.rot ?? 0))
      }
      img.ui.appliedOnce = false
      pendingApply.add(img.id)
      queueMicrotask(async () => {
        await applyCropToCropper(img, 'size-change-grid', cropperRefs.value.get(img.id), false)
        if (fs.open && fs.id === img.id) {
          await applyCropToCropper(img, 'size-change-fs', fsCropperRef.value, true)
        }
      })
    }

    if (preset.options !== undefined) {
      const nextFitInMode = isFitInMode(img)
      if (prevFitInMode !== nextFitInMode) {
        if (!nextFitInMode) {
          const nextStoredCrop = buildMaxCropForRecord(img.record as any, liveResolvedSize)
          if (nextStoredCrop) {
            ;(img.record as any).edits = {
              ...(((img.record as any).edits || {}) as any),
              crop: nextStoredCrop,
              lockAR: true,
              orientMode: 'auto',
              swapAR: false,
            }
            const dims = editorDimsForRecord(img.record as any)
            img.crop = cropFromStored(img.record as any, { width: dims.width, height: dims.height })
            img.ui.appliedOnce = false
            pendingApply.add(img.id)
            queueMicrotask(async () => {
              await applyCropToCropper(img, 'crop-mode-fillin-grid', cropperRefs.value.get(img.id), false)
              if (fs.open && fs.id === img.id) {
                await applyCropToCropper(img, 'crop-mode-fillin-fs', fsCropperRef.value, true)
              }
            })
          } else {
            setMaxCrop(img, normRot(img.crop?.rot ?? 0))
          }
        }
        syncCropMode(img, { persist: false, apply: true })
      }
    }

    if (preset.quantity != null) {
      img.ui.quantity = Math.max(1, Number(preset.quantity))
    }
  }
}

/* ------------------------------------------------
 * Rotate / flip AR
 * ------------------------------------------------ */
function rotate90(img: ImageVM, delta: number) {
  if (isLocked(img)) return
  if (isFitInMode(img)) return
  const rot = normRot(img.crop?.rot ?? 0)
  if (img.ui.lockAR) {
    // Bei fixem AR: Ausrichtung wirklich umdrehen (W/H tauschen) über swapAR
    const currentSwap = effectiveSwapState(img, rot)
    img.ui.orientMode = 'manual'
    img.ui.swapAR = !currentSwap
    setMaxCrop(img, rot)
    patchEdits(img.id, { swapAR: img.ui.swapAR, orientMode: img.ui.orientMode, lockAR: img.ui.lockAR })
  } else {
    if (!img.crop) setMaxCrop(img, rot)
    if (!img.crop) return

    // Ohne AR-Lock: nur das Crop-Rechteck drehen (w/h swap), Bild bleibt unverändert
    const turned = rotateSelectionRect90(img.crop)
    const { W: boxW, H: boxH } = activeBox(img, rot)
    const clamped = clampCoordsToBox(
        { left: turned.x, top: turned.y, width: turned.w, height: turned.h },
        boxW,
        boxH,
    )
    img.crop = {
      x: clamped.left,
      y: clamped.top,
      w: clamped.width,
      h: clamped.height,
      rot,
      sourceWidth: boxW,
      sourceHeight: boxH,
      ...((cropToNormalized({
        x: clamped.left,
        y: clamped.top,
        w: clamped.width,
        h: clamped.height,
      }, boxW, boxH)) || {}),
    }
  }

  img.ui.appliedOnce = false
  pendingApply.add(img.id)

  queueMicrotask(async () => {
    await applyCropToCropper(img, 'rotate90-grid', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) await applyCropToCropper(img, 'rotate90-fs', fsCropperRef.value, true)
  })

  if (img.crop) persistCropNow(img.id, img.crop, rot).catch(() => {})
}
function flipAR(img: ImageVM) {
  if (isLocked(img)) return
  if (isFitInMode(img)) return
  if (!img.ui.lockAR) return
  const rot = normRot(img.crop?.rot ?? 0)
  const currentSwap = effectiveSwapState(img, rot)
  img.ui.orientMode = 'manual'
  img.ui.swapAR = !currentSwap
  setMaxCrop(img, rot)

  img.ui.appliedOnce = false
  pendingApply.add(img.id)

  queueMicrotask(async () => {
    await applyCropToCropper(img, 'flip-ar-grid', cropperRefs.value.get(img.id), false)
    if (fs.open && fs.id === img.id) await applyCropToCropper(img, 'flip-ar-fs', fsCropperRef.value, true)
  })

  patchEdits(img.id, { swapAR: img.ui.swapAR, orientMode: img.ui.orientMode, lockAR: img.ui.lockAR })
  if (img.crop) persistCropNow(img.id, img.crop, img.crop.rot ?? 0).catch(() => {})
}

/* ------------------------------------------------
 * Load / hydrate
 * ------------------------------------------------ */
let loadImagesRunning = false
let loadImagesQueued = false
const editorInitialLoading = ref(false)

watch(editorInitialLoading, (loading) => {
  emit('initial-loading', !!loading)
}, { immediate: true })

async function loadImages() {
  if (loadImagesRunning) {
    loadImagesQueued = true
    return
  }
  loadImagesRunning = true
  try {
  const existingById = new Map(images.value.map((img) => [String(img.id), img] as const))
  const wantedPreselectIds = (props.preselectIds ?? []).filter(Boolean)
  if (wantedPreselectIds.length) {
    selectedIds.value = wantedPreselectIds
  } else {
    clearSelection()
  }
  editorInitialLoading.value = images.value.length === 0

  // Load all images for this editor session.
  // Primary: by projectCode (all categories). Fallback: all records when no session exists yet.
  project.loadFromStorage()
  const preferredProjectCode = String(Array.isArray(route.query.projectCode) ? route.query.projectCode[0] : (route.query.projectCode || '')).trim()
  const sessionCode = String(await project.ensureProject(preferredProjectCode || undefined).catch(() => '') || project.projectCode.value || '').trim()
  let records: ImageRecord[]
  if (sessionCode) {
    records = await fetchSessionRecordsServerFirst(sessionCode)
  } else {
    records = transientUploads.all
  }
  const hydrationPriorityIds = new Set<number>([
    ...((props.filterCategoryIds || []).map((x) => Number(x || 0)).filter((x) => x > 0)),
    ...((props.priorityCategoryIds || []).map((x) => Number(x || 0)).filter((x) => x > 0)),
  ])
  const hydrationRecords = [...records].sort((a, b) => {
    const aCategoryId = Number((a as any)?.categoryId ?? 0)
    const bCategoryId = Number((b as any)?.categoryId ?? 0)
    const aRootId = resolveRootCategoryIdForImageRecord(a as any)
    const bRootId = resolveRootCategoryIdForImageRecord(b as any)
    const aPriority = hydrationPriorityIds.has(aCategoryId) || hydrationPriorityIds.has(aRootId) ? 0 : 1
    const bPriority = hydrationPriorityIds.has(bCategoryId) || hydrationPriorityIds.has(bRootId) ? 0 : 1
    if (aPriority !== bPriority) return aPriority - bPriority
    if (sortBy.value === 'name') return String((a as any)?.name || '').localeCompare(String((b as any)?.name || ''), 'de')
    if (sortBy.value === 'oldest') return compareImageOrder(a as any, b as any, 'asc')
    return compareImageOrder(a as any, b as any, 'desc')
  })

  const hydrated: ImageVM[] = []
  const FIRST_PAINT_BATCH = 18
  const FOLLOW_UP_BATCH = 24
  let lastCommittedCount = 0
  const commitHydratedProgress = async (force = false) => {
    if (!hydrated.length) return
    const shouldCommit =
      force ||
      (hydrated.length >= FIRST_PAINT_BATCH && lastCommittedCount < FIRST_PAINT_BATCH) ||
      hydrated.length - lastCommittedCount >= FOLLOW_UP_BATCH
    if (!shouldCommit) return
    lastCommittedCount = hydrated.length
    images.value = hydrated.slice()
    if (images.value.length > 0) editorInitialLoading.value = false
    scheduleHeavyTileSync()
    if (!canvasPrototypeId.value || !hydrated.some((x) => x.id === canvasPrototypeId.value)) {
      canvasPrototypeId.value = hydrated[0]?.id || null
    }
    await nextTick()
    await sleepRaf(1)
  }
  for (const rec of hydrationRecords) {
    const blob = fullPreviewBlob(rec) || tilePreviewBlob(rec)
    const remoteUrl = remoteFullUrl(rec)
    if (!blob && !remoteUrl) continue
    const name = rec.name || rec.id
    const dims = editorDimsForRecord(rec)
    let crop: PxCrop | null = cropFromStored(rec, dims)
    const presetSize: any = rec.edits?.preset?.size ?? null
    if (presetSize && isClearlyInvalidTinyCrop(crop)) {
      crop = buildMaxCropForRecord(rec as any, presetSize) || crop
    }
    const preset = rec.edits?.preset
    const presetVariantId = presetSize?.variantId ? String(presetSize.variantId) : ''
    const presetTitle = String(presetSize?.title || '')

    const lock = computeLock(rec)

    const options: UIOption[] = Array.isArray(preset?.options)
        ? (preset!.options as any[])
            .map((o: any) => ({
              groupId: normId(o?.groupId ?? o?.id ?? ''),
              variantId: normId(o?.variantId ?? ''),
              value: String(o?.value ?? ''),
            }))
            .filter((o) => !!o.groupId && !!o.value)
        : []

    const swapAR = rec.edits?.swapAR != null ? !!rec.edits.swapAR : false
    const orientMode = rec.edits?.orientMode === 'manual' ? 'manual' : 'auto'
    const lockAR = rec.edits?.lockAR != null ? !!rec.edits.lockAR : true

    const existing = existingById.get(String(rec.id))
    const vm: ImageVM = existing ?? {
      id: rec.id,
      name,
      createdAt: rec.createdAt ?? Date.now(),
      record: rec,
      crop,
      ui: {
        previewUrl: '',
        fullPreviewUrl: '',
        previewLoading: false,
        imgW: dims.width,
        imgH: dims.height,
        baseW: dims.width,
        baseH: dims.height,
        ready: false,
        applying: false,
        armedGrid: false,
        armedFs: false,
        lockAR,
        swapAR,
        orientMode,
        appliedOnce: false,
        quantity: Math.max(1, Number((preset as any)?.quantity ?? 1)),
        sizeKey: '',
        sizeTitleHint: '',
        options,
        locked: lock.locked,
        lockedProductId: lock.pid,
        lockedProductTitle: lock.title,
        rev: 0,
      },
    }

    vm.name = name
    vm.createdAt = rec.createdAt ?? vm.createdAt ?? Date.now()
    vm.record = rec
    vm.crop = crop
    vm.ui.imgW = dims.width
    vm.ui.imgH = dims.height
    vm.ui.baseW = dims.width
    vm.ui.baseH = dims.height
    vm.ui.lockAR = lockAR
    vm.ui.swapAR = swapAR
    vm.ui.orientMode = orientMode
    vm.ui.quantity = Math.max(1, Number((preset as any)?.quantity ?? 1))
    vm.ui.options = options
    vm.ui.locked = lock.locked
    vm.ui.lockedProductId = lock.pid
    vm.ui.lockedProductTitle = lock.title

    const inferredPresetSize = presetSize || resolveSizeFromVariantOptions(options) || null
    const inferredPresetVariantId = inferredPresetSize?.variantId ? String(inferredPresetSize.variantId) : ''
    const inferredPresetTitle = String(inferredPresetSize?.title || presetTitle || '')
    const resolvedSize = resolveSizeUiState(
      normalizedSizesForImg(vm),
      inferredPresetSize,
      inferredPresetVariantId || presetVariantId || inferredPresetTitle || presetTitle || '',
      inferredPresetTitle || presetTitle,
      lock.locked,
    )
    vm.ui.sizeKey = resolvedSize.sizeKey
    vm.ui.sizeTitleHint = resolvedSize.sizeTitleHint


    // Produkt-Titel robust in record/preset spiegeln (wichtig wenn aus Modal/Pool kommt)
    if (!(vm.record as any).productTitle && (vm.record as any)?.edits?.preset?.productTitle) {
      ;(vm.record as any).productTitle = String((vm.record as any).edits.preset.productTitle)
    }
    if (!vm.crop) setMaxCrop(vm, 0)
    syncCropMode(vm, { persist: false, apply: false })
    ensureObjectUrlsForImage(vm)
    hydrated.push(vm)
    await commitHydratedProgress(false)
  }

  const hydratedIds = new Set(hydrated.map((img) => String(img.id)))
  const preservedTail: ImageVM[] = []
  for (const old of images.value) {
    const oldId = String(old.id)
    if (!hydratedIds.has(oldId) && (pendingVmInsertIds.has(oldId) || transientUploads.get(oldId))) {
      preservedTail.push(old)
      continue
    }
  }

  const nextImages = [...hydrated]
  for (const preserved of preservedTail) {
    if (!nextImages.some((img) => String(img.id) === String(preserved.id))) {
      nextImages.push(preserved)
    }
  }

  const nextIds = new Set(nextImages.map((img) => String(img.id)))
  for (const old of images.value) {
    if (nextIds.has(String(old.id))) continue
    if (old?.ui?.previewUrl) URL.revokeObjectURL(old.ui.previewUrl)
    if ((old as any)?.ui?.fullPreviewUrl) URL.revokeObjectURL((old as any).ui.fullPreviewUrl)
  }

  await commitHydratedProgress(true)
  images.value = nextImages
  if (images.value.length > 0) editorInitialLoading.value = false
  scheduleHeavyTileSync()
  if (!canvasPrototypeId.value || !nextImages.some((x) => x.id === canvasPrototypeId.value)) {
    canvasPrototypeId.value = nextImages[0]?.id || null
  }

  // Größenauswahl sicherstellen, falls sizes schon geladen waren als images noch fehlten
  reconcileUiForNewProduct()
  const pre = new Set((props.preselectIds ?? []).filter(Boolean))
  if (pre.size) {
    selectedIds.value = hydrated.map((x) => x.id).filter((id) => pre.has(id))
    if (selectedIds.value.length) {
      emitSelection()
      scheduleRevealPreselectedImage(images.value.length ? 'smooth' : 'auto')
    }
  }

  await nextTick()
  if (selectedIds.value.length) scheduleRevealPreselectedImage(images.value.length > 24 ? 'auto' : 'smooth')
  requestAnimationFrame(() => {
    for (const img of images.value) {
      img.ui.appliedOnce = false
      img.ui.armedGrid = false
      img.ui.armedFs = false
      pendingApply.add(img.id)
    }
  })

  } finally {
    editorInitialLoading.value = false
    loadImagesRunning = false
    if (loadImagesQueued) {
      loadImagesQueued = false
      queueMicrotask(() => {
        loadImages().catch(() => {})
      })
    }
  }
}

async function countImagesInCurrentView(): Promise<number> {
  project.loadFromStorage()
  const sessionCode = project.projectCode.value
  let records: ImageRecord[]
  if (sessionCode) {
    records = await fetchSessionRecordsServerFirst(sessionCode)
  } else {
    records = transientUploads.all
  }

  if (props.filterCategoryIds?.length) {
    const allowed = new Set(props.filterCategoryIds.map((x) => Number(x)))
    records = records.filter((r: any) => {
      const cid = Number(r?.categoryId ?? 0)
      const rootId = Number(r?.parentCategoryId ?? cid ?? 0)
      return allowed.has(cid) || allowed.has(rootId)
    })
  }

  return records.length
}


async function refreshImagesByIds(ids: string[]) {
  const idSet = new Set((ids || []).filter(Boolean))
  if (!idSet.size) return
  const openedGroupKeys = new Set<string>()
  const insertedVms: ImageVM[] = []
  const insertedIds: string[] = []

  project.loadFromStorage()
  const sessionCode = project.projectCode.value
  const sessionRecordMap = sessionCode
    ? new Map((await fetchSessionRecordsServerFirst(sessionCode)).map((rec) => [String(rec.id), rec] as const))
    : null

  for (const id of idSet) {
    const transientRec = transientUploads.get(id)
    const persistedRec = sessionRecordMap?.get(String(id)) || await db.get(id)
    const rec = mergePersistedRecordWithTransientPreview((persistedRec || null) as any, (transientRec || null) as any)
    if (!rec) continue
    if (!remoteTileUrl(rec) && !tilePreviewBlob(rec) && !fullPreviewBlob(rec) && !heavyPreviewBlob(rec)) continue

    const idx = images.value.findIndex((x) => x.id === id)
    if (idx < 0) {
      const vm = await hydrateVmFromRecord(rec)
      const groupCategoryId = Number((rec as any)?.categoryId ?? 0)
      if (groupCategoryId > 0) openedGroupKeys.add(String(groupCategoryId))
      insertedVms.push(vm)
      insertedIds.push(vm.id)
      continue
    }

    const vm = images.value[idx]
    const prevCrop = vm.crop
    const prevFitInMode = isFitInMode(vm)
    const prevPreset: any = ((vm.record as any)?.edits?.preset || {}) as any
    const nextPreset: any = ((rec as any)?.edits?.preset || {}) as any
    const prevSizeId = String(prevPreset?.size?.id || '')
    const nextSizeId = String(nextPreset?.size?.id || '')
    const prevSizeTitle = String(prevPreset?.size?.title || '')
    const nextSizeTitle = String(nextPreset?.size?.title || '')
    const prevWidthMm = Math.round(Number(prevPreset?.size?.widthMm ?? 0) || 0)
    const prevHeightMm = Math.round(Number(prevPreset?.size?.heightMm ?? 0) || 0)
    const nextWidthMm = Math.round(Number(nextPreset?.size?.widthMm ?? 0) || 0)
    const nextHeightMm = Math.round(Number(nextPreset?.size?.heightMm ?? 0) || 0)
    const sizeChanged =
      (prevSizeId && nextSizeId ? prevSizeId !== nextSizeId : false) ||
      prevSizeTitle !== nextSizeTitle ||
      prevWidthMm !== nextWidthMm ||
      prevHeightMm !== nextHeightMm

    // Preview URL nur erneuern, wenn sich der Blob wirklich geändert hat
    const oldBlob: any = fullPreviewBlob(vm.record)
    const newBlob: any = fullPreviewBlob(rec)
    if (newBlob && (!oldBlob || newBlob !== oldBlob)) {
      if (vm?.ui?.previewUrl) URL.revokeObjectURL(vm.ui.previewUrl)
      if (vm?.ui?.fullPreviewUrl) URL.revokeObjectURL(vm.ui.fullPreviewUrl)
      vm.ui.previewUrl = objectUrlForBlob(tilePreviewBlob(rec))
      vm.ui.fullPreviewUrl = objectUrlForBlob(newBlob)
      vm.ui.previewLoading = false
      const blobDims = rec.width && rec.height
        ? { width: Number(rec.width), height: Number(rec.height) }
        : await readImageDimensions(newBlob)
      const dims = resolvedEditorDimsForRecord(rec, blobDims)
      vm.ui.imgW = dims.width
      vm.ui.imgH = dims.height
    } else {
      const oldThumbBlob: any = tilePreviewBlob(vm.record)
      const newThumbBlob: any = tilePreviewBlob(rec)
      if (newThumbBlob && oldThumbBlob && newThumbBlob !== oldThumbBlob) {
        if (vm?.ui?.previewUrl) URL.revokeObjectURL(vm.ui.previewUrl)
        vm.ui.previewUrl = objectUrlForBlob(newThumbBlob)
        vm.ui.previewLoading = false
      }
    }

    // Fertig-Status der Kachel beim Teil-Reload nicht zurückstufen (Server meldet
    // bis zum Ende der Nachverarbeitung noch 'processing'/'uploaded').
    const nextUploadState = String((rec as any)?.upload?.state || '').trim()
    if (
      nextUploadState !== 'failed'
      && isRecordUploadEffectivelyDone(vm.record, null)
      && !isRecordUploadEffectivelyDone(rec, null)
    ) {
      const prevUpload = (((vm.record as any)?.upload || {}) as any)
      ;(rec as any).upload = {
        ...((((rec as any)?.upload || {}) as any)),
        state: 'done',
        phase: 'done',
        progressPct: 100,
        editorReadyAt: Number(prevUpload.editorReadyAt || 0) || Date.now(),
        ...(!(rec as any)?.upload?.remoteUrl && prevUpload.remoteUrl ? { remoteUrl: prevUpload.remoteUrl } : {}),
      }
    }
    vm.record = rec
    ensureObjectUrlsForImage(vm)
    ensureObjectUrlsForImage(vm, { full: true, preferHeavy: true })
    applyRemotePreviewToVm(vm)
    const resolvedDims = resolvedEditorDimsForRecord(rec)
    vm.ui.imgW = Math.max(1, Number(resolvedDims.width || 1))
    vm.ui.imgH = Math.max(1, Number(resolvedDims.height || 1))
    vm.ui.baseW = Math.max(1, Number(resolvedDims.width || 1))
    vm.ui.baseH = Math.max(1, Number(resolvedDims.height || 1))
    const groupCategoryId = Number((rec as any)?.categoryId ?? 0)
    if (groupCategoryId > 0) openedGroupKeys.add(String(groupCategoryId))

    const lock = computeLock(rec)
    vm.ui.locked = lock.locked
    vm.ui.lockedProductId = lock.pid
    vm.ui.lockedProductTitle = lock.title

    // UI flags
    vm.ui.swapAR = rec.edits?.swapAR != null ? !!rec.edits.swapAR : vm.ui.swapAR
    vm.ui.orientMode = rec.edits?.orientMode === 'manual' ? 'manual' : 'auto'
    vm.ui.lockAR = rec.edits?.lockAR != null ? !!rec.edits.lockAR : vm.ui.lockAR

    const preset: any = rec.edits?.preset ?? {}
    vm.ui.quantity = Math.max(1, Number(preset?.quantity ?? 1))

    const nextUiOptions: UIOption[] = Array.isArray(preset?.options)
      ? (preset.options as any[])
          .map((o: any) => ({
            groupId: normId(o?.groupId ?? o?.id ?? ''),
            variantId: normId(o?.variantId ?? ''),
            value: String(o?.value ?? ''),
          }))
          .filter((o: any) => !!o.groupId && !!o.value)
      : []

    // Size / Options
    const presetSize: any = preset?.size ?? null
    const inferredPresetSize = presetSize || resolveSizeFromVariantOptions(nextUiOptions) || null
    const presetVariantId = inferredPresetSize?.variantId ? String(inferredPresetSize.variantId) : ''
    const presetTitle = String(inferredPresetSize?.title || presetSize?.title || '')

    if (vm.ui.locked) {
      vm.ui.sizeKey = presetVariantId || presetTitle || vm.ui.sizeKey
      vm.ui.sizeTitleHint = presetTitle || vm.ui.sizeTitleHint
    } else {
      const resolvedSize = resolveSizeUiState(
        normalizedSizesForImg(vm),
        inferredPresetSize,
        presetVariantId || presetTitle || '',
        presetTitle,
        false,
      )
      vm.ui.sizeKey = resolvedSize.sizeKey
      vm.ui.sizeTitleHint = resolvedSize.sizeTitleHint
    }

    vm.ui.options = nextUiOptions

    if (rec.edits?.crop) {
      vm.crop = cropFromStored(rec, resolvedDims)
    } else if (sizeChanged && presetSize) {
      setMaxCrop(vm, normRot((rec.edits as any)?.rotate ?? vm.crop?.rot ?? 0))
    }

    syncCropMode(vm, { persist: false, apply: false })
    const nextFitInMode = isFitInMode(vm)

    const cropChanged =
      !prevCrop ||
      !vm.crop ||
      prevCrop.x !== vm.crop.x ||
      prevCrop.y !== vm.crop.y ||
      prevCrop.w !== vm.crop.w ||
      prevCrop.h !== vm.crop.h ||
      normRot(prevCrop.rot ?? 0) !== normRot(vm.crop.rot ?? 0)

    if (cropChanged || prevFitInMode !== nextFitInMode || (newBlob && oldBlob && newBlob !== oldBlob)) {
      vm.ui.rev += 1
      vm.ui.appliedOnce = false
      pendingApply.add(vm.id)
      queueMicrotask(async () => {
        const gridReason = sizeChanged ? 'size-change-grid' : 'db-refresh-grid'
        const fsReason = sizeChanged ? 'size-change-fs' : 'db-refresh-fs'
        await applyCropToCropper(vm, gridReason, cropperRefs.value.get(vm.id), false)
        if (fs.open && fs.id === vm.id) {
          await applyCropToCropper(vm, fsReason, fsCropperRef.value, true)
        }
      })
    }
  }

  if (openedGroupKeys.size) {
    const next = new Set(collapsedGroups.value)
    for (const key of openedGroupKeys) next.delete(String(key))
    collapsedGroups.value = next
  }

  if (insertedVms.length) {
    images.value = [...images.value, ...insertedVms]
    emitEditorImagesChanged({
      ids: insertedIds,
      action: 'inserted',
      records: insertedVms.map((vm) => vm.record),
    })
  }

  await nextTick()
}

async function reload(ids?: string[]) {
  const list = (ids || []).filter(Boolean)
  if (!list.length) {
    await loadImages()
    return
  }
  await refreshImagesByIds(list)
}
const preselectKey = computed(() => (props.preselectIds ?? []).filter(Boolean).join('|'))

watch(
  preselectKey,
  async (key) => {
    if (!key || !images.value.length) return
    const wanted = new Set((props.preselectIds ?? []).filter(Boolean))
    const nextSelected = images.value.map((img) => img.id).filter((id) => wanted.has(id))
    if (!nextSelected.length) return
    const prevKey = selectedIds.value.join('|')
    const nextKey = nextSelected.join('|')
    if (prevKey !== nextKey) {
      selectedIds.value = nextSelected
      emitSelection()
    }
    await nextTick()
    scheduleRevealPreselectedImage('smooth')
  },
  { flush: 'post' },
)

function reconcileUiForNewProduct() {
  if (!images.value.length) return

  for (const img of images.value) {
    const lock = computeLock(img.record)

    img.ui.locked = lock.locked
    img.ui.lockedProductId = lock.pid
    img.ui.lockedProductTitle = lock.title

    // Gesperrte Bilder: NICHT an das neue Produkt anpassen
    if (img.ui.locked) {
      const preset: any = img.record?.edits?.preset
      const presetSize: any = preset?.size ?? null
      const inferredPresetSize = presetSize || resolveSizeFromVariantOptions(preset?.options || img.ui.options || []) || null
      const presetVariantId = inferredPresetSize?.variantId ? String(inferredPresetSize.variantId) : ''
      const presetTitle = String(inferredPresetSize?.title || presetSize?.title || '')

      if (!img.ui.sizeKey) img.ui.sizeKey = presetVariantId || presetTitle || img.ui.sizeKey
      if (!img.ui.sizeTitleHint) img.ui.sizeTitleHint = presetTitle || img.ui.sizeTitleHint
      continue
    }

    // Per-Image sizes (eigenes Produkt wenn vorhanden, sonst global)
    const sizes = normalizedSizesForImg(img)
    if (!sizes.length) continue

    const preset: any = img.record?.edits?.preset
    const presetSize: any = preset?.size ?? null
    const inferredPresetSize = presetSize || resolveSizeFromVariantOptions(preset?.options || img.ui.options || []) || null
    const presetVariantId = inferredPresetSize?.variantId ? String(inferredPresetSize.variantId) : ''
    const presetTitle = String(inferredPresetSize?.title || presetSize?.title || '')

    const resolvedSize = resolveSizeUiState(
      sizes,
      inferredPresetSize,
      img.ui.sizeKey || presetVariantId,
      img.ui.sizeTitleHint || presetTitle,
      false,
    )
    if (resolvedSize.sizeKey && img.ui.sizeKey !== resolvedSize.sizeKey) img.ui.sizeKey = resolvedSize.sizeKey
    img.ui.sizeTitleHint = resolvedSize.sizeTitleHint
    syncCropMode(img, { persist: false, apply: false })
  }
}

function refreshInitialPricePayloadFromResolvedSizes() {
  if (!images.value.length) return

  const changedIds: string[] = []
  for (const img of images.value) {
    const preset: any = img.record?.edits?.preset
    const presetSize: any = preset?.size ?? null
    const inferredPresetSize = presetSize || resolveSizeFromVariantOptions(preset?.options || img.ui.options || []) || null
    const presetTitle = String(inferredPresetSize?.title || presetSize?.title || '')
    const resolvedSize = resolveSizeUiState(
      normalizedSizesForImg(img),
      inferredPresetSize,
      img.ui.sizeKey,
      img.ui.sizeTitleHint || presetTitle,
      !!img.ui.locked,
    )

    const nextKey = String(resolvedSize.sizeKey || '').trim()
    const nextHint = String(resolvedSize.sizeTitleHint || '').trim()
    const keyChanged = nextKey && nextKey !== String(img.ui.sizeKey || '').trim()
    const hintChanged = nextHint && nextHint !== String(img.ui.sizeTitleHint || '').trim()

    if (!keyChanged && !hintChanged) continue

    if (keyChanged) img.ui.sizeKey = nextKey
    if (hintChanged) img.ui.sizeTitleHint = nextHint
    changedIds.push(img.id)
  }

  if (changedIds.length) {
    emitMetaChangedDebounced(changedIds)
  }
}


watch(
    [() => props.categoryId, preselectKey],
    ([nextCategoryId, nextPreselectKey], [prevCategoryId, prevPreselectKey]) => {
      if (typeof window === 'undefined') return
      const categoryChanged = Number(nextCategoryId || 0) !== Number(prevCategoryId || 0)
      const preselectClearedAfterHydrate = !!String(prevPreselectKey || '').trim() && !String(nextPreselectKey || '').trim()
      if (preselectClearedAfterHydrate && !categoryChanged) return
      loadImages()
    },
    { immediate: true, flush: 'post' },
)

watch(
  [() => route.query.assetId ?? route.query.stockAssetId, () => props.categoryId, () => props.productId] as const,
  ([assetId, categoryId, productId]) => {
    const rawAssetId = String(Array.isArray(assetId) ? assetId[0] : assetId || '').trim()
    if (!rawAssetId) return
    importAdobeStockAsset(rawAssetId, Number(categoryId || 0), Number(productId || 0)).catch(() => {})
  },
  { immediate: true },
)

// Produktwechsel: KEIN hard reload der Bilder (keine neuen ObjectURLs),
// nur UI/Optionen anpassen.
watch(
    [() => props.productId, () => normalizedSizes.value.map((s) => s.key).join('|')],
    () => {
      if (typeof window !== 'undefined') reconcileUiForNewProduct()
    },
    { immediate: true, flush: 'post' },
)

watch(
  () => variantSizeIdMap.value.size,
  () => {
    if (typeof window !== 'undefined') refreshInitialPricePayloadFromResolvedSizes()
  },
  { immediate: true, flush: 'post' },
)

onBeforeUnmount(() => {
  for (const img of images.value) {
    if (img?.ui?.previewUrl) URL.revokeObjectURL(img.ui.previewUrl)
    if ((img as any)?.ui?.fullPreviewUrl) URL.revokeObjectURL((img as any).ui.fullPreviewUrl)
  }
})

/* ------------------------------------------------
 * Upload / delete / duplicate
 * ------------------------------------------------ */
async function onUploaded(
  files: any[],
  targetProductId?: number,
  initialSize?: { variantId?: string; title?: string; widthMm?: number; heightMm?: number } | null,
  initialOptions?: Array<{ groupId: string; variantId: string; value: string }> | null,
  overrideTitle?: string,
  targetCategoryId?: number,
  targetCategoryTitle?: string,
  targetCategoryThumbnail?: string,
): Promise<string[]> {
  const newIds: string[] = []
  const insertedVms: ImageVM[] = []
  let importFailures = 0
  const uploadPid = targetProductId ?? props.productId
  const uploadCid = targetCategoryId ?? props.categoryId
  const uploadCTitle = targetCategoryTitle ?? props.categoryLabel ?? undefined
  const uploadCThumb = targetCategoryThumbnail ?? undefined
  // Ensure project exists and get code — must be awaited so every record gets the same stable key
  const uploadProjectCode = await project.ensureProject().catch(() => null) ?? null
  const uploadPTitle = (() => {
    if (overrideTitle) return overrideTitle
    if (!targetProductId) return props.productName || undefined
    const p = productsInCategory.value.find((x: any) => Number(x?._id) === targetProductId)
    return p ? String((p as any)?.title || `Produkt #${targetProductId}`) : undefined
  })()
  let effectiveInitialOptions = Array.isArray(initialOptions) ? [...initialOptions] : []
  if (uploadPid && effectiveInitialOptions.length === 0) {
    await loadVariantGroupsForProduct(uploadPid)
    effectiveInitialOptions = defaultOptionsFromGroups(productVariantGroupsMap.value.get(uploadPid) || [])
  }
  const directMode = String((getWidgetConfig() as any)?.editorDirectUploadMode || 'auto').trim().toLowerCase()
  let processed = 0
  for (const f of files || []) {
    try {
      const sourceBlob: Blob = f.originalBlob ?? f.blob ?? f.file ?? f
      if (!sourceBlob) {
        importFailures++
        emitUploadEvent({ type: 'upload:preparing', delta: -1 })
        continue
      }

      const id = f.id ?? uuidv4()
      newIds.push(id)
      const createdAt = nextLocalCreatedAt()
      const inputName = f.name ?? f.fileName ?? (sourceBlob as any)?.name ?? id
      const useFastDirectImport = (directMode === 'prefer' || directMode === 'require') && !isHeicBlob(sourceBlob) && !isTiffBlob(sourceBlob)
      let originalBlob: Blob = sourceBlob
      let workingBlob: Blob = sourceBlob
      let name = String(inputName)
      let mime = String((sourceBlob as any)?.type || 'application/octet-stream')
      let dims = { width: 0, height: 0 }
      const bytes = workingBlob.size

      const editorDims = dims

      const tmp: ImageVM = {
        id: 'tmp',
        name: 'tmp',
        createdAt,
        record: {} as any,
        crop: null,
        ui: {
          previewUrl: '',
          fullPreviewUrl: '',
          previewLoading: false,
          imgW: editorDims.width,
          imgH: editorDims.height,
          baseW: editorDims.width,
          baseH: editorDims.height,
          ready: true,
          applying: false,
          armedGrid: false,
          armedFs: false,
          lockAR: true,
          swapAR: false,
          orientMode: 'auto',
          appliedOnce: false,
          quantity: 1,
          sizeKey: initialSize?.variantId ?? normalizedSizes.value[0]?.key ?? '',
          sizeTitleHint: initialSize?.title ?? normalizedSizes.value[0]?.title ?? '',
          options: [],
        },
      }
      setMaxCrop(tmp, 0)
      syncCropMode(tmp, { persist: false, apply: false })
      const initialCrop = tmp.crop!
      const initialNormalized = cropToNormalized(initialCrop, editorDims.width, editorDims.height)
      const initialStoredCrop = initialNormalized
        ? {
            x: Math.round(initialNormalized.nx * editorDims.width),
            y: Math.round(initialNormalized.ny * editorDims.height),
            w: Math.max(2, Math.round(initialNormalized.nw * editorDims.width)),
            h: Math.max(2, Math.round(initialNormalized.nh * editorDims.height)),
            ...initialNormalized,
            sourceWidth: editorDims.width,
            sourceHeight: editorDims.height,
          }
        : {
            x: initialCrop.x,
            y: initialCrop.y,
            w: initialCrop.w,
            h: initialCrop.h,
          }

      const firstSize = normalizedSizes.value[0] || null
      const appliedSize = persistedSizeValue(initialSize !== undefined ? initialSize : (firstSize ? {
        id: String(firstSize.id || '').trim() || undefined,
        variantId: firstSize.variantId || undefined,
        title: firstSize.title,
        widthMm: firstSize.widthMm,
        heightMm: firstSize.heightMm,
      } : null))
      const rec: ImageRecord = {
        id,
        name,
        createdAt,
        updatedAt: createdAt,
        mime,
        bytes,
        width: dims.width,
        height: dims.height,
        originalBlob,
        workingBlob,
        editorWidth: editorDims.width,
        editorHeight: editorDims.height,
        productId: uploadPid,
        ...(uploadPTitle ? ({ productTitle: String(uploadPTitle) } as any) : {}),
        categoryId: uploadCid,
        ...(uploadCTitle ? ({ categoryTitle: String(uploadCTitle) } as any) : {}),
        ...(uploadCThumb ? ({ categoryThumbnail: String(uploadCThumb) } as any) : {}),
        ...(uploadProjectCode ? { projectCode: uploadProjectCode } : {}),
        edits: {
          crop: initialStoredCrop,
          rotate: initialCrop.rot ?? 0,
          preset: {
            productId: uploadPid,
            ...(uploadPTitle ? ({ productTitle: String(uploadPTitle) } as any) : {}),
            ...(appliedSize ? { sizeId: String(appliedSize.id || '').trim() || undefined, size: appliedSize } : {}),
            options: effectiveInitialOptions,
            quantity: 1,
          },
          lockAR: true,
          swapAR: false,
          orientMode: 'auto',
        } as any,
        upload: { state: 'queued', tries: 0, updatedAt: createdAt },
      }

      transientUploads.upsert(rec)
      // Cart-Fix B: when the source is already upload-ready (fast-direct / non-HEIC/TIFF),
      // persist to IndexedDB right away so the upload runner — which only picks work from
      // IndexedDB via getByUploadState('queued') — can start immediately, instead of
      // waiting for the throttled local-processing job to persist it. HEIC/TIFF must be
      // normalized first (handled inside enqueueLocalImageProcessing), so we skip the
      // eager persist there. The processing job below later updates dims/thumb on the same
      // record; workingBlob (the uploaded bytes) stays the source blob, so no upload race.
      if (useFastDirectImport) {
        const eagerProjectCode = String(uploadProjectCode || project.projectCode.value || '').trim()
        void db.put({
          ...(rec as any),
          ...(eagerProjectCode && !(rec as any).projectCode ? { projectCode: eagerProjectCode } : {}),
        } as any).catch(() => null)
      }
      scheduleUploadsRunnerTick()
      emitUploadEvent({ type: 'upload:preparing', delta: -1 })
      enqueueLocalImageProcessing(id, async () => {
        let nextOriginal = originalBlob
        let nextWorking = workingBlob
        let nextName = name
        let nextMime = mime
        let nextDims = dims

        if (!useFastDirectImport) {
          const prepared = await normalizeImageForUpload(sourceBlob, String(inputName), { quality: 0.92 })
          nextOriginal = prepared.blob
          nextWorking = prepared.blob
          nextName = prepared.name
          nextMime = prepared.mime
          nextDims = { width: prepared.width, height: prepared.height }
        } else {
          nextDims = await readImageDimensions(sourceBlob).catch(() => ({ width: 0, height: 0 }))
        }

        const patch: any = {
          name: nextName,
          mime: nextMime,
          bytes: nextWorking.size,
          width: nextDims.width,
          height: nextDims.height,
          originalBlob: nextOriginal,
          workingBlob: nextWorking,
          editorWidth: nextDims.width,
          editorHeight: nextDims.height,
        }
        const jobs: PromiseSettledResult<any>[] = await Promise.allSettled([
          createThumb(nextWorking, 280, 'image/jpeg', 0.86),
          ...(useFastDirectImport ? [] : [createEditorPreview(nextWorking, 1600, 'image/webp', 0.82)]),
        ])
        const thumb = jobs[0]?.status === 'fulfilled' ? jobs[0].value : null
        const editorPreview = !useFastDirectImport && jobs[1]?.status === 'fulfilled' ? jobs[1].value : null
        if (thumb) {
          patch.thumbBlob = thumb.blob
          patch.thumbWidth = thumb.width
          patch.thumbHeight = thumb.height
        }
        if (editorPreview) {
          patch.editorBlob = editorPreview.blob
          patch.editorWidth = editorPreview.width
          patch.editorHeight = editorPreview.height
        }
        await persistRecordUpdate(id, patch as any)
        emitUploadEvent({ type: 'upload:patched', id, patch })
      })
      const vm = await hydrateVmFromRecord(rec)
      insertedVms.push(vm || createSkeletonVmForUpload(rec))
      processed++
      if (processed % 4 === 0) {
        if (insertedVms.length) {
          images.value.push(...insertedVms.splice(0))
        }
        await nextTick()
        await sleepRaf(1)
      } else {
        await sleepRaf(1)
      }
    } catch {
      importFailures++
      emitUploadEvent({ type: 'upload:preparing', delta: -1 })
    }
  }
  if (insertedVms.length) {
    images.value.push(...insertedVms.splice(0))
  }
  if (importFailures > 0) {
    emitUploadEvent({ type: 'upload:planned-adjust', delta: -importFailures })
  }
  if (newIds.length) {
    if (!canvasPrototypeId.value && images.value.length) {
      canvasPrototypeId.value = images.value[0]?.id || null
    }
    await nextTick()
    if (uploadCid) {
      uploadPinnedCategoryId.value = Number(uploadCid || 0) || null
      await revealCategoryGroup(uploadCid, 'smooth')
    }
  }
  try {
    runner.startAuto?.(900)
  } catch {}

  // Auto-select newly uploaded images and open sidebar for post-upload configuration
  if (newIds.length > 0) {
    const validIds = newIds.filter(id => images.value.some(img => img.id === id))
    if (validIds.length > 0) {
      selectedIds.value = validIds
      emitSelection()
      emit('open-details')
    }
  }
  return newIds
}

async function onFilesBuffered(files: any[], targetCategoryId?: number, targetProductId?: number) {
  if (!files?.length) return
  const nextCategoryId = targetCategoryId ?? pendingTargetCategoryId.value ?? props.categoryId ?? null
  const nextProductId = targetProductId ?? pendingTargetProductId.value ?? props.productId ?? null
  pendingTargetCategoryId.value = nextCategoryId
  pendingTargetProductId.value = nextProductId
  if (nextCategoryId) {
    uploadPinnedCategoryId.value = Number(nextCategoryId || 0) || null
    void revealCategoryGroup(nextCategoryId, 'smooth')
  }
  revokeOverlayFiles(pendingFiles.value as any[])
  pendingImportedRecordIds.value = []
  pendingFiles.value = await buildOverlayFilesFromInputFiles(files)
  showPostUploadOverlay.value = true
}

async function importEntryBufferedUploads(
  files: any[],
  payload: {
    productId: number
    productTitle?: string
    categoryId: number
    categoryTitle?: string
    categoryThumbnail?: string
    sizeVariantId?: string
    sizeTitle?: string
    sizeWidthMm?: number
    sizeHeightMm?: number
    quantity?: number
    grayscale?: boolean
    options: Array<{ groupId: string; variantId: string; value: string }>
  },
) {
  if (!Array.isArray(files) || !files.length) return
  const nextCategoryId = Number(payload?.categoryId || 0) || props.categoryId || null
  const nextProductId = Number(payload?.productId || 0) || props.productId || null
  pendingTargetCategoryId.value = nextCategoryId
  pendingTargetProductId.value = nextProductId
  pendingImportedRecordIds.value = []
  revokeOverlayFiles(pendingFiles.value as any[])
  pendingFiles.value = await buildOverlayFilesFromInputFiles(files)
  await onOverlayApply({
    ...payload,
    options: Array.isArray(payload?.options) ? payload.options : [],
  })
}

function openUploadPicker(targetCategoryId?: number, targetProductId?: number) {
  pendingTargetCategoryId.value = targetCategoryId ?? null
  pendingTargetProductId.value = targetProductId ?? null
  globalUploadRef.value?.openPicker()
}
function openUploadPrompt(targetCategoryId?: number, targetProductId?: number) {
  pendingTargetCategoryId.value = targetCategoryId ?? null
  pendingTargetProductId.value = targetProductId ?? null
  uploadPromptSource.value = 'desktop'
  showEmptyCategoryUploadPrompt.value = true
  void buildPhoneBridge()
}
function confirmEmptyCategoryUploadPrompt() {
  showEmptyCategoryUploadPrompt.value = false
  globalUploadRef.value?.openPicker()
}
function closeEmptyCategoryUploadPrompt() {
  showEmptyCategoryUploadPrompt.value = false
}

async function buildPhoneBridge() {
  const code = await project.ensureProject().catch(() => '')
  const categoryId = Number(pendingTargetCategoryId.value || props.categoryId || 0)
  const productId = Number(pendingTargetProductId.value || props.productId || 0)
  if (!code) {
    phoneUploadUrl.value = ''
    phoneQrDataUrl.value = ''
    return
  }

  phoneQrBusy.value = true
  try {
    phoneBridgeActive.value = true
    phoneUploadUrl.value = buildMobileUploadUrl({ projectCode: code, categoryId, productId })
    phoneQrDataUrl.value = await QRCode.toDataURL(phoneUploadUrl.value, MOBILE_UPLOAD_QR_OPTIONS)
  } finally {
    phoneQrBusy.value = false
  }
}

async function copyPhoneUploadUrl() {
  if (!phoneUploadUrl.value) await buildPhoneBridge()
  if (!phoneUploadUrl.value || typeof navigator === 'undefined' || !navigator.clipboard) return
  await navigator.clipboard.writeText(phoneUploadUrl.value)
}

async function buildOverlayFilesFromRecords(ids: string[]) {
  const out: Array<{ id: string; fileName: string; previewUrl: string }> = []
  for (const id of ids) {
    const rec = await getStoredRecord(id)
    if (!rec) continue
    let previewUrl = String((rec as any)?.upload?.previewUrl || (rec as any)?.upload?.thumbUrl || '').trim()
    const blob: any = (rec as any).thumbBlob || (rec as any).editorBlob
    if (!previewUrl && blob) {
      try {
        previewUrl = URL.createObjectURL(blob)
      } catch {}
    }
    out.push({
      id,
      fileName: String((rec as any)?.name || `Bild ${String(id).slice(0, 8)}`),
      previewUrl,
    })
  }
  return out
}

async function importAdobeStockAsset(assetIdRaw: string, targetCategoryId?: number, targetProductId?: number) {
  const assetId = String(assetIdRaw || '').trim()
  const categoryId = Number(targetCategoryId || props.categoryId || 0)
  const productId = Number(targetProductId || props.productId || 0)
  if (!assetId || stockImportBusy.value) return

  const importKey = `${assetId}:${categoryId}:${productId || 0}`
  if (handledStockImportKeys.has(importKey)) return

  stockImportBusy.value = true
  try {
    const projectCode = await project.ensureProject().catch(() => '')
    const { file, payload } = await fetchAdobeStockPreviewFile(assetId)
    const productTitle = productId > 0 ? (productLabel.value || undefined) : undefined
    const rec = await importImageToDb(file, {
      productId: productId > 0 ? productId : null,
      categoryId: categoryId > 0 ? categoryId : null,
      projectCode: projectCode || null,
      assetId,
      productTitle,
      categoryTitle: categoryId > 0 ? (props.categoryTitlesMap?.[categoryId] || props.categoryLabel || null) : null,
    })

    await loadImages()
    layoutVersion.value++
    try {
      runner.startAuto?.(900)
    } catch {}

    pendingTargetCategoryId.value = categoryId > 0 ? categoryId : null
    pendingTargetProductId.value = productId > 0 ? productId : null
    pendingImportedRecordIds.value = mergeUniqueIds(pendingImportedRecordIds.value, [rec.id])
    pendingFiles.value = mergeOverlayFiles(
      pendingFiles.value as any,
      await buildOverlayFilesFromRecords([rec.id]),
    )
    showEmptyCategoryUploadPrompt.value = false
    showPostUploadOverlay.value = true
    handledStockImportKeys.add(importKey)

    const nextQuery = { ...route.query } as Record<string, any>
    delete nextQuery.assetId
    delete nextQuery.stockAssetId
    delete nextQuery.stockContinue
    await router.replace({ query: nextQuery }).catch(() => {})
  } catch (e: any) {
    toast.error(e?.message ?? 'Adobe-Stock-Bild konnte nicht importiert werden')
  } finally {
    stockImportBusy.value = false
  }
}

function mergeUniqueIds(base: string[], incoming: string[]) {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of [...base, ...incoming]) {
    const key = String(id || '').trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

function mergeOverlayFiles(
  base: Array<{ id: string; fileName: string; previewUrl: string }>,
  incoming: Array<{ id: string; fileName: string; previewUrl: string }>,
) {
  const byId = new Map<string, { id: string; fileName: string; previewUrl: string }>()
  for (const item of base) {
    if (!item?.id) continue
    byId.set(String(item.id), item)
  }
  for (const item of incoming) {
    if (!item?.id) continue
    byId.set(String(item.id), item)
  }
  return [...byId.values()]
}

async function buildOverlayPreviewUrlFromFile(file: Blob | File): Promise<string> {
  const preview = await createEditorPreview(file, 1600, 'image/webp', 0.82).catch(() => null)
  if (preview?.blob) return URL.createObjectURL(preview.blob)

  const thumb = await createThumb(file, 560, 'image/jpeg', 0.86).catch(() => null)
  if (thumb?.blob) return URL.createObjectURL(thumb.blob)

  return URL.createObjectURL(file)
}

function buildImmediateOverlayPreviewUrlFromFile(file: Blob | File): string {
  try {
    return URL.createObjectURL(file)
  } catch {
    return ''
  }
}

const overlayPreviewUpgradeQueue: Array<() => Promise<void>> = []
let overlayPreviewUpgradeActive = 0
const OVERLAY_PREVIEW_UPGRADE_CONCURRENCY = 3

function pumpOverlayPreviewUpgradeQueue() {
  while (
    overlayPreviewUpgradeActive < OVERLAY_PREVIEW_UPGRADE_CONCURRENCY &&
    overlayPreviewUpgradeQueue.length > 0
  ) {
    const task = overlayPreviewUpgradeQueue.shift()
    if (!task) return
    overlayPreviewUpgradeActive += 1
    void task().finally(() => {
      overlayPreviewUpgradeActive = Math.max(0, overlayPreviewUpgradeActive - 1)
      pumpOverlayPreviewUpgradeQueue()
    })
  }
}

function enqueueOverlayPreviewUpgrade(task: () => Promise<void>) {
  overlayPreviewUpgradeQueue.push(task)
  pumpOverlayPreviewUpgradeQueue()
}

async function upgradeOverlayFilePreview(itemId: string, file: Blob | File) {
  const nextUrl = await buildOverlayPreviewUrlFromFile(file).catch(() => '')
  if (!nextUrl) return
  const nextList = [...(pendingFiles.value as any[])]
  const index = nextList.findIndex((entry: any) => String(entry?.id || '') === String(itemId || ''))
  if (index < 0) {
    if (nextUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(nextUrl) } catch {}
    }
    return
  }
  const prevUrl = String(nextList[index]?.previewUrl || '').trim()
  if (prevUrl === nextUrl) return
  nextList[index] = {
    ...nextList[index],
    previewUrl: nextUrl,
  }
  pendingFiles.value = nextList
  if (prevUrl.startsWith('blob:')) {
    try { URL.revokeObjectURL(prevUrl) } catch {}
  }
}

async function buildOverlayFilesFromInputFiles(files: any[]) {
  const out: any[] = []
  for (const [index, f] of (files || []).entries()) {
    const file = (f?.file || f?.blob || f) as Blob | File
    const id = String(f?.id || `local-${Date.now()}-${index}`)
    const fileName = String(f?.name || f?.fileName || (file as any)?.name || id)
    const previewUrl = file instanceof Blob ? buildImmediateOverlayPreviewUrlFromFile(file) : ''
    out.push({
      id,
      fileName,
      previewUrl,
      file,
      assetId: String(f?.assetId || f?.stockAssetId || '').trim() || undefined,
      stockAssetId: String(f?.stockAssetId || f?.assetId || '').trim() || undefined,
    })
    if (file instanceof Blob) {
      enqueueOverlayPreviewUpgrade(() => upgradeOverlayFilePreview(id, file))
    }
  }
  return out
}

function revokeOverlayFiles(items: any[]) {
  for (const item of items || []) {
    const url = String(item?.previewUrl || '').trim()
    if (!url || !url.startsWith('blob:')) continue
    try { URL.revokeObjectURL(url) } catch {}
  }
}

async function buildPlaceholderRecordForUpload(
  fileLike: any,
  uploadPid: number | undefined,
  uploadCid: number | undefined,
  uploadPTitle: string | undefined,
  uploadCTitle: string | undefined,
  uploadCThumb: string | undefined,
  uploadProjectCode: string | null,
  size: any,
  effectiveInitialOptions: Array<{ groupId: string; variantId: string; value: string }>,
  quantity = 1,
  grayscale = false,
) {
  const sourceBlob: Blob = fileLike?.file || fileLike?.blob || fileLike
  const id = fileLike?.id ?? uuidv4()
  const createdAt = nextLocalCreatedAt()
  const inputName = fileLike?.name ?? fileLike?.fileName ?? (sourceBlob as any)?.name ?? id
  const knownDims = (
    Number(fileLike?.width || 0) > 0 && Number(fileLike?.height || 0) > 0
      ? { width: Number(fileLike.width), height: Number(fileLike.height) }
      : await readImageDimensions(sourceBlob).catch(() => ({ width: 0, height: 0 }))
  )
  const initialCrop = (size && Number(knownDims.width || 0) > 0 && Number(knownDims.height || 0) > 0)
    ? buildMaxCropForRecord({
        width: Math.max(0, Number(knownDims.width || 0)),
        height: Math.max(0, Number(knownDims.height || 0)),
        editorWidth: Math.max(0, Number(knownDims.width || 0)),
        editorHeight: Math.max(0, Number(knownDims.height || 0)),
      } as any, size)
    : null
  const initialStoredCrop = initialCrop
    ? {
        x: initialCrop.x,
        y: initialCrop.y,
        w: initialCrop.w,
        h: initialCrop.h,
        sourceWidth: Math.max(1, Number(initialCrop.sourceWidth || knownDims.width || 1)),
        sourceHeight: Math.max(1, Number(initialCrop.sourceHeight || knownDims.height || 1)),
        ...(cropToNormalized(initialCrop, Math.max(1, Number(initialCrop.sourceWidth || knownDims.width || 1)), Math.max(1, Number(initialCrop.sourceHeight || knownDims.height || 1))) || {}),
      }
    : undefined
  const rec: ImageRecord = {
    id,
    assetId: String(fileLike?.assetId || fileLike?.stockAssetId || '').trim() || null,
    name: String(inputName),
    createdAt,
    updatedAt: createdAt,
    mime: String((sourceBlob as any)?.type || 'application/octet-stream'),
    bytes: Number((sourceBlob as any)?.size || 0),
    width: Math.max(0, Number(knownDims.width || 0)),
    height: Math.max(0, Number(knownDims.height || 0)),
    originalBlob: sourceBlob,
    workingBlob: sourceBlob,
    editorWidth: Math.max(0, Number(knownDims.width || 0)),
    editorHeight: Math.max(0, Number(knownDims.height || 0)),
    productId: uploadPid,
    ...(uploadPTitle ? ({ productTitle: String(uploadPTitle) } as any) : {}),
    categoryId: uploadCid,
    ...(uploadCTitle ? ({ categoryTitle: String(uploadCTitle) } as any) : {}),
    ...(uploadCThumb ? ({ categoryThumbnail: String(uploadCThumb) } as any) : {}),
    ...(uploadProjectCode ? { projectCode: uploadProjectCode } : {}),
    edits: {
      ...(initialStoredCrop ? { crop: initialStoredCrop } : {}),
      rotate: 0,
      preset: {
        productId: uploadPid,
        ...(uploadPTitle ? ({ productTitle: String(uploadPTitle) } as any) : {}),
        ...(size ? { sizeId: String(size.id || '').trim() || undefined, size } : {}),
        options: effectiveInitialOptions,
        quantity: Math.max(1, Number(quantity || 1)),
        grayscale: !!grayscale,
      },
      lockAR: true,
      swapAR: false,
      orientMode: 'auto',
    } as any,
    upload: {
      state: 'queued',
      phase: 'queued',
      progressPct: 0,
      uploadedBytes: 0,
      totalBytes: Number((sourceBlob as any)?.size || 0) || 0,
      tries: 0,
      updatedAt: createdAt,
    },
  }
  return rec
}

async function enrichUploadedRecordAfterQueue(
  rec: ImageRecord,
  fileLike: any,
  size: any,
) {
  const sourceBlob: Blob = fileLike?.file || fileLike?.blob || fileLike
  const inputName = fileLike?.name ?? fileLike?.fileName ?? (sourceBlob as any)?.name ?? rec.id
  const directMode = String((getWidgetConfig() as any)?.editorDirectUploadMode || 'auto').trim().toLowerCase()
  const useFastDirectImport = (directMode === 'prefer' || directMode === 'require') && !isHeicBlob(sourceBlob) && !isTiffBlob(sourceBlob)

  let nextOriginal: Blob = sourceBlob
  let nextWorking: Blob = sourceBlob
  let nextName = String(inputName)
  let nextMime = String((sourceBlob as any)?.type || 'application/octet-stream')
  let nextDims = { width: 0, height: 0 }

  if (!useFastDirectImport) {
    const prepared = await normalizeImageForUpload(sourceBlob, String(inputName), { quality: 0.92 })
    nextOriginal = prepared.blob
    nextWorking = prepared.blob
    nextName = prepared.name
    nextMime = prepared.mime
    nextDims = { width: prepared.width, height: prepared.height }
  } else {
    nextDims = await readImageDimensions(sourceBlob).catch(() => ({ width: 0, height: 0 }))
  }

  const crop = nextDims.width > 0 && nextDims.height > 0
    ? buildMaxCropForRecord({
        ...rec,
        width: nextDims.width,
        height: nextDims.height,
        editorWidth: nextDims.width,
        editorHeight: nextDims.height,
      } as any, size)
    : ((rec as any)?.edits?.crop || { x: 0, y: 0, w: 0, h: 0 })

  const patch: any = {
    name: nextName,
    mime: nextMime,
    bytes: nextWorking.size,
    width: nextDims.width,
    height: nextDims.height,
    originalBlob: nextOriginal,
    workingBlob: nextWorking,
    editorWidth: nextDims.width,
    editorHeight: nextDims.height,
    edits: {
      ...((rec as any).edits || {}),
      crop,
    },
  }
  const jobs: PromiseSettledResult<any>[] = await Promise.allSettled([
    createThumb(nextWorking, 280, 'image/jpeg', 0.86),
    ...(useFastDirectImport ? [] : [createEditorPreview(nextWorking, 1600, 'image/webp', 0.82)]),
  ])
  const thumb = jobs[0]?.status === 'fulfilled' ? jobs[0].value : null
  const editorPreview = !useFastDirectImport && jobs[1]?.status === 'fulfilled' ? jobs[1].value : null
  if (thumb) {
    patch.thumbBlob = thumb.blob
    patch.thumbWidth = thumb.width
    patch.thumbHeight = thumb.height
  }
  if (editorPreview) {
    patch.editorBlob = editorPreview.blob
    patch.editorWidth = editorPreview.width
    patch.editorHeight = editorPreview.height
  }
  await persistRecordUpdate(rec.id, patch as any)
  emitUploadEvent({ type: 'upload:patched', id: rec.id, patch })
}

async function syncRemoteProjectUploadsForPrompt() {
  if (remoteSyncBusy.value) return
  const code = await project.ensureProject().catch(() => '')
  const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
  const categoryId = Number(pendingTargetCategoryId.value || props.categoryId || 0)
  const productId = Number(pendingTargetProductId.value || props.productId || 0)
  if (!code || !editorBaseUrl) return

  remoteSyncBusy.value = true
  try {
    invalidateProjectUploadsCache({ editorBaseUrl, projectCode: code })
    const items = await fetchProjectUploads({
      editorBaseUrl,
      projectCode: code,
      ...(categoryId > 0 ? { categoryId } : {}),
      ...(productId > 0 ? { productId } : {}),
      source: 'phone',
      editorReady: false,
      statuses: ['done'],
      ...(lastRemoteSyncAt.value ? { since: lastRemoteSyncAt.value } : {}),
      limit: 50,
      offset: 0,
    })

    const importedIds: string[] = []
    const touchedIds: string[] = []
    let newestCreatedAt = lastRemoteSyncAt.value
    for (const item of items) {
      if (deletedRemoteIds.has(String(item.id || ''))) continue
      const rec = mapRemoteUploadToRecordMeta(item)
      const existing = await getStoredRecord(item.id)
      if (!Number((rec as any).productId || 0) && productId > 0) {
        ;(rec as any).productId = productId
        ;(rec as any).productTitle = String(productLabel.value || '')
        ;(rec as any).edits = (rec as any).edits || {}
        ;(rec as any).edits.preset = {
          ...(((rec as any).edits?.preset || {}) as any),
          productId,
          ...(productLabel.value ? { productTitle: String(productLabel.value) } : {}),
        }
      }
      if (existing) {
        const merged = mergeServerAndLocalImageRecord(existing, rec)
        transientUploads.upsert(merged)
        mergeSessionRecordPatch(merged.id, merged as any)
        touchedIds.push(merged.id)
      } else {
        transientUploads.upsert(rec)
        importedIds.push(rec.id)
      }
      newestCreatedAt = String(item?.createdAt || newestCreatedAt || '')
    }

    if (importedIds.length) {
      await reload(importedIds)
      pendingImportedRecordIds.value = mergeUniqueIds(pendingImportedRecordIds.value, importedIds)
      const nextOverlayFiles = await buildOverlayFilesFromRecords(importedIds)
      pendingFiles.value = mergeOverlayFiles(pendingFiles.value as any, nextOverlayFiles)
      showEmptyCategoryUploadPrompt.value = false
      showPostUploadOverlay.value = true
    }
    if (touchedIds.length) {
      await reload([...new Set(touchedIds)])
    }
    lastRemoteSyncAt.value = newestCreatedAt
  } finally {
    remoteSyncBusy.value = false
  }
}

async function pollSingleDuplicateUpload(id: string, projectCode: string) {
  const wantedId = String(id || '').trim()
  const code = String(projectCode || '').trim()
  const editorBaseUrl = String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()
  if (!wantedId || !code || !editorBaseUrl) return false

  invalidateProjectUploadsCache({ editorBaseUrl, projectCode: code })
  const items = await fetchProjectUploads({
    editorBaseUrl,
    projectCode: code,
    limit: 100,
    offset: 0,
  }).catch(() => [])
  const item = (items || []).find((entry: any) => String(entry?.id || '').trim() === wantedId) as any
  if (!item) return false

  upsertProjectUploadsCacheItem(item, { editorBaseUrl, projectCode: code })
  const remoteRecord = mapRemoteUploadToRecordMeta(item, { projectCode: code })
  const existing = await getStoredRecord(wantedId).catch(() => null)
  const merged = mergeServerAndLocalImageRecord(remoteRecord, existing || transientUploads.get(wantedId) || sessionRecordsState.value[wantedId] || null)
  transientUploads.upsert(merged as any)
  mergeSessionRecordPatch(wantedId, merged as any)
  await db.put(merged as any).catch(() => null)
  const liveVm = images.value.find((img) => img.id === wantedId) || null
  if (liveVm) {
    liveVm.record = {
      ...((liveVm.record || {}) as any),
      ...((merged || {}) as any),
      edits: {
        ...((((merged as any)?.edits || {}) as any)),
        ...((((liveVm.record as any)?.edits || {}) as any)),
      },
      upload: {
        ...((((liveVm.record as any)?.upload || {}) as any)),
        ...((((merged as any)?.upload || {}) as any)),
      },
    } as any
    liveVm.ui.previewLoading = false
  } else {
    await reload([wantedId])
  }

  const status = String(item?.status || (item?.processing ? 'processing' : 'done') || 'done').trim() || 'done'
  return status === 'done'
}

function startDuplicateUploadPolling(id: string, projectCode: string) {
  const wantedId = String(id || '').trim()
  const code = String(projectCode || '').trim()
  if (!wantedId || !code || duplicateRemotePollTimers.has(wantedId)) return

  let tries = 0
  const stop = () => {
    const timer = duplicateRemotePollTimers.get(wantedId)
    if (timer) clearInterval(timer)
    duplicateRemotePollTimers.delete(wantedId)
  }

  const run = async () => {
    tries += 1
    const done = await pollSingleDuplicateUpload(wantedId, code).catch(() => false)
    if (done || tries >= 12) stop()
  }

  const timer = setInterval(() => {
    void run()
  }, 1500)
  duplicateRemotePollTimers.set(wantedId, timer)
  void run()
}

function startRemotePolling() {
  stopRemotePolling()
  remotePollTimer = setInterval(() => {
    syncRemoteProjectUploadsForPrompt().catch(() => {})
  }, 3500)
}

function stopRemotePolling() {
  if (remotePollTimer) clearInterval(remotePollTimer)
  remotePollTimer = null
}

async function startRealtimeUploadSubscription() {
  stopRealtimeUploadSubscription()
  const code = await project.ensureProject().catch(() => '')
  const cfg = getWidgetConfig() as any
  if (!code) return

  const phoneModeActive =
    (showEmptyCategoryUploadPrompt.value && uploadPromptSource.value === 'phone') ||
    inlineUploadSource.value === 'phone' ||
    phoneBridgeActive.value
  const uploadActive = uploadStats.value.active > 0 || Object.values(liveUploadState.value).some((live) => {
    const state = String(live?.state || '').trim()
    return state === 'processing' || state === 'uploading' || state === 'queued'
  })
  if (!phoneModeActive && !uploadActive) return

  // Local desktop uploads already drive state through the upload bus.
  // Polling is cheaper and more stable here than an additional Mercure stream.
  if (uploadActive && !phoneModeActive) {
    startRemotePolling()
    return
  }

  const categoryIds = new Set<number>()
  const explicitCategoryId = Number(pendingTargetCategoryId.value || props.categoryId || 0)
  if (phoneModeActive && explicitCategoryId <= 0) {
    startRemotePolling()
    return
  }
  if (explicitCategoryId > 0) categoryIds.add(explicitCategoryId)
  for (const [id, live] of Object.entries(liveUploadState.value)) {
    const state = String(live?.state || '').trim()
    if (!['queued', 'uploading', 'processing', 'completing', 'presigning'].includes(state)) continue
    const rec = sessionRecordsState.value[id] || transientUploads.get(id)
    const categoryId = Number((rec as any)?.categoryId || 0)
    if (categoryId > 0) categoryIds.add(categoryId)
  }
  if (!categoryIds.size && explicitCategoryId > 0) categoryIds.add(explicitCategoryId)
  if (!categoryIds.size) return
  mercureSubscribedCategoriesKey.value = [...categoryIds].sort((a, b) => a - b).join(',')

  stopMercure = subscribeToUploadEvents({
    editorBaseUrl: cfg.editorBaseUrl,
    mercureHubUrl: cfg.mercureHubUrl,
    projectCode: code,
    categoryId: [...categoryIds],
    onDeleted: async ({ ids }) => {
      // Server removed these uploads (other tab, cart drawer, phone flow, cleanup).
      // Tombstone them so a concurrent poll cannot resurrect them, then drop
      // whatever is still present locally. No remote delete call: it is already gone.
      markRemoteDeleted(ids)
      const present = ids.filter((id) => images.value.some((img) => img.id === id) || !!transientUploads.get(id))
      if (!present.length) return
      runner.cancel(present)
      await removeManyLocal(present)
      invalidateProjectUploadsCache({ editorBaseUrl: String(cfg.editorBaseUrl || ''), projectCode: code })
    },
    onUpload: async (item) => {
      const id = String(item?.id || '')
      if (id && wasRemoteDeleted(id)) return
      if (phoneModeActive) {
        await syncRemoteProjectUploadsForPrompt()
        return
      }
      const previewUrl = String((item as any)?.thumbUrl || (item as any)?.previewUrl || '').trim()
      const state = String((item as any)?.status || ((item as any)?.processing ? 'processing' : 'done') || 'done').trim() || 'done'
      const responseEdits = (item as any)?.meta && typeof (item as any).meta === 'object'
        ? ((((item as any).meta || {}) as any).edits || undefined)
        : undefined
      const responseCategoryId = Number((item as any)?.categoryId || responseEdits?.preset?.categoryId || 0) || undefined
      const responseProductId = Number((item as any)?.productId || responseEdits?.preset?.productId || 0) || undefined
      const responseCategoryTitle = String((item as any)?.categoryTitle || responseEdits?.preset?.categoryTitle || '').trim() || undefined
      const responseProductTitle = String((item as any)?.productTitle || responseEdits?.preset?.productTitle || '').trim() || undefined
      emitUploadEvent({
        type: 'upload:patched',
        id,
        patch: {
          ...(responseCategoryId ? { categoryId: responseCategoryId } : {}),
          ...(responseProductId ? { productId: responseProductId } : {}),
          ...(responseCategoryTitle ? { categoryTitle: responseCategoryTitle } : {}),
          ...(responseProductTitle ? { productTitle: responseProductTitle } : {}),
          ...(responseEdits ? { edits: responseEdits } : {}),
          width: (item as any)?.width ?? undefined,
          height: (item as any)?.height ?? undefined,
          upload: {
            state,
            phase: state,
            progressPct: state === 'done' ? 100 : 0,
            uploadedBytes: Number((item as any)?.bytes || 0),
            totalBytes: Number((item as any)?.bytes || 0),
            ...(previewUrl ? { previewUrl, thumbUrl: previewUrl } : {}),
            remoteUrl: String((item as any)?.remoteUrl || ''),
            updatedAt: Date.now(),
          },
        },
      })
    },
    onError: () => {
      startRemotePolling()
    },
  })

  if (!stopMercure) startRemotePolling()
}

function stopRealtimeUploadSubscription() {
  if (stopMercure) stopMercure()
  stopMercure = null
  mercureSubscribedCategoriesKey.value = ''
  stopRemotePolling()
}

async function onOverlayApply(payload: {
  productId: number
  productTitle?: string
  categoryId: number
  categoryTitle?: string
  categoryThumbnail?: string
  sizeVariantId?: string
  sizeTitle?: string
  sizeWidthMm?: number
  sizeHeightMm?: number
  quantity?: number
  grayscale?: boolean
  options: Array<{ groupId: string; variantId: string; value: string }>
  allGroups?: any[]
}) {
  const localPendingFiles = [...(pendingFiles.value as any[])]
  const options = Array.isArray(payload.options) ? payload.options : []
  const quantity = Math.max(1, Number(payload.quantity || 1))
  const grayscale = !!payload.grayscale

  // Cache variant groups for this product so per-tile dropdowns show the right options
  if (payload.productId && payload.allGroups?.length) {
    const m = new Map(productVariantGroupsMap.value)
    m.set(payload.productId, payload.allGroups)
    productVariantGroupsMap.value = m
  }

  const size = (payload.sizeVariantId || payload.sizeTitle)
      ? persistedSizeValue({
          id: payload.sizeId,
          variantId: payload.sizeVariantId,
          title: payload.sizeTitle,
          widthMm: payload.sizeWidthMm,
          heightMm: payload.sizeHeightMm,
        })
      : null

  lastUploadTags.value = normalizeTags((payload as any).tags)
  try { localStorage.setItem(LAST_UPLOAD_TAGS_KEY, JSON.stringify(lastUploadTags.value)) } catch {}
  if (pendingImportedRecordIds.value.length) {
    const importedApplyIds = [...pendingImportedRecordIds.value]
    for (const id of pendingImportedRecordIds.value) {
      const rec = await getStoredRecord(id)
      if (!rec) continue
      const nextProductTitle = productTitleForSwitch(
        (rec as any).productId ?? (rec as any).edits?.preset?.productId,
        payload.productId,
        payload.productTitle,
        (rec as any).edits?.preset?.productTitle || (rec as any).productTitle,
      )
      const nextPreset = {
        ...(((rec as any).edits?.preset || {}) as any),
        productId: payload.productId,
        productTitle: nextProductTitle,
        ...(payload.categoryId ? { categoryId: payload.categoryId } : {}),
        ...(payload.categoryTitle ? { categoryTitle: String(payload.categoryTitle) } : {}),
        ...(size ? { sizeId: String(size.id || '').trim() || undefined, size } : {}),
        options,
        quantity,
        grayscale,
      }
      const nextCrop = buildMaxCropForRecord(
        {
          ...(rec as any),
          productId: payload.productId,
          categoryId: payload.categoryId,
          edits: {
            ...((rec as any).edits || {}),
            preset: nextPreset,
          },
        } as any,
        size,
      )
      const merged: any = {
        ...rec,
        productId: payload.productId,
        productTitle: nextProductTitle,
        categoryId: payload.categoryId,
        ...(payload.categoryTitle ? { categoryTitle: String(payload.categoryTitle) } : {}),
        ...(payload.categoryThumbnail ? { categoryThumbnail: String(payload.categoryThumbnail) } : {}),
        ...(normalizeTags((payload as any).tags).length ? { tags: normalizeTags((payload as any).tags) } : {}),
        edits: {
          ...((rec as any).edits || {}),
          ...(nextCrop ? { crop: nextCrop } : {}),
          lockAR: true,
          orientMode: 'auto',
          swapAR: false,
          preset: nextPreset,
        },
        updatedAt: Date.now(),
      }
      await persistRecordPut(merged)
      scheduleUploadMetaSync(id, true)
    }
    await reload(pendingImportedRecordIds.value)
    for (const id of importedApplyIds) {
      previewPreset(
        id,
        size,
        options,
        {
          productId: payload.productId,
          ...(payload.productTitle ? { productTitle: String(payload.productTitle) } : {}),
          categoryId: payload.categoryId,
          ...(payload.categoryTitle ? { categoryTitle: String(payload.categoryTitle) } : {}),
          ...(payload.categoryThumbnail ? { categoryThumbnail: String(payload.categoryThumbnail) } : {}),
        },
      )
    }
    if (payload.categoryId) {
      await nextTick()
      uploadPinnedCategoryId.value = Number(payload.categoryId || 0) || null
      await revealCategoryGroup(payload.categoryId, 'smooth')
    }
    selectedIds.value = pendingImportedRecordIds.value.filter((id) => images.value.some((img) => img.id === id))
    emitSelection()
    emit('open-details')
  } else {
    if (payload.categoryId) {
      uploadPinnedCategoryId.value = Number(payload.categoryId || 0) || null
      void revealCategoryGroup(payload.categoryId, 'smooth')
    }
    emitUploadEvent({ type: 'upload:planned', total: localPendingFiles.length })
    emitUploadEvent({ type: 'upload:preparing', delta: localPendingFiles.length })
    uploadPreparingModal.value = {
      title: 'Fotos werden für den Upload vorbereitet',
      body: `${localPendingFiles.length} Bild${localPendingFiles.length !== 1 ? 'er werden' : ' wird'} vorbereitet. Danach startet der Upload automatisch.`,
    }
    showPostUploadOverlay.value = false
    revokeOverlayFiles(localPendingFiles)
    pendingFiles.value = []
    pendingImportedRecordIds.value = []
    pendingTargetCategoryId.value = null
    pendingTargetProductId.value = null
    selectedIds.value = []
    emitSelection()
    await nextTick()
    const uploadProjectCode = await project.ensureProject().catch(() => null) ?? null
    const placeholders: Array<{ rec: ImageRecord; file: any }> = []
    const insertedIds: string[] = []
    const bufferedInsertedIds: string[] = []
    const bufferedInsertedVms: ImageVM[] = []
    const flushInsertedVms = async () => {
      if (!bufferedInsertedVms.length) return
      const nextIds = [...bufferedInsertedIds]
      const nextVms = bufferedInsertedVms.splice(0)
      images.value = [...images.value, ...nextVms]
      if (bufferedInsertedIds.length) {
        emitEditorImagesChanged({
          ids: bufferedInsertedIds.splice(0),
          action: 'inserted',
          records: nextVms
            .filter((vm) => nextIds.includes(vm.id))
            .map((vm) => vm.record),
        })
      }
      await nextTick()
      await sleepRaf(1)
    }
    for (const f of localPendingFiles) {
      const rec = await buildPlaceholderRecordForUpload(
        f,
        payload.productId,
        payload.categoryId,
        payload.productTitle,
        payload.categoryTitle,
        payload.categoryThumbnail,
        uploadProjectCode,
        size,
        options,
        quantity,
        grayscale,
      )
      if ((rec as any)?.upload) {
        ;(rec as any).upload.state = 'idle'
        ;(rec as any).upload.phase = 'queued'
        ;(rec as any).upload.progressPct = 0
        ;(rec as any).upload.uploadedBytes = 0
        ;(rec as any).upload.totalBytes = Number((f?.file || f?.blob || f)?.size || rec.bytes || 0) || 0
      }
      transientUploads.upsert(rec)
      await db.put(rec as any).catch(() => null)
      placeholders.push({ rec, file: f })
      insertedIds.push(rec.id)
      bufferedInsertedIds.push(rec.id)
      bufferedInsertedVms.push(createSkeletonVmForUpload(rec))
      emitUploadEvent({ type: 'upload:preparing', delta: -1 })
      emitUploadEvent({
        type: 'upload:patched',
        id: rec.id,
        patch: {
          upload: {
            ...(((rec as any)?.upload || {}) as any),
            state: 'idle',
            phase: 'queued',
            updatedAt: Date.now(),
          },
        },
      })
      if (bufferedInsertedVms.length >= 4 || placeholders.length === 1) {
        await flushInsertedVms()
      }
    }
    await flushInsertedVms()
    if (loadImagesRunning) {
      // Entry-handoff uploads can land while the initial session hydration is still
      // running. Queue one follow-up reload so a stale first pass cannot hide the
      // last inserted placeholder until a manual page reload.
      loadImagesQueued = true
    } else if (insertedIds.length) {
      await reload(insertedIds)
    }
    if (!props.isMultiMode) {
      emit('navigate', { categoryId: payload.categoryId, productId: payload.productId })
    }
    void (async () => {
      try {
        await uploadImageRecordsBatch(placeholders.map((entry) => entry.rec), String(uploadProjectCode || ''))
      } catch (e: any) {
        const failedIds = Array.isArray(e?.failedIds)
          ? e.failedIds.map((id: any) => String(id || '').trim()).filter(Boolean)
          : placeholders.map(({ rec }) => String(rec.id))
        for (const { rec } of placeholders) {
          if (!failedIds.includes(String(rec.id))) continue
          emitUploadEvent({
            type: 'upload:patched',
            id: rec.id,
            patch: {
              upload: {
                state: 'failed',
                phase: 'failed',
                progressPct: 0,
                uploadedBytes: 0,
                totalBytes: Number((rec as any)?.bytes || 0) || 0,
                lastError: String(e?.message || 'Upload fehlgeschlagen.'),
                updatedAt: Date.now(),
              },
            },
          })
          transientUploads.patch(rec.id, {
            upload: {
              ...((rec as any)?.upload || {}),
              state: 'failed',
              phase: 'failed',
              progressPct: 0,
              uploadedBytes: 0,
              totalBytes: Number((rec as any)?.bytes || 0) || 0,
              lastError: String(e?.message || 'Upload fehlgeschlagen.'),
              updatedAt: Date.now(),
            },
          } as any)
        }
        toast.error(e?.message ?? 'Upload fehlgeschlagen.')
      }
    })()
    return
  }
  showPostUploadOverlay.value = false
  revokeOverlayFiles(localPendingFiles)
  pendingFiles.value = []
  pendingImportedRecordIds.value = []
  pendingTargetCategoryId.value = null
  pendingTargetProductId.value = null

  if (!props.isMultiMode) {
    emit('navigate', { categoryId: payload.categoryId, productId: payload.productId })
  }
}

function onOverlayCancel() {
  showPostUploadOverlay.value = false
  revokeOverlayFiles(pendingFiles.value as any[])
  pendingFiles.value = []
  pendingImportedRecordIds.value = []
  pendingTargetCategoryId.value = null
  pendingTargetProductId.value = null
}

async function removeOneLocal(id: string) {
  const img = images.value.find((i) => i.id === id)
  deletedRemoteIds.add(String(id))

  if (img?.ui?.previewUrl) URL.revokeObjectURL(img.ui.previewUrl)
  if ((img as any)?.ui?.fullPreviewUrl) URL.revokeObjectURL((img as any).ui.fullPreviewUrl)

  transientUploads.remove(id)
  await db.delete(id).catch(() => {})
  images.value = images.value.filter((i) => i.id !== id)
  if (canvasPrototypeId.value === id) {
    canvasPrototypeId.value = images.value[0]?.id || null
  }

  selectedIds.value = selectedIds.value.filter((x) => x !== id)
  emitSelection()
  emitMetaChangedDebounced([id])
  emitEditorImagesChanged({ ids: [id], action: 'deleted' })
  setTimeout(() => deletedRemoteIds.delete(String(id)), 60000)
}

async function removeManyLocal(ids: string[]) {
  const wanted = Array.from(new Set((ids || []).map((id) => String(id || '').trim()).filter(Boolean)))
  if (!wanted.length) return

  const wantedSet = new Set(wanted)
  for (const id of wanted) {
    const img = images.value.find((i) => i.id === id)
    deletedRemoteIds.add(id)
    transientUploads.remove(id)
    if (img?.ui?.previewUrl) URL.revokeObjectURL(img.ui.previewUrl)
    if ((img as any)?.ui?.fullPreviewUrl) URL.revokeObjectURL((img as any).ui.fullPreviewUrl)
  }

  await Promise.all(wanted.map((id) => db.delete(id).catch(() => {})))
  images.value = images.value.filter((i) => !wantedSet.has(i.id))
  if (canvasPrototypeId.value && wantedSet.has(canvasPrototypeId.value)) {
    canvasPrototypeId.value = images.value[0]?.id || null
  }

  selectedIds.value = selectedIds.value.filter((x) => !wantedSet.has(x))
  emitSelection()
  emitMetaChangedDebounced(wanted)
  emitEditorImagesChanged({ ids: wanted, action: 'deleted' })
  setTimeout(() => {
    for (const id of wanted) deletedRemoteIds.delete(id)
  }, 60000)
}

/* ---------- Löschen: erst Server, dann lokal (Bilder in Designs bleiben stehen) ---------- */
const uploadInUseError = ref<UploadInUseError | null>(null)

/** Liefert die IDs, die lokal entfernt werden dürfen. */
async function deleteRemoteFirst(ids: string[]): Promise<string[]> {
  try {
    await deleteImageRecordsRemoteBulk(ids)
    return ids
  } catch (e) {
    if (isUploadInUseError(e)) {
      uploadInUseError.value = e
      const blocked = new Set(e.blockedIds)
      return ids.filter((id) => !blocked.has(id))
    }
    // Netz-/Serverfehler: wie bisher lokal trotzdem entfernen.
    console.warn('[image-tool] remote delete failed', e)
    return ids
  }
}

async function openBlockingDesign(design: BlockingDesign) {
  uploadInUseError.value = null
  const projectCode = String(project.projectCode.value || '').trim()
  await router.push({
    path: '/design',
    query: {
      designId: design.id,
      ...(design.productId > 0 ? { productId: String(design.productId) } : {}),
      ...(design.categoryId > 0 ? { categoryId: String(design.categoryId) } : {}),
      ...(projectCode ? { projectCode } : {}),
    },
  })
}

async function removeOneCore(id: string) {
  runner.cancel(id)
  const removable = await deleteRemoteFirst([id])
  if (!removable.length) return
  await removeOneLocal(id)
  invalidateProjectUploadsCache({ editorBaseUrl: getWidgetConfig().editorBaseUrl, projectCode: String(project.projectCode.value || '') })
  await syncProjectImagesInCart().catch(() => cart.fetchCart().catch(() => cart.cart))
}

async function removeOne(id: string) {
  deleteBusyCount.value = 1
  deleteBusy.value = true
  try {
    await removeOneCore(id)
  } finally {
    deleteBusy.value = false
    deleteBusyCount.value = 0
  }
}

async function deleteSelected() {
  const ids = [...selectedIds.value]
  if (!ids.length) return
  deleteBusyCount.value = ids.length
  deleteBusy.value = true
  try {
    runner.cancel(ids)
    const removable = await deleteRemoteFirst(ids)
    if (removable.length) await removeManyLocal(removable)
    clearSelection()
    if (!removable.length) return
    invalidateProjectUploadsCache({ editorBaseUrl: getWidgetConfig().editorBaseUrl, projectCode: String(project.projectCode.value || '') })
    await syncProjectImagesInCart().catch(() => cart.fetchCart().catch(() => cart.cart))
  } finally {
    deleteBusy.value = false
    deleteBusyCount.value = 0
  }
}

function mapSizeToCurrentProduct(oldSize: any): any | null {
  const sizes = normalizedSizes.value
  if (!sizes.length) return null

  const title = String(oldSize?.title || '').trim()
  if (title) {
    const s = sizes.find((x) => String(x.title) === title)
    if (s) return { title: s.title, widthMm: s.widthMm, heightMm: s.heightMm, variantId: s.variantId }
  }

  const w = Number(oldSize?.widthMm || 0)
  const h = Number(oldSize?.heightMm || 0)
  if (w > 0 && h > 0) {
    const s1 = sizes.find((x) => Number(x.widthMm || 0) === w && Number(x.heightMm || 0) === h)
    if (s1) return { title: s1.title, widthMm: s1.widthMm, heightMm: s1.heightMm, variantId: s1.variantId }

    // toleranter: gedreht
    const s2 = sizes.find((x) => Number(x.widthMm || 0) === h && Number(x.heightMm || 0) === w)
    if (s2) return { title: s2.title, widthMm: s2.widthMm, heightMm: s2.heightMm, variantId: s2.variantId }
  }

  const s0 = sizes[0]
  return s0 ? { title: s0.title, widthMm: s0.widthMm, heightMm: s0.heightMm, variantId: s0.variantId } : null
}

async function hydrateVmFromRecord(rec: ImageRecord): Promise<ImageVM | null> {
  const blob = (fullPreviewBlob(rec) || tilePreviewBlob(rec)) as any
  if (!blob && !remoteTileUrl(rec) && !remoteFullUrl(rec)) return null

  const name = rec.name || rec.id
  const holdSkeleton = shouldHoldUploadSkeleton(rec)
  const blobDims = !holdSkeleton && blob ? await readImageDimensions(blob).catch(() => ({ width: 0, height: 0 })) : null
  const dims = resolvedEditorDimsForRecord(rec, blobDims)

  const preset: any = rec.edits?.preset ?? {}
  const presetSize: any = preset?.size ?? null
  let crop: PxCrop | null = cropFromStored(rec, dims)
  if (presetSize && isClearlyInvalidTinyCrop(crop)) {
    crop = buildMaxCropForRecord(rec as any, presetSize) || crop
  }
  const presetVariantId = presetSize?.variantId ? String(presetSize.variantId) : ''
  const presetTitle = String(presetSize?.title || '')

  const lock = computeLock(rec)

  const options: UIOption[] = Array.isArray(preset?.options)
      ? (preset.options as any[])
          .map((o: any) => ({
            groupId: normId(o?.groupId ?? o?.id ?? ''),
            variantId: normId(o?.variantId ?? ''),
            value: String(o?.value ?? ''),
          }))
          .filter((o: any) => !!o.groupId && !!o.value)
      : []

  const swapAR = rec.edits?.swapAR != null ? !!rec.edits.swapAR : false
  const orientMode = rec.edits?.orientMode === 'manual' ? 'manual' : 'auto'
  const lockAR = rec.edits?.lockAR != null ? !!rec.edits.lockAR : true

  const vm: ImageVM = {
    id: rec.id,
    name,
    createdAt: rec.createdAt ?? Date.now(),
    record: rec,
    crop,
    ui: {
      previewUrl: '',
      fullPreviewUrl: '',
      remotePreviewKey: null,
      imgW: dims.width,
      imgH: dims.height,
      baseW: dims.width,
      baseH: dims.height,
      ready: false,
      applying: false,
      armedGrid: false,
      armedFs: false,
      lockAR,
      swapAR,
      orientMode,
      appliedOnce: false,
      quantity: Math.max(1, Number(preset?.quantity ?? 1)),
      sizeKey: '',
      sizeTitleHint: '',
      options,
      locked: lock.locked,
      lockedProductId: lock.pid,
      lockedProductTitle: lock.title,
      rev: 0,
    },
  }

  const resolvedSize = resolveSizeUiState(
    normalizedSizesForImg(vm),
    presetSize,
    presetVariantId || presetTitle || '',
    presetTitle,
    lock.locked,
  )
  vm.ui.sizeKey = resolvedSize.sizeKey
  vm.ui.sizeTitleHint = resolvedSize.sizeTitleHint

  if (!vm.crop) setMaxCrop(vm, 0)
  syncCropMode(vm, { persist: false, apply: false })
  if (!(rec as any)?.edits?.crop && vm.crop) {
    persistCropNow(vm.id, vm.crop, vm.crop.rot ?? 0).catch(() => {})
  }
  ensureObjectUrlsForImage(vm)
  return vm
}

function createSkeletonVmForUpload(rec: ImageRecord): ImageVM {
  const dims = editorDimsForRecord(rec)
  const lock = computeLock(rec)
  const preset: any = rec.edits?.preset ?? {}
  const options: UIOption[] = Array.isArray(preset?.options)
    ? (preset.options as any[])
        .map((o: any) => ({
          groupId: normId(o?.groupId ?? o?.id ?? ''),
          variantId: normId(o?.variantId ?? ''),
          value: String(o?.value ?? ''),
        }))
        .filter((o: any) => !!o.groupId && !!o.value)
    : []
  const vm: ImageVM = {
    id: rec.id,
    name: rec.name || rec.id,
    createdAt: rec.createdAt ?? Date.now(),
    record: rec,
    crop: cropFromStored(rec, dims),
    ui: {
      previewUrl: '',
      fullPreviewUrl: '',
      remotePreviewKey: null,
      previewLoading: true,
      imgW: dims.width,
      imgH: dims.height,
      baseW: dims.width,
      baseH: dims.height,
      ready: false,
      applying: false,
      armedGrid: false,
      armedFs: false,
      lockAR: rec.edits?.lockAR != null ? !!rec.edits.lockAR : true,
      swapAR: rec.edits?.swapAR != null ? !!rec.edits.swapAR : false,
      orientMode: rec.edits?.orientMode === 'manual' ? 'manual' : 'auto',
      appliedOnce: false,
      quantity: Math.max(1, Number(preset?.quantity ?? 1)),
      sizeKey: '',
      sizeTitleHint: '',
      options,
      locked: lock.locked,
      lockedProductId: lock.pid,
      lockedProductTitle: lock.title,
      rev: 0,
    },
  }
  const presetSize: any = preset?.size ?? null
  const resolvedSize = resolveSizeUiState(
    normalizedSizesForImg(vm),
    presetSize,
    String(presetSize?.variantId || presetSize?.title || ''),
    String(presetSize?.title || ''),
    lock.locked,
  )
  const holdSkeleton = shouldHoldUploadSkeleton(rec)
  vm.ui.sizeKey = resolvedSize.sizeKey
  vm.ui.sizeTitleHint = resolvedSize.sizeTitleHint
  if (!vm.crop) setMaxCrop(vm, 0)
  syncCropMode(vm, { persist: false, apply: false })
  ensureObjectUrlsForImage(vm)
  if (!shouldShowUploadSkeleton(rec)) vm.ui.previewLoading = false
  return vm
}

async function duplicateSelected() {
  const toClone = images.value.filter((i) => selectedIds.value.includes(i.id))
  const currentPid = Number(props.productId || 0)
  if (currentPid > 0) await loadVariantGroupsForProduct(currentPid).catch(() => {})
  const sessionCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()

  for (const src of toClone) {
    const latest = (src.record || (await getStoredRecord(src.id).catch(() => null)) || null) as any
    if (!latest) continue

    const newId = uuidv4()
    const r = latest
    const clonedEdits = clonePlainForDuplicate((r as any)?.edits || {})
    const clonedUpload = clonePlainForDuplicate((r as any)?.upload || {})
    const createdAt = nextLocalCreatedAt()
    const nextName = duplicateImageName(String(r?.name || ''), images.value.map((img) => img?.name || ''))
    const sourceUploadState = String((r?.upload?.state || '')).trim()
    const canDuplicateOnServer = sourceUploadState === 'done' && !!String((r?.upload?.remoteUrl || '')).trim() && !!sessionCode

    if (canDuplicateOnServer) {
      let provisionalRec: ImageRecord = enrichCategoryMeta({
        ...(r as any),
        edits: clonedEdits,
        id: newId,
        assetId: undefined,
        createdAt,
        updatedAt: createdAt,
        name: nextName,
        originalBlob: undefined,
        workingBlob: undefined,
        editorBlob: (r as any).editorBlob ? (r as any).editorBlob.slice(0, (r as any).editorBlob.size, (r as any).editorBlob.type) : undefined,
        thumbBlob: r.thumbBlob ? r.thumbBlob.slice(0, r.thumbBlob.size, r.thumbBlob.type) : undefined,
        upload: {
          ...(clonedUpload as any),
          state: 'processing',
          phase: 'processing',
          source: 'desktop',
          progressPct: 0,
          uploadedBytes: 0,
          totalBytes: Number(r?.bytes || (r as any)?.upload?.totalBytes || 0) || 0,
          previewUrl: String((r as any)?.upload?.previewUrl || (r as any)?.upload?.thumbUrl || '').trim() || undefined,
          thumbUrl: String((r as any)?.upload?.thumbUrl || (r as any)?.upload?.previewUrl || '').trim() || undefined,
          fullscreenPreviewUrl: String((r as any)?.upload?.fullscreenPreviewUrl || '').trim() || undefined,
          remoteUrl: undefined,
          updatedAt: createdAt,
        },
      } as any) as ImageRecord

      transientUploads.upsert(provisionalRec as any)
      await db.put(provisionalRec as any).catch(() => null)
      const skeletonVm = createSkeletonVmForUpload(provisionalRec)
      markRecentInsert(skeletonVm.id)
      images.value = [...images.value, skeletonVm]
      emitEditorImagesChanged({ ids: [newId], action: 'inserted', records: [provisionalRec] })
      if (!canvasPrototypeId.value) canvasPrototypeId.value = skeletonVm.id
      skeletonVm.ui.appliedOnce = false
      pendingApply.add(skeletonVm.id)

      try {
        toast.success(`Duplikat ${nextName} wird erstellt`)
        const serverItem: any = await duplicateEditorUpload({
          sourceId: String(src.id || ''),
          projectCode: sessionCode,
          id: newId,
          name: nextName,
          categoryId: Number(r?.categoryId || 0) || undefined,
          productId: Number(r?.productId || 0) || undefined,
        })

        let rec: ImageRecord = {
          ...(provisionalRec as any),
          id: newId,
          createdAt,
          updatedAt: createdAt,
          name: nextName,
          edits: clonePlainForDuplicate(clonedEdits),
          editorBlob: (r as any).editorBlob ? (r as any).editorBlob.slice(0, (r as any).editorBlob.size, (r as any).editorBlob.type) : undefined,
          editorWidth: (r as any).editorWidth,
          editorHeight: (r as any).editorHeight,
          thumbBlob: r.thumbBlob ? r.thumbBlob.slice(0, r.thumbBlob.size, r.thumbBlob.type) : undefined,
          upload: {
            ...(clonePlainForDuplicate(clonedUpload) as any),
            state: String(serverItem?.status || (serverItem?.processing ? 'processing' : 'done') || 'processing').trim() || 'processing',
            phase: serverItem?.processing ? 'processing' : 'done',
            source: String(serverItem?.source || 'desktop').trim() as any,
            progressPct: serverItem?.processing ? 100 : 100,
            uploadedBytes: Number(serverItem?.bytes || r?.bytes || 0) || 0,
            totalBytes: Number(serverItem?.bytes || r?.bytes || 0) || 0,
            previewUrl: String(serverItem?.previewUrl || serverItem?.thumbUrl || '').trim() || undefined,
            thumbUrl: String(serverItem?.thumbUrl || serverItem?.previewUrl || '').trim() || undefined,
            fullscreenPreviewUrl: String(serverItem?.fullscreenPreviewUrl || '').trim() || undefined,
            remoteUrl: String(serverItem?.remoteUrl || '').trim() || undefined,
            updatedAt: createdAt,
          },
          assetId: String(serverItem?.assetId || r?.assetId || '').trim() || undefined,
          bytes: Number(serverItem?.bytes || r?.bytes || 0) || Number(r?.bytes || 0) || 0,
          width: Number(serverItem?.width || r?.width || 0) || undefined,
          height: Number(serverItem?.height || r?.height || 0) || undefined,
        } as ImageRecord

        rec = enrichCategoryMeta(rec as any) as ImageRecord
        transientUploads.upsert(rec as any)
        mergeSessionRecordPatch(newId, rec as any)
        await db.put(rec as any).catch(() => null)
        const existingVm = images.value.find((img) => img.id === newId)
        if (existingVm) {
          existingVm.record = {
            ...((existingVm.record || {}) as any),
            ...((rec || {}) as any),
            edits: {
              ...((((rec as any)?.edits || {}) as any)),
              ...((((existingVm.record as any)?.edits || {}) as any)),
            },
            upload: {
              ...((((existingVm.record as any)?.upload || {}) as any)),
              ...((((rec as any)?.upload || {}) as any)),
            },
          } as any
          ensureObjectUrlsForImage(existingVm)
          existingVm.ui.previewLoading = false
        } else {
          const vm = await hydrateVmFromRecord(rec)
          if (vm) {
            markRecentInsert(vm.id)
            images.value = [...images.value, vm]
            if (!canvasPrototypeId.value) canvasPrototypeId.value = vm.id
            vm.ui.appliedOnce = false
            pendingApply.add(vm.id)
          }
        }
        startDuplicateUploadPolling(newId, sessionCode)
        continue
      } catch (error: any) {
        transientUploads.remove(newId)
        sessionRecordsState.value = Object.fromEntries(
          Object.entries(sessionRecordsState.value).filter(([key]) => String(key || '').trim() !== newId),
        )
        images.value = images.value.filter((img) => img.id !== newId)
        emitEditorImagesChanged({ ids: [newId], action: 'deleted' })
        await db.delete(newId).catch(() => null)
        toast.error(error?.message || 'Duplizieren fehlgeschlagen')
        continue
      }
    }

    await ensureWorkingBlobForImage(src)
    const sourceOriginal = r.originalBlob || r.workingBlob
    if (!sourceOriginal || !r.workingBlob) continue
    const originalBlob = sourceOriginal.slice(0, sourceOriginal.size, sourceOriginal.type)
    const workingBlob = r.workingBlob.slice(0, r.workingBlob.size, r.workingBlob.type)
    const totalBytes = Number(sourceOriginal.size || workingBlob.size || r?.bytes || 0) || 0

    let rec: ImageRecord = {
      ...r,
      edits: clonePlainForDuplicate(clonedEdits),
      id: newId,
      assetId: undefined,
      createdAt,
      updatedAt: createdAt,
      name: nextName,
      originalBlob,
      workingBlob,
      editorBlob: (r as any).editorBlob ? (r as any).editorBlob.slice(0, (r as any).editorBlob.size, (r as any).editorBlob.type) : undefined,
      editorWidth: (r as any).editorWidth,
      editorHeight: (r as any).editorHeight,
      thumbBlob: r.thumbBlob ? r.thumbBlob.slice(0, r.thumbBlob.size, r.thumbBlob.type) : undefined,
      upload: {
        state: 'queued',
        phase: 'queued',
        source: 'desktop',
        progressPct: 0,
        uploadedBytes: 0,
        totalBytes,
        tries: 0,
        updatedAt: createdAt,
      },
    }
    rec = enrichCategoryMeta(rec as any) as ImageRecord

    const srcPid = recordProductId(r)
    const shouldReassign = srcPid != null && currentPid > 0 && Number(srcPid) !== Number(currentPid)

    if (shouldReassign) {
      ;(rec as any).productId = currentPid
      ;(rec as any).productTitle = productLabel.value

      rec.edits = (rec.edits as any) ?? {}
      ;(rec.edits as any).preset = (rec.edits as any).preset ?? {}
      ;(rec.edits as any).preset.productId = currentPid
      ;(rec.edits as any).preset.productTitle = productLabel.value

      const preset: any = (rec.edits as any).preset
      const mapped = mapSizeToCurrentProduct(preset?.size)
      if (mapped) preset.size = mapped
      preset.options = defaultTileOptionsForProduct(productVariantGroupsMap.value.get(currentPid) || [])
    }

    rec = enrichCategoryMeta(rec as any) as ImageRecord

    transientUploads.upsert(rec as any)
    scheduleUploadsRunnerTick()

    const vm = await hydrateVmFromRecord(rec)
    if (vm) {
      markRecentInsert(vm.id)
      images.value = [...images.value, vm]
      if (!canvasPrototypeId.value) canvasPrototypeId.value = vm.id
      vm.ui.appliedOnce = false
      pendingApply.add(vm.id)
    }
  }
}

async function duplicateOne(imageId: string) {
  const prev = [...selectedIds.value]
  selectedIds.value = [imageId]
  try {
    await duplicateSelected()
  } finally {
    selectedIds.value = prev
    emitSelection()
  }
}



function onDrop() {
  isDragging.value = false
}

onMounted(() => {
  loadToolbarPrefs()
})

onBeforeUnmount(() => {
  if (revealSelectedTimer) clearTimeout(revealSelectedTimer)
})

/* expose */
defineExpose({
  images,
  uploadStats,
  totalQuantity,
  firstSelectedImage: computed(() => selectedIds.value.length ? (images.value.find(img => img.id === selectedIds.value[0]) ?? null) : null),
  viewImages,
  search,
  sortBy,
  productLabel,
  isDragging,
  selectedIds,
  fs,
  fsImg,
  layoutVersion,

  setCropperRef,
  setFsCropperRef,

  stencilProps,
  optionPills,
  priceText,
  priceTitle,

  isSelected,
  toggleSelect,
  onTileClick,
  onSelectClick,
  selectAll,
  clearSelection,
  openDetailsForSingle,
  importEntryBufferedUploads,
  openDetailsForSelection,
  revealImageInGrid,
  revealCategoryGroup,

  onCropperReady,
  onCropChange,

  reload,
  bulkPriceRequestsForCurrentImages,
  applySidebarSizeLocally,

  onUploaded,
  onDrop,
  duplicateSelected,
  deleteSelected,
  removeOne,
  rotate90,
  flipAR,
  onSizeSelectKey,
  persistQuantity,
  openFullscreen,
  closeFullscreen,
  setMaxCropAndApply,

  addSelectionToCart,
  addAllToCart,
  addOneToCart,
  openUploadPicker,
  openUploadPrompt,
  countImagesInCurrentView,

  previewPreset,
  setOptionPreview,
})
</script>

<template>
  <section class="mi">
    <div v-if="deleteBusy" class="busy-overlay" aria-live="polite" aria-busy="true">
      <div class="busy-card">
        <span class="busy-spinner" />
        <strong>{{ deleteBusyCount === 1 ? 'Bild wird gelöscht' : 'Bilder werden gelöscht' }}</strong>
      </div>
    </div>
    <!-- Toolbar -->
    <header class="tb">
      <!-- Mobile -->
      <div v-if="isCompactHeader" class="mhead">
        <div class="mbar">
          <div class="mbar-row mbar-row--top">
            <button
                class="btn back-to-products"
                type="button"
                @click="backToProductSelection"
                aria-label="Zur Produktauswahl"
                title="Zur Produktauswahl"
            >
              <ArrowLeft :size="16" />
              <span>Übersicht</span>
            </button>
            <div class="dd">
              <button
                  class="btn dd-btn dd-btn--compact"
                  type="button"
                  @click.stop="toggleTbMenu"
                  :disabled="!images.length && !designerTarget"
                  aria-haspopup="menu"
                  :aria-expanded="tbMenuOpen"
              >
                Aktionen
                <span class="count-inline" v-if="selectedIds.length">{{ selectedIds.length }}</span>
                <span class="chev">▾</span>
              </button>

              <div v-if="tbMenuOpen" class="dd-menu" role="menu" @click.stop>
                <button v-if="designerTarget" class="menu-item menu-item--designer" role="menuitem" @click="closeAllTopMenus(); openDesigner()">
                  <Sparkles :size="14" /> {{ hasSelection ? 'Auswahl im Designer gestalten' : 'Mit Vorlage gestalten' }}
                </button>
                <div v-if="designerTarget" class="menu-sep" />
                <button v-if="canSelectAll" class="menu-item" role="menuitem" @click="selectAll(); closeAllTopMenus()">Alle auswählen</button>
                <button v-if="hasSelection" class="menu-item" role="menuitem" @click="clearSelection(); closeAllTopMenus()">Auswahl aufheben</button>
                <div v-if="hasSelection" class="menu-sep" />
                <button v-if="hasSelection" class="menu-item" role="menuitem" @click="duplicateSelected(); closeAllTopMenus()">Duplizieren</button>
                <button v-if="hasSelection" class="menu-item danger" role="menuitem" @click="deleteSelected(); closeAllTopMenus()">Löschen</button>
                <div v-if="hasSelection" class="menu-sep" />
                <button v-if="hasSelection" class="menu-item" role="menuitem" @click="openDetailsForSelection(); closeAllTopMenus()">Details bearbeiten</button>
                <div v-if="hasSelection" class="menu-sep" />
                <button v-if="hasSelection" class="menu-item" role="menuitem" @click="openTagDialog('add'); closeAllTopMenus()">Tag vergeben…</button>
                <button v-if="hasSelection && selectionTags.length" class="menu-item" role="menuitem" @click="openTagDialog('remove'); closeAllTopMenus()">Tag entfernen…</button>
                <div v-if="hasSelection" class="menu-sep" />
                <button v-if="hasSelection" class="menu-item" role="menuitem" @click="addSelectionToCart(); closeAllTopMenus()" :disabled="cartSyncBusy || cart.loading">
                  {{ cartSyncBusy ? 'Wird in den Warenkorb gelegt…' : 'In den Warenkorb' }}
                </button>

              </div>
            </div>
          </div>

          <div class="mbar-row mbar-row--search">
            <div class="msearch-inline msearch-inline--compact">
              <span class="msearch-ico">⌕</span>
              <input class="msearch-input" type="search" v-model="search" placeholder="Suchen…" />
              <button v-if="search" class="btn mini icon-only" type="button" @click="search=''" aria-label="Suche leeren">✕</button>
            </div>
            <label v-if="allTags.length" class="tag-filter tag-filter--mobile" :data-on="filterTag !== 'all' ? '1' : '0'">
              <span class="tag-filter__ico" aria-hidden="true">#</span>
              <select v-model="filterTag" class="tag-filter__select" aria-label="Nach Tag filtern">
                <option v-for="o in tagFilterOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </label>
          </div>

          <div v-if="uploadIssueCount > 0" class="mbar-row mbar-row--filters">
            <button
              class="btn mini icon-only issue-filter-btn"
              type="button"
              :data-on="uploadIssueFilter === 'issues' ? '1' : '0'"
              @click="uploadIssueFilter = uploadIssueFilter === 'issues' ? 'all' : 'issues'"
              :title="uploadIssueFilter === 'issues' ? `Problemfälle ausblenden (${uploadIssueCount})` : `Problemfälle anzeigen (${uploadIssueCount})`"
              :aria-label="uploadIssueFilter === 'issues' ? `Problemfälle ausblenden (${uploadIssueCount})` : `Problemfälle anzeigen (${uploadIssueCount})`"
            >
              <AlertCircle :size="16" />
            </button>
          </div>

          <div class="mbar-row mbar-row--bottom">
            <button v-if="images.length" class="btn add-to-cart add-to-cart--compact" @click="addAllToCart" :disabled="cart.loading || cartSyncBusy" title="In den Warenkorb" aria-label="In den Warenkorb">
              <span class="cart-btn-main">
                <span class="cart-btn-icon-wrap">
                  <span v-if="cartSyncBusy" class="busy-spinner cart-btn-spinner" />
                  <template v-else>
                    <ShoppingCart :size="17" />
                    <Plus :size="13" class="cart-plus" />
                  </template>
                </span>
                <span>{{ cartSyncBusy ? 'Wird hinzugefügt…' : 'Warenkorb' }}</span>
              </span>
              <strong v-if="effectiveCartButtonPriceLabel" class="cart-btn-price">{{ effectiveCartButtonPriceLabel }}</strong>
            </button>
          </div>
        </div>
      </div>

      <!-- Desktop -->
      <template v-else>
        <div class="tb-left">
          <button
              class="btn back-to-products"
              type="button"
              @click="backToProductSelection"
              aria-label="Zur Produktauswahl"
              title="Zur Produktauswahl"
          >
            <ArrowLeft :size="16" />
            <span>Übersicht</span>
          </button>

          <div class="search-inline search-inline--compact">
            <span class="msearch-ico">⌕</span>
            <input class="msearch-input" type="search" v-model="search" placeholder="Suchen…" />
            <button v-if="search" class="btn mini icon-only" type="button" @click="search=''" aria-label="Suche leeren">✕</button>
          </div>

          <label v-if="allTags.length" class="tag-filter" :data-on="filterTag !== 'all' ? '1' : '0'" title="Nach Tag/Album filtern">
            <span class="tag-filter__ico" aria-hidden="true">#</span>
            <select v-model="filterTag" class="tag-filter__select" aria-label="Nach Tag filtern">
              <option v-for="o in tagFilterOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </label>

          <button
            v-if="uploadIssueCount > 0"
            class="btn mini icon-only issue-filter-btn"
            type="button"
            :data-on="uploadIssueFilter === 'issues' ? '1' : '0'"
            @click="uploadIssueFilter = uploadIssueFilter === 'issues' ? 'all' : 'issues'"
            :title="uploadIssueFilter === 'issues' ? `Problemfälle ausblenden (${uploadIssueCount})` : `Problemfälle anzeigen (${uploadIssueCount})`"
            :aria-label="uploadIssueFilter === 'issues' ? `Problemfälle ausblenden (${uploadIssueCount})` : `Problemfälle anzeigen (${uploadIssueCount})`"
          >
            <AlertCircle :size="16" />
          </button>
        </div>

        <div class="tb-center">
          <button
            v-if="canSelectAll"
            class="btn icon-only"
            type="button"
            @click="selectAll()"
            title="Alle auswählen"
            aria-label="Alle auswählen"
          >
            <CheckSquare :size="16" />
          </button>
          <button
            v-if="anyGeometryGuides"
            class="btn icon-only guides-toggle"
            :class="{ 'guides-toggle--on': guidesMode !== 'off', 'guides-toggle--cut': guidesMode === 'cut-only' }"
            type="button"
            @click="cycleGuidesMode()"
            :title="guidesButtonTitle"
            aria-label="Produktionsansicht umschalten"
          >
            <Scissors :size="16" />
          </button>
          <button
            v-if="hasSelection"
            class="btn icon-only"
            type="button"
            @click="clearSelection()"
            title="Auswahl aufheben"
            aria-label="Auswahl aufheben"
          >
            <XSquare :size="16" />
          </button>
          <button
            v-if="hasSelection"
            class="btn icon-only"
            type="button"
            @click="duplicateSelected()"
            title="Duplizieren"
            aria-label="Duplizieren"
          >
            <Copy :size="16" />
          </button>
          <button
            v-if="hasSelection"
            class="btn danger icon-only"
            type="button"
            @click="deleteSelected()"
            title="Löschen"
            aria-label="Löschen"
          >
            <Trash2 :size="16" />
          </button>

          <button
            v-if="designerTarget"
            class="btn designer-switch"
            type="button"
            @click="openDesigner()"
            :title="designerButtonTitle"
          >
            <Sparkles :size="16" />
            <span>{{ hasSelection ? 'Im Designer gestalten' : 'Mit Vorlage gestalten' }}</span>
          </button>

          <button v-if="images.length" class="btn add-to-cart add-to-cart--toolbar" @click="addAllToCart" :disabled="cart.loading || cartSyncBusy" title="In den Warenkorb" aria-label="In den Warenkorb">
            <span class="cart-btn-main">
              <span class="cart-btn-icon-wrap">
                <span v-if="cartSyncBusy" class="busy-spinner cart-btn-spinner" />
                <template v-else>
                  <ShoppingCart :size="18" />
                  <Plus :size="14" class="cart-plus" />
                </template>
              </span>
              <span>{{ cartSyncBusy ? 'Wird hinzugefügt…' : 'Warenkorb' }}</span>
            </span>
            <strong v-if="effectiveCartButtonPriceLabel" class="cart-btn-price">{{ effectiveCartButtonPriceLabel }}</strong>
          </button>
        </div>

      </template>
    </header>

    <div v-if="anyGeometryGuides && guidesMode !== 'off'" class="guides-legend" role="note">
      <strong class="guides-legend__title">So wird produziert:</strong>
      <span class="guides-legend__item"><i class="guides-legend__swatch guides-legend__swatch--cut" />Schnittkante</span>
      <span class="guides-legend__item"><i class="guides-legend__swatch guides-legend__swatch--bleed" />wird abgeschnitten (Beschnitt)</span>
      <span v-if="guidesMode === 'full'" class="guides-legend__item"><i class="guides-legend__swatch guides-legend__swatch--safe" />Sicherheitsbereich: Wichtiges hier hinein</span>
    </div>

    <!-- Upload-Zone (modern, immer sichtbar oben) -->
    <div class="global-upload-zone" :class="{ drag: isDragging }">
      <div class="upload-source-switch" aria-label="Upload-Quelle">
        <button class="upload-source-btn" :data-on="inlineUploadSource === 'desktop' ? '1' : '0'" type="button" @click="inlineUploadSource = 'desktop'">
          <Monitor :size="16" />
          Desktop
        </button>
        <button class="upload-source-btn" :data-on="inlineUploadSource === 'phone' ? '1' : '0'" type="button" @click="inlineUploadSource = 'phone'; buildPhoneBridge()">
          <Smartphone :size="16" />
          Fotos vom Smartphone
        </button>
      </div>

      <div
        v-if="inlineUploadSource === 'desktop'"
        class="upload-zone-inner"
        :class="{ drag: isDragging }"
        role="button"
        tabindex="0"
        @click="globalUploadRef?.openPicker()"
        @keydown.enter.prevent="globalUploadRef?.openPicker()"
        @keydown.space.prevent="globalUploadRef?.openPicker()"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="(e: DragEvent) => { isDragging = false; if (e.dataTransfer?.files?.length) globalUploadRef?.handleFiles(e.dataTransfer.files) }"
      >
        <div class="upload-zone-icon"><Upload :size="30" /></div>
        <div class="upload-zone-title">Bilder hinzufügen</div>
        <div class="upload-zone-sub">Klicken oder Dateien hierher ziehen</div>
        <div class="upload-zone-hint">JPG · PNG · TIFF · WEBP · max. 50 MB</div>
      </div>
      <div v-else class="upload-phone-box">
        <div class="upload-phone-qr">
          <img v-if="phoneQrDataUrl" :src="phoneQrDataUrl" alt="QR Code für Handy-Upload" />
          <div v-else class="upload-phone-qrph">{{ phoneQrBusy ? 'QR wird geladen…' : 'Kein QR verfügbar' }}</div>
        </div>
        <div class="upload-phone-copy">
          <div class="upload-phone-head">
            <div>
              <div class="upload-phone-title">Mit dem Handy hochladen</div>
              <div class="upload-phone-sub">QR scannen und Bilder direkt in diese Kategorie{{ productId ? ' und Produkt-Auswahl' : '' }} laden.</div>
            </div>
          </div>
          <div class="upload-phone-actions">
            <button class="btn" type="button" @click="copyPhoneUploadUrl">
              <Copy :size="16" />
              Link kopieren
            </button>
            <button class="btn" type="button" @click="syncRemoteProjectUploadsForPrompt" :disabled="remoteSyncBusy">
              <RefreshCcw :size="16" :class="{ spin: remoteSyncBusy }" />
              {{ remoteSyncBusy ? 'Synchronisiere…' : 'Synchronisieren' }}
            </button>
          </div>
        </div>
      </div>
      <!-- Hidden UploadBox: only used for file input + drop handling, not for UI -->
      <UploadBox
          ref="globalUploadRef"
          :key="`uploadbox:${currentUploadTargetCategoryId}:${currentUploadTargetProductId}`"
          class="upload-zone-hidden"
          :product-id="currentUploadTargetProductId"
          :category-id="currentUploadTargetCategoryId"
          @uploaded="(files: any[]) => onFilesBuffered(files, currentUploadTargetCategoryId, currentUploadTargetProductId)"
      />
    </div>

    <div
      v-if="showUploadQueue"
      class="upload-queue-widget"
      :class="{ 'is-minimized': uploadQueueMinimized, 'is-dragging': uploadQueueDragging }"
      :data-busy="uploadStats.active > 0 ? '1' : '0'"
      :style="uploadQueueWidgetStyle"
    >
      <div class="upload-queue-widget__head" @pointerdown="startUploadQueueDrag">
        <strong>Bilder werden hochgeladen</strong>
        <div class="upload-queue-widget__head-actions">
          <span>{{ uploadStats.visualDone }} / {{ uploadStats.total }}</span>
          <button class="upload-queue-widget__toggle" type="button" @click.stop="toggleUploadQueueMinimized" :aria-expanded="!uploadQueueMinimized">
            {{ uploadQueueMinimized ? 'Mehr' : 'Weniger' }}
          </button>
        </div>
      </div>
      <template v-if="!uploadQueueMinimized">
      <div class="upload-queue-widget__sub">
        <span class="upload-queue-widget__progress">{{ uploadStats.progress }}%</span>
      </div>
      <div class="upload-queue-widget__track" aria-hidden="true">
        <span class="upload-queue-widget__fill" :style="{ width: `${uploadStats.progress}%` }" />
      </div>
      <div class="upload-queue-widget__hint">Bearbeitung läuft lokal weiter. Der Warenkorb wird erst nach fertigem Upload aktualisiert.</div>
      <div v-if="failedUploadItems.length" class="upload-queue-widget__failed">
        <div class="upload-queue-widget__failed-head">
          <strong>Fehlgeschlagene Uploads</strong>
          <button class="upload-queue-widget__link" type="button" @click="retryAllFailedUploads">Alle erneut versuchen</button>
        </div>
        <div v-for="item in failedUploadItems" :key="item.id" class="upload-queue-widget__failed-item">
          <div class="upload-queue-widget__failed-copy">
            <strong>{{ item.name }}</strong>
          </div>
          <div class="upload-queue-widget__failed-actions">
            <button class="upload-queue-widget__action" type="button" @click="retryFailedUpload(item.id)">Erneut</button>
            <button class="upload-queue-widget__action danger" type="button" @click="removeFailedUpload(item.id)">Löschen</button>
          </div>
        </div>
      </div>
      </template>
      <div v-else class="upload-queue-widget__mini">
        <span class="upload-queue-widget__mini-label">{{ uploadQueueMiniLabel }}</span>
        <span class="upload-queue-widget__progress">{{ uploadStats.progress }}%</span>
      </div>
    </div>

    <div v-if="editorInitialLoading && !images.length" class="editor-loading-shell" aria-live="polite" aria-busy="true">
      <div
        v-for="group in initialLoadingGroups"
        :key="group.key"
        class="editor-loading-group"
      >
        <div class="product-group-header product-group-header--loading" aria-hidden="true">
          <span class="group-collapse-btn group-collapse-btn--loading">
            <ChevronDown class="group-collapse-chevron" :size="16" />
          </span>
          <span class="editor-loading-group__title" />
          <span class="editor-loading-group__count" />
        </div>
        <div class="grid editor-loading-grid" :class="{ mobile: isMobile }" :style="{ '--grid-cols': String(gridCols) }" aria-hidden="true">
          <div
            v-for="n in group.count"
            :key="`${group.key}-${n}`"
            class="editor-loading-tile"
          />
        </div>
      </div>
    </div>

    <!-- Gruppen nach Kategorie -->
    <template v-for="(group, groupIndex) in viewImagesByCategory" :key="group.key">
      <div
        v-if="shouldShowRootLabel(group, groupIndex)"
        class="product-root-label"
      >
        <span class="product-root-label__text">{{ group.rootCategoryTitle }}</span>
        <span class="product-root-label__line" aria-hidden="true" />
        <button
          class="product-root-label__toggle"
          type="button"
          @click.stop="toggleRootGroups(group.rootCategoryKey)"
        >
          {{ areAllRootGroupsCollapsed(group.rootCategoryKey) ? 'Alle aufklappen' : 'Alle zuklappen' }}
        </button>
      </div>
      <div
          class="product-group"
          :ref="(el) => setCategoryGroupRef(el, group.key)"
      >
      <!-- Gruppen-Header -->
      <div class="product-group-header" @click="toggleGroupCollapse(group.key)" style="cursor:pointer">
        <button
            class="group-collapse-btn"
            type="button"
            :aria-expanded="!collapsedGroups.has(group.key)"
            @click.stop="toggleGroupCollapse(group.key)"
        >
          <ChevronDown
              class="group-collapse-chevron"
              :class="{ collapsed: collapsedGroups.has(group.key) }"
              :size="16"
          />
        </button>
        <img v-if="group.thumbnail" class="group-thumb" :src="group.thumbnail" alt="" />
        <span class="product-group-title">{{ group.categoryTitle }}</span>
        <span class="product-group-count">{{ group.items.length }}</span>
        <div
          class="group-select-wrap"
          :title="isGroupFullySelected(group) ? `Auswahl für ${group.categoryTitle} aufheben` : `Alle Bilder in ${group.categoryTitle} auswählen`"
          @click.stop
        >
          <button
            class="group-select-btn"
            type="button"
            @click.stop="toggleGroupSelection(group)"
            :aria-label="isGroupFullySelected(group) ? `Auswahl für ${group.categoryTitle} aufheben` : `Alle Bilder in ${group.categoryTitle} auswählen`"
            :aria-pressed="isGroupFullySelected(group)"
          >
            <XSquare v-if="isGroupFullySelected(group)" :size="15" class="group-select-icon" aria-hidden="true" />
            <CheckSquare v-else :size="15" class="group-select-icon" aria-hidden="true" />
          </button>
        </div>
        <div class="group-upload-wrap" :title="`Bilder hochladen für ${group.categoryTitle}`" @click.stop>
          <button
            class="group-upload-btn"
            type="button"
            @click.stop="openUploadPrompt(group.categoryId ?? categoryId, productId || undefined)"
            :aria-label="`Bilder hochladen für ${group.categoryTitle}`"
          >
            <Upload :size="15" class="group-upload-icon" aria-hidden="true" />
          </button>
        </div>
      </div>

      <!-- Grid für diese Gruppe (zusammenklappbar) -->
      <div
          v-show="!collapsedGroups.has(group.key)"
          class="grid"
          :class="{ mobile: isMobile }"
          :style="{ '--grid-cols': String(gridCols) }"
      >
        <EditorImageTile
            v-for="{ img, vIdx } in group.items"
            :key="img.id"
          v-memo="[
            img.id,
            isSelected(img.id),
            img.ui.rev,
            cropSnapshot(img),
            img.ui.previewLoading,
            img.ui.previewUrl,
            img.ui.fullPreviewUrl,
            img.ui.sizeKey,
            img.ui.sizeTitleHint,
            img.ui.quantity,
            JSON.stringify(img.ui.options || []),
            JSON.stringify(optionPreviewById[img.id] || null),
            img.crop?.x,
            img.crop?.y,
            img.crop?.w,
            img.crop?.h,
            img.crop?.rot,
            img.ui.locked,
            img.record?.productId,
            img.record?.categoryId,
            img.record?.updatedAt,
            img.record?.edits?.preset?.grayscale,
            img.record?.upload?.state,
            img.record?.upload?.phase,
            img.record?.upload?.progressPct,
            p3dLibraryRev,
          ]"
          :img="img"
          :v-idx="vIdx"
          :selected="isSelected(img.id)"
          :skeleton="shouldShowUploadSkeleton(img.record)"
          :layout-version="layoutVersion"
          :display-url="tileDisplayUrl(img)"
          :fit-in-mode="displayFitInMode(img)"
          :white-border-label="displayWhiteBorderLabel(img)"
          :option-preview="!!optionPreviewById[img.id]"
          :mount-tile-cropper="shouldMountTileCropper(img)"
          :show-uploading-overlay="shouldShowUploadingOverlay(img)"
          :upload-issue-label="uploadIssueLabel(img)"
          :upload-issue-tone="uploadIssueKind(img)"
          :preview3d="hasPreview3dTile(img)"
          :frame2d="frame2dFor(img)"
          :tile-quality-tone="tileQualityTone(img)"
          :tile-quality-label="tileQualityLabel(img)"
          :tile-quality-text="tileQualityTooltip(img)"
          :tile-product-value="tileProductValue(img)"
          :product-options="productSelectOptionsForImg(img)"
          :size-options="sizeSelectOptionsForImg(img)"
          :visible-groups="tileVisibleGroupsFor(img)"
          :size-key="img.ui.sizeKey || null"
          :observer-ref="setTileObserverRef"
          :cropper-ref="setCropperRef"
          :fit-frame-style="fitFrameStyle"
          :stencil-props="stencilProps"
          :inactive-crop-window-style="inactiveCropWindowStyle"
          :inactive-crop-image-style="inactiveCropImageStyle"
          :tile-group-value="tileGroupValue"
          :tile-group-options="tileGroupOptions"
          @tile-click="onTileClick"
          @tile-dblclick="onTileDblClick"
          @tile-enter="toggleSelect"
          @select-click="onSelectClick"
          @remove="removeOne"
          @tag-click="filterTag = $event"
          @quality-open="openQualityTooltip"
          @quality-move="moveQualityTooltip"
          @quality-close="closeQualityTooltip"
          @product-change="onTileProductChange"
          @size-change="onSizeSelectKey"
          @group-change="onTileGroupChange"
          @qty-step="stepTileQty"
          @qty-input="setTileQty"
          @qty-persist="persistQuantity"
          @duplicate="duplicateOne"
          @details="openDetailsForSingle"
          @fullscreen="openFullscreen"
          @preview3d="openPreview3d"
          @rotate="rotate90"
          @retry-upload="retryFailedUpload"
          @cropper-ready="(img:any, p:any) => onCropperReady(img, p, false)"
          @cropper-complete="(img:any, p:any) => onCanvasCropComplete(img, p, false)"
        />
      </div><!-- /grid -->
      </div><!-- /product-group -->
    </template>

    <!-- Fullscreen Crop Modal -->
    <div v-if="fs.open" class="fs-backdrop" @click="closeFullscreen">
      <div class="fs-panel" @click.stop>
        <div class="fs-head">
          <div class="fs-title">
            <strong>Ausschnitt bearbeiten<span v-if="fsOrder.length > 1 && fsIndex >= 0" class="fs-count">{{ fsIndex + 1 }} / {{ fsOrder.length }}</span></strong>
            <span class="fs-sub">{{ fsImg?.name || '' }}</span>
            <span v-if="showPerfBadge" class="fs-bench">Canvas: {{ canvasAvgMs.toFixed(2) }} ms · Cropper: {{ cropperAvgMs.toFixed(2) }} ms</span>
          </div>
          <button class="fs-x" @click="closeFullscreen">✕</button>
        </div>

        <div class="fs-body">
          <div class="fs-stage">
            <button v-if="fsOrder.length > 1" type="button" class="fs-nav fs-nav--prev" :disabled="!fsCanPrev" aria-label="Vorheriges Bild (Pfeil links)" title="Vorheriges Bild (←)" @click="stepFullscreen(-1)"><ChevronLeft :size="26" /></button>
            <button v-if="fsOrder.length > 1" type="button" class="fs-nav fs-nav--next" :disabled="!fsCanNext" aria-label="Nächstes Bild (Pfeil rechts)" title="Nächstes Bild (→)" @click="stepFullscreen(1)"><ChevronRight :size="26" /></button>
            <div
              v-if="!fsImg?.ui.fullPreviewUrl || fsImg?.ui.previewLoading"
              class="tile-preview-loading tile-preview-loading--fs"
              aria-hidden="true"
            >
              <div class="tile-skeleton tile-skeleton--fs" aria-hidden="true">
                <div class="tile-skeleton__media" />
                <div class="tile-skeleton__lines">
                  <span class="tile-skeleton__line tile-skeleton__line--lg" />
                  <span class="tile-skeleton__line tile-skeleton__line--md" />
                  <span class="tile-skeleton__line tile-skeleton__line--sm" />
                </div>
              </div>
            </div>

            <div v-if="fsImg?.ui.fullPreviewUrl && fsImg && isFitInMode(fsImg)" class="fit-preview-shell fit-preview-shell--fs">
              <div class="fit-preview-frame fit-preview-frame--fs" :class="{ 'fit-preview-frame--white-border': !!whiteBorderLabel(fsImg) }" :style="fitFrameStyle(fsImg)">
                <img
                    :src="fsFitPreviewSrc(fsImg)"
                    :alt="fsImg.name"
                    class="fit-preview-image"
                    draggable="false"
                    @error="onFsFitPreviewError(fsImg)"
                />
                <span v-if="whiteBorderLabel(fsImg)" class="white-border-preview white-border-preview--fs" aria-hidden="true" />
              </div>
            </div>

            <div v-else-if="fsImg?.ui.fullPreviewUrl" class="tile-preview-active-shell tile-preview-active-shell--fs">
              <CanvasCropperLite
                  :key="[
                    fsImg.id,
                    'fs',
                    fsImg.ui.fullPreviewUrl,
                    Number(fsImg.ui.previewLoading),
                    fsImg.crop?.rot || 0,
                    fsImg.ui.swapAR ? 1 : 0,
                    fsImg.ui.orientMode || 'auto',
                    fsImg.ui.baseW || fsImg.ui.imgW || 0,
                    fsImg.ui.baseH || fsImg.ui.imgH || 0,
                  ].join(':')"
                  :ref="setFsCropperRef"
                  :src="fsImg.ui.fullPreviewUrl"
                  class="fs-cropper"
                  :disabled="fsImg.ui.locked"
                  :rotation="fsImg.crop?.rot || 0"
                  :coordinates="fsImg.crop ? { left: fsImg.crop.x, top: fsImg.crop.y, width: fsImg.crop.w, height: fsImg.crop.h } : null"
                  :image-width="fsImg.ui.baseW || fsImg.ui.imgW"
                  :image-height="fsImg.ui.baseH || fsImg.ui.imgH"
                  :stencil-props="stencilProps(fsImg)"
                  @ready="p => onCropperReady(fsImg!, p, true)"
                  @change="p => onCropChange(fsImg!, p, true, 'canvas')"
                  @complete="p => onCanvasCropComplete(fsImg!, p, true)"
              />
            </div>

          </div>
        </div>

        <div class="fs-foot">
          <button class="btn fs-foot__btn" @click="setMaxCropAndApply(fsImg!)" :disabled="!fsImg || (fsImg ? isFitInMode(fsImg) : true)">Max. Ausschnitt</button>
          <span class="spacer" />
          <template v-if="fsOrder.length > 1">
            <button class="btn fs-foot__btn" :disabled="!fsCanPrev" @click="stepFullscreen(-1)"><ChevronLeft :size="16" /> Zurück</button>
            <button class="btn fs-foot__btn" :disabled="!fsCanNext" @click="stepFullscreen(1)">Weiter <ChevronRight :size="16" /></button>
          </template>
          <button class="btn primary fs-foot__btn" @click="closeFullscreen">Fertig</button>
        </div>
      </div>
    </div>

    <CartDrawer
        v-model:open="cartOpen"
        :auto-load="true"
        variant="drawer"
        :checkout-href="cartUrl"
        @checkout="goToCart"
    />

    <Teleport to="body">
      <div
        v-if="qualityTooltip"
        class="tile-quality-popover"
        :style="{ left: `${qualityTooltip.x}px`, top: `${qualityTooltip.y}px` }"
        role="tooltip"
      >
        {{ qualityTooltip.text }}
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="uploadPreparingModal" class="mini-dialog-backdrop">
        <div class="mini-dialog" role="dialog" aria-modal="true" aria-label="Upload wird vorbereitet">
          <div class="mini-dialog__head">
            <strong>{{ uploadPreparingModal.title }}</strong>
          </div>
          <div class="mini-dialog__body">
            <div class="mini-dialog__progress">
              <div class="mini-dialog__progress-meta">
                <span>Bitte kurz warten…</span>
              </div>
              <div class="mini-dialog__progress-track" aria-hidden="true">
                <span class="mini-dialog__progress-fill mini-dialog__progress-fill--indeterminate" />
              </div>
            </div>
            <p>{{ uploadPreparingModal.body }}</p>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="cartSyncModal" class="mini-dialog-backdrop">
        <div class="mini-dialog" role="dialog" aria-modal="true" aria-label="Warenkorb wird vorbereitet">
          <div class="mini-dialog__head">
            <strong>{{ cartSyncModal.title }}</strong>
          </div>
          <div class="mini-dialog__body">
            <div class="mini-dialog__progress">
              <div class="mini-dialog__progress-meta">
                <span>Bitte kurz warten…</span>
              </div>
              <div class="mini-dialog__progress-track" aria-hidden="true">
                <span class="mini-dialog__progress-fill mini-dialog__progress-fill--indeterminate" />
              </div>
            </div>
            <p>{{ cartSyncModal.body }}</p>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="cartInfoModal" class="mini-dialog-backdrop" @click.self="cartInfoModal = null">
        <div class="mini-dialog" role="dialog" aria-modal="true" aria-label="Hinweis zum Warenkorb">
          <div class="mini-dialog__head">
            <strong>{{ cartInfoModal.title }}</strong>
            <button class="mini-dialog__close" type="button" @click="cartInfoModal = null" aria-label="Schließen">✕</button>
          </div>
          <div class="mini-dialog__body">
            <div class="mini-dialog__progress">
              <div class="mini-dialog__progress-meta">
                <span>{{ cartInfoStatusLabel(cartInfoModal) }}</span>
                <span v-if="(cartInfoModal?.total ?? 0) > 0">{{ cartInfoModal?.progress ?? 0 }}%</span>
              </div>
              <div class="mini-dialog__progress-track" aria-hidden="true">
                <span class="mini-dialog__progress-fill" :style="{ width: cartInfoProgressWidth(cartInfoModal) }" />
              </div>
            </div>
            <p v-for="(line, idx) in cartInfoBodyLines(cartInfoModal)" :key="idx">{{ line }}</p>
          </div>
          <div class="mini-dialog__foot">
            <button class="btn btn--primary" type="button" @click="cartInfoModal = null">Verstanden</button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="uploadLeaveGuardModal" class="mini-dialog-backdrop" @click.self="cancelUploadLeaveGuard">
        <div class="mini-dialog" role="dialog" aria-modal="true" aria-label="Uploads laufen noch">
          <div class="mini-dialog__head">
            <strong>Uploads laufen noch</strong>
            <button class="mini-dialog__close" type="button" @click="cancelUploadLeaveGuard" aria-label="Schließen">✕</button>
          </div>
          <div class="mini-dialog__body">
            <p>Der Upload ist noch nicht abgeschlossen. Wenn Sie den Editor jetzt neu laden oder verlassen, werden laufende Uploads unterbrochen.</p>
            <p>Möchten Sie wirklich fortfahren?</p>
          </div>
          <div class="mini-dialog__foot">
            <button class="btn" type="button" @click="cancelUploadLeaveGuard">Bleiben</button>
            <button class="btn btn--primary" type="button" @click="confirmUploadLeaveGuard">
              {{ uploadLeaveGuardModal?.kind === 'reload' ? 'Neu laden' : 'Verlassen' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showEmptyCategoryUploadPrompt" class="uprompt-backdrop" @click.self="closeEmptyCategoryUploadPrompt">
        <div class="uprompt-panel">
          <div class="uprompt-title">Noch keine Bilder in dieser Kategorie</div>
          <div class="uprompt-text">Wählen Sie Desktop oder Smartphone. Der Upload wird direkt dem aktuellen Projekt zugeordnet.</div>
          <div class="uprompt-switch" aria-label="Upload-Quelle">
            <button class="uprompt-source" :data-on="uploadPromptSource === 'desktop' ? '1' : '0'" type="button" @click="uploadPromptSource = 'desktop'">
              <Monitor :size="16" />
              Desktop
            </button>
            <button class="uprompt-source" :data-on="uploadPromptSource === 'phone' ? '1' : '0'" type="button" @click="uploadPromptSource = 'phone'; buildPhoneBridge()">
              <Smartphone :size="16" />
              Fotos vom Smartphone
            </button>
          </div>
          <div
            v-if="uploadPromptSource === 'desktop'"
            class="uprompt-dropzone"
            role="button"
            tabindex="0"
            @click="confirmEmptyCategoryUploadPrompt"
            @keydown.enter.prevent="confirmEmptyCategoryUploadPrompt"
            @keydown.space.prevent="confirmEmptyCategoryUploadPrompt"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="(e: DragEvent) => { isDragging = false; showEmptyCategoryUploadPrompt = false; if (e.dataTransfer?.files?.length) globalUploadRef?.handleFiles(e.dataTransfer.files) }"
          >
            <div class="uprompt-dropzone__icon"><Upload :size="28" /></div>
            <div class="uprompt-dropzone__title">Bilder hochladen</div>
            <div class="uprompt-dropzone__sub">Klicken oder Dateien hierher ziehen</div>
            <div class="uprompt-dropzone__hint">JPG · PNG · TIFF · WEBP</div>
          </div>
          <div v-if="uploadPromptSource === 'phone'" class="uprompt-phone">
            <div class="uprompt-phone-qr">
              <img v-if="phoneQrDataUrl" :src="phoneQrDataUrl" alt="QR Code für Handy-Upload" />
              <div v-else class="uprompt-phone-qrph">{{ phoneQrBusy ? 'QR wird geladen…' : 'Kein QR verfügbar' }}</div>
            </div>
            <div class="uprompt-phone-copy">
              <div class="uprompt-phone-head">
                <div>
                  <div class="uprompt-phone-title">Mit dem Handy hochladen</div>
                  <div class="uprompt-phone-sub">QR scannen und Bilder direkt in diese Kategorie{{ pendingTargetProductId || productId ? ' und Produkt-Auswahl' : '' }} laden.</div>
                </div>
              </div>
              <div class="uprompt-phone-actions">
                <button class="btn" type="button" @click="copyPhoneUploadUrl">
                  <Copy :size="16" />
                  Link kopieren
                </button>
              </div>
            </div>
          </div>
          <div class="uprompt-actions">
            <button class="btn" type="button" @click="closeEmptyCategoryUploadPrompt">Abbrechen</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Post-Upload Overlay: Kategorie / Produkt / Optionen wählen -->
    <PostUploadOverlay
        v-if="showPostUploadOverlay"
        :files="pendingFiles"
        :initial-category-id="pendingTargetCategoryId ?? categoryId"
        :initial-product-id="pendingTargetProductId ?? productId"
        :current-editor-type="currentEditorType ?? null"
        :known-tags="allTags.map((t) => t.tag)"
        :initial-tags="lastUploadTags"
        @apply="onOverlayApply"
        @cancel="onOverlayCancel"
    />

    <!-- Tag-Dialog: Tags für die Auswahl vergeben / entfernen -->
    <Teleport to="body">
      <div v-if="tagDialog.open" class="tagdlg-backdrop" @click.self="tagDialog.open = false">
        <form class="tagdlg" role="dialog" aria-modal="true" @submit.prevent="tagDialog.mode === 'add' ? applyTagsToSelection(tagDialog.value) : removeTagsFromSelection(tagDialog.value)">
          <strong class="tagdlg__title">{{ tagDialog.mode === 'add' ? 'Tag vergeben' : 'Tag entfernen' }} · {{ selectedIds.length }} {{ selectedIds.length === 1 ? 'Bild' : 'Bilder' }}</strong>
          <template v-if="tagDialog.mode === 'add'">
            <input v-model="tagDialog.value" class="tagdlg__input" type="text" list="tagdlg-suggestions" placeholder="z. B. Kindergarten, Hochzeit" autofocus autocomplete="off" />
            <datalist id="tagdlg-suggestions"><option v-for="t in allTags" :key="t.tag" :value="t.tag" /></datalist>
            <div v-if="allTags.length" class="tagdlg__chips">
              <button v-for="t in allTags" :key="t.tag" type="button" class="tagdlg__chip" @click="applyTagsToSelection(t.tag)">{{ t.tag }}</button>
            </div>
            <p class="tagdlg__help">Mehrere Tags mit Komma trennen. Bestehende Tags der Bilder bleiben erhalten.</p>
          </template>
          <template v-else>
            <div class="tagdlg__chips">
              <button v-for="t in selectionTags" :key="t.tag" type="button" class="tagdlg__chip tagdlg__chip--remove" @click="removeTagsFromSelection(t.tag)">✕ {{ t.tag }} ({{ t.count }})</button>
            </div>
            <p class="tagdlg__help">Klick entfernt den Tag von allen ausgewählten Bildern.</p>
          </template>
          <div class="tagdlg__actions">
            <button type="button" class="btn" @click="tagDialog.open = false">Abbrechen</button>
            <button v-if="tagDialog.mode === 'add'" type="submit" class="btn primary" :disabled="!normalizeTags(tagDialog.value).length">Tag vergeben</button>
            <button v-else type="button" class="btn" @click="removeTagsFromSelection('')">Alle Tags entfernen</button>
          </div>
        </form>
      </div>
    </Teleport>

    <!-- 3D-Vorschau-Overlay -->
    <Teleport to="body">
      <div v-if="preview3dImageId" class="preview3d-backdrop" @click.self="preview3dImageId = null; preview3dRecord = null">
        <div class="preview3d-panel">
          <button class="preview3d-close" type="button" @click="preview3dImageId = null; preview3dRecord = null" aria-label="Schließen">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <ConfiguratorPreview
            :image-id="preview3dImageId"
            :record="preview3dRecord"
            :aspect-ratio="preview3dAspectRatio"
            :model-config="preview3dModelConfig || props.previewModelConfig"
            :preview3d="preview3dV2Layers"
            :selected-variant-ids="preview3dSelectedIds"
            :size-mm="preview3dSizeMm"
            :option-groups="preview3dOptionGroups"
            @select-option="onPreview3dSelect"
            :geometry="preview3dGeometry"
          />
        </div>
      </div>
    </Teleport>
    <UploadInUseDialog :error="uploadInUseError" @close="uploadInUseError = null" @open-design="openBlockingDesign" />
  </section>
</template>

<style scoped lang="scss">
/* =========
   FLAT / MODERN / MOBILE-FIRST
   ========= */
.mi, .grid, .tile, .meta, .erow, .field, .name { min-width: 0; }
* { box-sizing: border-box; }

.mi{
  --line: var(--pfe-color-border, rgba(15, 23, 42, 0.14));
  --text: var(--pfe-color-text, #0b1220);
  --muted: var(--pfe-color-muted, rgba(15, 23, 42, 0.62));
  --muted2: rgba(15, 23, 42, 0.42);
  --shadow: none;
  --accent: var(--pfe-color-accent, #e30b17);
  --soft: #f3f4f6;
  --bg: var(--pfe-color-bg, #fff);
  width:100%;
  color: var(--text);
  background: var(--bg);
}

/* ========= TOOLBAR ========= */
.tb{
  position: sticky;
  top: 0;
  z-index: 50;
  background: #fff;
  border-bottom: 1px solid var(--line);
  overflow: visible;
  padding-top: env(safe-area-inset-top, 0px);
}
.tb-left, .tb-right, .tb-center{
  display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  min-width: 0;
}
.tb-total-price {
  font-size: 14px; font-weight: 700; color: #141414;
  white-space: nowrap;
}
.tb-left, .tb-center, .tb-right{ padding: 10px 10px 12px; }
@media (min-width: 1181px){
  .tb{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: 10px;
  }
  .tb-left, .tb-center, .tb-right{
    padding: 8px 16px 10px;
    flex-wrap: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .tb-left{
    flex: 1 1 auto;
    min-width: 0;
  }
  .tb-center{
    flex: 0 0 auto;
    min-width: auto;
    justify-content: center;
  }
  .tb-right{
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: 8px;
  }
}

/* Mobile / tablet compact header block */
.mhead{ display:flex; flex-direction:column; gap: 10px; padding: 12px 0; }
.crumb{ display:flex; align-items:center; gap: 8px; font-size: 13px; color: rgba(15,23,42,.65); white-space: nowrap; overflow: hidden; }
.crumb-item{ overflow:hidden; text-overflow: ellipsis; }
.crumb-item.strong{ color: rgba(15,23,42,.88); font-weight: 700; }
.crumb-sep{ opacity: .55; }

.mbar{
  position: relative;
  display:grid;
  grid-template-columns: 1fr;
  gap: 8px;
  align-items:stretch;
}
.mbar-row{
  display:grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  align-items:center;
}
.mbar-row--search{
  grid-template-columns: minmax(0, 1fr);
}
.mbar-row--top .back-to-products{
  min-width: 0;
}
.mbar-row--top .dd,
.mbar-row--top .dd-btn--compact{
  width: 100%;
}
.mbar-row--bottom .add-to-cart--compact{
  width: 100%;
}
.mbar-row--bottom{
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
}
.mbar-row--bottom .add-to-cart--compact{
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 0 14px;
}
.mbar-row--bottom .add-to-cart--compact .cart-btn-icon-wrap{
  margin-left: 0;
  flex: 0 0 auto;
}
.mbar-row--bottom .add-to-cart--compact .cart-btn-main{
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.mbar-row--bottom .add-to-cart--compact .cart-btn-price{
  margin-left: 0;
  min-height: 100%;
  padding: 0 14px 0 12px;
  display: inline-flex;
  align-items: center;
}
.msearch-inline{
  position: relative;
  display:block;
  border: 1px solid var(--line);
  background:#fff;
  padding: 0;
  width: 100%;
  min-width: 0;
}
.mfilters{
  position: relative;
}
.mfilters-btn{
  width: 100%;
  height: 32px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
}
.mfilters-pop{
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 120;
  width: min(320px, 92vw);
  border: 1px solid var(--line);
  background: #fff;
  padding: 8px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}
.search-inline{
  position: relative;
  width: min(520px, 100%);
}
.search-inline--compact,
.msearch-inline--compact{
  width: 100%;
}
.dd-btn--toolbar,
.dd-btn--compact{
  height: 36px;
  padding: 0 12px;
}
.add-to-cart--toolbar,
.add-to-cart--compact{
  height: 36px;
  padding: 0 12px;
  gap: 8px;
}
.designer-switch{
  height: 36px;
  padding: 0 12px;
  gap: 7px;
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(124,58,237,.35);
  background: linear-gradient(135deg, rgba(124,58,237,.08), rgba(227,11,23,.06));
  color: #5b21b6;
  font-weight: 700;
  white-space: nowrap;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.designer-switch:hover{
  border-color: rgba(124,58,237,.6);
  background: linear-gradient(135deg, rgba(124,58,237,.14), rgba(227,11,23,.10));
  box-shadow: 0 4px 14px rgba(124,58,237,.15);
}
.cart-btn-icon-wrap{
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cart-plus{
  position: absolute;
  right: -7px;
  top: -5px;
  background: #fff;
  color: #e30b17;
  border: 1px solid rgba(255,255,255,.88);
  border-radius: 999px;
}
.cart-btn-price{
  margin-left: 2px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}
.mini-select{
  width: 100%;
  min-width: 0;
}
.tb-filter{ min-width: 0; width: 100%; }
.tb-sort{ min-width: 0; }

.grid-filters{
  display:flex;
  justify-content:flex-end;
  padding: 8px 10px 10px;
  background:#fff;
  border-bottom:1px solid var(--line);
}
.grid-filters-inner{
  width: min(50%, 820px);
  min-width: 0;
  margin-left: auto;
  display:grid;
  grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(120px, .85fr);
  gap: 8px;
}
.grid-filters :deep(.fs-trigger){
  border:1px solid var(--line);
  background:#fff;
}
.grid-filters :deep(.fs-pop){
  z-index: 90;
}
@media (max-width: 820px){
  .tb{
    width: 100%;
    box-sizing: border-box;
    padding: 16px;
    border-bottom: 1px solid var(--line);
  }
  .tb-left, .tb-center, .tb-right{
    padding: 0;
  }
  .mhead{
    padding: 0;
  }
  .msearch-inline{
    width: 100%;
  }
  .msearch-filter-row{
    grid-template-columns: 70% 30%;
  }
  .grid-filters{
    padding: 8px 0 8px;
    justify-content: stretch;
  }
  .grid-filters-inner{
    width: 100%;
    min-width: 0;
    grid-template-columns: 1fr;
  }
  .grid.mobile .tile-topbar{
    height: 30px;
  }
  .grid.mobile .delete-chip,
  .grid.mobile .check{
    width: 38px;
    height: 30px;
  }
  .grid.mobile .tile-title{
    font-size: 11px;
    padding: 0 6px;
  }
  .grid.mobile .control-cell{
    min-height: 30px;
  }
  .grid.mobile :deep(.control-cell .fs-trigger){
    height: 30px;
    padding: 0 8px;
  }
  .grid.mobile :deep(.control-cell .fs-label){
    font-size: 11px;
  }
  .grid.mobile :deep(.control-cell .fs-right){
    font-size: 10px;
  }
  .grid.mobile .control-row--qty{
    height: 28px;
  }
  .grid.mobile .qty-step,
  .grid.mobile .qty-input{
    height: 28px;
    font-size: 11px;
  }
  .grid.mobile .tile-action{
    height: 34px;
  }
  .grid.mobile .tile-action :deep(svg){
    width: 15px;
    height: 15px;
  }
  .mbar{
    gap: 8px;
  }
  .mbar-row{
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .mbar-row--top .back-to-products{
    padding-inline: 10px;
  }
}

@media (min-width: 821px) and (max-width: 1180px){
  .tb{
    width: 100%;
    box-sizing: border-box;
    padding: 16px 18px 14px;
    border-bottom: 1px solid var(--line);
  }
  .tb-left, .tb-center, .tb-right{
    padding: 0;
  }
  .mhead{
    padding: 0;
    gap: 12px;
  }
  .mbar{
    gap: 10px;
  }
  .mbar-row{
    gap: 10px;
  }
  .msearch-inline .msearch-input{
    height: 38px;
  }
  .dd-btn--compact,
  .add-to-cart--compact,
  .back-to-products{
    height: 38px;
  }
}

@media (max-width: 640px){
  .mbar-row{
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .mbar-row--bottom{
    grid-template-columns: minmax(0, 1fr);
  }
  .grid.mobile{
    gap: 12px;
  }
  .grid.mobile .tile{
    min-width: 0;
  }
  .grid.mobile .tile-topbar{
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 34px;
  }
  .grid.mobile .preview{
    min-width: 0;
  }
  .grid.mobile .fit-preview-shell{
    padding: 6px;
  }
  .grid.mobile .fit-preview-frame{
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
  }
  .grid.mobile .tile-preview-active-shell{
    inset: 0;
  }
  .grid.mobile .tile-preview-active-image-canvas,
  .grid.mobile .tile-preview-static-image,
  .grid.mobile .tile-preview-active-shell :deep(.cc),
  .grid.mobile .tile-preview-active-shell :deep(.cc-canvas){
    inset: 0;
  }
  .grid.mobile .control-row--split{
    display: grid;
    grid-template-columns: 1fr;
  }
  .grid.mobile .control-row--split .control-cell{
    width: 100%;
  }
  .grid.mobile .control-row--split .control-cell + .control-cell{
    border-left: 0;
    border-top: 1px solid #d1d5db;
  }
  .grid.mobile .control-row--split .control-cell:first-child :deep(.fs-pop),
  .grid.mobile .control-row--split .control-cell:last-child :deep(.fs-pop){
    left: 0;
    right: 0;
    width: 100%;
  }
  .grid.mobile .control-row--qty{
    grid-template-columns: 34px minmax(0, 1fr) 34px;
  }
  .grid.mobile .qty-input{
    min-width: 0;
  }
  .grid.mobile .actions--three{
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .grid.mobile .actions--four{
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .grid.mobile .actions--five{
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

.back-to-products{
  display:inline-flex;
  align-items:center;
  gap:8px;
  height:36px;
  padding:0 10px;
  font-size:12px;
  font-weight:700;
  border:1px solid var(--line);
  background:#fff;
  color: rgba(15,23,42,.88);
  min-width: 0;
}
.back-to-products span{
  white-space:nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Product pill (desktop) */
.product-pill{
  display:inline-flex; align-items:center; gap:10px;
  padding:8px 10px; border:1px solid var(--line);
  background:#fff;
}
.product-pill .dot{ width:10px; height:10px; background:#22c55e; }
.product-text{ font-weight: 700; font-size:13px; color: rgba(15,23,42,.78); }

.sep{ width:1px; height:22px; background: var(--line); }
.count{ font-size:13px; color: var(--muted); }
.perf-pill{
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line);
  font-size: 13px;
  font-weight: 700;
  color: rgba(15,23,42,.8);
  background: #fff;
  white-space: nowrap;
}

/* Buttons (flat) */
.btn{
  height:36px;
  border:2px solid rgba(15,23,42,.16);
  background:#fff; padding:0 12px;
  cursor:pointer; font-weight: 700;
  color: rgba(15,23,42,.88);
  border-radius: 4px;
  box-shadow: none;
}
.btn:hover{ background:#fafafa; }
.btn:active{ transform: translateY(1px); }
.btn:disabled{ opacity:.45; cursor:not-allowed; box-shadow:none; }
.btn.ghost{ background:transparent; }
.btn.primary{ border-color: var(--accent, #e30b17); background:#fff; color: var(--accent, #e30b17); }
.btn.danger{ border-color: rgba(239,68,68,.42); color:#b91c1c; background: rgba(239,68,68,.06); }
.btn.mini{ height:34px; padding: 0 10px; }
.issue-filter-btn{
  color: #c2410c;
  border-color: rgba(249,115,22,.32);
  background: rgba(255,247,237,.96);
}
.issue-filter-btn:hover{
  background: rgba(255,237,213,.98);
  border-color: rgba(249,115,22,.48);
}
.issue-filter-btn[data-on='1']{
  color: #fff;
  background: #ea580c;
  border-color: #ea580c;
}
.issue-filter-btn[data-on='1']:hover{
  background: #c2410c;
  border-color: #c2410c;
}
.icon-only{ padding: 0 10px; display:inline-flex; align-items:center; justify-content:center; }
.icon-only :deep(svg){ display:block; }
.action-btn{
  display:inline-flex;
  align-items:center;
  gap:6px;
  white-space: nowrap;
  padding: 0 10px;
  font-weight: 700;
}
.action-btn span{
  font-size: 12px;
  line-height: 1;
}

/* Dropdowns */
.dd{ position: relative; }
.dd-btn{ width: 100%; justify-content: space-between; display:flex; align-items:center; gap:8px; }
.count-inline{
  display:inline-flex; align-items:center; justify-content:center;
  min-width: 18px; height:18px; padding:0 6px;
  border:1px solid var(--line);
  background:#fff;
  font-size: 12px;
  font-weight: 700;
  margin-left: 6px;
}
.chev{ opacity:.75; }

.dd-menu, .item-menu{
  position:absolute;
  right:0;
  top: calc(100% + 6px);
  z-index:500;
  min-width: 220px;
  background:#fff;
  border:1px solid var(--line);
  border-radius:0;
  padding: 6px;
}
.item-menu{ top: inherit; bottom: calc(100% + 6px); }
.menu-item{
  width:100%;
  text-align:left;
  height:38px;
  padding: 0 10px;
  border:1px solid transparent;
  background:#fff;
  border-radius:0;
  cursor:pointer;
  font-weight: 700;
  color: rgba(15,23,42,.88);
}
.menu-item:hover{ background:#fafafa; border-color: rgba(15,23,42,.10); }
.menu-item:disabled{ opacity:.45; cursor:not-allowed; }
.menu-item.danger{ color:#b91c1c; }
.menu-item--designer{ display:flex; align-items:center; gap:8px; color:#5b21b6; }
.menu-sep{ height:1px; background: rgba(15,23,42,.10); margin: 6px 0; }

.msearch-row{ display:flex; align-items:center; gap: 8px; padding: 6px; }
.msearch-ico{
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  opacity: .62;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  pointer-events: none;
}
.msearch-input{
  width: 100%;
  min-width: 0;
  height: 36px;
  border: 1px solid var(--line);
  outline: none;
  padding: 0 34px 0 36px;
  background: #fff;
  border-radius: 5px;
}
.msearch-inline .msearch-input{
  height: 34px;
  border: 0;
  padding: 0 34px 0 36px;
}
.msearch-inline .btn.mini.icon-only,
.search-inline .btn.mini.icon-only{
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  height: 26px;
  width: 26px;
  min-width: 26px;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(15,23,42,.55);
}
.msearch-inline .btn.mini.icon-only:hover,
.search-inline .btn.mini.icon-only:hover{
  background: rgba(15,23,42,.06);
}
.add-to-cart{
  white-space: nowrap;
  background: #e30b17;
  border-color: #e30b17;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0;
}
.add-to-cart:hover{
  background: #c80913;
  border-color: #c80913;
}
.cart-btn-main{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cart-btn-icon-wrap{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 0 0 auto;
}
.cart-btn-price{
  font-size: 14px;
  font-weight: 700;
  margin-left: 2px;
  padding-left: 12px;
  border-left: 1px solid rgba(255,255,255,.35);
  white-space: nowrap;
}
.msearch-input:focus{
  border-color: rgba(239,68,68,.55);
  box-shadow: 0 0 0 4px rgba(239,68,68,.10);
}

/* Cart badge */
.cart-btn{ position:relative; }
.badge{
  position:absolute;
  right:-6px;
  top:-6px;
  min-width:18px;
  height:18px;
  padding:0 6px;
  border:1px solid var(--line);
  background:#fff;
  color: rgba(15,23,42,.9);
  font-size:11px;
  font-weight: 700;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:0;
}

.add-to-cart > span:not(.cart-btn-main),
.add-to-cart > strong:not(.cart-btn-price){ margin-left: 8px; }

/* ========= GRID (AUTO RESPONSIVE) ========= */
.grid{
  --grid-cols: 4;
  display:grid;
  grid-template-columns: repeat(var(--grid-cols), minmax(0, 1fr));
  gap: 12px;
  padding: 12px;
  width:100%;
  align-content:start;
}
.grid.mobile{
  gap: 8px;
  padding: 20px;
}

/* ========= TILES ========= */
.tile{
  position: relative;
  border:1px solid var(--line);
  background:#fff;
  border-radius: 4px;
  box-shadow: none;
  display:flex;
  flex-direction:column;
}
.tile.selected{
  outline: none;
  box-shadow: none;
}
.tile.selected::after{
  content: "";
  position: absolute;
  inset: 0;
  border: 2px solid rgba(239,68,68,.65);
  border-radius: 4px;
  pointer-events: none;
  z-index: 25;
}

/* ========= MODERN UPLOAD ZONE ========= */
.global-upload-zone {
  position: relative;
  margin: 16px;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 10px;
  background: #fafafa;
  overflow: hidden;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
  user-select: none;
}
.global-upload-zone:hover,
.global-upload-zone.drag {
  border-color: rgba(15,23,42,.18);
  background: #f8f8f8;
  box-shadow: inset 0 0 0 1px rgba(15,23,42,.03);
}
.global-upload-zone.drag {
  border-color: var(--accent, #e30b17);
  border-style: dashed;
  background: linear-gradient(180deg, rgba(227,11,23,.08), rgba(255,245,245,.98));
  box-shadow:
    inset 0 0 0 1px rgba(227,11,23,.16),
    0 0 0 3px rgba(227,11,23,.08);
}
.upload-zone-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px 18px 16px;
  cursor: pointer;
  margin: 8px;
  border: 1px dashed rgba(15,23,42,.18);
  border-radius: 8px;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}
.global-upload-zone:hover .upload-zone-inner {
  border-color: rgba(15,23,42,.3);
  background: rgba(15,23,42,.02);
}
.upload-zone-inner.drag {
  border-color: var(--accent, #e30b17);
  background: rgba(227,11,23,.04);
  box-shadow: inset 0 0 0 1px rgba(227,11,23,.08);
}
.upload-source-switch{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
  padding:10px 10px 0;
}
.upload-source-btn{
  min-height:34px;
  border:2px solid rgba(15,23,42,.16);
  border-radius:4px;
  background:#fff;
  color:#18212f;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  font-size:12px;
  font-weight:700;
}
.upload-source-btn[data-on='1']{
  color: rgba(15,23,42,.88);
  border-color: rgba(15,23,42,.54);
  background: rgba(15,23,42,.06);
}
.upload-zone-icon {
  color: var(--accent, #e30b17);
  margin-bottom: 0;
  transition: color .15s;
}
.global-upload-zone:hover .upload-zone-icon,
.global-upload-zone.drag .upload-zone-icon {
  color: var(--accent, #e30b17);
}
.global-upload-zone.drag .upload-zone-icon {
  color: var(--accent, #e30b17);
}
.global-upload-zone.drag .upload-zone-title {
  color: var(--accent, #e30b17);
}
.global-upload-zone.drag .upload-zone-sub {
  color: rgba(227,11,23,.78);
}
.upload-zone-title {
  font-size: 14px;
  font-weight: 700;
  color: rgba(15,23,42,.88);
}
.upload-zone-sub {
  font-size: 12px;
  color: rgba(15,23,42,.52);
}
.upload-zone-hint {
  font-size: 11px;
  color: rgba(15,23,42,.38);
  margin-top: 2px;
}
.upload-phone-box{
  display:grid;
  grid-template-columns:112px minmax(0,1fr);
  gap:12px;
  align-items:center;
  padding:12px;
}
.upload-phone-qr{
  min-height:112px;
  border:1px solid rgba(15,23,42,.12);
  border-radius:4px;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
}
.upload-phone-qr img{
  width:96px;
  height:96px;
  display:block;
}
.upload-phone-qrph{
  padding:12px;
  text-align:center;
  font-size:12px;
  color: rgba(15,23,42,.56);
}
.upload-phone-head{
  display:flex;
  gap:8px;
  align-items:flex-start;
}
.upload-phone-title{
  font-size:14px;
  font-weight:700;
  color:#141414;
}
.upload-phone-sub{
  margin-top:2px;
  font-size:12px;
  line-height:1.4;
  color: rgba(15,23,42,.64);
}
.upload-phone-actions{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
}
/* Hide the UploadBox UI — we use our own visuals; only the file input matters */
/* Note: class is added to the root .ub element, so target it directly */
.upload-zone-hidden {
  position: absolute !important;
  left: -9999px !important;
  top: -9999px !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
.upload-queue-widget{
  position: fixed;
  z-index: 120;
  width: min(380px, calc(100vw - 28px));
  max-height: min(88vh, 720px);
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 14px;
  background: rgba(255,255,255,.92);
  box-shadow: 0 24px 60px rgba(15,23,42,.18), 0 4px 18px rgba(15,23,42,.08);
  backdrop-filter: blur(16px) saturate(1.15);
  touch-action: none;
  overflow: hidden auto;
}
.upload-queue-widget__head,
.upload-queue-widget__sub{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  flex-wrap:wrap;
}
.upload-queue-widget__head{
  cursor: grab;
  user-select: none;
  padding-bottom: 2px;
}
.upload-queue-widget.is-dragging .upload-queue-widget__head{
  cursor: grabbing;
}
.upload-queue-widget__head-actions{
  display:flex;
  align-items:center;
  gap:8px;
  margin-left:auto;
}
.upload-queue-widget__toggle{
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(15,23,42,.1);
  border-radius: 999px;
  background: rgba(15,23,42,.04);
  color: rgba(15,23,42,.76);
  font-size: 11px;
  font-weight: 700;
}
.upload-queue-widget__head strong{
  font-size: 14px;
  font-weight: 700;
  color: rgba(15,23,42,.92);
}
.upload-queue-widget__sub{
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:8px;
  font-size: 12px;
  color: rgba(15,23,42,.62);
}
.upload-queue-widget__progress{
  min-height: 24px;
  min-width: 58px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(15,23,42,.045);
  white-space: nowrap;
  font-weight: 700;
  color: rgba(15,23,42,.76);
}
.upload-queue-widget__track{
  height: 9px;
  background: rgba(15,23,42,.07);
  overflow: hidden;
  border-radius: 999px;
}
.upload-queue-widget__fill{
  display:block;
  height:100%;
  width:0;
  background: linear-gradient(90deg, #e30b17 0%, #ff6b57 100%);
  transition: width .22s ease;
}
.upload-queue-widget[data-busy='1'] .upload-queue-widget__fill{
  animation: upload-queue-pulse 1.4s ease-in-out infinite;
}
.upload-queue-widget__hint{
  font-size: 12px;
  line-height: 1.45;
  color: rgba(15,23,42,.56);
  padding: 2px 2px 0;
}
.upload-queue-widget__failed{
  display:grid;
  gap:8px;
  padding-top:10px;
  border-top:1px solid rgba(15,23,42,.08);
}
.upload-queue-widget__failed-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.upload-queue-widget__failed-head strong{
  font-size:12px;
  font-weight:700;
  color: rgba(15,23,42,.82);
}
.upload-queue-widget__link{
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(15,23,42,.1);
  border-radius: 999px;
  background: rgba(15,23,42,.03);
  color: rgba(15,23,42,.72);
  font-size: 11px;
  font-weight: 700;
}
.upload-queue-widget__failed-item{
  display:grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap:10px;
  align-items:center;
  padding:10px 12px;
  border:1px solid rgba(217,119,6,.14);
  border-radius:12px;
  background: linear-gradient(180deg, rgba(245,158,11,.08), rgba(245,158,11,.05));
}
.upload-queue-widget__failed-copy{
  min-width:0;
  display:grid;
  gap:3px;
}
.upload-queue-widget__failed-copy strong{
  font-size:12px;
  font-weight:700;
  color: rgba(15,23,42,.84);
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.upload-queue-widget__failed-copy span{
  font-size:11px;
  line-height:1.35;
  color: rgba(15,23,42,.62);
}
.upload-queue-widget__failed-actions{
  display:flex;
  align-items:center;
  gap:6px;
}
.upload-queue-widget__action{
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(15,23,42,.1);
  border-radius: 999px;
  background: #fff;
  color: rgba(15,23,42,.76);
  font-size: 11px;
  font-weight: 700;
}
.upload-queue-widget__action.danger{
  color: #b42318;
  border-color: rgba(180,35,24,.18);
  background: rgba(180,35,24,.04);
}
.upload-queue-widget__mini{
  display:flex;
  align-items:center;
  justify-content:flex-start;
  gap:10px;
  font-size:12px;
  color: rgba(15,23,42,.62);
}
.upload-queue-widget__mini-label{
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.mini-dialog-backdrop{
  position: fixed;
  inset: 0;
  z-index: 240;
  background: rgba(15,23,42,.34);
  display: grid;
  place-items: center;
  padding: 16px;
}
.mini-dialog{
  width: min(520px, 100%);
  background: #fff;
  border: 1px solid rgba(15,23,42,.1);
  border-radius: 6px;
  box-shadow: 0 26px 60px rgba(15,23,42,.22);
  display: grid;
  gap: 0;
  overflow: hidden;
}
.mini-dialog__head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}
.mini-dialog__head strong{
  font-size: 18px;
  line-height: 22px;
  color: var(--accent, #e30b17);
}
.mini-dialog__close{
  width: 34px;
  height: 34px;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 4px;
  background: #fff;
  color: rgba(15,23,42,.8);
}
.mini-dialog__body{
  padding: 18px;
  display:grid;
  gap: 10px;
}
.mini-dialog__progress{
  display:grid;
  gap:8px;
}
.mini-dialog__progress-meta{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  font-size:12px;
  line-height:16px;
  color: rgba(15,23,42,.62);
}
.mini-dialog__progress-track{
  height:8px;
  background: rgba(15,23,42,.08);
  overflow:hidden;
  border-radius:999px;
}
.mini-dialog__progress-fill{
  display:block;
  height:100%;
  width:0;
  background: linear-gradient(90deg, #e30b17 0%, #ff6b57 100%);
}
.mini-dialog__progress-fill--indeterminate{
  width: 42%;
  animation: mini-dialog-indeterminate 1.1s ease-in-out infinite;
}
.mini-dialog__body p{
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: rgba(15,23,42,.72);
}
.mini-dialog__foot{
  padding: 0 18px 18px;
  display:flex;
  justify-content:flex-end;
  gap:10px;
}
.mini-dialog__foot .btn{
  height: 40px;
  padding: 0 14px;
  font-size: 14px;
  line-height: 14px;
  font-weight: 700;
  border-radius: 3px;
}
@keyframes upload-queue-pulse{
  0%,100%{ filter:saturate(1); }
  50%{ filter:saturate(1.25) brightness(1.05); }
}
@keyframes mini-dialog-indeterminate{
  0%{ transform: translateX(-35%); }
  50%{ transform: translateX(120%); }
  100%{ transform: translateX(-35%); }
}
@media (max-width: 640px){
  .upload-phone-box{
    grid-template-columns:1fr;
  }
  .upload-queue-widget{
    right: 12px;
    left: 12px;
    bottom: 12px;
    width: auto;
  }
}

.hint{ margin-top:10px; font-size:13px; color: var(--muted); }
.subhint{ margin-top:6px; font-size:13px; color: var(--muted2); }

/* Produkt-Gruppen */
.product-root-label {
  margin: 30px 14px 10px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.product-root-label__text {
  flex: 0 0 auto;
  color: rgba(15,23,42,.76);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.product-root-label__line {
  flex: 1 1 auto;
  height: 1px;
  background: rgba(15,23,42,.14);
}
.product-root-label__toggle {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 0;
  color: rgba(15,23,42,.52);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .02em;
  cursor: pointer;
}
.product-root-label__toggle:hover {
  color: rgba(15,23,42,.78);
}
.product-group {
  margin: 8px 14px 10px;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 8px;
  background: #fff;
}
.product-group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f8f8;
  border-bottom: 1px solid rgba(15,23,42,.08);
  min-width: 0;
}
.group-collapse-btn {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: rgba(15,23,42,.5);
  padding: 0;
  border-radius: 4px;
  &:hover { background: rgba(15,23,42,.07); }
}
.group-collapse-chevron {
  transition: transform .18s ease;
}
.group-collapse-chevron.collapsed {
  transform: rotate(-90deg);
}
.group-thumb {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border: 1px solid rgba(15,23,42,.12);
  flex: 0 0 auto;
  border-radius: 2px;
}
.product-group-title {
  font-size: 13px;
  font-weight: 700;
  color: rgba(15,23,42,.88);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-group-count {
  font-size: 11px;
  color: rgba(15,23,42,.5);
  background: rgba(15,23,42,.06);
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
  flex: 0 0 auto;
}
.group-select-wrap,
/* Upload-Icon pro Gruppe */
.group-upload-wrap {
  position: relative;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(15,23,42,.14);
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  &:hover { background: rgba(15,23,42,.05); }
}
.group-select-icon,
.group-upload-icon {
  pointer-events: none;
  color: rgba(15,23,42,.6);
}
.group-select-btn,
.group-upload-btn{
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.has-badge{ position: relative; }

.editor-loading-shell{
  margin: 16px;
  display: grid;
  gap: 22px;
}
.editor-loading-shell__head{
  display: grid;
  gap: 6px;
}
.editor-loading-group{
  display:grid;
  gap: 12px;
}
.product-group-header--loading{
  cursor: default;
}
.editor-loading-group__title{
  display:block;
  flex: 0 0 160px;
  max-width: 38%;
  height: 14px;
  border-radius: 999px;
  background: rgba(203,213,225,.68);
}
.editor-loading-group__count{
  display:block;
  width: 34px;
  height: 18px;
  border-radius: 999px;
  background: rgba(226,232,240,.95);
}
.group-collapse-btn--loading{
  pointer-events: none;
  opacity: .5;
}
.editor-loading-grid{
  opacity: .82;
}
.editor-loading-tile{
  min-height: 280px;
  border: 1px solid #d1d5db;
  border-radius: 0;
  background: linear-gradient(180deg, rgba(241,245,249,.92), rgba(226,232,240,.86));
}
.in-cart-badge{
  position: absolute;
  right: -6px;
  top: -6px;

  min-width: 18px;
  height: 18px;
  padding: 0 6px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 11px;
  font-weight: 700;

  background: #22c55e;         /* grün */
  color: #fff;
  border: 1px solid rgba(0,0,0,.10);
  border-radius: 999px;
  box-shadow: 0 6px 14px rgba(34,197,94,.28);
}

/* Preview */
.preview{
  position:relative;
  aspect-ratio: 1 / 1;
  height: auto;
  background: #e5e7eb;
  overflow: visible;
}
.preview--uploading{
  opacity: .52;
}
.tile-preview-loading{
  position: absolute;
  inset: 0;
  z-index: 4;
  display: grid;
  place-items: stretch;
  background: linear-gradient(180deg, rgba(248,250,252,.96), rgba(241,245,249,.92));
  pointer-events: none;
}
.tile-preview-pending{
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}
.tile-skeleton{
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 14px;
  overflow: hidden;
}
.tile-skeleton::after{
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.55) 50%, rgba(255,255,255,0) 100%);
  animation: tile-skeleton-shimmer 1.25s ease-in-out infinite;
}
.tile-skeleton__media{
  border-radius: 4px;
  background: linear-gradient(180deg, rgba(203,213,225,.72), rgba(226,232,240,.9));
  box-shadow: inset 0 0 0 1px rgba(148,163,184,.16);
}
.tile-skeleton__lines{
  display: grid;
  gap: 8px;
  align-content: end;
}
.tile-skeleton__line{
  display: block;
  height: 9px;
  border-radius: 999px;
  background: rgba(148,163,184,.42);
}
.tile-skeleton__line--lg{ width: 78%; }
.tile-skeleton__line--md{ width: 56%; }
.tile-skeleton__line--sm{ width: 34%; }
@keyframes tile-skeleton-shimmer{
  100%{ transform: translateX(100%); }
}
.fit-preview-shell{
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #d1d5db;
}
.fit-preview-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.78), rgba(209,213,219,.92));
}
.fit-preview-frame{
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(15,23,42,.08);
}
.fit-preview-shell--fs{
  position: relative;
  flex: 1 1 auto;
  padding: 18px;
  background: #d1d5db;
}
.fit-preview-frame--fs{
  width: min(100%, 1000px);
  height: auto;
}
/* Große Vorschau, Fit-In + weißer Rand (nur Vorschau, nicht maßstabsgetreu) */
.fit-preview-frame--fs.fit-preview-frame--white-border{
  padding: 12px;
  box-sizing: border-box;
}
.white-border-preview--fs{
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 0 0 0 10px #fff, inset 0 0 0 11px rgba(15,23,42,.18);
}
.fit-preview-image{
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.fit-preview-image--pending,
.inactive-crop-image--pending{
  opacity: .58;
}
.tile-preview-lite-shell{
  position: absolute;
  inset: 0;
  background: #d1d5db;
  overflow: hidden;
}
.tile-preview-lite-base{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  opacity: .82;
  filter: saturate(.92);
}
.tile-preview-lite-base--pending{
  opacity: .52;
}
.tile-preview-lite-window{
  position: absolute;
  overflow: hidden;
  border: 1.5px solid rgba(255,255,255,.96);
  box-shadow:
    0 0 0 999px rgba(10,14,24,.38),
    0 0 0 1px rgba(15,23,42,.16) inset;
  border-radius: 2px;
}
.tile-preview-lite-window--pending{
  border-color: rgba(255,255,255,.82);
  box-shadow:
    0 0 0 999px rgba(10,14,24,.24),
    0 0 0 1px rgba(15,23,42,.12) inset;
}
.tile-preview-lite-handle{
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: rgba(255,255,255,.98);
  border: 1px solid rgba(15,23,42,.28);
  box-shadow: 0 1px 3px rgba(15,23,42,.18);
}
.tile-preview-lite-handle--tl{ left: -5px; top: -5px; }
.tile-preview-lite-handle--tr{ right: -5px; top: -5px; }
.tile-preview-lite-handle--br{ right: -5px; bottom: -5px; }
.tile-preview-lite-handle--bl{ left: -5px; bottom: -5px; }
.tile-preview-active-shell{
  position: absolute;
  inset: 0;
  background: #d1d5db;
}
.tile-preview-active-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.72), rgba(209,213,219,.9));
}
.inactive-crop-shell{
  position: absolute;
  inset: 0;
  display: block;
  background: #d1d5db;
  overflow: hidden;
}
.inactive-crop-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.78), rgba(209,213,219,.92));
}
.inactive-crop-image{
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  user-select: none;
  pointer-events: none;
}
.tile-uploading-overlay{
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.tile-uploading-overlay__label{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(15,23,42,.66);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .01em;
  backdrop-filter: blur(4px);
}
.tile-preview-static-image{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.tile-preview-active-image-canvas{
  position: absolute;
  inset: 0;
  z-index: 1;
}
.tile-preview-active-shell--pending .tile-preview-active-image-canvas{
  opacity: .52;
}
.tile-preview-active-shell :deep(.cc),
.tile-preview-active-shell :deep(.cc-canvas){
  position: absolute;
  inset: 0;
}
.tile-preview-active-shell .cropper{
  z-index: 2;
}
.tile-preview-active-shell .fs-cropper{
  z-index: 2;
}
.tile-preview-active-shell .cropper--passive{
  pointer-events: none;
}
.tile-preview-active-shell--fs{
  position: relative;
  flex: 1 1 auto;
}
.tile.skeleton{
  pointer-events: none;
}
.tile.skeleton .preview{
  aspect-ratio: 1 / 1;
}
.cropper{ width:100%; height:100%; }
.preview :deep(.vue-advanced-cropper__image){ object-fit: contain !important; }

/* Tile top controls */
.tile-topbar{
  display:flex; align-items: center;
  align-items:center;
  height:34px;
  padding: 0;
  background:#f3f4f6;
  border-bottom:1px solid #d1d5db;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.tile-title{
  flex: 1 1 auto; min-width: 0;
  text-align:center;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: rgba(15,23,42,.9);
  overflow:hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
  text-overflow: ellipsis;
  max-height: calc(2 * 1.2em);
  padding: 0px 4px;
}

/* 3D-Preview overlay */
.preview3d-backdrop {
  position: fixed; inset: 0; z-index: 11000;
  background: rgba(0,0,0,.7);
  display: flex; align-items: center; justify-content: center;
  padding: 2.5vh 2.5vw;
}
.preview3d-panel {
  position: relative;
  width: 95vw;
  height: 95vh;
  max-width: 95vw;
  max-height: 95vh;
  background: #1a1a1a;
  border-radius: 10px; overflow: hidden;
}
.preview3d-close {
  position: absolute; top: 12px; right: 12px; z-index: 10;
  width: 32px; height: 32px; border-radius: 50%;
  border: 1px solid rgba(255,255,255,.2);
  background: rgba(0,0,0,.4); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(0,0,0,.6); }
}
.preview3d-panel :deep(.preview-wrapper) {
  width: 100%; height: 100%;
}
@media (max-width: 640px){
  .preview3d-backdrop{
    padding: 10px;
  }
  .preview3d-panel{
    width: calc(100vw - 20px);
    height: calc(100dvh - 20px);
    max-width: calc(100vw - 20px);
    max-height: calc(100dvh - 20px);
    border-radius: 6px;
  }
}

/* Checkbox */
.check{
  flex-shrink: 0;
  width:44px;
  height:34px;
  border-left:1px solid #d1d5db;
  border-top:0;
  border-right:0;
  border-bottom:0;
  background:transparent;
  padding:0;
  cursor:pointer;
  z-index:20;
}
.delete-chip{
  flex-shrink: 0;
  width:44px;
  height:34px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border:0;
  border-right:1px solid #d1d5db;
  background: transparent;
  color:#6b7280;
  cursor:pointer;
}
.delete-chip:hover{ background:#fff; color:#374151; }
.delete-chip:active{ transform: translateY(1px); }
.box{
  width:16px; height:16px;
  border:1px solid rgba(15,23,42,.25);
  background: rgba(255,255,255,.92);
  display:inline-flex; align-items:center; justify-content:center;
}
.tick{ opacity:0; font-size:10px; transform: translateY(-1px); }
.box.on{ background: var(--accent); border-color: var(--accent); }
.box.on .tick{ opacity:1; color:#fff; }

/* Meta */
.meta{
  padding: 0;
  display:flex;
  flex-direction:column;
  gap: 0;
}
.grid.mobile .meta{ padding: 0; gap: 0; }

.erow{ display:flex; align-items:center; gap:10px; min-width:0; }
.spacer{ flex:1 1 auto; }

.topline{ justify-content:space-between; gap:10px; }
.name{
  flex:1 1 auto;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  font-size:14px;
  font-weight: 700;
  color: rgba(15,23,42,.90);
}
.grid.mobile .name{ font-size:12px; }

.price-pill{
  font-size:13px;
  font-weight: 700;
  color: rgba(15,23,42,.90);
  padding: 5px 8px;
  border: 1px solid rgba(15,23,42,.12);
  background: rgba(15,23,42,.03);
  white-space: nowrap;
  border-radius: 0;
}

/* Desktop size/qty */
.sizeline{
  display:block;
  width:100%;
  margin: 0;
}

.control-stack{
  width:100%;
  border-top: 1px solid #d1d5db;
}
.control-row{
  display:flex;
  align-items:stretch;
  width:100%;
  border-bottom:1px solid #d1d5db;
}
.control-row--split .control-cell{
  width: 50%;
}
.control-row--split .control-cell + .control-cell{
  border-left:1px solid #d1d5db;
}
/* Tile mit offenem Dropdown nach vorne holen */
.tile:has(:deep(.fs.open)) { z-index: 10; }
/* Popup in split-row spans full tile width */
.control-row--split .control-cell:first-child :deep(.fs-pop){
  left: 0;
  right: auto;
  width: 200%;
}
.control-row--split .control-cell:last-child :deep(.fs-pop){
  right: 0;
  left: auto;
  width: 200%;
}
.control-cell{
  min-height:36px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  background:#f9fafb;
  min-width: 0;
}
.control-cell--full{
  width:100%;
}
.control-cell--plain{
  border:0;
  width:100%;
  padding: 0 16px;
  font-size: 12px;
  color: rgba(15,23,42,.92);
  font-weight: 500;
}
.control-cell--plain:disabled{
  opacity: 1;
}

.control-row--qty{
  background:#f9fafb;
  height:32px;
  display:grid;
  grid-template-columns: 1fr 2fr 1fr;
}
.qty-step{
  width:100%;
  height:32px;
  border:0;
  background:#fff;
  color: rgba(15,23,42,.78);
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
}
.qty-step + .qty-input{
  border-left:1px solid #d1d5db;
}
.qty-input + .qty-step{
  border-left:1px solid #d1d5db;
}
.qty-input{
  width:100%;
  min-width:0;
  border:0;
  height:32px;
  background:#fff;
  text-align:center;
  font-size:12px;
  font-weight:500;
  line-height:1.2;
  color: rgba(15,23,42,.92);
  appearance:textfield;
  -moz-appearance:textfield;
}
.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button{
  -webkit-appearance:none;
  margin:0;
}

.field{ display:flex; flex-direction:column; gap:6px; min-width:0; }
.field span{ font-size:12px; color: var(--muted); font-weight: 700; }

.qty{ display:flex; flex-direction:column; gap:6px; align-items:flex-end; }
.qtybox{
  display:inline-flex; align-items:center; gap:6px;
  border:1px solid var(--line);
  background:#fff;
  height: 36px;
  padding: 0 10px;
  border-radius: 0;
}
.qtybox input{
  width: 38px;
  height: 100%;
  border:none; outline:none; background:transparent;
  font-weight: 700;
  color: rgba(15,23,42,.88);
  text-align: center;
  appearance:textfield;
  -moz-appearance:textfield;
}
.qtybox input::-webkit-outer-spin-button,
.qtybox input::-webkit-inner-spin-button{
  -webkit-appearance:none;
  margin:0;
}

:deep(.size-dd .cs-trigger){
  height: 36px;
  border: 0 !important;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 0 10px;
}
:deep(.size-dd .cs-label){
  font-size: 12px;
  font-weight: 500;
}
:deep(.control-cell .fs-trigger){
  height: 34px;
  padding: 0 10px;
  gap: 6px;
}
:deep(.control-cell .fs-label){ font-size: 12px; }
:deep(.control-cell .fs-right){ font-size: 11px; }
:deep(.control-cell .fs-caret){ font-size: 10px; }
:deep(.control-cell .fs-pop .fs-opt){
  height: 34px;
  padding: 0 8px;
  gap: 8px;
}
:deep(.control-cell .fs-pop .fs-opt .left){ font-size: 12px; }
:deep(.control-cell .fs-pop .fs-opt .right){ font-size: 11px; }

/* Mobile: size dropdown constraints */
.size-dd-mobile{ max-width: 100%; }
:deep(.size-dd-mobile select),
:deep(.size-dd-mobile .trigger),
:deep(.size-dd-mobile button){
  max-width: 100%;
  width: 100%;
  font-size: 12px;
}

/* Pills/meta */
.pills{
  display:flex;
  flex-wrap:wrap;
  gap:0;
  row-gap: 3px;
  margin-top: 2px;
}
.pill{
  font-size:12px;
  font-weight: 700;
  color: rgba(15,23,42,.62);
  padding:0;
  border:0;
  background:transparent;
  border-radius:0;
  line-height:1.25;
}
.pill.muted{ color: rgba(15,23,42,.45); }
.pill + .pill::before{
  content:"•";
  margin:0 6px;
  color: rgba(15,23,42,.24);
}

/* Mobile bottom row */
.m-bottom{
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap: 10px;
  padding-top: 2px;
}
.m-qty{ display:flex; flex-direction:column; gap:6px; min-width: 0; }

/* Desktop actions */
.actions{
  display: grid;
  gap: 0;
  padding: 0;
  background: #f3f4f6;
  border-top: 1px solid #d1d5db;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.actions--four{
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.actions--five{
  grid-template-columns: repeat(5, minmax(0, 1fr));
}
.tile-action{
  width: 100%;
  height: 52px;
  background: transparent;
  border: 0;
  padding: 0;
  color:#374151;
  box-shadow: none;
  border-radius: 0;
  min-width: 0;
}
.tile-action + .tile-action{
  border-left: 1px solid #d1d5db;
}
.tile-quality{
  width: 12px;
  height: 12px;
  margin: 0 6px 0 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  position: relative;
  cursor: help;
  flex: 0 0 auto;
}
.tile-quality:focus-visible{
  outline: 2px solid rgba(37,99,235,.35);
  outline-offset: 3px;
  border-radius: 999px;
}
.tile-quality__dot{
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(148,163,184,.32);
  box-shadow: inset 0 0 0 1px rgba(15,23,42,.08);
}
.tile-quality-popover{
  position: fixed;
  z-index: 10040;
  width: max-content;
  max-width: min(220px, calc(100vw - 24px));
  padding: 6px 8px;
  border-radius: 5px;
  background: rgba(15,23,42,.94);
  color: #fff;
  font-size: 10px;
  line-height: 1.3;
  font-weight: 500;
  white-space: pre-line;
  box-shadow: 0 8px 20px rgba(15,23,42,.18);
  pointer-events: none;
  transform: translateY(-100%);
}
.tile-quality[data-tone='great'] .tile-quality__dot{ background: #16a34a; }
.tile-quality[data-tone='good'] .tile-quality__dot{ background: #65a30d; }
.tile-quality[data-tone='warn'] .tile-quality__dot{ background: #eab308; }
.tile-quality[data-tone='bad'] .tile-quality__dot{ background: #f59e0b; }
.mini-link{
  border:none; background:transparent; padding:0;
  color: var(--accent);
  font-weight: 700; cursor:pointer;
}
.mini-link:hover{ text-decoration: underline; }

/* Cropper optic closer to provided reference */
.mi :deep(.vue-advanced-cropper__background){
  background: #1f2937 !important;
  opacity: 1 !important;
}
.mi :deep(.vue-advanced-cropper__foreground){
  background: #000 !important;
  opacity: 0.46 !important;
}
.mi :deep(.vue-rectangle-stencil__preview){
  border: 2px solid #fff !important;
  box-shadow: none !important;
}
.mi :deep(.vue-simple-handler){
  width: 10px !important;
  height: 10px !important;
  border-radius: 2px !important;
  border: 0 !important;
  box-shadow: none !important;
  background: #fff !important;
}
.mi :deep(.vue-simple-handler--north),
.mi :deep(.vue-simple-handler--south),
.mi :deep(.vue-simple-handler--east),
.mi :deep(.vue-simple-handler--west){
  display: none !important;
}
.mi :deep(.vue-simple-handler--north-west),
.mi :deep(.vue-simple-handler--north-east),
.mi :deep(.vue-simple-handler--south-west),
.mi :deep(.vue-simple-handler--south-east),
.mi :deep(.vue-simple-handler--west-north),
.mi :deep(.vue-simple-handler--east-north),
.mi :deep(.vue-simple-handler--west-south),
.mi :deep(.vue-simple-handler--east-south){
  display: block !important;
}
.mi :deep(.vue-line){
  background: rgba(255,255,255,1) !important;
  opacity: 1 !important;
}

/* Fullscreen */
.fs-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,.45);
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 16px;
}
.fs-panel{
  width: min(1100px, 96vw);
  height: min(800px, 92vh);
  min-height: 0;
  background: #fff;
  border-radius: 0;
  border: 1px solid rgba(15,23,42,.12);
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.fs-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(15,23,42,.12);
}
.fs-title{ display:flex; flex-direction:column; gap:2px; min-width:0; }
.fs-sub{ font-size: 12px; color: rgba(15,23,42,.55); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.fs-bench{ font-size: 11px; color: rgba(15,23,42,.7); }
.fs-x{
  border:1px solid rgba(15,23,42,.12);
  background: rgba(15,23,42,.03);
  border-radius:0;
  height:36px;
  padding:0 12px;
  cursor:pointer;
}
.fs-body{
  flex: 1 1 auto;
  min-height: 0;
  background: var(--soft);
  display:flex;
  padding: 14px;
}
.fs-stage{
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background: var(--soft);
  display:flex;
  border: 1px solid rgba(15,23,42,.12);
}
.fs-stage :deep(.vue-advanced-cropper){ width: 100%; height: 100%; }
.fs-stage{ position: relative; }
.fs-nav{
  position: absolute; top: 50%; z-index: 5; transform: translateY(-50%);
  width: 44px; height: 64px; display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(15,23,42,.14); background: rgba(255,255,255,.92); color: #0f172a;
  cursor: pointer; box-shadow: 0 4px 14px rgba(15,23,42,.12); transition: background .15s ease, opacity .15s ease;
}
.fs-nav:hover:not(:disabled){ background: #fff; }
.fs-nav:disabled{ opacity: .3; cursor: default; }
.fs-nav:focus-visible{ outline: 2px solid var(--accent, #e30b17); outline-offset: 2px; }
.fs-nav--prev{ left: 10px; }
.fs-nav--next{ right: 10px; }
.fs-count{ margin-left: 10px; font-size: 12px; font-weight: 600; color: rgba(15,23,42,.55); }
.fs-stage :deep(.vue-advanced-cropper__image){ object-fit: contain !important; }
.fs-foot{
  display:flex;
  align-items:center;
  gap:10px;
  padding: 12px 14px;
  border-top: 1px solid rgba(15,23,42,.12);
}
.fs-foot__btn{
  min-height: 36px;
  padding: 0 14px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Per-tile kebab menu (mobile) */
.item-menu-container{ position: relative; flex: 0 0 auto; }
.item-menu-btn{ height: 34px; padding: 0 10px; }
.item-menu{ min-width: 200px; }

/* Reduce motion */
@media (prefers-reduced-motion: reduce) {
  .btn:active{ transform: none; }
}


/* -------------------------
 * Locked (foreign product)
 * ------------------------- */
.tile.locked .mi-cropper {
  pointer-events: none;
  opacity: 0.95;
  filter: grayscale(0.25);
}

.tile.locked .meta {
  opacity: 0.78;
}

.preview {
  position: relative;
}

.locked-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  padding: 10px;
  pointer-events: none;
  background: linear-gradient(to top, rgba(2, 6, 23, 0.55), rgba(2, 6, 23, 0.0) 62%);
  color: #fff;
}

.busy-overlay{
  position: fixed;
  inset: 0;
  z-index: 1600;
  background: rgba(255,255,255,.68);
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
}
.busy-card{
  display:flex;
  align-items:center;
  gap:12px;
  padding: 16px 20px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  box-shadow: 0 18px 40px rgba(15,23,42,.12);
  color:#141414;
  font-size: 15px;
}
.busy-spinner{
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(15,23,42,.16);
  border-top-color: #e30b17;
  animation: busy-spin .8s linear infinite;
}
.cart-btn-spinner{
  width: 16px;
  height: 16px;
  border-color: rgba(255,255,255,.28);
  border-top-color: #fff;
}
@keyframes busy-spin{
  to { transform: rotate(360deg); }
}
.uprompt-backdrop{
  position: fixed;
  inset: 0;
  z-index: 1700;
  background: rgba(15,23,42,.42);
  display: grid;
  place-items: center;
  padding: 16px;
}
.uprompt-panel{
  width: min(420px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid rgba(15,23,42,.10);
  box-shadow: 0 22px 70px rgba(2,6,23,.22);
  border-radius: 4px;
  padding: 18px;
  display: grid;
  gap: 8px;
}
.uprompt-title{
  font-size: 16px;
  font-weight: 700;
  color: #141414;
}
.uprompt-text{
  font-size: 14px;
  color: rgba(15,23,42,.64);
}
.uprompt-switch{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
  margin-top: 2px;
}
.uprompt-source{
  min-height:34px;
  border:2px solid rgba(15,23,42,.16);
  border-radius:4px;
  background:#fff;
  color:#18212f;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  font-size:12px;
  font-weight:700;
}
.uprompt-source[data-on='1']{
  color: var(--accent, #e30b17);
  border-color: var(--accent, #e30b17);
}
.uprompt-dropzone{
  border:2px dashed rgba(15,23,42,.18);
  border-radius:4px;
  background:#fafafa;
  display:grid;
  place-items:center;
  gap:4px;
  padding:18px 14px;
  text-align:center;
  cursor:pointer;
  transition:border-color .15s ease, background .15s ease;
}
.uprompt-dropzone:hover{
  border-color:rgba(15,23,42,.28);
  background:#f8f8f8;
}
.uprompt-dropzone__icon{
  color:var(--accent, #e30b17);
}
.uprompt-dropzone__title{
  font-size:14px;
  font-weight:700;
  color:#141414;
}
.uprompt-dropzone__sub{
  font-size:12px;
  color:rgba(15,23,42,.62);
}
.uprompt-dropzone__hint{
  font-size:11px;
  color:rgba(15,23,42,.42);
}
.uprompt-phone{
  display:grid;
  grid-template-columns:108px minmax(0,1fr);
  gap:10px;
  align-items:center;
  margin-top: 2px;
}
.uprompt-phone-qr{
  min-height:108px;
  border:1px solid rgba(15,23,42,.12);
  border-radius:8px;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
}
.uprompt-phone-qr img{
  width:92px;
  height:92px;
  display:block;
}
.uprompt-phone-qrph{
  padding:12px;
  text-align:center;
  font-size:12px;
  color: rgba(15,23,42,.56);
}
.uprompt-phone-head{
  display:flex;
  gap:8px;
  align-items:flex-start;
}
.uprompt-phone-title{
  font-size:14px;
  font-weight:700;
  color:#141414;
}
.uprompt-phone-sub{
  margin-top:2px;
  font-size:12px;
  line-height:1.4;
  color: rgba(15,23,42,.64);
}
.uprompt-phone-actions{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
}
.uprompt-actions{
  display:flex;
  justify-content:flex-end;
  gap:8px;
  margin: 2px -18px -18px;
  padding: 12px 18px;
  border-top:1px solid rgba(15,23,42,.08);
}
@media (max-width: 640px){
  .uprompt-phone{
    grid-template-columns:1fr;
  }
  .fs-backdrop{
    padding: 0;
  }
  .fs-panel{
    width: 100vw;
    height: 100dvh;
    border: 0;
  }
  .fs-body{
    padding: 0;
  }
  .fs-stage{
    border: 0;
  }
  .fit-preview-shell--fs{
    padding: 6px;
  }
  .fit-preview-frame--fs{
    width: 100%;
    max-width: none;
    height: 100%;
  }
  .tile-preview-active-shell--fs{
    inset: 0;
  }
}

.locked-title {
  font-weight: 700;
  font-size: 12px;
  opacity: 0.95;
}
.locked-sub {
  font-size: 12px;
  line-height: 1.25;
  opacity: 0.92;
}

/* Fullscreen locked overlay */
.fs-stage {
  position: relative;
}
.fsImg.locked, .fs-locked { }
.fs-locked {
  position: absolute;
  inset: auto 14px 14px 14px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.72);
  color: #fff;
  pointer-events: none;
}
.fs-cropper[disabled] {
  pointer-events: none;
}
/* =========================
 * Locked options tooltip (.iPop)
 * ========================= */
.lockedRow{
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  max-width: 100%;
}

.lockedTitle{
  font-size: 11px;
  font-weight: 700;
  color: rgba(15,23,42,.78);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

/* Info button (flat, icon only) */
.iBtn{
  height: 24px;
  width: 24px;
  padding: 0;
  display: inline-grid;
  place-items: center;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  border-radius: 0;
  cursor: pointer;
  color: rgba(15,23,42,.72);
}
.iBtn:hover{
  background: rgba(15,23,42,.03);
  border-color: rgba(15,23,42,.18);
}
.iBtn:active{ transform: translateY(1px); }
.iBtn:focus-visible{
  outline: none;
  border-color: rgba(239,68,68,.55);
  box-shadow: 0 0 0 4px rgba(239,68,68,.10);
}
.iBtn :deep(svg){ display:block; }

/* Tooltip panel */
.iPop{
  position: absolute;
  z-index: 1200;
  right: 0;
  top: calc(100% + 8px);

  width: min(320px, 88vw);
  padding: 10px 10px 10px;

  background: #fff;
  border: 1px solid rgba(15,23,42,.14);
  border-radius: 0;
  box-shadow: 0 18px 44px rgba(2, 6, 23, .10);

  color: rgba(15,23,42,.88);
}

/* little arrow */
.iPop::before{
  content:"";
  position:absolute;
  top: -7px;
  right: 10px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-left: 1px solid rgba(15,23,42,.14);
  border-top: 1px solid rgba(15,23,42,.14);
  transform: rotate(45deg);
}

/* header + list */
.iHead{
  font-size: 11px;
  font-weight: 700;
  color: rgba(15,23,42,.78);
  margin-bottom: 8px;
  text-transform: uppercase;
}

.iList{
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.iPill{
  display: inline-flex;
  align-items: center;

  font-size: 12px;
  font-weight: 700;
  line-height: 1;

  padding: 6px 8px;
  border: 1px solid rgba(15,23,42,.12);
  background: rgba(15,23,42,.03);
  border-radius: 0;
  color: rgba(15,23,42,.82);
}

.iPop .muted{
  font-size: 12px;
  font-weight: 700;
  color: rgba(15,23,42,.55);
}

/* ── Price overview overlay ─────────────────────────────── */
.tb-total-price {
  cursor: pointer;
  background: none;
  border: 1px solid rgba(15,23,42,.15);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 700;
  color: #141414;
  transition: background 0.1s, border-color 0.1s;
}
.tb-total-price:hover { background: rgba(15,23,42,.05); border-color: rgba(15,23,42,.25); }

.pov-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.pov-panel {
  background: #fff;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -4px 32px rgba(0,0,0,.15);
}
.pov-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #ececec;
  flex-shrink: 0;
}
.pov-title { font-size: 15px; font-weight: 700; color: #141414; }
.pov-close {
  background: none; border: none; cursor: pointer;
  color: #666; padding: 4px; border-radius: 4px;
  display: flex; align-items: center;
}
.pov-close:hover { background: rgba(0,0,0,.06); }
.pov-body { overflow-y: auto; flex: 1; padding: 12px 20px; }
.pov-group { margin-bottom: 18px; }
.pov-group-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700;
  color: #888; margin-bottom: 8px;
}
.pov-cat-thumb {
  width: 28px; height: 28px; border-radius: 4px; object-fit: cover;
  flex-shrink: 0; background: #f0f0f0;
}
.pov-cat-thumb--empty { background: #e8e8e8; }
.pov-row {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 0; border-bottom: 1px solid #f0f0f0;
}
.pov-row:last-child { border-bottom: none; }
.pov-thumb {
  width: 40px; height: 40px; border-radius: 4px; object-fit: cover;
  flex-shrink: 0; background: #f0f0f0;
}
.pov-thumb--empty { background: #e8e8e8; }
.pov-info { flex: 1; min-width: 0; }
.pov-name { display: block; font-size: 13px; font-weight: 600; color: #141414; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pov-meta { display: block; font-size: 12px; color: #888; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pov-right { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; gap: 2px; }
.pov-qty { font-size: 12px; color: #888; }
.pov-price { font-size: 13px; font-weight: 700; color: #141414; }
.pov-empty { color: #888; font-size: 13px; text-align: center; padding: 24px 0; }
.pov-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-top: 1px solid #ececec;
  flex-shrink: 0;
}
.pov-footer-label { font-size: 13px; color: #666; }
.pov-footer-price { font-size: 16px; font-weight: 700; color: #141414; }

/* "So wird produziert" – Umschalter + Legende */
.guides-toggle--on {
  color: var(--pfe-color-accent, #e30b17);
  border-color: currentColor;
}
.guides-toggle--cut {
  opacity: 0.8;
}
.guides-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  margin: 6px 0 2px;
  padding: 6px 10px;
  border: 1px dashed var(--pfe-color-border, rgba(15, 23, 42, 0.14));
  border-radius: 8px;
  font-size: 12px;
  color: var(--pfe-color-muted, rgba(15, 23, 42, 0.62));
}
.guides-legend__title {
  color: var(--pfe-color-text, #0f172a);
  font-weight: 600;
}
.guides-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.guides-legend__swatch {
  display: inline-block;
  width: 22px;
  height: 12px;
  border-radius: 2px;
  box-sizing: border-box;
}
.guides-legend__swatch--cut {
  border: 2px dashed #0f172a;
}
.guides-legend__swatch--bleed {
  background: repeating-linear-gradient(45deg, rgba(15, 23, 42, 0.55) 0 2px, rgba(15, 23, 42, 0.12) 2px 6px);
}
.guides-legend__swatch--safe {
  border: 1px dotted rgba(15, 23, 42, 0.7);
}

/* Tag-Filter + Tag-Dialog (Alben) */
.tag-filter{ display:inline-flex; align-items:center; gap:4px; height:32px; padding:0 8px 0 10px; border:1px solid rgba(15,23,42,.16); border-radius:8px; background:#fff; font-size:12px; max-width:220px; }
.tag-filter[data-on="1"]{ border-color:var(--pfe-color-accent,#e30b17); box-shadow:0 0 0 2px rgba(227,11,23,.12); }
.tag-filter__ico{ font-weight:700; opacity:.55; }
.tag-filter__select{ border:0; background:transparent; font:inherit; font-size:12px; max-width:170px; text-overflow:ellipsis; cursor:pointer; outline:none; }
.tag-filter--mobile{ margin-top:6px; max-width:100%; }
.tagdlg-backdrop{ position:fixed; inset:0; z-index:9000; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:16px; }
.tagdlg{ width:min(440px,100%); background:#fff; border-radius:14px; padding:16px; display:grid; gap:10px; box-shadow:0 20px 60px rgba(15,23,42,.35); font-size:14px; }
.tagdlg__title{ font-size:15px; }
.tagdlg__input{ height:40px; padding:0 12px; border:1px solid rgba(15,23,42,.2); border-radius:8px; font:inherit; }
.tagdlg__chips{ display:flex; flex-wrap:wrap; gap:6px; }
.tagdlg__chip{ border:1px solid rgba(15,23,42,.16); border-radius:999px; background:#fff; padding:4px 12px; font:inherit; font-size:12px; cursor:pointer; }
.tagdlg__chip:hover{ border-color:var(--pfe-color-accent,#e30b17); color:var(--pfe-color-accent,#e30b17); }
.tagdlg__chip--remove:hover{ background:#fdecea; }
.tagdlg__help{ margin:0; font-size:12px; color:rgba(15,23,42,.6); }
.tagdlg__actions{ display:flex; justify-content:flex-end; gap:8px; }
</style>
